(function () {
  'use strict';

  /* ============================================
     LOCALE-AWARE PATHS
     Shopify serves secondary languages under a prefix — Shopify.routes.root is "/"
     in English and "/ar/" in Arabic. Liquid links get this free via routes.*; paths
     built in JS do not. Verified on the /ar preview: Liquid rendered
     /ar/collections/courses while our JS rendered /collections/courses on the SAME
     page, which would silently kick an Arabic visitor back to the English site.
     Cart AJAX (/cart/*.js) and the app proxy (/apps/saif/*) are deliberately NOT
     routed through this — they are session/proxy endpoints, not navigation, and a
     wrong prefix there breaks checkout.
     ============================================ */
  function saifPath(p) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/+$/, '') + p;
  }

  // Verbose debug logging, silenced in production so visitors don't get internal
  // state / clip URLs in their console. Set window.SAIF_STUDIO_DEBUG = true in the
  // console (or flip the var) to surface the trace. console.warn / console.error
  // are always emitted. All studio console.log calls go through slog(). (review)
  var SAIF_STUDIO_DEBUG = false;
  function slog() { if (SAIF_STUDIO_DEBUG || (typeof window !== 'undefined' && window.SAIF_STUDIO_DEBUG)) console.log.apply(console, arguments); }

  // Hard ceiling on recording length. Anything past this auto-stops so a forgotten
  // tab / sleeping mic can't grow into a multi-hour upload that fills R2.
  // Keep in sync with MAX_RECORDING_BYTES in the Worker — the Worker cap is the
  // authoritative one (frontend can be bypassed) but matching the two avoids
  // surprise rejections for legitimate users near the time limit.
  var MAX_RECORDING_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  // One label everywhere a take can be sent to the instructor — the history
  // rows and the result-view action button must read the same (REQ-COPY-001).
  var SUBMIT_LABEL = 'Submit for feedback';

  /* ==============================================
     PRE-COMPUTED PITCH DATA
     For static maqam-478-*.mp3 clips, scripts/build-maqam-pitch-data.js
     ships pitch data as JSON so the studio doesn't have to fetch+decode+
     analyze on every clip click. The data file is tiny relative to the
     audio (a few hundred KB total for 96 clips) and lets the studio
     populate refPitchData synchronously after one network fetch on init.
     ============================================== */
  var pitchManifestPromise = null;

  function loadPitchManifest() {
    if (pitchManifestPromise) return pitchManifestPromise;
    var url = (window.SaifMaqamPitchDataUrl || '');
    if (!url) {
      pitchManifestPromise = Promise.resolve(null);
      return pitchManifestPromise;
    }
    pitchManifestPromise = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return pitchManifestPromise;
  }

  // Kick off the manifest fetch as soon as the script parses so the JSON
  // is in flight before the user clicks Practice. Result is cached for the
  // lifetime of the page.
  loadPitchManifest();

  function extractAssetName(url) {
    if (!url) return null;
    try {
      // Strip query string + path; take the file name. Works for Shopify CDN
      // URLs (cdn.shopify.com/.../maqam-478-x-00.mp3?v=123) and bare paths.
      var noQuery = String(url).split('?')[0].split('#')[0];
      var parts = noQuery.split('/');
      var name = parts[parts.length - 1];
      return name || null;
    } catch (e) { return null; }
  }

  /**
   * Returns the cached pitch series for a maqam-478 clip URL, or null if
   * either (a) the manifest isn't loaded yet, (b) this clip isn't in the
   * pre-computed dataset, or (c) the manifest fetch failed. Synchronous —
   * the manifest must already be resolved. Caller falls back to live decode.
   */
  function lookupPrecomputedPitch(url) {
    if (!pitchManifestPromise || !pitchManifestPromise._resolvedValue) return null;
    var manifest = pitchManifestPromise._resolvedValue;
    if (!manifest || !manifest.items) return null;
    var name = extractAssetName(url);
    if (!name) return null;
    var raw = manifest.items[name];
    if (!raw) return null;
    // Stored as [[time, freq], ...] to halve JSON size; unpack to objects
    // so the rest of the code doesn't need to change.
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      out.push({ time: raw[i][0], freq: raw[i][1] });
    }
    return out;
  }

  // Stash the resolved manifest on the promise object once it lands so
  // lookupPrecomputedPitch can stay synchronous (analyzeReference's caller
  // expects an immediate decision: precomputed or live decode).
  loadPitchManifest().then(function (m) {
    pitchManifestPromise._resolvedValue = m;
    if (m && m.items) {
      slog('[saif-practice] pitch manifest loaded —', Object.keys(m.items).length, 'clips');
    }
  });

  /* ==============================================
     VOCAL RANGE — fit engine + saved-range cache
     Shared by the Range-tab state machine (the maqam/clip prescription) and the
     reference picker (fit badges). Per-clip range stats are computed from the
     already-loaded pitch manifest — there is no separate index asset to build
     or keep in sync. See docs/practice-studio.md.
     ============================================== */
  var MAQAM_CLIP_BASE = 'https://api.saifvoice.net/maqam-clips/';
  function capWord(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  // One-line personality for each maqam family — layman-facing copy on the
  // range result's hero card. Keys match the maqam-478-<handle> file handles.
  var MAQAM_MOODS = {
    bayati:   'Reflective & melancholic',
    hijaz:    'Dramatic & spiritual',
    saba:     'Deeply emotional & sorrowful',
    rast:     'Balanced & neutral',
    nahawand: 'Gentle & beautiful',
    ajam:     'Bright & joyful',
    kurd:     'Tender & contemplative',
    sikah:    'Mystical & ethereal'
  };

  var _clipStatsCache = {};

  // p05 / p95 / median MIDI for a manifest clip, memoized. null when the clip
  // isn't in the loaded manifest or has too few points to be meaningful.
  function clipRangeStats(name) {
    var key = extractAssetName(name);
    if (!key) return null;
    if (Object.prototype.hasOwnProperty.call(_clipStatsCache, key)) return _clipStatsCache[key];
    var manifest = pitchManifestPromise && pitchManifestPromise._resolvedValue;
    var raw = manifest && manifest.items && manifest.items[key];
    if (!raw || raw.length < 20) { _clipStatsCache[key] = null; return null; }
    var midis = [];
    for (var i = 0; i < raw.length; i++) {
      var f = raw[i][1];
      if (f > 0) midis.push(freqToMidi(f));
    }
    if (midis.length < 20) { _clipStatsCache[key] = null; return null; }
    // The precomputed pitch data carries autocorrelation octave-jumps (frames
    // that lock onto a harmonic/subharmonic). Left raw, they inflate a clip's
    // p05–p95 range by ~5 semitones (median span 13, some clips 28!) so almost
    // nothing "fits" a real voice. Fold every frame toward the clip's own median
    // — the same octave repair medianSmoothPitch applies for scoring — so the
    // range reflects the true melody (median span ~8), not the artifacts.
    var byPitch = midis.slice().sort(function (a, b) { return a - b; });
    var med = byPitch[byPitch.length >> 1];
    var folded = midis.map(function (x) {
      var v = x;
      while (v - med > 6) v -= 12;
      while (med - v > 6) v += 12;
      return v;
    }).sort(function (a, b) { return a - b; });
    function pct(p) {
      var idx = Math.floor(p * (folded.length - 1));
      return folded[Math.max(0, Math.min(folded.length - 1, idx))];
    }
    var stats = { lo: pct(0.05), hi: pct(0.95), med: pct(0.5) };
    _clipStatsCache[key] = stats;
    return stats;
  }

  // Comfortable singing zone derived from the tested extremes (pull each end in
  // by 2 semitones). Never stored, so the formula can change without migration.
  function rangeComfortZone(range) {
    if (!range) return null;
    var lo = Number(range.lowMidi), hi = Number(range.highMidi);
    if (!isFinite(lo) || !isFinite(hi) || hi <= lo) return null;
    // Pull each end in by 1 semitone. (Only 1 now that capture is full-voice —
    // the tested extremes are already comfortable, so a bigger shave was
    // double-penalizing narrow voices and making "nothing fit".)
    var zlo = lo + 1, zhi = hi - 1;
    if (zhi < zlo) { zlo = lo; zhi = hi; } // very narrow range — don't invert
    return { lo: zlo, hi: zhi };
  }

  // Classify a clip's {lo,hi,med} stats against a comfort zone.
  function classifyClipStats(stats, zone) {
    if (!stats || !zone) return null;
    if (stats.lo >= zone.lo - 1 && stats.hi <= zone.hi + 1) return { fit: 'good', delta: 0 };
    if (stats.hi > zone.hi) return { fit: 'high', delta: Math.max(1, Math.round(stats.hi - zone.hi)) };
    return { fit: 'low', delta: Math.max(1, Math.round(zone.lo - stats.lo)) };
  }

  // (Removed: rangeFitLabel / rangeFitTitle — the "Fits your voice / Sits high /
  //  Well above your range" copy. Every maqam suits every voice; the reference
  //  auto-transposes to the student's key instead of being labelled by fit.)

  // Saved-range cache so surfaces (picker badges) can read synchronously.
  var _userRange = undefined;   // undefined = not loaded, null = none, object = range
  var _userRangePromise = null;
  function loadUserRange() {
    if (_userRangePromise) return _userRangePromise;
    _userRangePromise = new Promise(function (resolve) {
      if (!window.SaifSync || !window.SaifSync.vocalRange) { resolve(null); return; }
      window.SaifSync.vocalRange.get().then(function (r) {
        resolve((r && r.ok && r.range) ? r.range : null);
      }).catch(function () { resolve(null); });
    }).then(function (range) { _userRange = range; return range; });
    return _userRangePromise;
  }
  function getUserRangeSync() { return _userRange || null; }
  function setUserRange(range) {
    _userRange = range || null;
    _userRangePromise = Promise.resolve(_userRange);
  }

  /**
   * Rank the CORE clips (maqam-478-<fam>-NN, NN<=11 — stable counts, always in
   * the core manifest) against a comfort zone. Never returns an empty hero when
   * any core clip has stats: if nothing classifies 'good', hero falls back to
   * the closest clip so the result screen is never a dead end.
   * Returns { hero, alternates, good, goodCount, closest }; each entry is
   * { name, fam, n, stats, res, margin } where margin = comfort headroom.
   * `good` (sorted by margin desc) is the single ranking source the picker
   * shelf also consumes, so the two surfaces never disagree on the best clip.
   */
  function rangePickBest(zone) {
    var out = { hero: null, alternates: [], good: [], goodCount: 0, closest: [] };
    var manifest = pitchManifestPromise && pitchManifestPromise._resolvedValue;
    if (!zone || !manifest || !manifest.items) return out;
    var center = (zone.lo + zone.hi) / 2;
    Object.keys(manifest.items).forEach(function (name) {
      var m = name.match(/^maqam-478-([a-z]+)-(\d+)\.mp3$/);
      if (!m || parseInt(m[2], 10) > 11) return; // core clips only
      var stats = clipRangeStats(name);
      if (!stats) return;
      var res = classifyClipStats(stats, zone);
      if (!res) return;
      var entry = {
        name: name, fam: m[1], n: parseInt(m[2], 10), stats: stats, res: res,
        margin: Math.min(zone.hi - stats.hi, stats.lo - zone.lo),
        _center: Math.abs(stats.med - center)
      };
      out.closest.push(entry);
      if (res.fit === 'good') out.good.push(entry);
    });
    out.closest.sort(function (a, b) {
      if (a.res.delta !== b.res.delta) return a.res.delta - b.res.delta;
      return a._center - b._center;
    });
    out.good.sort(function (a, b) {
      if (b.margin !== a.margin) return b.margin - a.margin;
      return a._center - b._center;
    });
    out.goodCount = out.good.length;
    if (out.good.length) {
      out.hero = out.good[0];
      var perFam = {};
      perFam[out.hero.fam] = 1;
      for (var i = 1; i < out.good.length && out.alternates.length < 3; i++) {
        var g = out.good[i];
        if ((perFam[g.fam] || 0) >= 2) continue; // <=2 per family (incl. hero's)
        perFam[g.fam] = (perFam[g.fam] || 0) + 1;
        out.alternates.push(g);
      }
    } else if (out.closest.length) {
      out.hero = out.closest[0];
    }
    return out;
  }

  /* ==============================================
     INLINE CLIP PREVIEW — one shared lazy Audio()
     Used by the range result (hero/alternates) and the picker shelf/chips so a
     student can choose "by ear". SEPARATE element from the studio's reference
     player (refAudioEl) — never reuse it (its pause listener pauses an in-flight
     MediaRecorder). Only one preview plays at a time, mirroring the maqam
     library's card player.
     ============================================== */
  var _previewAudio = null;   // created on first toggle (user gesture — play() allowed)
  var _previewBtn = null;     // button currently showing the playing state
  var _previewUrl = '';

  function _previewSetBtn(btn, playing) {
    if (!btn) return;
    btn.classList.toggle('saif-practice__preview-btn--playing', !!playing);
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    if (btn.getAttribute('data-preview-label')) {
      btn.textContent = playing ? '❚❚ Pause' : '▶ Listen';
      // Keep the visible verb in the accessible name (WCAG 2.5.3 Label in Name)
      // so voice-control users can say "click Listen".
      btn.setAttribute('aria-label', (playing ? 'Pause ' : 'Listen to ') + (btn.getAttribute('data-clip-label') || 'clip'));
    } else {
      btn.textContent = playing ? '❚❚' : '▶';
    }
  }
  function previewStop() {
    if (_previewAudio) { try { _previewAudio.pause(); } catch (e) {} }
    if (_previewBtn) { _previewSetBtn(_previewBtn, false); _previewBtn = null; }
  }
  function previewToggle(url, btn) {
    if (_previewBtn === btn && _previewAudio && !_previewAudio.paused) { previewStop(); return; }
    previewStop();
    // Pause the studio's reference player so two clips never overlap.
    try {
      var st = window.SaifPracticeStudio;
      if (st && st.refAudioEl && !st.refAudioEl.paused) st.refAudioEl.pause();
    } catch (e) {}
    if (!_previewAudio) {
      _previewAudio = new Audio();
      _previewAudio.preload = 'none'; // nothing fetches until the first tap sets .src
      _previewAudio.addEventListener('ended', previewStop);
      _previewAudio.addEventListener('pause', function () {
        // External pause (OS media keys, natural end) clears the button — but a
        // DEFERRED pause task from a same-URL switch (src unchanged, so the media
        // load algorithm doesn't flush it) must NOT clobber the clip now playing.
        // Only act when the element is genuinely paused.
        if (_previewBtn && (!_previewAudio || _previewAudio.paused)) {
          _previewSetBtn(_previewBtn, false);
          _previewBtn = null;
        }
      });
    }
    if (_previewUrl !== url) { _previewAudio.src = url; _previewUrl = url; }
    try { _previewAudio.currentTime = 0; } catch (e) {}
    var p = _previewAudio.play();
    if (p && typeof p.catch === 'function') p.catch(function () { previewStop(); });
    _previewBtn = btn;
    _previewSetBtn(btn, true);
  }
  /** ▶ button factory — chips place it as a SIBLING (never nested in a button). */
  function createPreviewButton(url, clipLabel) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'saif-practice__preview-btn';
    b.setAttribute('aria-label', 'Preview ' + clipLabel);
    b.setAttribute('aria-pressed', 'false');
    b.setAttribute('data-clip-label', clipLabel || 'clip');
    b.textContent = '▶';
    b.addEventListener('click', function (ev) {
      ev.stopPropagation(); // don't trigger the adjacent choose/practice button
      previewToggle(url, b);
    });
    return b;
  }

  /**
   * Per-maqam pitch extensions. The core manifest carries the curated 12
   * clips per maqam + all drills; the remaining maqam-478 dataset clips
   * (~45-59 per family) live in eight maqam-478-pitch-data-<handle>.json
   * files next to it, fetched only when a clip from that family misses the
   * core lookup. Fetched families merge into the in-memory manifest so
   * later lookups are synchronous. Resolves null (→ live decode fallback)
   * for non-dataset URLs or fetch failures.
   */
  var extendedPitchFetches = {};

  // Fire-and-forget warm-up used by the maqam library: it calls this when a
  // family's examples gallery opens, so the ~380KB (gzipped) pitch file is
  // usually already local by the time the user clicks Practice on a clip.
  window.SaifPrefetchPitchFamily = function (handle) {
    try { fetchExtendedPitch('maqam-478-' + String(handle) + '-00.mp3'); } catch (e) {}
  };

  function fetchExtendedPitch(url) {
    var name = extractAssetName(url);
    var m = name && name.match(/^maqam-478-([a-z]+)-\d+\.mp3$/);
    if (!m) return Promise.resolve(null);
    var handle = m[1];
    var base = (window.SaifMaqamPitchDataUrl || '');
    // Matches both the base manifest name and a versioned "-v2" rebuild, so the
    // per-family extension URL is derived correctly whichever the snippet pins
    // (pre-prod may point at maqam-478-pitch-data-v2.json; live at the original).
    if (!base || !/maqam-478-pitch-data(-v2)?\.json/.test(base)) return Promise.resolve(null);
    if (!extendedPitchFetches[handle]) {
      var extUrl = base.replace(/maqam-478-pitch-data(-v2)?\.json/, 'maqam-478-pitch-data-' + handle + '$1.json');
      extendedPitchFetches[handle] = fetch(extUrl)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && data.items && pitchManifestPromise && pitchManifestPromise._resolvedValue &&
              pitchManifestPromise._resolvedValue.items) {
            var items = pitchManifestPromise._resolvedValue.items;
            for (var k in data.items) items[k] = data.items[k];
          }
          return data;
        })
        .catch(function () { return null; });
    }
    return extendedPitchFetches[handle].then(function (data) {
      if (!data || !data.items || !data.items[name]) return null;
      var raw = data.items[name];
      var out = [];
      for (var i = 0; i < raw.length; i++) out.push({ time: raw[i][0], freq: raw[i][1] });
      return out;
    });
  }

  /**
   * Collapse a decoded AudioBuffer to one mono channel and peak-normalize it so
   * quiet or one-sided-stereo recordings clear detectPitch's loudness gate.
   * Real uploads are often mastered well below 0 dBFS and/or have the voice
   * weaker on the left channel — analyzing only channel 0 at the raw level made
   * the vast majority of frames fall under the rms floor, so almost no pitch
   * points came out and the graph stayed empty. Averaging every channel and
   * lifting the peak to ~0.9 recovers the signal. Pitch is amplitude-invariant
   * (autocorrelation finds the period regardless of scale), so the detected
   * frequencies are unchanged — we simply keep far more frames.
   */
  function toMonoNormalized(audioBuffer) {
    var len = audioBuffer.length;
    var chCount = audioBuffer.numberOfChannels || 1;
    var data = new Float32Array(len);
    for (var c = 0; c < chCount; c++) {
      var cd = audioBuffer.getChannelData(c);
      for (var n = 0; n < len; n++) data[n] += cd[n];
    }
    if (chCount > 1) { for (var n = 0; n < len; n++) data[n] /= chCount; }
    var peak = 0;
    for (var n = 0; n < len; n++) { var a = data[n] < 0 ? -data[n] : data[n]; if (a > peak) peak = a; }
    // Boost only (never attenuate); skip effectively-silent buffers; cap the
    // gain so a near-silent file's noise floor isn't amplified into false pitch.
    if (peak > 0.005 && peak < 0.9) {
      var gain = Math.min(0.9 / peak, 30);
      for (var n = 0; n < len; n++) data[n] *= gain;
    }
    return data;
  }

  /**
   * RMS-normalize a mono signal to a target loudness (returns a NEW array).
   * Used to match an uploaded reference's PLAYBACK loudness to the curated
   * clips. Those are loudness-normalized (~0.13 RMS) not peak-normalized — peaks
   * vary 0.3–0.9 — so we match RMS, not peak: peak-matching would over-amplify a
   * quiet-but-transient upload into something LOUDER than the curated clips
   * (more mic bleed). A peak limiter still prevents clipping, and the gain is
   * capped so a near-silent file's noise floor isn't blown up. Pure + testable.
   */
  function rmsNormalize(mono, targetRms) {
    var n = mono.length, peak = 0, sq = 0;
    for (var i = 0; i < n; i++) { var a = mono[i] < 0 ? -mono[i] : mono[i]; if (a > peak) peak = a; sq += mono[i] * mono[i]; }
    var rms = Math.sqrt(sq / (n || 1));
    var out = new Float32Array(n);
    if (rms < 1e-4 || peak < 1e-4) { for (var j = 0; j < n; j++) out[j] = mono[j]; return out; } // silent → leave
    var gain = targetRms / rms;
    if (peak * gain > 0.98) gain = 0.98 / peak; // never clip
    if (gain > 30) gain = 30;                   // don't amplify a near-silent file's floor into noise
    for (var k = 0; k < n; k++) out[k] = mono[k] * gain;
    return out;
  }

  /**
   * Encode a mono Float32 signal as a 16-bit PCM WAV (returns a Uint8Array).
   * Pure — no Blob/URL — so it is byte-verified in Node (test-loudness.mjs).
   */
  function encodeWavBytes(samples, sampleRate) {
    var n = samples.length;
    var buf = new ArrayBuffer(44 + n * 2);
    var view = new DataView(buf);
    function wstr(off, s) { for (var i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); }
    wstr(0, 'RIFF'); view.setUint32(4, 36 + n * 2, true); wstr(8, 'WAVE');
    wstr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    wstr(36, 'data'); view.setUint32(40, n * 2, true);
    // Write the PCM samples through an Int16Array view (native-endian) rather
    // than per-sample DataView.setInt16 — materially faster on the big per-sample
    // loop of a long upload. WAV is little-endian and every target browser runs
    // little-endian, so a direct Int16 write is byte-identical (round-trip
    // verified in test-loudness.mjs). The header above stays DataView (explicit LE).
    var pcm = new Int16Array(buf, 44, n); // byteOffset 44 is 2-aligned
    for (var i = 0; i < n; i++) {
      var s = samples[i]; if (s > 1) s = 1; else if (s < -1) s = -1;
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return new Uint8Array(buf);
  }

  /**
   * Build a loudness-matched WAV blob URL for PLAYBACK of an uploaded reference
   * (mono mixdown → RMS-normalize → 16-bit WAV → blob URL). PLAYBACK SOURCE
   * only — no Web Audio graph, no gain node, no MediaElementSource — so it can't
   * touch the recording/analyser/bleed path. Returns null on any failure (the
   * caller then keeps the raw-file playback it already loaded).
   */
  function toPlaybackWavUrl(audioBuffer, targetRms) {
    try {
      var len = audioBuffer.length, ch = audioBuffer.numberOfChannels || 1;
      var mono = new Float32Array(len);
      for (var c = 0; c < ch; c++) { var cd = audioBuffer.getChannelData(c); for (var n = 0; n < len; n++) mono[n] += cd[n]; }
      if (ch > 1) for (var m = 0; m < len; m++) mono[m] /= ch;
      var bytes = encodeWavBytes(rmsNormalize(mono, targetRms), audioBuffer.sampleRate);
      return URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
    } catch (e) { return null; }
  }

  /**
   * Live-path mirror of toMonoNormalized for a single analyser frame. With
   * autoGainControl off, a soft or far-from-mic voice is no longer boosted by
   * the browser, so its frames fall under detectPitch's absolute rms gate and
   * produce no pitch points. This lifts frames using a SESSION-LEVEL peak
   * (tracker.peak, boost-only, slow ~6%/s decay at 60fps) rather than per-frame
   * gain — so the gain anchors to the speaker's own voiced level and, unlike
   * browser AGC, does NOT ramp up during breaths/pauses hunting for signal
   * (which is what caused the false-pitch pumping we turned AGC off to stop).
   * Returns the original array untouched when no boost is needed; otherwise a
   * scaled COPY, so waveform/level displays keep showing the true input level.
   */
  function adaptiveBoostFrame(arr, tracker, floorRatio) {
    var peak = 0, rms = 0;
    for (var i = 0; i < arr.length; i++) {
      var a = arr[i] < 0 ? -arr[i] : arr[i];
      if (a > peak) peak = a;
      rms += arr[i] * arr[i];
    }
    rms = Math.sqrt(rms / arr.length);
    tracker.framePeak = peak; // this frame's own peak — used by the reference-bleed gate
    // Session anchor: instant-set on first voicing, then ATTACK-CLAMPED to
    // ~6%/frame. A genuinely loud voice still reaches its true level in ~0.3s,
    // but a 1-5 frame transient (cough, mic bump, plosive) can no longer pin
    // the anchor high and starve a soft speaker's boost for the next minute.
    var prev = tracker.peak || 0;
    // Unvoiced-aware decay: the caller's loop counts consecutive unvoiced frames
    // in tracker.unvoicedRun. After ~0.5s of silence the anchor decays ~4x faster
    // (0.996 vs 0.999/frame) so a loud phrase's stale anchor can't starve the
    // quiet start of the NEXT phrase (the relative gate and the bleed quietBound
    // both scale off this anchor — a stale-high value ate soft onsets).
    var decay = (tracker.unvoicedRun || 0) > 30 ? 0.996 : 0.999;
    var next = Math.max(peak, prev * decay);
    if (prev > 0.003 && next > prev * 1.06) next = prev * 1.06;
    tracker.peak = next;
    var p = tracker.peak;
    // Engage floor 0.003 (was 0.008): with autoGainControl off, conservative
    // laptop/phone input gains put normal singing at peak 0.004-0.008 — the old
    // floor refused to boost exactly those users, so detectPitch's rms<0.015
    // gate rejected every frame and "the studio doesn't pick up my voice".
    // The clarity gate (maxVal/c[0] >= 0.5) backstops noise false-voicing that
    // the higher floor implicitly suppressed.
    if (p <= 0.003 || p >= 0.9) return arr; // silent, or already a healthy level
    // Relative noise gate: breaths/room noise sit far below the speaker's own
    // voiced level (this frame's rms vs the session anchor). Boosting them
    // would feed detectPitch false pitch AND keep freq>0 through breaths,
    // defeating _liveDespikeFreq's voicing-gap reset. >26dB below the anchor
    // is treated as silence — returned unboosted so the absolute rms gate
    // rejects it, exactly like an unboosted quiet frame.
    if (rms < p * (floorRatio || 0.05)) return arr;
    var gain = Math.min(0.9 / p, 60);
    var out = new Float32Array(arr.length);
    for (var j = 0; j < arr.length; j++) out[j] = arr[j] * gain;
    return out;
  }

  /* ==============================================
     PITCH DETECTION — Autocorrelation method
     Returns frequency in Hz or -1 if too quiet
     ============================================== */
  function detectPitch(buffer, sampleRate) {
    var SIZE = buffer.length;
    var rms = 0;
    for (var i = 0; i < SIZE; i++) rms += buffer[i] * buffer[i];
    rms = Math.sqrt(rms / SIZE);
    if (rms < 0.015) return -1;

    var r1 = 0, r2 = SIZE - 1;
    var threshold = 0.2;
    for (var i = 0; i < SIZE / 2; i++) {
      if (Math.abs(buffer[i]) < threshold) { r1 = i; break; }
    }
    for (var i = 1; i < SIZE / 2; i++) {
      if (Math.abs(buffer[SIZE - i]) < threshold) { r2 = SIZE - i; break; }
    }

    buffer = buffer.slice(r1, r2);
    SIZE = buffer.length;

    // Bound the autocorrelation to the lags that map to the 60–1500 Hz pitch
    // band: a period below sr/1500 or above sr/60 can never win — it's rejected
    // by the band check below — so computing those lags is pure waste. This makes
    // the loop O(SIZE·maxLag) instead of O(SIZE²): ~40% fewer multiply-accumulates
    // per frame on the hot detection path (the #1 mobile-jank cost). Verified
    // mathematically identical to the old full-lag search across the whole
    // synthetic battery (test-pitch-accuracy.mjs); matches the manifest builder.
    var minLag = Math.max(1, Math.floor(sampleRate / 1500));
    var maxLag = Math.min(SIZE - 1, Math.ceil(sampleRate / 60));
    if (maxLag <= minLag) return -1;

    var c = new Float32Array(maxLag + 1);
    for (var j0 = 0; j0 < SIZE; j0++) c[0] += buffer[j0] * buffer[j0]; // zero-lag energy for the clarity gate
    for (var i = 1; i <= maxLag; i++) {
      var sum = 0, lim = SIZE - i;
      for (var j = 0; j < lim; j++) sum += buffer[j] * buffer[j + i];
      c[i] = sum;
    }

    // Walk past the initial declining region, then take the peak within the band.
    var d = minLag;
    while (d < maxLag && c[d] > c[d + 1]) d++;
    if (d >= maxLag) return -1;

    var maxVal = -1, maxPos = -1;
    for (var i2 = d; i2 <= maxLag; i2++) {
      if (c[i2] > maxVal) { maxVal = c[i2]; maxPos = i2; }
    }

    if (maxPos === -1 || maxVal < 0.01) return -1;

    // Normalized clarity gate: the peak autocorrelation vs the zero-lag energy.
    // Noise, room tone, and breath make a weak, blurry peak (ratio far below a
    // real periodic voice), so this rejects the phantom pitch the absolute
    // maxVal<0.01 test let through — the student's trail no longer invents notes
    // during breaths/silences. Measured: pink-noise false-voicing 80%→0% with no
    // voiced-case regression (test-pitch-accuracy.mjs). Live student capture only
    // — the manifest builders don't carry this (references are octave-coherence
    // corrected on load via coherReferenceContour).
    if (c[0] > 0 && (maxVal / c[0]) < 0.5) return -1;

    // Parabolic interpolation for sub-sample accuracy
    var y1 = maxPos > 0 ? c[maxPos - 1] : c[maxPos];
    var y2 = c[maxPos];
    var y3 = maxPos < maxLag ? c[maxPos + 1] : c[maxPos];
    var shift = (y3 - y1) / (2 * (2 * y2 - y1 - y3));
    var refined = maxPos + (isNaN(shift) ? 0 : shift);

    var freq = sampleRate / refined;
    return (freq > 60 && freq < 1500) ? freq : -1;
  }

  /* ==============================================
     FREQUENCY → NOTE HELPERS
     ============================================== */
  function freqToMidi(freq) {
    return 12 * Math.log2(freq / 440) + 69;
  }

  function freqToNote(freq) {
    var notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    var midi = Math.round(freqToMidi(freq));
    return notes[((midi % 12) + 12) % 12] + Math.floor(midi / 12 - 1);
  }

  function midiToFreq(midi) {
    return 440 * Math.pow(2, (midi - 69) / 12);
  }

  /**
   * Despike a pitch contour. The autocorrelation detector occasionally locks
   * onto a harmonic and reports double/triple the true pitch (an octave jump),
   * and breath/onset transients produce one-frame spikes. Either wrecks the
   * steadiness + consistency scores and makes the pitch trail jump around.
   *
   * For each sample we take the median of a small window (±2 samples) in MIDI
   * (log-frequency) space. If the sample sits more than `thr` semitones off
   * that local median, it's an outlier: if it's about an octave away we FOLD it
   * back toward the median's octave (recovers the real pitch class); otherwise
   * we snap it to the median. In-range samples are left untouched, so genuine
   * melodic movement is preserved — only the spikes are repaired.
   *
   * Returns a NEW array of {time, freq}; the input is not mutated.
   */
  function medianSmoothPitch(data, thr) {
    if (!data || data.length < 3) return data ? data.slice() : data;
    var T = thr || 1.6;
    var R = 2;
    var midis = new Array(data.length);
    for (var i = 0; i < data.length; i++) midis[i] = freqToMidi(data[i].freq);
    var out = new Array(data.length);
    for (var i2 = 0; i2 < data.length; i2++) {
      var lo = Math.max(0, i2 - R), hi = Math.min(data.length - 1, i2 + R);
      var win = [];
      for (var k = lo; k <= hi; k++) win.push(midis[k]);
      win.sort(function (a, b) { return a - b; });
      var med = win[Math.floor(win.length / 2)];
      var m = midis[i2];
      if (Math.abs(m - med) > T) {
        // Fold toward the median's octave; if that lands within tolerance it
        // was an octave error — use it. Otherwise it's a real jump/spike →
        // snap to the local median so it can't poison the scores.
        var folded = m;
        while (folded - med > 6) folded -= 12;
        while (med - folded > 6) folded += 12;
        var useMidi = (Math.abs(folded - med) <= T) ? folded : med;
        out[i2] = { time: data[i2].time, freq: midiToFreq(useMidi) };
      } else {
        out[i2] = { time: data[i2].time, freq: data[i2].freq };
      }
    }
    // SEGMENT-ONSET repair. The ±R window above runs in ARRAY index space, so
    // at a phrase boundary it straddles the silence and mixes two phrases'
    // pitches; and a 2-3 frame octave lock at a phrase START is its own local
    // majority, so it survives the pass untouched — the Compare chart then
    // shows a spike at exactly the "mic first picks up audio" moment, and the
    // score pays for it. Split into continuity runs (gap > 0.15s) and re-anchor
    // the FIRST two samples of each run against the median of the next 5
    // samples inside the run. Fold-only (never value-snap) so a genuine
    // new-register phrase start survives. STARTS ONLY: the detector's octave
    // locks happen at onset (low periodicity while the voice ramps up); a leap
    // in a run's final frames is usually a real musical figure — repairing
    // ends destroyed genuine phrase-final octave leaps (caught by
    // test-pitch-accuracy.mjs's leap-preservation battery).
    var runStart = 0;
    for (var e = 1; e <= out.length; e++) {
      var boundary = (e === out.length) || (out[e].time - out[e - 1].time > 0.15);
      if (!boundary) continue;
      if (e - runStart >= 5) {
        for (var ei = 0; ei < 2; ei++) {
          var idx = runStart + ei;
          // Median of the next 5 in-run samples after the onset pair (they may
          // share the same octave lock, so exclude both).
          var inner = [];
          for (var q = runStart + 2; q < e && inner.length < 5; q++) inner.push(freqToMidi(out[q].freq));
          if (inner.length < 2) continue;
          inner.sort(function (a, b) { return a - b; });
          var rmed = inner[inner.length >> 1];
          var em = freqToMidi(out[idx].freq);
          var ef = em;
          while (ef - rmed > 6) ef -= 12;
          while (rmed - ef > 6) ef += 12;
          if (ef !== em && Math.abs(ef - rmed) <= T) {
            out[idx] = { time: out[idx].time, freq: midiToFreq(ef) };
          }
        }
      }
      runStart = e;
    }
    return out;
  }

  /**
   * Octave-coherence pass for REFERENCE contours. The precomputed pitch data
   * was built with an autocorrelation detector that octave-locks on low,
   * harmonic-rich recitation — up to ~44% of a clip's frames get drawn a full
   * octave off (measured; docs/practice-studio-accuracy.md). Their true pitch
   * CLASS is intact (octave-folding a clip collapses the confusion to ~0), so
   * we snap each stray frame back into the clip's own register.
   *
   * Two stages, so a genuinely drifting tessitura survives while dense
   * octave-locked runs are still fixed:
   *   1. Fold every frame toward the GLOBAL median — a robust anchor that holds
   *      even when >50% of a short span is octave-locked.
   *   2. Rolling median of that coherent contour → a smooth register track that
   *      follows slow, genuine register drift.
   *   3. Fold the ORIGINAL frames toward that track; in-register frames are left
   *      exactly as they were.
   * Validated across all 122 core clips: mean confusion 9.3%→1.9%, worst
   * 44%→1%, and 0 already-coherent clips disturbed (test-pitch-accuracy.mjs
   * --manifest). Returns a NEW array; the input is not mutated. Cheap
   * (~one rolling median over ≤1800 points) and runs once per reference load.
   */
  function coherReferenceContour(data) {
    if (!data || data.length < 8) return data ? data.slice() : data;
    var n = data.length;
    var midis = new Array(n);
    for (var i = 0; i < n; i++) midis[i] = freqToMidi(data[i].freq);
    // LOCAL register track = a wide symmetric rolling median of the RAW pitch.
    // Within a ~3s window the detector's true octave is the majority even on
    // octave-flippy low voices, so the median lands on the true register — and
    // because it is LOCAL it FOLLOWS genuine wide-range singing (a high jawab
    // phrase stays high, a low qarar phrase stays low). The previous version
    // first folded toward a single GLOBAL median, which pulled every genuine >6st
    // excursion into one octave — INTRODUCING octave errors on correct wide-range
    // notes (measured vs an independent YIN+HPS consensus: rast-07 161 confirmed
    // octave-wrong frames, rast-04 65). Two passes: fold toward the raw-median
    // register, then recompute the register on the now-coherent contour (more
    // stable) and fold the ORIGINAL frames toward it.
    var W = 46; // ±46 frames ≈ ±1.5s at the ~31 fps build rate
    function rollMedian(src) {
      var reg = new Array(n);
      for (var i2 = 0; i2 < n; i2++) {
        var lo = i2 - W; if (lo < 0) lo = 0;
        var hi = i2 + W; if (hi > n - 1) hi = n - 1;
        var win = [];
        for (var k = lo; k <= hi; k++) win.push(src[k]);
        win.sort(function (a, b) { return a - b; });
        reg[i2] = win[win.length >> 1];
      }
      return reg;
    }
    function foldTo(reg) {
      var o = new Array(n);
      for (var f = 0; f < n; f++) { var v = midis[f], r = reg[f]; while (v - r > 6) v -= 12; while (r - v > 6) v += 12; o[f] = v; }
      return o;
    }
    var coh = foldTo(rollMedian(midis));   // pass 1: fold toward the raw-median register (local)
    var folded = foldTo(rollMedian(coh));  // pass 2: register from the coherent contour = stable, still local
    var out = new Array(n);
    for (var i3 = 0; i3 < n; i3++) out[i3] = { time: data[i3].time, freq: midiToFreq(folded[i3]) };
    return out;
  }

  /**
   * Decide whether a frame is REFERENCE BLEED (the reference clip leaking into
   * the mic on speakers) rather than the student. Replaces the old blunt
   * "framePeak < 35% of session peak → reject" rule, which also cut genuinely
   * soft singing. A frame is bleed only when it is BOTH quiet relative to the
   * student's own established voiced level AND in unison with the reference at
   * that instant — so soft singing at the student's OWN pitch (different from
   * the reference) is always kept. Pure + self-contained (MIDI in, boolean out)
   * so it is unit-tested without a mic (scripts/test-bleed-gate.mjs).
   *
   *   framePeak   this frame's peak amplitude
   *   sessionPeak the student's established voiced peak (adaptiveBoostFrame anchor)
   *   bleedPeak   measured bleed level this take (0 until calibrated)
   *   studentMidi detected pitch of this frame, in MIDI (null if unvoiced)
   *   refMidi     reference pitch at this instant, in MIDI (null if ref silent here)
   */
  function isReferenceBleed(framePeak, sessionPeak, bleedPeak, studentMidi, refMidi) {
    if (!(sessionPeak > 0.02)) return false;        // no established voice level yet — never gate
    var quietBound = sessionPeak * 0.22;            // softer than the old 0.35 so soft voice survives
    // Escalation is CAPPED at the old blunt 0.35 rule. bleedPeak is estimated from
    // rejected frames, and a mis-seeded estimate (the student's own soft unison
    // voice) used to ratchet quietBound upward each rejection — a self-poisoning
    // spiral that ate more and more genuine singing. The cap bounds the damage:
    // this gate can never reject more than the pre-rework rule did.
    if (bleedPeak > 0) quietBound = Math.max(quietBound, Math.min(2.5 * bleedPeak, sessionPeak * 0.35));
    if (framePeak >= quietBound) return false;      // loud enough to be the direct voice
    if (bleedPeak > 0 && framePeak > 3 * bleedPeak) return false; // clearly above the measured bleed
    if (studentMidi == null || refMidi == null) return false;     // no ref line here → can't call it bleed
    // LITERAL distance, not octave-folded: real speaker bleed arrives at the
    // reference's actual octave. Folding also caught a student singing the same
    // pitch class an octave away from the reference — a legitimate (and common)
    // way to follow a reference outside one's range — and silenced them.
    var semis = studentMidi - refMidi;
    return Math.abs(semis) <= 1.0;                  // quiet AND at the ref's own pitch = bleed
  }

  /**
   * Compress a {time, freq} pitch contour into the compact {t, f} shape used in
   * storage, uniformly downsampled to at most `cap` points. We now persist BOTH
   * the student's and the reference's contour per recording (so the instructor
   * can see the comparison chart), so keeping each small bounds the metafield.
   */
  function packPitchForStorage(arr, cap) {
    if (!arr || !arr.length) return [];
    cap = cap || 300;
    var src = arr;
    if (arr.length > cap) {
      var stride = arr.length / cap;
      src = [];
      for (var i = 0; i < cap; i++) {
        // Anchor the final point to the true last sample so the stored contour
        // ends where the recording ends (Math.floor((cap-1)*stride) is always
        // < arr.length-1, which would otherwise drop the tail).
        var idx = (i === cap - 1) ? arr.length - 1 : Math.floor(i * stride);
        src.push(arr[idx]);
      }
    }
    return src.map(function (d) {
      return { t: Math.round(d.time * 100) / 100, f: Math.round(d.freq) };
    });
  }

  function midiToNoteName(midi) {
    var notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    var rounded = Math.round(midi);
    return notes[((rounded % 12) + 12) % 12] + Math.floor(rounded / 12 - 1);
  }

  /**
   * Tell the audio element to time-stretch instead of pitch-shifting when
   * playbackRate isn't 1. Without this, slowing a clip down also drops it
   * an octave or so — useless for matching pitch. The flag is set on every
   * vendor name because Firefox/Safari historically used prefixes.
   * Idempotent — safe to call any time, on any HTMLMediaElement.
   */
  function setPreservePitch(audio) {
    if (!audio) return;
    try { audio.preservesPitch = true; } catch (e) {}
    try { audio.mozPreservesPitch = true; } catch (e) {}
    try { audio.webkitPreservesPitch = true; } catch (e) {}
  }

  /**
   * Create an AudioContext synchronously inside a user gesture. iOS Safari
   * starts contexts created after an `await` in the 'suspended' state, so
   * the analyser reads silence — call this FIRST in click-driven flows,
   * before any getUserMedia/countdown awaits. Returns null on failure
   * (callers fall back to lazy creation, matching the old behavior).
   */
  function createGestureAudioContext() {
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    try {
      var ctx = new Ctx();
      if (ctx.state === 'suspended') {
        try { ctx.resume(); } catch (e) {}
      }
      return ctx;
    } catch (e) {
      console.warn('[saif-practice] AudioContext creation failed:', e && e.message);
      return null;
    }
  }

  /** Close an AudioContext, swallowing the InvalidStateError double-close throws. */
  function closeAudioContext(ctx) {
    if (ctx) { try { ctx.close(); } catch (e) {} }
  }

  /* ==============================================
     PRACTICE STREAK + SCORE HISTORY (localStorage)
     ==============================================
     Per-device only — no cross-device sync. Wraps every read in try/catch
     because Safari Private Mode throws on localStorage.setItem and we'd
     rather silently lose tracking than break the studio for those users. */
  var STREAK_KEY = 'saif:practice:streak';
  var SCORES_KEY_PREFIX = 'saif:practice:scores:';
  var SCORE_HISTORY_LIMIT = 50;

  /* ----- Per-account key namespacing (device-local) -----------------------
     Historically the studio's localStorage keys were global per browser, so on
     a shared computer every signed-in account saw the same streak / score-trend
     / clip-notes. We now scope student-data keys by Shopify customer id:
        signed in  ->  saif:practice:<cid>:<leaf>
        anonymous  ->  saif:practice:<leaf>          (unchanged - the anon store)
     Device traits (headphones, aecon) and one-time UI teaching (tourDone,
     modesSeen, recordingTitles) stay GLOBAL on purpose - see docs. Cross-device
     sync is out of scope (that needs the server). The cid is resolved once,
     lazily, from the SaifSync config (SaifSync's script runs before this one -
     see the snippet's script order) with a direct DOM read as a fallback. */
  var NS_BASE = 'saif:practice:';
  var _nsCid; // undefined = unresolved, null = anonymous, string = customer id
  function nsCid() {
    if (_nsCid !== undefined) return _nsCid;
    var cid = null;
    try { cid = window.SaifSync && window.SaifSync.config && window.SaifSync.config.customerId; } catch (e) {}
    if (!cid) {
      try {
        var el = document.getElementById('SaifSyncConfig');
        if (el) cid = (JSON.parse(el.textContent) || {}).customerId;
      } catch (e) {}
    }
    return (_nsCid = (cid ? String(cid) : null));
  }
  // Account-scoped variant of a legacy 'saif:practice:...' key or prefix.
  // Returns the input unchanged for anonymous visitors (their store IS the
  // legacy key) and for any prefix not under NS_BASE (defensive).
  function nsScope(legacyKey) {
    var cid = nsCid();
    if (!cid || legacyKey.indexOf(NS_BASE) !== 0) return legacyKey;
    return NS_BASE + cid + ':' + legacyKey.slice(NS_BASE.length);
  }

  // Format a Date object as a local-time YYYY-MM-DD string. We use local
  // components rather than .toISOString() so the date label matches the user's
  // calendar (toISOString uses UTC which can show "tomorrow's" date during
  // late-evening usage in negative-offset timezones).
  function fmtLocalISO(d) {
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }
  function todayISO() {
    return fmtLocalISO(new Date());
  }
  function yesterdayISO() {
    // DST-safe yesterday: subtract one calendar day from the local date.
    // Naively subtracting 24h breaks on spring-forward (23h day) and fall-back
    // (25h day), making the streak miscount once or twice a year.
    var d = new Date();
    d.setDate(d.getDate() - 1);
    return fmtLocalISO(d);
  }

  function getStreakData() {
    var key = nsScope(STREAK_KEY);
    var raw = null;
    try { raw = localStorage.getItem(key); } catch (e) {}
    // Signed-in adopt: the first read after this account claims the device
    // pulls the un-namespaced streak forward, then removes it. The legacy key
    // is only deleted after a successful write, so a quota failure keeps it.
    if (raw == null && key !== STREAK_KEY) {
      var legacy = null;
      try { legacy = localStorage.getItem(STREAK_KEY); } catch (e) {}
      if (legacy != null) {
        raw = legacy;
        try { localStorage.setItem(key, legacy); localStorage.removeItem(STREAK_KEY); } catch (e) {}
      }
    }
    if (!raw) return { count: 0, lastDate: '', totalDays: 0 };
    try {
      var parsed = JSON.parse(raw);
      return {
        count: typeof parsed.count === 'number' ? parsed.count : 0,
        lastDate: parsed.lastDate || '',
        totalDays: typeof parsed.totalDays === 'number' ? parsed.totalDays : 0
      };
    } catch (e) {
      return { count: 0, lastDate: '', totalDays: 0 };
    }
  }

  /**
   * Mark today as a practice day. Returns the updated streak data.
   *   - same day  → no-op (one credit per day)
   *   - yesterday → streak +1
   *   - earlier   → streak resets to 1 (broken)
   */
  function recordPracticeDay() {
    var today = todayISO();
    var data = getStreakData();
    if (data.lastDate === today) return data;
    if (data.lastDate === yesterdayISO()) data.count += 1;
    else data.count = 1;
    data.lastDate = today;
    data.totalDays += 1;
    try {
      localStorage.setItem(nsScope(STREAK_KEY), JSON.stringify(data));
    } catch (e) {
      // Surface quota / Private-Mode failures once per session so the user
      // knows their streak isn't actually being saved.
      notifyStorageWriteFailure(e);
    }
    return data;
  }

  /**
   * Legacy 32-bit XOR hash. Collision-prone (~1-in-50k after 200 clips —
   * enough to leak notes/scores between two clips that hash the same).
   * Kept ONLY so we can read pre-migration localStorage entries; new writes
   * use the encoded URL directly for collision-free keys.
   */
  function legacyHashStr(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) {
      h = ((h << 5) - h) + s.charCodeAt(i);
      h |= 0;
    }
    return h.toString(36);
  }

  /**
   * Build the modern, collision-free localStorage suffix for a reference
   * URL. encodeURIComponent ensures the key contains only safe chars and
   * uniquely identifies the URL. Trade-off: longer keys, but typical clip
   * URLs are well under 1KB so localStorage's quota isn't a concern.
   */
  function urlKeySuffix(s) {
    return encodeURIComponent(s);
  }

  /**
   * Read a localStorage entry, falling back to the legacy hashed key on
   * miss, and migrate-in-place on first read (delete the old key after
   * the next write under the new key). Returns the raw string or null.
   */
  function readWithLegacy(prefix, refUrl) {
    var suffix = urlKeySuffix(refUrl);
    var nsPrefix = nsScope(prefix);
    // 1. Account-scoped modern key — the canonical location for new writes.
    var newKey = nsPrefix + suffix;
    var raw = null;
    try { raw = localStorage.getItem(newKey); } catch (e) {}
    if (raw != null) return { key: newKey, raw: raw, migrated: false };
    // 2. Signed-in only: adopt an un-namespaced modern key written before this
    //    account claimed the device (the natural "practiced before signing in"
    //    hand-off). writeWithLegacy deletes oldKey after the next write.
    if (nsPrefix !== prefix) {
      var legacyKey = prefix + suffix;
      var legacyRaw = null;
      try { legacyRaw = localStorage.getItem(legacyKey); } catch (e) {}
      if (legacyRaw != null) return { key: newKey, raw: legacyRaw, migrated: true, oldKey: legacyKey };
    }
    // 3. Adopt the ancient hashed key (only ever written un-namespaced).
    var oldKey = prefix + legacyHashStr(refUrl);
    var oldRaw = null;
    try { oldRaw = localStorage.getItem(oldKey); } catch (e) {}
    if (oldRaw != null) {
      return { key: newKey, raw: oldRaw, migrated: true, oldKey: oldKey };
    }
    return { key: newKey, raw: null, migrated: false };
  }

  /**
   * Write a localStorage entry under the modern key. Returns true on success,
   * false on quota / private-mode failure (so callers can surface a status).
   * If `migratedFromOldKey` is supplied, deletes that key on successful write.
   */
  function writeWithLegacy(prefix, refUrl, value, migratedFromOldKey) {
    var newKey = nsScope(prefix) + urlKeySuffix(refUrl);
    try {
      localStorage.setItem(newKey, value);
      if (migratedFromOldKey) {
        try { localStorage.removeItem(migratedFromOldKey); } catch (e) {}
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    }
  }

  // Module-level "warned once this session" flag for storage quota errors.
  // Keyed at module scope (not on PracticeStudio) so save helpers that run
  // before/independent of the studio instance share the same gate.
  var _lsQuotaWarned = false;

  /**
   * Surface a storage-quota or private-mode write error to the user — once
   * per session — via the active studio's setStatus(). Subsequent failures
   * silently no-op (we don't want to spam every save).
   */
  function notifyStorageWriteFailure(err) {
    if (_lsQuotaWarned) return;
    var name = err && err.name ? err.name : '';
    var isQuota = (name === 'QuotaExceededError' ||
      name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      // Some browsers return code 22 instead of a name
      (err && err.code === 22));
    var msg = isQuota
      ? 'Local storage is full — practice history won\'t be saved on this device until you free space.'
      : 'Local storage unavailable (Private Mode?) — practice history won\'t be saved on this device.';
    _lsQuotaWarned = true;
    var studio = window.SaifPracticeStudio;
    if (studio && typeof studio.setStatus === 'function') {
      try { studio.setStatus(msg); } catch (e) {}
    }
    console.warn('[saif-practice] localStorage write failed:', name || err, '—', msg);
  }

  function recordScore(refUrl, score) {
    if (!refUrl || typeof score !== 'number' || !isFinite(score)) return;
    var entry = readWithLegacy(SCORES_KEY_PREFIX, refUrl);
    var arr;
    try { arr = entry.raw ? JSON.parse(entry.raw) : []; } catch (e) { arr = []; }
    arr.push({ ts: Date.now(), score: Math.round(score * 10) / 10 });
    if (arr.length > SCORE_HISTORY_LIMIT) {
      arr = arr.slice(-SCORE_HISTORY_LIMIT);
    }
    var res = writeWithLegacy(SCORES_KEY_PREFIX, refUrl, JSON.stringify(arr),
      entry.migrated ? entry.oldKey : null);
    if (!res.ok) notifyStorageWriteFailure(res.error);
  }

  function getScoreHistory(refUrl) {
    if (!refUrl) return [];
    var entry = readWithLegacy(SCORES_KEY_PREFIX, refUrl);
    if (entry.raw == null) return [];
    try { return JSON.parse(entry.raw); } catch (e) { return []; }
  }

  /* ==============================================
     CLIP ANNOTATIONS (per-clip, localStorage)
     ==============================================
     Per-clip persistent notes — keyed by reference URL hash so a single
     clip accumulates notes across recording sessions. Each note: a
     timestamp into the recording (or reference) and free text. */
  var NOTES_KEY_PREFIX = 'saif:practice:notes:';

  /* _noteScope — notes belong to the take, not the clip -------------------
     Returns the storage scope for the take currently on screen: the saved
     take being replayed from history, else the take just recorded. A practice
     run saves nothing, so it has no id and gets the in-memory bucket below —
     its notes last for that run, matching "Practice run — nothing saved". */
  var TRANSIENT_NOTES = { scope: null, notes: [] };
  PracticeStudio.prototype._noteScope = function () {
    var id = this._reviewingSavedId || this._lastSavedRecordingId || null;
    return id ? 'rec:' + id : null;
  };
  PracticeStudio.prototype._getTakeNotes = function () {
    var scope = this._noteScope();
    if (!scope) return TRANSIENT_NOTES.notes.slice();
    return getNotes(scope);
  };
  PracticeStudio.prototype._setTakeNotes = function (notes) {
    var scope = this._noteScope();
    if (!scope) { TRANSIENT_NOTES.notes = (notes || []).slice(); return; }
    saveNotes(scope, notes);
  };
  /* Called when a fresh take starts, so an unsaved run never inherits the
     previous run's in-memory notes. */
  PracticeStudio.prototype._resetTransientNotes = function () {
    TRANSIENT_NOTES.scope = null;
    TRANSIENT_NOTES.notes = [];
  };

  function getNotes(refUrl) {
    if (!refUrl) return [];
    var entry = readWithLegacy(NOTES_KEY_PREFIX, refUrl);
    if (entry.raw == null) return [];
    try { return JSON.parse(entry.raw); } catch (e) { return []; }
  }

  function saveNotes(refUrl, notes) {
    if (!refUrl) return;
    // Re-read so we can pick up the legacy old-key for cleanup on first write.
    var entry = readWithLegacy(NOTES_KEY_PREFIX, refUrl);
    var res = writeWithLegacy(NOTES_KEY_PREFIX, refUrl, JSON.stringify(notes || []),
      entry.migrated ? entry.oldKey : null);
    if (!res.ok) notifyStorageWriteFailure(res.error);
  }

  function fmtClock(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* ==============================================
     MAQAM DETECTION
     ==============================================
     Heuristic — analyses the pitch contour of a reference clip and labels
     it with the most likely tonic + maqam. Uses standard Arabic-music
     templates with quarter-tone resolution (1.5 = "half-flat" intervals).
     The fit is not exact: melodies wander, modulate, and our pitch tracker
     has noise. We score each candidate (tonic, maqam) pair and accept only
     when the winner clears a confidence floor; otherwise leave the label
     blank rather than mislabel the clip. */
  var MAQAM_TEMPLATES = [
    // ENCODING: each `intervals` array lists scale degrees as semitones above
    // the tonic. 0 = tonic itself, 12 = octave (implicit). A `.5` value is a
    // quarter-tone step (half-flat / half-sharp) — these distinguish Arab
    // maqamat from the Western 12-tone equal temperament. So "1.5" means a
    // pitch ~150 cents above the tonic; "8.5" means ~850 cents.
    //
    // TODO: verify with music theorist — current values are approximate.
    // Reviewer flagged two ambiguities that need expert sign-off:
    //   • Saba's 7th: some Levantine traditions place it at 8.5 (half-flat 6
    //     plus half-flat 7 from tonic), not 8 as encoded here. We keep 8 for
    //     now because that's the value the matcher was tuned against.
    //   • Sikah's first interval depends on whether you start from the
    //     half-flat tonic itself (giving 0, 1.5, 3.5, ...) or a step below
    //     it. Current encoding assumes the tonic IS the half-flat note.
    // Don't speculate-fix without a citation; this is a labeling heuristic,
    // not playback, so a near-miss just leaves the badge hidden — see the
    // 0.55 confidence floor in detectMaqam.
    { name: 'Bayati',   intervals: [0, 1.5, 3, 5, 7, 8, 10] },
    { name: 'Hijaz',    intervals: [0, 1, 4, 5, 7, 8, 10] },
    { name: 'Saba',     intervals: [0, 1.5, 3, 4, 7, 8, 10] },
    { name: 'Rast',     intervals: [0, 2, 3.5, 5, 7, 9, 10.5] },
    { name: 'Sikah',    intervals: [0, 1.5, 3.5, 5, 7, 8.5, 10.5] },
    { name: 'Nahawand', intervals: [0, 2, 3, 5, 7, 8, 10] },
    { name: 'Kurd',     intervals: [0, 1, 3, 5, 7, 8, 10] }
  ];

  /**
   * Format a midi number (which may be fractional thanks to quarter-tones)
   * as a tonic label like "D" or "E half-flat". Western notes round to the
   * nearest semitone; quarter-tone tonics get the "half-flat" qualifier.
   */
  function formatTonicName(midi) {
    var bin = Math.round(midi * 2) / 2;     // quantize to ¼-tone (½ semitone)
    var halfFlat = (bin % 1 !== 0);
    var roundedDown = Math.floor(bin);
    var name = midiToNoteName(roundedDown);
    // midiToNoteName already includes octave digit — strip it for the label
    name = name.replace(/-?\d+$/, '');
    return halfFlat ? name + ' half-flat' : name;
  }

  /**
   * Detect the most likely (tonic, maqam) combination given a pitch contour.
   * Returns { name, tonicMidi, tonicLabel, fitFraction } or null when the
   * data is too sparse / ambiguous to label.
   *
   * Algorithm:
   *   1. Histogram the pitches into ¼-tone bins, weighted by how long each
   *      pitch is held (each sample point is one tick).
   *   2. For every candidate tonic in the lower 60% of the pitch range
   *      (Arab tonics typically sit at the bottom), score every maqam
   *      template by summing histogram weight that falls within ±25¢ of
   *      one of the template's intervals (mod 12 — octave-equivalent).
   *   3. The winner is the (tonic, maqam) with the highest fit fraction.
   *      We require ≥0.55 fit and ≥30 sample points before labeling.
   */
  function detectMaqam(pitchData, restrictName) {
    if (!pitchData || pitchData.length < 30) return null;

    var bins = {};   // key = quarter-tone-quantized midi, value = sample count
    var minMidi = Infinity, maxMidi = -Infinity;
    var totalCount = 0;
    for (var i = 0; i < pitchData.length; i++) {
      var freq = pitchData[i].freq;
      if (!freq || freq <= 0) continue;
      var midi = freqToMidi(freq);
      if (!isFinite(midi)) continue;
      var bin = Math.round(midi * 2) / 2;
      bins[bin] = (bins[bin] || 0) + 1;
      totalCount += 1;
      if (midi < minMidi) minMidi = midi;
      if (midi > maxMidi) maxMidi = midi;
    }
    if (totalCount < 30) return null;

    var range = maxMidi - minMidi;
    var tonicCutoff = minMidi + range * 0.6;
    var candidateTonics = [];
    for (var key in bins) {
      var midiVal = parseFloat(key);
      if (midiVal <= tonicCutoff) candidateTonics.push(midiVal);
    }
    if (!candidateTonics.length) return null;

    var best = null;
    for (var ti = 0; ti < candidateTonics.length; ti++) {
      var tonic = candidateTonics[ti];
      for (var mi = 0; mi < MAQAM_TEMPLATES.length; mi++) {
        var template = MAQAM_TEMPLATES[mi];
        // Constrained mode: the caller already KNOWS the maqam (from the clip
        // filename) and only wants the tonic — score that one template so the
        // name can't be misclassified and the tonic is picked within it.
        if (restrictName && template.name !== restrictName) continue;
        var fitCount = 0;
        for (var binKey in bins) {
          var rel = (parseFloat(binKey) - tonic) % 12;
          if (rel < 0) rel += 12;
          for (var k = 0; k < template.intervals.length; k++) {
            if (Math.abs(rel - template.intervals[k]) <= 0.25) {
              fitCount += bins[binKey];
              break;
            }
          }
        }
        // Bias slightly toward candidates whose tonic itself appears
        // frequently — tonics that are barely-played pitches usually
        // produce false-positive matches against random scales.
        var tonicWeight = (bins[tonic] || 0) / totalCount;
        var fraction = fitCount / totalCount + tonicWeight * 0.05;
        if (!best || fraction > best.fraction) {
          best = {
            name: template.name,
            tonicMidi: tonic,
            fraction: fraction
          };
        }
      }
    }

    if (!best || best.fraction < 0.55) return null;
    return {
      name: best.name,
      tonicMidi: best.tonicMidi,
      tonicLabel: formatTonicName(best.tonicMidi),
      fitFraction: Math.min(1, best.fraction)
    };
  }

  /* A maqam-library clip carries its maqam in the filename
     (maqam-478-<family>-NN.mp3) — that is GROUND TRUTH, so we label from it
     instead of guessing the scale from a noisy contour (the heuristic confuses
     similar minor-ish maqamat, e.g. Bayati vs Nahawand, on low male voices).
     Uploads / unknown URLs return null and fall back to detectMaqam. */
  var MAQAM_HANDLE_NAMES = {
    ajam: 'Ajam', bayati: 'Bayati', hijaz: 'Hijaz', kurd: 'Kurd',
    nahawand: 'Nahawand', rast: 'Rast', saba: 'Saba', sikah: 'Sikah'
  };
  function knownRefMaqamName(url) {
    if (!url) return null;
    var base = String(url).split('/').pop() || '';
    var m = base.match(/maqam-478-([a-z]+)-\d+/i);
    return (m && MAQAM_HANDLE_NAMES[m[1].toLowerCase()]) || null;
  }

  /* ==============================================
     UI POLISH HELPERS
     ============================================== */

  /**
   * Color a pitch trail segment by how far off it is from the reference,
   * in cents (1 semitone = 100 cents). Returns a hex color string.
   *   ≤50¢ = emerald (in tune), ≤100¢ = gold (close), >100¢ = red (off).
   * Returns null if no reference is available — caller should fall back.
   */
  function pitchAccuracyColor(centsOff) {
    if (centsOff == null || isNaN(centsOff)) return null;
    var abs = Math.abs(centsOff);
    if (abs <= 50)  return '#2D6A5A';
    if (abs <= 100) return '#D4A84B';
    return '#C53030';
  }

  /* ══════════════════ PLAYER-B v2 · canvas ══════════════════
     The pitch canvas is the product's hero, so it is lit like an instrument
     rather than filled like a div. Same three accuracy thresholds as
     pitchAccuracyColor — only brighter, because on the dark stage these read
     as EMITTED light where the on-cream hexes read as muddy paint. The cream
     hexes stay in charge everywhere else (legend dots, score bands, tuner). */
  function pitchAccuracyColorLit(centsOff) {
    if (centsOff == null || isNaN(centsOff)) return null;
    var abs = Math.abs(centsOff);
    if (abs <= 50)  return '#34C79A';
    if (abs <= 100) return '#E0A94A';
    return '#E86A5C';
  }

  /* One 128px monochrome noise tile, built once and reused as a canvas
     pattern. Grain kills the banding a large flat gradient shows on cheap
     phone panels, and reads as printed paper rather than plastic. */
  var _ps_grainTile = null;
  function stageGrainTile() {
    if (_ps_grainTile) return _ps_grainTile;
    try {
      var c = document.createElement('canvas');
      c.width = c.height = 128;
      var x = c.getContext('2d');
      var img = x.createImageData(128, 128), d = img.data;
      for (var i = 0; i < d.length; i += 4) {
        var v = 118 + Math.random() * 74;
        d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 30;
      }
      x.putImageData(img, 0, 0);
      _ps_grainTile = c;
    } catch (e) { _ps_grainTile = null; }
    return _ps_grainTile;
  }

  /** The stage: a warm vertical gradient, a barely-moving radial "lift" so the
   *  instrument is never dead, and film grain over the top. Replaces the flat
   *  fill both canvases used to start from. */
  /**
   * Mark where the clip itself begins and ends on a sliding stage. With the
   * playhead pinned at 70%, a clip sitting at t=0 leaves the left two thirds of
   * the canvas showing negative time — without an edge that reads as a stage
   * that failed to paint, rather than as time that has not started yet.
   * Kept deliberately faint: this is a boundary, not a feature.
   */
  function drawClipEdges(ctx, x0, x1, W, H, dpr, gutterX) {
    var from = Math.max(gutterX || 0, 0);
    ctx.save();
    ctx.fillStyle = 'rgba(8, 7, 6, 0.34)';
    if (x0 > from) ctx.fillRect(from, 0, Math.min(x0, W) - from, H);
    if (x1 < W) ctx.fillRect(Math.max(from, x1), 0, W - Math.max(from, x1), H);
    ctx.strokeStyle = 'rgba(232, 200, 119, 0.18)';
    ctx.lineWidth = 1 * dpr;
    [x0, x1].forEach(function (x) {
      if (x < from || x > W) return;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
      ctx.stroke();
    });
    ctx.restore();
  }

  function drawStageBackground(ctx, W, H, dpr) {
    var g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#201D18');
    g.addColorStop(1, '#151310');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    var drift = prefersReducedMotion() ? 0 : Math.sin(performance.now() / 9000) * 0.03;
    var cx = W * (0.5 + drift);
    var rg = ctx.createRadialGradient(cx, H * 0.42, 0, cx, H * 0.42, W * 0.62);
    rg.addColorStop(0, 'rgba(232, 200, 119, 0.06)');
    rg.addColorStop(1, 'rgba(232, 200, 119, 0)');
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, W, H);
    var tile = stageGrainTile();
    if (!tile) return;
    try {
      if (!ctx._psGrainPat) ctx._psGrainPat = ctx.createPattern(tile, 'repeat');
      if (ctx._psGrainPat) {
        ctx.globalAlpha = 0.05;
        ctx.fillStyle = ctx._psGrainPat;
        ctx.fillRect(0, 0, W, H);
        ctx.globalAlpha = 1;
      }
    } catch (e) {}
  }

  /** Which pitch classes this reference actually lives in — i.e. the maqam's
   *  own degrees, quarter-tones included (Bayati's 1.5, Rast's 3.5). Derived
   *  from the clip's measured contour rather than a hardcoded scale table, so
   *  transposed / range-fitted clips shade correctly for free. Cached by
   *  sample count, the same key drawLiveVisualization uses for _refRange. */
  var _ps_laneCache = { n: -1, classes: null, tonic: null };
  function refPitchClasses(refPitchData) {
    if (!refPitchData || refPitchData.length < 8) return null;
    if (_ps_laneCache.n === refPitchData.length) return _ps_laneCache;
    var counts = {}, i, pc;
    for (i = 0; i < refPitchData.length; i++) {
      pc = Math.round(freqToMidi(refPitchData[i].freq) * 2) / 2;
      pc = ((pc % 12) + 12) % 12;
      counts[pc] = (counts[pc] || 0) + 1;
    }
    var classes = [], floor = refPitchData.length * 0.03, tonic = null, bestN = 0;
    for (var k in counts) {
      if (counts[k] < floor) continue;              // ignore glide smear between notes
      classes.push(parseFloat(k));
      if (counts[k] > bestN) { bestN = counts[k]; tonic = parseFloat(k); }
    }
    _ps_laneCache = { n: refPitchData.length, classes: classes, tonic: tonic };
    return _ps_laneCache;
  }

  /** Faint horizontal wash on every row that is a degree of the reference's
   *  maqam (tonic warmer). Melodyne shades scale rows this way; no competitor
   *  in this space visualises the MODE itself, only the pitch. */
  function drawMaqamLanes(ctx, opts, refPitchData) {
    var info = refPitchClasses(refPitchData);
    if (!info || !info.classes || !info.classes.length) return;
    var lo = Math.floor(opts.minMidi), hi = Math.ceil(opts.maxMidi);
    if (!(hi > lo)) return;
    for (var m = lo; m <= hi; m++) {
      for (var half = 0; half < 2; half++) {
        var mv = m + half * 0.5;
        var pc = ((mv % 12) + 12) % 12;
        if (info.classes.indexOf(pc) < 0) continue;
        var yTop = opts.midiToY(mv + 0.5), yBot = opts.midiToY(mv - 0.5);
        if (!(yBot - yTop > 0)) continue;
        ctx.fillStyle = (pc === info.tonic)
          ? 'rgba(212, 168, 75, 0.055)'
          : 'rgba(250, 247, 242, 0.032)';
        ctx.fillRect(opts.gutterX, yTop, opts.width - opts.gutterX, yBot - yTop);
      }
    }
  }

  /**
   * Cents-off between user's pitch and the reference pitch at the same time.
   * Returns null if there's no nearby reference sample.
   */
  /**
   * A silence in the reference longer than this is a REST: no target, no line,
   * no band, and no grade. computeScore has always used this figure to decide
   * whether a sung frame has anything to be graded against (a frame during a
   * reciter breath is skipped rather than scored against the note before or
   * after it), so every renderer has to use the same number — otherwise the
   * stage draws a target where the scorer credits nothing, which is what the
   * owner caught: a highlighted band across blank space.
   * The reference is sampled every ~16-50ms, so any real gap is far wider.
   */
  var REF_REST_SEC = 0.15;

  function centsOffFromRef(userFreq, refFreq) {
    if (!userFreq || !refFreq) return null;
    return 1200 * Math.log2(userFreq / refFreq);
  }

  /** True when the user asked the OS for reduced motion. */
  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /**
   * Platform-aware mic-permission-denied instructions. "Click the lock icon
   * in the address bar" is desktop-Chrome advice — iOS Safari has no lock
   * icon, so the single most common first-take failure used to get
   * instructions that don't exist on the student's device.
   */
  /* SAIF_MIC_INAPP_V1 — measured 2026-08-09.

     Every microphone failure recorded in the first month was NotAllowedError: 7 people, 16
     attempts, and not one of them was our code breaking. Four of the seven were inside
     INSTAGRAM'S IN-APP BROWSER, arriving from l.instagram.com. That browser cannot grant
     microphone access at all: it is a webview inside another app, and the host app never asks
     the OS for the permission, so the request is refused before the student sees anything they
     can act on. iOS reports it as "The request is not allowed by the user agent or the
     platform", which is the fingerprint.

     Instagram is 44% of all traffic to this site, so the Practice Studio has been quietly
     unusable for a large share of the people it was built to impress. */
  function saifInAppBrowser() {
    var ua = navigator.userAgent || '';
    /* Instagram and Facebook stamp themselves; TikTok, Snapchat and Line likewise. Android
       webviews carry "; wv)" but so do some legitimate wrappers, so that alone is only a hint
       and is deliberately not treated as proof. */
    if (/Instagram/i.test(ua)) return 'Instagram';
    if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'Facebook';
    if (/TikTok|BytedanceWebview/i.test(ua)) return 'TikTok';
    if (/Snapchat/i.test(ua)) return 'Snapchat';
    if (/\bLine\//i.test(ua)) return 'LINE';
    return null;
  }

  function micDeniedMessage() {
    var ua = navigator.userAgent || '';
    /* ⚠️ Check this FIRST. The instructions below tell people to tap the "aA" icon in the
       address bar, and an in-app browser has no address bar, so for four of the seven people
       who hit this the advice was impossible to follow. */
    var inApp = saifInAppBrowser();
    if (inApp) {
      return 'Recording does not work inside the ' + inApp + ' browser, which cannot reach ' +
        'your microphone. Tap the three dots at the top and choose Open in browser, then come ' +
        'back here.';
    }
    var isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (ua.indexOf('Macintosh') !== -1 && navigator.maxTouchPoints > 1); // iPadOS masquerades as Mac
    if (isIOS) {
      return 'Microphone permission denied. Tap the "aA" (or puzzle) icon in the address bar → Website Settings → allow Microphone — or check Settings → Safari → Microphone. Then try again.';
    }
    if (/Android/i.test(ua)) {
      return 'Microphone permission denied. Tap the lock/tune icon next to the address bar → Permissions → allow Microphone, then try again.';
    }
    return 'Microphone permission denied. Click the lock icon in the address bar to allow it, then try again.';
  }

  /**
   * SAIF_MIC_ERR_V1 (2026-08-04) — Tuner, Drill and Range used to call micDeniedMessage()
   * for EVERY getUserMedia failure, so a user with no microphone plugged in was told to
   * "click the lock icon in the address bar to allow it" — and there is no lock-icon entry
   * to click, because nothing was ever asked for. Record already branched on the error name;
   * this puts that branching in one place so all four paths agree.
   */
  function micErrorMessage(e) {
    var name = e && e.name;
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
      return 'No microphone found. Please connect one and try again.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return 'Your microphone is in use by another app. Close it and try again.';
    }
    if (name === 'SecurityError') {
      return 'Microphone access requires a secure (HTTPS) connection.';
    }
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return micDeniedMessage();
    }
    return 'Could not start the microphone. Please check it is connected and try again.';
  }

  /**
   * True on phones/tablets (where the mic and speaker share one small device,
   * so the reference bleeds into the recording on the loudspeaker). Same UA
   * idiom as micDeniedMessage — iPadOS masquerades as a Mac, so also treat a
   * touch-capable "Macintosh" as mobile.
   */
  function isMobileUA() {
    var ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod|Android/i.test(ua) ||
      (ua.indexOf('Macintosh') !== -1 && navigator.maxTouchPoints > 1);
  }

  /**
   * Quiet note grid for the pitch canvases (live + compare views).
   *
   * Picks a "musical" semitone step (1, 2, 3, 4, 6, 12 …) so that at most
   * ~5 evenly spaced gridlines appear in the visible range, each anchored
   * to multiples of the step from MIDI 0 — which keeps C anchors whenever
   * the step divides the octave. Labels render once, on the left edge only,
   * in small Plus Jakarta at ~38% ink; gridlines are 1px hairlines at ~6%
   * ink so they orient the eye without competing with the pitch trace.
   *
   * opts: { minMidi, maxMidi, midiToY(fn), width, height, dpr, gutterX }
   */
  function drawNoteGrid(ctx, opts) {
    var span = opts.maxMidi - opts.minMidi;
    // Bail on a zero/degenerate span OR a 0-height canvas. The 0-height case is
    // real: the Compare canvas lives in a hidden result panel, so it can be
    // 0×0 when drawComparison runs on a fresh result — and without this guard
    // the step-widening loop below would spin forever (height 0 → spacing 0 <
    // MIN_LABEL_PX always), freezing the tab.
    if (span <= 0 || !(opts.height > 0)) return;
    // Choose the FINEST note interval whose on-screen spacing stays readable.
    // Previously hard-capped at ~4 labels regardless of height; now we label
    // every semitone when there's room (the common 10-16 semitone view), and
    // only thin to 2/3/4/6/12 on wide ranges — so the axis is far denser and
    // still never overlaps. MIN_LABEL_PX is in device px (height + font are too).
    var MIN_LABEL_PX = 14 * opts.dpr;
    var stepChoices = [1, 2, 3, 4, 6, 12];
    var step = stepChoices[stepChoices.length - 1];
    for (var i = 0; i < stepChoices.length; i++) {
      if ((opts.height / span) * stepChoices[i] >= MIN_LABEL_PX) { step = stepChoices[i]; break; }
    }
    // Widen to octave multiples on ultra-wide ranges. Bounded by `span` so it
    // always terminates (belt-and-suspenders beyond the height guard above).
    while ((opts.height / span) * step < MIN_LABEL_PX && step < span) step += 12;
    var first = Math.ceil(opts.minMidi / step) * step;
    ctx.font = '500 ' + Math.round(10.5 * opts.dpr) + 'px "Plus Jakarta Sans", sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    var edge = 8 * opts.dpr; // skip labels that would clip at top/bottom
    for (var n = first; n <= opts.maxMidi; n += step) {
      var y = opts.midiToY(n);
      // Emphasize the C of each octave so the eye has an anchor among the
      // denser semitone lines.
      var isOctave = ((((n % 12) + 12) % 12) === 0);
      ctx.strokeStyle = isOctave ? 'rgba(250, 247, 242, 0.13)' : 'rgba(250, 247, 242, 0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(opts.gutterX, y);
      ctx.lineTo(opts.width, y);
      ctx.stroke();
      if (y < edge || y > opts.height - edge) continue;
      ctx.fillStyle = isOctave ? 'rgba(250, 247, 242, 0.58)' : 'rgba(250, 247, 242, 0.42)';
      ctx.fillText(midiToNoteName(n), 6 * opts.dpr, y);
    }
  }

  /**
   * Animate a numeric value from `from` to `to` over `durationMs` with an
   * ease-out cubic curve. Calls onUpdate(currentValue) on each frame.
   */
  function tween(from, to, durationMs, onUpdate, onComplete) {
    var start = performance.now();
    function frame(now) {
      var t = Math.min(1, (now - start) / durationMs);
      var eased = 1 - Math.pow(1 - t, 3);
      onUpdate(from + (to - from) * eased);
      if (t < 1) requestAnimationFrame(frame);
      else if (onComplete) onComplete();
    }
    requestAnimationFrame(frame);
  }

  /**
   * Ephemeral confetti burst over a host element. Spawns a temporary canvas,
   * runs a particle simulation for `durationMs`, then removes itself.
   * Triggered for high-score recordings (≥9.0/10).
   */
  function spawnConfetti(host, durationMs) {
    if (!host) return;
    if (prefersReducedMotion()) return; // celebration, not vestibular distress
    durationMs = durationMs || 2200;
    var rect = host.getBoundingClientRect();
    var canvas = document.createElement('canvas');
    var dpr = window.devicePixelRatio || 1;
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;';
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    var palette = ['#D4A84B', '#B8860B', '#2D6A5A', '#FAF7F2', '#C53030'];
    var originX = (rect.left + rect.width / 2) * dpr;
    var originY = (rect.top + rect.height / 3) * dpr;
    var particles = [];
    for (var i = 0; i < 90; i++) {
      var angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
      var speed = (4 + Math.random() * 9) * dpr;
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.3,
        size: (4 + Math.random() * 6) * dpr,
        color: palette[(Math.random() * palette.length) | 0]
      });
    }
    var startMs = performance.now();
    function frame(now) {
      var elapsed = now - startMs;
      if (elapsed > durationMs) {
        canvas.remove();
        return;
      }
      var fade = Math.max(0, 1 - elapsed / durationMs);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.vy += 0.35 * dpr; // gravity
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.rotSpeed;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = fade;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.5);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* ==============================================
     PRACTICE STUDIO
     ============================================== */
  function PracticeStudio(container) {
    this.container = container;
    this.state = 'idle'; // idle | recording | recorded | analyzing
    this._tootSeatState = null;
    this._tootTreeObserver = null;
    this._tootTreeFrame = 0;
    this.audioCtx = null;
    this.stream = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.recordedBlob = null;
    this.recordedUrl = null;

    // Pitch data arrays: { time, freq }
    this.refPitchData = [];
    this.recPitchData = [];

    // Canvas elements
    this.liveCanvas = container.querySelector('#SaifPracticeLiveCanvas');
    this.compareCanvas = container.querySelector('#SaifPracticeCompareCanvas');

    // Buttons
    this.refPlayBtn = container.querySelector('#SaifPracticeRefPlay');
    this.recordBtn = container.querySelector('#SaifPracticeRecordBtn');
    // Practice-run button — runs a take that gives live pitch feedback + a
    // score but is NEVER recorded or saved. Lets a student rehearse freely.
    this.practiceRunBtn = container.querySelector('#SaifPracticePracticeBtn');
    this.stopBtn = container.querySelector('#SaifPracticeStopBtn');
    this.playbackBtn = container.querySelector('#SaifPracticePlaybackBtn');
    this.retryBtn = container.querySelector('#SaifPracticeRetryBtn');
    this.submitBtn = container.querySelector('#SaifPracticeSubmitBtn');
    // "Submit for feedback" — sends a SAVED take to the student's instructor.
    // Distinct from the AI-feedback button (submitBtn) above.
    this.feedbackSubmitBtn = container.querySelector('#SaifPracticeFeedbackBtn');
    // "Practice run — not saved" notice, shown in the result view after a
    // no-record run so the student knows nothing was kept.
    this.practiceRunNotice = container.querySelector('#SaifPracticePracticeNotice');
    // Holds the id of the most recently SAVED take so the feedback button
    // knows which recording to submit. Cleared on reset / new take.
    this._lastSavedRecordingId = null;

    // Submit-for-feedback note dialog + history multi-select controls.
    this.noteOverlay = container.querySelector('#SaifPracticeNoteOverlay');
    this.noteInput = container.querySelector('#SaifPracticeNoteInput');
    this.noteSendBtn = container.querySelector('#SaifPracticeNoteSend');
    this.noteCancelBtn = container.querySelector('#SaifPracticeNoteCancel');
    this.noteTitleEl = container.querySelector('#SaifPracticeNoteTitle');
    this.noteSubEl = container.querySelector('#SaifPracticeNoteSub');
    this.historySelectBtn = container.querySelector('#SaifPracticeHistorySelect');
    this.historyBulkbar = container.querySelector('#SaifPracticeHistoryBulkbar');
    this.historyBulkCount = container.querySelector('#SaifPracticeHistoryBulkCount');
    this.historyBulkSubmit = container.querySelector('#SaifPracticeHistoryBulkSubmit');
    this.historyBulkDelete = container.querySelector('#SaifPracticeHistoryBulkDelete');
    this._selectMode = false;
    this._selectedRecs = {}; // id -> true

    // Player transport
    this.playerSkipBackBtn = container.querySelector('#SaifPracticePlayerSkipBack');
    this.playerSkipFwdBtn = container.querySelector('#SaifPracticePlayerSkipFwd');
    this.playerProgress = container.querySelector('#SaifPracticePlayerProgress');
    this.playerFill = container.querySelector('#SaifPracticePlayerFill');
    this.playerThumb = container.querySelector('#SaifPracticePlayerThumb');
    this.playerTime = container.querySelector('#SaifPracticePlayerTime');
    this.playerIconPlay = this.playbackBtn ? this.playbackBtn.querySelector('.saif-practice__player-icon-play') : null;
    this.playerIconPause = this.playbackBtn ? this.playbackBtn.querySelector('.saif-practice__player-icon-pause') : null;
    this.playbackLabel = container.querySelector('#SaifPracticePlaybackLabel');
    this._playerHasPlayed = false; // tracks whether the recording has ever started — distinguishes "Play" from "Resume"

    // 2026-06 intuitiveness pass — single primary action per state:
    //   idle      → big Record button + one-line hint (recordHint)
    //   recording → Stop button + live timer (recTimer)
    //   result    → score + Listen / Compare / Try Again next-steps row;
    //               the full transport (playerEl) stays collapsed until
    //               the student presses Listen.
    // All optional — older snippet variants / the test harness may lack some.
    this.recordHint = container.querySelector('#SaifPracticeRecordHint');
    this.recTimer = container.querySelector('#SaifPracticeRecTimer');
    this.listenBtn = container.querySelector('#SaifPracticeListenBtn');
    this.listenLabel = container.querySelector('#SaifPracticeListenLabel');
    this.listenIconPlay = this.listenBtn ? this.listenBtn.querySelector('.saif-practice__listen-icon-play') : null;
    this.listenIconPause = this.listenBtn ? this.listenBtn.querySelector('.saif-practice__listen-icon-pause') : null;
    this.compareBtn = container.querySelector('#SaifPracticeCompareBtn');
    this.playerEl = container.querySelector('#SaifPracticePlayer');

    // Display elements
    this.liveSection = container.querySelector('#SaifPracticeLive');
    this.resultSection = container.querySelector('#SaifPracticeResult');
    this.scoreEl = container.querySelector('#SaifPracticeScore');
    this.scoreBreakdown = container.querySelector('#SaifPracticeBreakdown');

    // Tools discovery: the dock's More button wears the studio's unseen dot
    // until it has been opened once. Same idiom as the mode tabs
    // (.saif-practice__tab--unseen), so it reads as native rather than new.
    var TOOLS_SEEN_KEY = 'saif:practice:toolsSeen';
    var dockMore = container.querySelector('#SaifPracticeDockMore');
    if (dockMore) {
      var toolsSeen = false;
      try { toolsSeen = localStorage.getItem(TOOLS_SEEN_KEY) === '1'; } catch (e) {}
      if (!toolsSeen) {
        dockMore.classList.add('saif-practice__dock-btn--unseen');
        dockMore.addEventListener('click', function () {
          dockMore.classList.remove('saif-practice__dock-btn--unseen');
          try { localStorage.setItem(TOOLS_SEEN_KEY, '1'); } catch (e) {}
        }, { once: true });
      }
    }
    this.feedbackEl = container.querySelector('#SaifPracticeFeedback');
    this.feedbackLoading = container.querySelector('#SaifPracticeFeedbackLoading');
    this.pitchDisplay = container.querySelector('#SaifPracticePitchDisplay');
    this.statusEl = container.querySelector('#SaifPracticeStatus');

    // Reference audio
    this.refSection = container.querySelector('#SaifPracticeReference');
    this.refAudioEl = container.querySelector('#SaifPracticeRefAudio');
    this.refMaqamLabel = container.querySelector('#SaifPracticeRefMaqam');
    // Reference transport (2026-07) — playhead for the reference clip so a
    // student can repeat a specific phrase instead of re-listening from the
    // top. All optional (older snippet variants may lack the row).
    this.refProgress = container.querySelector('#SaifPracticeRefProgress');
    this.refTransport = container.querySelector('#SaifPracticeRefTransport');
    this.refFill = container.querySelector('#SaifPracticeRefFill');
    this.refThumb = container.querySelector('#SaifPracticeRefThumb');
    this.refTimeEl = container.querySelector('#SaifPracticeRefTime');
    this.onboardEl = container.querySelector('#SaifPracticeOnboard');
    this.streakBadge = container.querySelector('#SaifPracticeStreak');
    this.streakCountEl = container.querySelector('#SaifPracticeStreakCount');
    this.scoreTrendBox = container.querySelector('#SaifPracticeScoreTrend');
    this.scoreTrendSvg = container.querySelector('#SaifPracticeScoreTrendSvg');
    this.notesAddBtn = container.querySelector('#SaifPracticeNotesAddBtn');
    this.notesListEl = container.querySelector('#SaifPracticeNotesList');
    this.notesEmptyEl = container.querySelector('#SaifPracticeNotesEmpty');
    this.refUrl = container.getAttribute('data-reference-url') || '';

    // Mode tabs + Tuner + Drill
    this.tabs = container.querySelectorAll('.saif-practice__tab');
    this.tunerSection = container.querySelector('#SaifPracticeTuner');
    this.tunerNote = container.querySelector('#SaifTunerNote');
    this.tunerCents = container.querySelector('#SaifTunerCents');
    this.tunerFreq = container.querySelector('#SaifTunerFreq');
    this.tunerNeedle = container.querySelector('#SaifTunerNeedle');
    this.tunerNeedleLabel = container.querySelector('#SaifTunerNeedleLabel');
    this.tunerHint = container.querySelector('#SaifTunerHint');
    this.tunerStartBtn = container.querySelector('#SaifTunerStartBtn');
    this.tunerTargetBox = container.querySelector('#SaifTunerTargetBox');
    this.tunerYouBox = container.querySelector('#SaifTunerYouBox');
    this.tunerTargetNote = container.querySelector('#SaifTunerTargetNote');
    this.tunerTargetDetail = container.querySelector('#SaifTunerTargetDetail');
    this.tunerCaptureBtn = container.querySelector('#SaifTunerCaptureBtn');
    this.tunerClearBtn = container.querySelector('#SaifTunerClearBtn');
    this.tunerTargetMidi = null; // null = no target locked
    this._tunerLastFreq = -1;    // for capture button
    this.tunerRefBtn = container.querySelector('#SaifTunerRefBtn');
    this.tunerRefLabel = container.querySelector('#SaifTunerRefLabel');
    this.tunerRefIconPlay = this.tunerRefBtn ? this.tunerRefBtn.querySelector('.saif-practice__tuner-ref-icon-play') : null;
    this.tunerRefIconPause = this.tunerRefBtn ? this.tunerRefBtn.querySelector('.saif-practice__tuner-ref-icon-pause') : null;
    this.tunerTargetGhost = container.querySelector('#SaifTunerTargetGhost');
    this.drillSection = container.querySelector('#SaifPracticeDrill');
    this.drillEmpty = container.querySelector('#SaifDrillEmpty');
    this.drillRunner = container.querySelector('#SaifDrillRunner');
    this.drillProgress = container.querySelector('#SaifDrillProgress');
    this.drillTargetNote = container.querySelector('#SaifDrillTargetNote');
    this.drillTargetDetail = container.querySelector('#SaifDrillTargetDetail');
    this.drillNeedle = container.querySelector('#SaifDrillNeedle');
    this.drillHoldFill = container.querySelector('#SaifDrillHoldFill');
    this.drillCurrent = container.querySelector('#SaifDrillCurrent');
    this.drillStartBtn = container.querySelector('#SaifDrillStartBtn');
    this.drillSkipBtn = container.querySelector('#SaifDrillSkipBtn');
    this.drillRestartBtn = container.querySelector('#SaifDrillRestartBtn');
    // ---- Range test (vocal-range mode) ----
    this.rangeSection = container.querySelector('#SaifPracticeRange');
    this.rangeStage = container.querySelector('#SaifPracticeRangeStage');
    this.rangeStep = container.querySelector('#SaifPracticeRangeStep');
    this.rangeNote = container.querySelector('#SaifPracticeRangeNote');
    this.rangeLevelEl = container.querySelector('#SaifPracticeRangeLevel'); // live mic-input meter fill
    this.rangeHoldFill = container.querySelector('#SaifPracticeRangeHoldFill');
    this.rangeHint = container.querySelector('#SaifPracticeRangeHint');
    this.rangeResult = container.querySelector('#SaifPracticeRangeResult');
    this.rangeBarLow = container.querySelector('#SaifPracticeRangeBarLow');
    this.rangeBarHigh = container.querySelector('#SaifPracticeRangeBarHigh');
    this.rangeBarFill = container.querySelector('#SaifPracticeRangeBarFill');
    this.rangeSummary = container.querySelector('#SaifPracticeRangeSummary');
    this.rangeRecs = container.querySelector('#SaifPracticeRangeRecs');
    this.rangeStartBtn = container.querySelector('#SaifPracticeRangeStartBtn');
    this.rangeAcceptBtn = container.querySelector('#SaifPracticeRangeAcceptBtn');
    this.rangeRedoBtn = container.querySelector('#SaifPracticeRangeRedoBtn');
    this.rangeRetestBtn = container.querySelector('#SaifPracticeRangeRetestBtn');
    // Last-resort "set your range by ear" manual picker (surfaced only on a long stall).
    this.rangeManualEl = container.querySelector('#SaifPracticeRangeManual');
    this.rangeManualLowNote = container.querySelector('#SaifPracticeRangeManualLowNote');
    this.rangeManualHighNote = container.querySelector('#SaifPracticeRangeManualHighNote');
    this._rangeManualLow = null;
    this._rangeManualHigh = null;
    this._rangeManualShown = false;
    this._rangeCaptureStart = 0;
    this._toneCtx = null;
    this._rangeLastMidi = null;   // last voiced note — persists through silence so it can still be locked
    this._rangeShownMidi = null;  // note currently displayed (best / live / last) — what Accept locks
    this._rangeAcceptShown = false;
    this.rangeActive = false;
    this.rangeState = 'idle';
    this._rangeHoldStart = null;
    this._rangeHoldMidis = [];
    this._rangeBest = null;       // most extreme sustained note this step
    this._rangeBestAt = 0;        // when the extreme last improved (settle timer)
    this._rangeStepStart = 0;     // when the current listening step began
    this._rangeLowMidi = null;
    this._rangeHighMidi = null;
    this._rangeCandidate = null;
    this._rangeStarting = false;
    this._rangeCenterMidi = null;   // natural-voice anchor (hummed pitch)
    this._rangeCenterRms = 0;       // loudness reference for the full-voice guard
    this._rangeHoldRms = [];        // per-frame loudness of the current steady hold
    this._rangeWeakHint = false;    // user has pushed past full voice into fry/breath
    this._rangeSawWeak = 0;         // timestamp of the last qualified-but-too-weak note
    this._rangeLastTick = 0;        // last _rangeTick time (to detect a backgrounded-tab gap)
    this._rangeUnsteadyUntil = 0;   // voice is wavering (hold keeps resetting) until this time
    this._rangeTooCloseUntil = 0;   // a full note too near center to be a confident extreme
    this._rangeFallback = null;     // closest full-voice note beyond center (narrow-voice lock)
    this._rangeWeakFallback = null; // most-extreme *weak* note — last-resort lock so a quiet/raspy voice never hangs
    this.controlsSection = container.querySelector('.saif-practice__controls');
    this.historySection = container.querySelector('.saif-practice__history');
    this.currentMode = 'practice';
    this.tunerActive = false;
    this.drillActive = false;
    this.drillSequence = [];
    this.drillIndex = 0;

    // API config
    this.apiUrl = container.getAttribute('data-api-url') || '';

    // ---- Practice Studio paywall gate ----
    // Only enforced when the snippet is rendered with data-gated="true" (the
    // standalone Practice Studio page). Inside purchased course lessons the
    // studio is ungated so paying students are never blocked.
    this.gated = container.getAttribute('data-gated') === 'true';
    this.takeButtonsEl = container.querySelector('#SaifPracticeTakeButtons');
    this.lockEl = container.querySelector('#SaifPracticeLock');
    this.lockTitleEl = container.querySelector('#SaifPracticeLockTitle');
    this.lockTextEl = container.querySelector('#SaifPracticeLockText');
    this.lockMsgEl = container.querySelector('#SaifPracticeLockMsg');
    this.trialBtn = container.querySelector('#SaifPracticeTrialBtn');
    this.registerBtn = container.querySelector('#SaifPracticeRegisterBtn');
    this.subscribeBtn = container.querySelector('#SaifPracticeSubscribeBtn');
    // Free-trial countdown strip (populated from the entitlement snapshot).
    this.trialStripEl = container.querySelector('#SaifPracticeTrialStrip');
    this.trialDayEl = container.querySelector('#SaifPracticeTrialDay');
    this.trialBarEl = container.querySelector('#SaifPracticeTrialBar');
    this._trialTimer = null;   // setInterval handle for the live countdown
    // Falls back to the courses collection when no dedicated subscription URL
    // is configured yet, so the lock card is NEVER a dead end (there's always
    // an actionable CTA) and the Subscribe button never points at a bare "#".
    this.subscribeUrl = container.getAttribute('data-subscribe-url') || saifPath('/collections/courses');
    this.registerUrl = container.getAttribute('data-register-url') || saifPath('/account/register');
    // Storefront login URL (with return-to-this-page baked in by Liquid) —
    // used to make every "Sign in to …" status actionable instead of a dead
    // sentence. Falls back to the bare account page.
    this.loginUrl = container.getAttribute('data-login-url') || saifPath('/account/login');
    this._studioStatus = null;        // last entitlement snapshot from the server
    this._studioStatusPromise = null; // in-flight status load (awaited before a take)
    this._pendingStudioConsume = false; // this take should spend the free try on completion
    this._pendingRangeConsume = false;  // a completed range test should spend the free try

    // ---- First-visit spotlight tour ----
    // Only offered on the standalone Practice Studio page (data-tour="true").
    // Lesson-embedded studios omit the tour param entirely, so tourEnabled is
    // false there and the whole feature no-ops (normal open studio). The four
    // teaser/replay elements only exist under {% if tour %}; cache them anyway
    // (querySelector returns null harmlessly on the lesson variant).
    this.tourEnabled = container.getAttribute('data-tour') === 'true';
    this.tourTeaserEl = container.querySelector('#SaifPracticeTourTeaser');
    this.tourStartBtn = container.querySelector('#SaifPracticeTourStart');
    this.tourSkipBtn = container.querySelector('#SaifPracticeTourSkip');
    this.tourReplayBtn = container.querySelector('#SaifPracticeTourReplay');
    this._tour = null;         // live tour engine (DOM refs + handlers) while running
    this._tourStarting = false; // guards a double-start while the status promise resolves

    this.init();
  }

  PracticeStudio.prototype.init = function () {
    var self = this;

    // Kick off the paywall gate first (no-op unless gated). Loads the
    // entitlement status and wires the lock-card CTAs. Recording is hard-gated
    // in startRecording(), which awaits this status before proceeding.
    this._initStudioGate();

    // First-visit spotlight tour. No-op unless the studio was rendered
    // tour-enabled. Runs after the gate is kicked off so _tourStart can await
    // the entitlement status before measuring the (async-revealed) take buttons.
    this._initTour();

    setPreservePitch(this.refAudioEl);
    if (this.refUrl && this.refAudioEl) {
      this.refAudioEl.src = this.refUrl;
    }

    // Show the streak badge immediately on load if the user has a live streak.
    // No-op if the badge element isn't in this snippet variant.
    this._refreshStreakBadge();

    if (this.refPlayBtn) {
      this.refPlayBtn.addEventListener('click', function () {
        if (!self.refAudioEl) {
          console.warn('[saif-practice] Listen clicked but refAudioEl missing');
          return;
        }
        if (!self.refAudioEl.src) {
          console.warn('[saif-practice] Listen clicked but no audio source set (refUrl:', self.refUrl, ')');
          return;
        }
        if (self.refAudioEl.paused) {
          // Undo any volume/mute the maqam-library clip bar left on this shared
          // element — Listen must always be audible (see startRecording note).
          try { self.refAudioEl.volume = 1; self.refAudioEl.muted = false; } catch (e2) {}
          var p = self.refAudioEl.play();
          if (p && typeof p.then === 'function') {
            p.then(function () {
              slog('[saif-practice] reference playing —', self.refAudioEl.currentSrc.slice(-60));
            }).catch(function (err) {
              console.error('[saif-practice] reference play() rejected:', err && err.name, err && err.message,
                '— maybe autoplay policy / CORS / bad URL:', self.refAudioEl.src);
            });
          }
          self.refPlayBtn.classList.add('saif-practice__btn--active');
        } else {
          self.refAudioEl.pause();
          self.refPlayBtn.classList.remove('saif-practice__btn--active');
        }
      });
      if (this.refAudioEl) {
        this.refAudioEl.addEventListener('ended', function () {
          self.refPlayBtn.classList.remove('saif-practice__btn--active');
        });
      }
    }

    // Speed controls for reference audio
    var speedBtns = this.container.querySelectorAll('.saif-practice__speed-btn');
    speedBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!self.refAudioEl) return;
        var rate = parseFloat(btn.getAttribute('data-speed'));
        self.refAudioEl.playbackRate = rate;
        speedBtns.forEach(function (b) {
          b.classList.remove('saif-practice__speed-btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('saif-practice__speed-btn--active');
        btn.setAttribute('aria-pressed', 'true');
        if (window.SaifTrack) window.SaifTrack('practice_speed_changed', { rate: rate });
      });
    });

    // Reference seek bar — same transport pattern as the recorded-take player
    // below (pointer-drag scrub with capture, click-to-seek fallback,
    // arrows/Home/End, ARIA slider), but driving refAudioEl. 2026-07 client
    // request: repeating one phrase used to mean listening to the whole clip
    // and letting it start over.
    if (this.refProgress) {
      if (window.PointerEvent) {
        (function () {
          var bar = self.refProgress;
          var dragging = false;
          bar.style.touchAction = 'none'; // the drag owns the gesture; page shouldn't scroll
          function ratioAt(e) {
            var rect = bar.getBoundingClientRect();
            if (!rect.width) return null;
            return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          }
          bar.addEventListener('pointerdown', function (e) {
            if (self.state === 'recording' || self.state === 'starting') return; // bar is inert during a take — don't capture the gesture
            var r = ratioAt(e);
            if (r == null) return;
            dragging = true;
            try { bar.setPointerCapture(e.pointerId); } catch (err) {}
            self.seekReference(r);
          });
          bar.addEventListener('pointermove', function (e) {
            if (!dragging) return;
            var r = ratioAt(e);
            if (r != null) self.seekReference(r);
          });
          function endDrag() { dragging = false; }
          bar.addEventListener('pointerup', endDrag);
          bar.addEventListener('pointercancel', endDrag);
        })();
      } else {
        this.refProgress.addEventListener('click', function (e) {
          var rect = self.refProgress.getBoundingClientRect();
          if (!rect.width) return;
          var ratio = (e.clientX - rect.left) / rect.width;
          self.seekReference(Math.max(0, Math.min(1, ratio)));
        });
      }
      this.refProgress.addEventListener('keydown', function (e) {
        if (self.state === 'recording' || self.state === 'starting') return; // bar is inert during a take
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.skipReference(-5); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self.skipReference(5); }
        else if (e.key === 'Home') { e.preventDefault(); self.seekReference(0); }
        else if (e.key === 'End') { e.preventDefault(); self.seekReference(1); }
      });
    }
    // Keep the playhead honest for every way position or clip can change:
    // natural playback (timeupdate is rate-independent, so speed pills need
    // nothing), user seeks, the record flow's programmatic rewinds, and clip
    // swaps via picker / upload / deep link / history replay — all assign src
    // on this same element, firing emptied -> durationchange -> loadedmetadata
    // (duration is NaN in between, so the bar shows a zeroed 0:00 / 0:00 until
    // the new metadata lands).
    if (this.refAudioEl && this.refProgress) {
      var refreshRefBar = function () { self._refreshRefTransportUI(); };
      ['loadedmetadata', 'durationchange', 'timeupdate', 'seeked', 'emptied'].forEach(function (evt) {
        self.refAudioEl.addEventListener(evt, refreshRefBar);
      });
      // The stage playhead needs frames, not timeupdate (which fires ~4x a
      // second and would visibly step). The loop exists ONLY while the clip is
      // actually sounding: it stops itself on pause/end, and refuses to run
      // during a take, when the live renderer owns the canvas and draws its own
      // playhead. A resting instrument must not cost a frame loop.
      var pumpPlayhead = function () {
        self._refPlayheadRAF = 0;
        if (!self.refAudioEl || self.refAudioEl.paused) return;
        if (self.state === 'recording') return;
        try { self._drawIdleCanvas(); } catch (e) {}
        self._refPlayheadRAF = window.requestAnimationFrame(pumpPlayhead);
      };
      if (window.requestAnimationFrame) {
        this.refAudioEl.addEventListener('play', function () {
          if (!self._refPlayheadRAF) self._refPlayheadRAF = window.requestAnimationFrame(pumpPlayhead);
        });
      }
      // A pause or a scrub still has to land the playhead on its new spot, and
      // the loop is not running then to do it.
      ['pause', 'seeked', 'ended', 'emptied'].forEach(function (evt) {
        self.refAudioEl.addEventListener(evt, function () { try { self._drawIdleCanvas(); } catch (e) {} });
      });
      this.refAudioEl.addEventListener('ended', function () {
        // Finished clip: snap back to the start (same as the recorded-take
        // player's onended) so the next press of Listen plays from the top.
        // A mid-clip pause keeps its position — that's the point of the playhead.
        try { self.refAudioEl.currentTime = 0; } catch (e) {}
        self._refreshRefTransportUI();
      });
      this._refreshRefTransportUI();
    }

    if (this.recordBtn) {
      this.recordBtn.addEventListener('click', function () {
        slog('[saif-practice] record button clicked');
        self.startRecording(true);
      });
    } else {
      console.warn('[saif-practice] #SaifPracticeRecordBtn not found in container');
    }
    if (this.practiceRunBtn) {
      this.practiceRunBtn.addEventListener('click', function () {
        slog('[saif-practice] practice-run button clicked');
        self.startRecording(false);
      });
    }
    if (this.stopBtn) {
      this.stopBtn.addEventListener('click', function () {
        slog('[saif-practice] stop button clicked');
        self.stopRecording();
      });
    } else {
      console.warn('[saif-practice] #SaifPracticeStopBtn not found in container');
    }
    // init runs as a prototype method — `container` is not in scope here
    // (same trap the view-toggle block notes below). Use this.container.
    this.dockWireV2(this.container);
    this.sheetWireV2(this.container);
    this.resultWireV2(this.container);
    this.nowbarWireV2(this.container);
    this._idleStage();
    // The first call runs before the card has been laid out, so the canvas is
    // 0x0 and the paint is thrown away — the stage stayed black until something
    // else happened to repaint it. Repaint once layout exists, and again
    // whenever the canvas actually changes size.
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { try { self._idleStage(); } catch (e) {} });
    }
    if (window.ResizeObserver && this.liveCanvas && this.liveCanvas.parentElement) {
      try {
        // Observe the WRAPPER, not the canvas. resizeCanvases() measures the
        // parent and then writes canvas.style.width/height, so watching the
        // canvas meant watching the one element that only changes as a RESULT
        // of a redraw. And the callback has to re-size before it redraws:
        // _drawIdleCanvas reads canvas.width — the backing store, which stays 0
        // until resizeCanvases() runs — so a bare redraw returned early and the
        // stage stayed black for as long as the page lived.
        new ResizeObserver(function () {
          try { self.resizeCanvases(); } catch (e) {}
          try { self._drawIdleCanvas(); } catch (e) {}
        }).observe(this.liveCanvas.parentElement);
      } catch (e) {}
    }
    if (this.playbackBtn) {
      this.playbackBtn.addEventListener('click', function () { self.togglePlayback(); });
    }
    if (this.playerSkipBackBtn) {
      this.playerSkipBackBtn.addEventListener('click', function () { self.skipPlayback(-5); });
    }
    if (this.playerSkipFwdBtn) {
      this.playerSkipFwdBtn.addEventListener('click', function () { self.skipPlayback(5); });
    }
    if (this.playerProgress) {
      // Pointer-drag scrubbing (not click-only) — dragging to the exact phrase
      // you drifted on is the core review gesture, and click-only seeking
      // simply doesn't work as scrubbing on touch screens. Pointer capture
      // keeps the drag alive even when the finger wanders off the 7px track.
      if (window.PointerEvent) {
        (function () {
          var bar = self.playerProgress;
          var dragging = false;
          bar.style.touchAction = 'none'; // the drag owns the gesture; page shouldn't scroll
          function ratioAt(e) {
            var rect = bar.getBoundingClientRect();
            if (!rect.width) return null;
            return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          }
          bar.addEventListener('pointerdown', function (e) {
            var r = ratioAt(e);
            if (r == null) return;
            dragging = true;
            try { bar.setPointerCapture(e.pointerId); } catch (err) {}
            self.seekPlayback(r);
          });
          bar.addEventListener('pointermove', function (e) {
            if (!dragging) return;
            var r = ratioAt(e);
            if (r != null) self.seekPlayback(r);
          });
          function endDrag() { dragging = false; }
          bar.addEventListener('pointerup', endDrag);
          bar.addEventListener('pointercancel', endDrag);
        })();
      } else {
        // Ancient browsers without PointerEvent keep the old click-to-seek.
        this.playerProgress.addEventListener('click', function (e) {
          var rect = self.playerProgress.getBoundingClientRect();
          if (!rect.width) return;
          var ratio = (e.clientX - rect.left) / rect.width;
          self.seekPlayback(Math.max(0, Math.min(1, ratio)));
        });
      }
      // Keyboard support for the role="slider" seek bar: arrows nudge ±5s,
      // Home/End jump to the start/end of the take.
      this.playerProgress.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          self.skipPlayback(-5);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          self.skipPlayback(5);
        } else if (e.key === 'Home') {
          e.preventDefault();
          self.seekPlayback(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          self.seekPlayback(1);
        }
      });
    }
    if (this.retryBtn) {
      // "Try Again" actually tries again: reset AND start the next take in one
      // tap, preserving the take type (a Practice-run student stays in
      // practice mode — reset() alone silently flipped them back to Record).
      // This is the tightest loop in the product (~20 taps a session); the old
      // reset-only behavior forced scroll-up + a second tap every time.
      // The click gesture carries through reset() into startRecording, so mic
      // permission and autoplay priming still work.
      this.retryBtn.addEventListener('click', function () {
        // Always a REAL take. This used to preserve the previous take type, so
        // a student who had tapped "Practice run" once stayed in practice mode
        // every Try Again after it and nothing they recorded was saveable.
        // (startRecording closes the open result sheet and runs the 3·2·1
        // countdown, so one tap goes straight from the score back into a take.)
        self.reset();
        self.startRecording(true);
      });
    }
    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', function () { self.requestAIFeedback(); });
    }
    if (this.feedbackSubmitBtn) {
      this.feedbackSubmitBtn.addEventListener('click', function () { self.submitForFeedback(); });
    }
    // Submit-for-feedback note dialog.
    if (this.noteSendBtn) {
      this.noteSendBtn.addEventListener('click', function () {
        var note = (self.noteInput && self.noteInput.value || '').trim();
        var cb = self._noteSendCb;
        self._closeNoteDialog();
        if (cb) cb(note);
      });
    }
    if (this.noteCancelBtn) {
      this.noteCancelBtn.addEventListener('click', function () { self._closeNoteDialog(); });
    }
    if (this.noteOverlay) {
      this.noteOverlay.addEventListener('click', function (e) {
        if (e.target === self.noteOverlay) self._closeNoteDialog();
      });
    }
    // History multi-select mode + bulk actions.
    if (this.historySelectBtn) {
      this.historySelectBtn.addEventListener('click', function () { self._toggleSelectMode(); });
    }
    if (this.historyBulkSubmit) {
      this.historyBulkSubmit.addEventListener('click', function () { self._bulkSubmit(); });
    }
    if (this.historyBulkDelete) {
      this.historyBulkDelete.addEventListener('click', function () { self._bulkDelete(); });
    }
    // "Listen" next-step — starts/pauses playback of the take and reveals
    // the full transport (skip/scrub/time) which stays collapsed until the
    // student asks for it. Keeps the first look at a result score-focused.
    if (this.listenBtn) {
      this.listenBtn.addEventListener('click', function () {
        if (self.playerEl) self.playerEl.hidden = false;
        self.togglePlayback();
      });
    }
    // "Compare" next-step — jumps straight to the pitch comparison.
    if (this.compareBtn) {
      this.compareBtn.addEventListener('click', function () {
        self._switchResultTab('compare');
        // Older markup has no result tabs (compare always visible) —
        // either way, bring the graph into view.
        var wrap = self.compareCanvas && self.compareCanvas.parentElement;
        if (wrap && typeof wrap.scrollIntoView === 'function') {
          wrap.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'nearest'
          });
        }
      });
    }
    // Clickable 1-2-3 onboarding strip — each step performs (or scrolls to)
    // the action it describes instead of just naming it.
    var onboardBtns = this.container.querySelectorAll('.saif-practice__onboard-step-btn');
    onboardBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = btn.getAttribute('data-onboard');
        var scrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
        if (step === 'listen') {
          if (self.refUrl && self.refPlayBtn) {
            if (self.refSection) {
              self.refSection.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
            }
            // Only start it — a second tap on the strip shouldn't stop a
            // clip the student is mid-listen on (the Listen button toggles).
            if (self.refAudioEl && self.refAudioEl.paused) self.refPlayBtn.click();
          } else {
            // No reference yet — open the in-studio picker right here instead
            // of narrating a trip to another page. Fallback text only when
            // this snippet variant has no picker button.
            if (!self._openRefPicker()) {
              self.setStatus('Pick a clip in the Maqam Library first — it loads here as your reference.');
            }
          }
        } else if (step === 'record') {
          if (self.recordBtn && !self.recordBtn.hidden) {
            self.recordBtn.scrollIntoView({ behavior: scrollBehavior, block: 'center' });
            // Two gentle pulses to land the eye (none under reduced motion —
            // the CSS keyframes are disabled there).
            self.recordBtn.classList.remove('saif-practice__record-btn--nudge');
            void self.recordBtn.offsetWidth;
            self.recordBtn.classList.add('saif-practice__record-btn--nudge');
            try { self.recordBtn.focus({ preventScroll: true }); } catch (e) {}
          }
        } else if (step === 'compare') {
          if (self.resultSection && !self.resultSection.hidden) {
            self._switchResultTab('compare');
            self.resultSection.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
          } else {
            self.setStatus('Record a take first — your comparison appears right after you press Stop.');
          }
        }
      });
    });

    // Mode tab switching + first-visit "NEW" pills. The pill is a small
    // onboarding nudge — it disappears the first time the student clicks
    // that mode (state persists in localStorage so it doesn't keep
    // re-appearing on refresh). Practice itself is always seen on init.
    if (this.tabs && this.tabs.length) {
      var SEEN_KEY = 'saif:practice:modesSeen';
      var seen = {};
      try { seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}'); }
      catch (e) {}
      seen.practice = 1;
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch (e) {}
      this.tabs.forEach(function (tab) {
        var mode = tab.getAttribute('data-tab');
        // The "NEW" pill is reserved for genuinely-new features (tabs flagged
        // with data-new) — NOT every tab the student simply hasn't opened yet.
        // Otherwise a first-time visitor sees "NEW" on Tune/Drill/Range at once.
        if (mode && tab.hasAttribute('data-new') && !seen[mode]) tab.classList.add('saif-practice__tab--unseen');
        tab.addEventListener('click', function () {
          if (mode && !seen[mode]) {
            seen[mode] = 1;
            try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch (e) {}
            tab.classList.remove('saif-practice__tab--unseen');
          }
          self.switchMode(mode);
        });
      });
    }
    if (this.tunerStartBtn) {
      this.tunerStartBtn.addEventListener('click', function () {
        if (self.tunerActive) self.stopTuner();
        else self.startTuner();
      });
    }
    if (this.tunerCaptureBtn) {
      this.tunerCaptureBtn.addEventListener('click', function () {
        if (self._tunerLastFreq > 0) {
          self.tunerTargetMidi = Math.round(freqToMidi(self._tunerLastFreq));
          self._tunerRefreshTarget();
        }
      });
    }
    if (this.tunerClearBtn) {
      this.tunerClearBtn.addEventListener('click', function () {
        self.tunerTargetMidi = null;
        self._tunerRefreshTarget();
      });
    }
    // Reference play/pause inside the tuner panel — saves the user from
    // hunting for the Listen button below. Same audio element either way.
    if (this.tunerRefBtn) {
      this.tunerRefBtn.addEventListener('click', function () {
        if (!self.refAudioEl) {
          console.warn('[saif-practice] tuner ref button: no refAudioEl');
          return;
        }
        if (!self.refAudioEl.src) {
          console.warn('[saif-practice] tuner ref button: no source set');
          return;
        }
        if (self.refAudioEl.paused) {
          var p = self.refAudioEl.play();
          if (p && typeof p.then === 'function') {
            p.then(function () { self._tunerSyncRefBtn(); })
              .catch(function (err) {
                console.error('[saif-practice] tuner ref play rejected:', err && err.name, err && err.message);
              });
          }
          self._tunerSyncRefBtn();
        } else {
          self.refAudioEl.pause();
          self._tunerSyncRefBtn();
        }
      });
    }
    // Keep the in-panel button's icon synced if the user starts/pauses the
    // reference from the OTHER button (the existing Listen button below).
    if (this.refAudioEl) {
      this.refAudioEl.addEventListener('play', function () {
        self._tunerSyncRefBtn();
        // Header Listen button reflects playback started from ANY source
        // (Tune-tab button, maqam modal, programmatic) so its icon stays synced.
        if (self.refPlayBtn) self.refPlayBtn.classList.add('saif-practice__btn--active');
        // Resume the active recording (if any) when the reference resumes.
        // The user is following the clip — when it stops, they should stop
        // singing too, so the recording shouldn't keep eating their voice.
        // Resume whenever a reference-driven take is rolling — record take OR
        // Practice run. A Practice run has no MediaRecorder, but the Date.now()
        // pitch clock still ran through the pause, so the clock compensation
        // must not be gated on the recorder existing.
        if (self.state === 'recording' && self.refUrl) {
          try {
            if (self.mediaRecorder && self.mediaRecorder.state === 'paused') {
              self.mediaRecorder.resume();
              slog('[saif-practice] mediaRecorder resumed (ref playing)');
            }
            // Shift the pitch clock past the paused wall-time (see the pause
            // listener below) so contour time keeps matching audio time.
            if (self._recPausedAtMs && self._recClockAdjust) {
              self._recClockAdjust(Date.now() - self._recPausedAtMs);
            }
            self._recPausedAtMs = null;
          }
          catch (e) { console.warn('[saif-practice] resume/clock-adjust failed:', e && e.message); }
        }
      });
      this.refAudioEl.addEventListener('pause', function () {
        self._tunerSyncRefBtn();
        if (self.refPlayBtn) self.refPlayBtn.classList.remove('saif-practice__btn--active');
        // Pause the recording so the user's silence during a clip pause
        // doesn't get mixed into the saved take. MediaRecorder.pause() is
        // supported in Chrome, Firefox, and Safari 14.5+.
        // Pause whenever a reference-driven take is rolling — record take OR
        // Practice run. A Practice run has no MediaRecorder to pause, but the
        // Date.now() pitch clock still needs the pause-stamp; without it the
        // contour runs on through the pause and desyncs from the reference.
        if (self.state === 'recording' && self.refUrl) {
          try {
            if (self.mediaRecorder && self.mediaRecorder.state === 'recording') {
              self.mediaRecorder.pause();
              slog('[saif-practice] mediaRecorder paused (ref paused)');
            }
            // Stamp when the pause began — on resume the recording clock is
            // shifted by the paused duration so the pitch contour stays in
            // sync with the AUDIO timeline (MediaRecorder drops paused time
            // from the file; the Date.now()-based pitch clock didn't, so
            // every pause desynced the saved take from its pitch data).
            self._recPausedAtMs = Date.now();
          }
          catch (e) { console.warn('[saif-practice] mediaRecorder.pause failed:', e && e.message); }
        }
      });
      this.refAudioEl.addEventListener('ended', function () {
        self._tunerSyncRefBtn();
        // Auto-stop the recording when the reference clip finishes. The
        // pause/resume listeners above already mirror MediaRecorder state to
        // refAudioEl state, but `pause` doesn't fire on natural end — only
        // `ended` does. Without this, the user keeps recording silence past
        // the clip's last note. Works at any playback rate because `ended`
        // fires when the audio's currentTime reaches its actual duration,
        // regardless of how fast we played through it.
        if (self.state === 'recording') {
          slog('[saif-practice] reference clip ended — auto-stopping recording');
          try { self.stopRecording(); }
          catch (e) { console.warn('[saif-practice] auto-stop on ended failed:', e && e.message); }
        }
      });
    }
    if (this.drillStartBtn) {
      this.drillStartBtn.addEventListener('click', function () {
        if (self.drillActive) self.stopDrill();
        else self.startDrill();
      });
    }
    if (this.drillSkipBtn) {
      this.drillSkipBtn.addEventListener('click', function () { self._drillAdvance(true); });
    }
    if (this.drillRestartBtn) {
      this.drillRestartBtn.addEventListener('click', function () {
        // Completion stopped the mic + closed the drill context, so a real
        // restart must go back through startDrill (which re-acquires the mic
        // and resets drillIndex/UI itself). Faking the running state here
        // left a dead meter that claimed to be "Listening…".
        if (!self.drillActive) self.startDrill();
      });
    }
    // ---- Range test controls ----
    if (this.rangeStartBtn) {
      this.rangeStartBtn.addEventListener('click', function () { self.startRangeTest(); });
    }
    if (this.rangeAcceptBtn) {
      this.rangeAcceptBtn.addEventListener('click', function () { self._rangeAccept(); });
    }
    if (this.rangeRedoBtn) {
      this.rangeRedoBtn.addEventListener('click', function () { self._rangeRedo(); });
    }
    if (this.rangeRetestBtn) {
      this.rangeRetestBtn.addEventListener('click', function () { self._rangeRetest(); });
    }
    // By-ear manual picker (last resort).
    [['#SaifPracticeRangeManualLowDown', function () { self._rangeManualStep('low', -1); }],
     ['#SaifPracticeRangeManualLowUp', function () { self._rangeManualStep('low', 1); }],
     ['#SaifPracticeRangeManualHighDown', function () { self._rangeManualStep('high', -1); }],
     ['#SaifPracticeRangeManualHighUp', function () { self._rangeManualStep('high', 1); }],
     ['#SaifPracticeRangeManualLowPlay', function () { self._rangePlayTone(self._rangeManualLow); }],
     ['#SaifPracticeRangeManualHighPlay', function () { self._rangePlayTone(self._rangeManualHigh); }],
     ['#SaifPracticeRangeManualSave', function () { self._rangeManualSave(); }]
    ].forEach(function (pair) {
      var b = self.container.querySelector(pair[0]);
      if (b) b.addEventListener('click', pair[1]);
    });

    // View toggle (Graph ↔ Verse text on comparison view).
    // Use this.container (init runs as a prototype method — `container`
    // isn't in scope here, only inside the constructor body).
    var viewBtns = this.container.querySelectorAll('.saif-practice__view-btn');
    var compareWrap = this.container.querySelector('#SaifPracticeCompareWrap');
    var versePanel = this.container.querySelector('#SaifPracticeVersePanel');
    if (viewBtns.length && compareWrap && versePanel) {
      viewBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var view = btn.getAttribute('data-view');
          viewBtns.forEach(function (b) {
            b.classList.toggle('saif-practice__view-btn--active', b === btn);
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          compareWrap.hidden = (view !== 'graph');
          versePanel.hidden = (view !== 'text');
        });
      });
    }

    // ── Notes / annotations ─────────────────────────────────────────────
    // Add-note: capture the current playhead time (recording playback if
    // available, otherwise the reference clip), prompt for text, persist.
    if (this.notesAddBtn) {
      this.notesAddBtn.addEventListener('click', function () {
        if (!self.refUrl) {
          self.setStatus('Pick a clip in the Maqam Library first — notes attach to a specific clip.');
          return;
        }
        var time = 0;
        if (self._playbackAudio && self._playbackAudio.duration) {
          time = self._playbackAudio.currentTime || 0;
        } else if (self.refAudioEl) {
          time = self.refAudioEl.currentTime || 0;
        }
        // window.prompt is intentionally simple — keeps v1 small and avoids
        // shipping a custom modal that needs its own focus / keyboard
        // handling. Trim + length-cap the input so a paste-bomb doesn't
        // explode localStorage.
        var raw = window.prompt('Note for ' + fmtClock(time) + ':', '');
        if (raw == null) return;
        var text = String(raw).trim().slice(0, 240);
        if (!text) return;
        var notes = self._getTakeNotes();
        notes.push({ ts: Date.now(), time: time, text: text });
        notes.sort(function (a, b) { return a.time - b.time; });
        self._setTakeNotes(notes);
        self._renderNotes();
      });
    }
    // Delegated handler for delete + seek inside the list.
    if (this.notesListEl) {
      this.notesListEl.addEventListener('click', function (evt) {
        var delBtn = evt.target.closest('.saif-practice__notes-delete');
        if (delBtn) {
          var idx = parseInt(delBtn.getAttribute('data-idx'), 10);
          if (isNaN(idx)) return;
          var notes = self._getTakeNotes();
          notes.splice(idx, 1);
          self._setTakeNotes(notes);
          self._renderNotes();
          return;
        }
        var seekBtn = evt.target.closest('.saif-practice__notes-seek');
        if (seekBtn) {
          var t = parseFloat(seekBtn.getAttribute('data-time'));
          if (isNaN(t)) return;
          var audio = self._playbackAudio;
          if (audio && isFinite(audio.duration)) {
            audio.currentTime = Math.max(0, Math.min(audio.duration, t));
            if (audio.paused) audio.play().catch(function () {});
          } else if (self.refAudioEl) {
            self.refAudioEl.currentTime = t;
            if (self.refAudioEl.paused) self.refAudioEl.play().catch(function () {});
          }
        }
      });
    }
    // Render any existing notes for this clip on first paint
    this._renderNotes();

    // ── Result tab strip ──────────────────────────────────────────────
    // Same pattern as the lesson-page tabs: click switches the visible
    // panel via [hidden]; persistent across recordings within a session.
    // When we open the result panel after a recording we always reset
    // to the Score tab (in onRecordingComplete) so users see the score
    // first regardless of which tab was active during the previous take.
    var resultTabs = this.container.querySelectorAll('.saif-practice__result-tab');
    var resultPanels = this.container.querySelectorAll('.saif-practice__result-panel');
    if (resultTabs.length && resultPanels.length) {
      this._resultTabs = resultTabs;
      this._resultPanels = resultPanels;
      resultTabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var name = tab.getAttribute('data-result-tab');
          self._switchResultTab(name);
        });
      });
    }

    // ── Interruption handling ───────────────────────────────────────────
    // Screen lock, incoming call, or app switch cuts the mic on phones; the
    // page previously kept "recording" a dead stream and the student lost the
    // take. On hidden: stop (not discard) a rolling take — MediaRecorder's
    // onstop salvages everything captured so far — and shut down tuner/drill
    // mics so the browser's mic indicator doesn't stay lit in the background.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      if (self.state === 'recording') {
        slog('[saif-practice] page hidden mid-take — stopping to save what we have');
        self.setStatus('Recording stopped when the page went to the background — everything up to that point was kept.');
        try { self.stopRecording(); } catch (e) {}
      }
      if (self.tunerActive) { try { self.stopTuner(); } catch (e) {} }
      if (self.drillActive) { try { self.stopDrill(); } catch (e) {} }
    });

    // Escape closes the note dialog (and the review upsell) — expected of any
    // modal; the overlay previously only closed via its buttons/backdrop.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.noteOverlay && !self.noteOverlay.hidden) self._closeNoteDialog();
      if (self._upsellOverlay && !self._upsellOverlay.hidden) self._upsellOverlay.hidden = true;
    });

    // Delegated clicks for JS-injected controls (they don't exist at init):
    //  - .saif-practice__pick-ref-link  → open the in-studio reference picker
    //  - .saif-practice__save-retry    → retry a failed library save
    //  - .saif-practice__review-callout-btn → submit this take for review
    this.container.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest && t.closest('.saif-practice__pick-ref-link')) {
        self._openRefPicker();
      } else if (t.closest && t.closest('.saif-practice__save-retry')) {
        // A history replay has overwritten the shared contour with a saved
        // take's data — retrying the save now would persist the WRONG take.
        // Ignore until a fresh take (which clears _historyReplayActive) resets it.
        if (self._historyReplayActive) return;
        if (self.recordedBlob) self.saveRecordingToLibrary(self._lastSaveScore || null);
      } else if (t.closest && t.closest('.saif-practice__review-callout-btn')) {
        self.submitForFeedback();
      }
    });

    // ?ref=<clip-url>&reftitle=<label> deep link — lets the Weekly Challenge
    // (and emails) land students on the studio with the clip already loaded
    // instead of an empty page. Restricted to our own audio hosts so the
    // param can't point the studio at arbitrary third-party URLs.
    try {
      var qs = new URLSearchParams(window.location.search);
      var deepRef = qs.get('ref');
      if (deepRef && /^https:\/\/(api\.saifvoice\.net|cdn\.shopify\.com)\//.test(deepRef)) {
        this.loadReference(deepRef, { clipTitle: qs.get('reftitle') || '' });
        if (this.refSection) this.refSection.hidden = false;
      }
    } catch (e) { /* URLSearchParams missing on ancient browsers — fine */ }

    this.resizeCanvases();
    window.addEventListener('resize', function () {
      self.resizeCanvases();
      try { self._nowbarSync(); } catch (e) {}
      try { self._syncTootTree(true); } catch (e) {}
      // Reassigning canvas.width/height wipes the pixels. The live canvas
      // self-heals (its rAF loop repaints every frame), but the comparison
      // graph only draws on demand — repaint it whenever the result panel is
      // visible so orientation changes / iOS URL-bar resizes don't leave a
      // blank graph.
      if (self.resultSection && !self.resultSection.hidden) {
        var a = self._playbackAudio;
        try {
          self.drawComparison(a && !a.paused && !a.ended ? (a.currentTime || 0) : undefined);
        } catch (e) {}
      }
    });

    // Visibility audit — catches CSS regressions where a class rule like
    // `display: flex` overrides the [hidden] attribute. Uses getComputedStyle
    // because offsetParent is unreliable for SVG elements (returns null even
    // when the SVG is visible) and `el.id` on SVG returns an SVGAnimatedString
    // object, not a string — both made the previous check spam false warnings.
    setTimeout(function () {
      // Debug-only: this forces a getComputedStyle() over every [hidden] node in
      // the studio (dozens), a full style recalc on EVERY production load for a
      // CSS-regression check that only matters during development. Gate it behind
      // SAIF_STUDIO_DEBUG so real visitors — especially on older phones — don't
      // pay for it.
      if (!(SAIF_STUDIO_DEBUG || (typeof window !== 'undefined' && window.SAIF_STUDIO_DEBUG))) return;
      var hiddenEls = self.container.querySelectorAll('[hidden]');
      var fails = 0;
      hiddenEls.forEach(function (el) {
        var style = getComputedStyle(el);
        if (style.display !== 'none') {
          fails++;
          var label = (typeof el.id === 'string' && el.id) ? el.id
                    : (typeof el.className === 'string' && el.className) ? el.className
                    : el.tagName;
          console.warn('[saif-practice] visibility audit — element marked [hidden] is still visible:',
            label, '(display=' + style.display + ') — CSS is overriding the hidden attribute');
        }
      });
      slog('[saif-practice] visibility audit complete:', hiddenEls.length, 'checked,', fails, 'fail(s)');
      slog('[saif-practice] config — refUrl:', self.refUrl || '(none)', '| apiUrl:', self.apiUrl || '(none)',
        '| AI feedback:', self.apiUrl ? 'ENABLED' : 'disabled');
    }, 0);

    // Practice history — load on init so the list shows past sessions
    var historyEl = this.container.querySelector('#SaifPracticeHistoryList');
    if (historyEl) {
      // Delegate clicks for play / delete. Top-level entry log so the next
      // time delete "does nothing" we can see whether the click is even
      // reaching this handler or being captured elsewhere.
      // Checkbox selection in multi-select mode (delegated change event).
      historyEl.addEventListener('change', function (evt) {
        var cb = evt.target.closest('.saif-practice__history-check');
        if (cb) self._onRecCheck(cb);
      });
      historyEl.addEventListener('click', function (evt) {
        var t = evt.target;
        // In multi-select mode the whole row toggles selection — short-circuit
        // play/submit/delete so selecting can't trigger them by accident.
        if (self._selectMode) {
          if (t.closest('.saif-practice__history-check')) return; // native change handles it
          var selRow = t.closest('.saif-practice__history-item');
          if (selRow) {
            var cbx = selRow.querySelector('.saif-practice__history-check');
            if (cbx) { cbx.checked = !cbx.checked; self._onRecCheck(cbx); }
          }
          return;
        }
        slog('[saif-practice] history list click — target:',
          t.tagName,
          t.className && typeof t.className === 'string' ? t.className.slice(0, 60) : '',
          '| closest delete?', !!t.closest('.saif-practice__history-delete'),
          '| closest play?', !!t.closest('.saif-practice__history-play'));
        var playBtn = evt.target.closest('.saif-practice__history-play');
        if (playBtn) {
          var url = playBtn.getAttribute('data-url');
          var recId = playBtn.closest('.saif-practice__history-item') &&
                      playBtn.closest('.saif-practice__history-item').getAttribute('data-rec-id');
          if (!url) return;

          // If this row is already the active one, toggle pause/play.
          if (self._historyAudio && self._historyPlayingId === recId) {
            if (self._historyAudio.paused) {
              self._historyAudio.play().catch(function () {});
            } else {
              self._historyAudio.pause();
            }
            return;
          }

          // Different row (or first play) — stop any currently-playing
          // recording first, restore previous play button visuals, then
          // boot a fresh audio + comparison-graph replay for this take.
          if (self._historyAudio) {
            try { self._historyAudio.pause(); } catch (e) {}
            self._historyResetButton();
          }
          self._startHistoryPlayback(playBtn, url, recId);
          return;
        }
        // Feedback pill — expand the instructor's text + voice note inline
        // and mark it seen (clears the site-wide unread badge).
        var fbPill = evt.target.closest('.saif-practice__history-fb');
        if (fbPill) {
          var fbItem = fbPill.closest('.saif-practice__history-item');
          var fbRecId = fbItem && fbItem.getAttribute('data-rec-id');
          var fbRec = null;
          var fbItems = self._historyItems || [];
          for (var fi = 0; fi < fbItems.length; fi++) {
            if (fbItems[fi].id === fbRecId) { fbRec = fbItems[fi]; break; }
          }
          if (!fbItem || !fbRec || !fbRec.feedback) return;
          var existingDetail = fbItem.querySelector('.saif-practice__history-fbdetail');
          if (existingDetail) { existingDetail.remove(); return; } // toggle closed
          var d = document.createElement('div');
          d.className = 'saif-practice__history-fbdetail';
          var fbWhen = fbRec.feedback.createdAt
            ? new Date(fbRec.feedback.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
          var html = '<p class="saif-practice__history-fbdetail-meta">Feedback from your instructor' +
            (fbWhen ? ' · ' + escapeAttr(fbWhen) : '') + '</p>';
          if (fbRec.feedback.text) {
            var tEl = document.createElement('p');
            tEl.className = 'saif-practice__history-fbdetail-text';
            tEl.textContent = fbRec.feedback.text;
            html += tEl.outerHTML;
          }
          if (fbRec.feedback.audio && fbRec.feedback.audio.url) {
            html += '<audio controls preload="none" src="' + escapeAttr(fbRec.feedback.audio.url) + '"></audio>';
          }
          d.innerHTML = html;
          fbItem.appendChild(d);
          // Mark seen (once) — server + local state + pill + header badge.
          if (!fbRec.feedback.seenAt) {
            fbRec.feedback.seenAt = new Date().toISOString();
            fbPill.classList.remove('saif-practice__history-fb--new');
            fbPill.textContent = (window.SaifI18n && window.SaifI18n.feedback_pill) || '💬 feedback';
            fetch('/apps/saif/recordings/feedback-seen', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ recordingId: fbRecId })
            }).then(function () {
              return fetch('/apps/saif/recordings/feedback-status').then(function (r) { return r.json(); });
            }).then(function (st) {
              if (st && st.ok && window.SaifFeedbackBadgeApply) window.SaifFeedbackBadgeApply(st.unread);
            }).catch(function () {});
          }
          return;
        }
        var subBtn = evt.target.closest('.saif-practice__history-submit');
        if (subBtn) {
          var subId = subBtn.getAttribute('data-id');
          if (!subId) return;
          var subSync = window.SaifSync;
          if (!subSync || !subSync.recordings || !subSync.recordings.submit) {
            self._bulkStatus('Sign in to submit recordings for instructor feedback.', true, { signIn: true });
            return;
          }
          self._promptSubmitNote({
            title: 'Submit for feedback',
            sub: 'Add a note for your teacher (optional) — a question, or what you’d like them to focus on.',
            onSend: function (note) {
              subBtn.disabled = true;
              subBtn.textContent = (window.SaifI18n && window.SaifI18n.submitting) || 'Submitting…';
              if (window.SaifTrack) window.SaifTrack('practice_submitted_for_feedback', { recording_id: subId, source: 'history', has_note: !!(note && note.length) });
              subSync.recordings.submit(subId, true, null, note || null).then(function (rr) {
                if (rr && rr.ok) {
                  if (rr.reviewCreditsRemaining != null) self._renderReviewCredits(rr.reviewCreditsRemaining);
                  self.refreshHistory();
                } else {
                  if (rr && rr.error === 'NOT_SIGNED_IN') {
                    self._bulkStatus('Sign in to submit recordings for instructor feedback.', true, { signIn: true });
                  } else if (rr && rr.error === 'NO_REVIEW_CREDITS') {
                    self._renderReviewCredits(0);
                    self._bulkStatus('This take wasn’t sent — you’re out of Personal Reviews.', true);
                    self._upsellReviewDialog('This take wasn’t sent — you’re out of Personal Reviews. Top up to get Saif’s notes on it.');
                  }
                  subBtn.disabled = false;
                  subBtn.textContent = SUBMIT_LABEL;
                }
              }).catch(function (err) {
                console.error('[saif-practice] history submit failed:', err);
                self._bulkStatus('We couldn’t submit that recording — please try again.', true);
                subBtn.disabled = false;
                subBtn.textContent = SUBMIT_LABEL;
              });
            }
          });
          return;
        }
        var delBtn = evt.target.closest('.saif-practice__history-delete');
        if (delBtn) {
          var id = delBtn.getAttribute('data-id');
          if (!id) {
            console.warn('[saif-practice] delete clicked but no data-id on button');
            return;
          }
          if (!confirm((window.SaifI18n && window.SaifI18n.delete_recording_confirm) || 'Delete this recording?')) return;
          // If this is the take that is currently playing, stop it first. The
          // row (and its pause button) is about to be removed from the DOM, so
          // the audio would otherwise keep playing with nothing left to stop it.
          if (self._historyPlayingId === id) self._historyStop();
          var sync = window.SaifSync;
          if (!sync || !sync.recordings) {
            console.error('[saif-practice] delete: SaifSync.recordings unavailable — must be signed in');
            self._bulkStatus('Sign in to delete saved recordings.', true, { signIn: true });
            return;
          }
          slog('[saif-practice] deleting recording', id);
          // Optimistic removal — pull the item from the DOM right away so
          // the user gets immediate feedback. Shopify metafield reads can
          // briefly return stale data after a write, and the previous
          // implementation re-fetched the list straight after delete which
          // sometimes brought the deleted item back briefly. With optimistic
          // removal, the item disappears the moment the user confirms.
          var itemEl = delBtn.closest('.saif-practice__history-item');
          if (itemEl) {
            itemEl.style.opacity = '0.4';
            itemEl.style.pointerEvents = 'none';
          }
          sync.recordings.delete(id).then(function (r) {
            slog('[saif-practice] delete response:', r);
            if (r && r.ok) {
              if (itemEl && itemEl.parentNode) itemEl.parentNode.removeChild(itemEl);
              // Defer the full refresh so Shopify's metafield write can
              // propagate before we re-read the list.
              setTimeout(function () { self.refreshHistory(); }, 800);
            } else {
              // Restore the item visually since the delete failed
              if (itemEl) {
                itemEl.style.opacity = '';
                itemEl.style.pointerEvents = '';
              }
              var msg = (r && r.error) || 'unknown error';
              console.error('[saif-practice] delete failed:', msg);
              self._bulkStatus('We couldn’t delete that recording — please try again.', true);
            }
          }).catch(function (err) {
            if (itemEl) {
              itemEl.style.opacity = '';
              itemEl.style.pointerEvents = '';
            }
            console.error('[saif-practice] delete threw:', err);
            self._bulkStatus('We couldn’t delete that recording — please try again.', true);
          });
        }
      });
      this.refreshHistory();
    }
  };

  /**
   * Swap the reference audio URL at runtime. Used by the maqam library
   * popup to point the studio at a freshly-clicked clip without a full
   * re-init. Clears pitch caches so the karaoke target redraws from
   * scratch instead of mixing data from the previous reference.
   */
  PracticeStudio.prototype.loadReference = function (newUrl, options) {
    previewStop(); // a new reference supersedes any inline clip preview
    var url = String(newUrl || '');
    slog('[saif-practice] loadReference called with', url || '(empty)');
    // Switching clips supersedes any history-replay reference snapshot — the
    // new clip is now the source of truth, so drop the stale snapshot.
    this._refSnapshot = null;
    this.refUrl = url;
    // Optional clip title — used when stamping recordings into history so
    // the user can tell "this take was for Bayati" vs "for Hijaz" instead
    // of just seeing date+score with no context. Falls back to whatever
    // is already on the container (set by openPracticeModal in
    // saif-maqam-library.liquid).
    this.refClipTitle = (options && options.clipTitle) ||
      (this.container && this.container.getAttribute('data-reference-title')) || '';
    // Custom (user-uploaded) reference: local practice only — takes made
    // against it can't be submitted for a Personal Review, and analysis is
    // handled by the chunked uploader (skipAnalysis) instead of the live path.
    this._customRef = !!(options && options.custom);
    var customNote = document.getElementById('SaifPracticeRefCustomNote');
    if (customNote) customNote.hidden = !this._customRef;
    if (this.container) this.container.setAttribute('data-reference-url', url);
    // Clips load asynchronously, well after the seat-on-init timer — without
    // this he stayed wherever he first landed for the rest of the session.
    var _selfRef = this;
    setTimeout(function () { try { _selfRef._tootHome(); } catch (e) {} }, 700);
    // Hide the post-recording result panel — switching to a new clip
    // shouldn't carry the previous clip's score over. Without this, the
    // student opens Practice on a new clip and sees "0 / 10" or the
    // previous take's score before they've even recorded anything.
    if (this.resultSection) this.resultSection.hidden = true;
    // Reset internal recording buffers so a stale recPitchData from the
    // previous clip can't leak into the new clip's score / graph.
    this.recPitchData = [];
    this.recordedChunks = [];
    // Also clear the compare-chart alignment offset — it belongs to the PREVIOUS
    // take's score; a reference preview or a new clip must not inherit its shift.
    this._scoreAlignOffset = 0;
    this.recordedBlob = null;
    this.recordedUrl = null;
    this.state = 'idle';
    if (this.scoreEl) this.scoreEl.textContent = '0.0';
    if (this.refAudioEl) {
      try { this.refAudioEl.pause(); } catch (e) {}
      // Empty url = "Remove reference" → clear the element cleanly (setting
      // src='' would try to load the page as audio and fire a bogus error).
      if (url) { this.refAudioEl.src = url; } else { try { this.refAudioEl.removeAttribute('src'); } catch (e) {} }
      try { this.refAudioEl.currentTime = 0; } catch (e) {}
      // preload="auto" — start downloading the full file as soon as the
      // modal opens. The user is in the practice modal specifically to
      // practice this clip, so they almost always click Play within a
      // few seconds. Pre-downloading means clicking Play feels instant
      // because the file is already in the HTTP cache. The previous
      // metadata-only preload meant Play kicked off the actual download
      // (1-3s lag on a 500-700KB MP3). The old "loading spinner" issue
      // came from a blocking overlay, not from preload=auto itself —
      // we removed the overlay; the studio stays interactive while the
      // file streams in the background.
      this.refAudioEl.preload = 'auto';
      // Re-apply preservesPitch — some browsers reset the flag when src changes,
      // so the slow-down speed buttons would silently revert to pitch-shifting.
      setPreservePitch(this.refAudioEl);
      try { this.refAudioEl.load(); } catch (e) {}
    } else {
      // Snippet rendered without reference audio block (older theme version).
      // Karaoke target still works (analyzeReference uses fetch + AudioContext)
      // but the user can't preview the reference clip — flag it once.
      console.warn('[saif-practice] loadReference: refAudioEl missing — Listen-to-Reference button will not work');
    }
    // Auto-transpose our reference clips into the student's saved vocal range.
    // No-op for uploads, when no range is saved, or when the DSP asset is
    // absent — in those cases the studio keeps the clip's original key.
    this._refShiftInit(url);
    // Show/hide the entire reference section based on whether we have a URL.
    if (this.refSection) this.refSection.hidden = !url;
    this._refExtraSync(); // remove-reference button + headphones hint
    // Reset analysis caches so the next recording uses this clip's pitch range
    this.refPitchData = [];
    this._pitchRange = null;
    this._refRange = null;
    var self = this;
    // Refresh the in-tuner Play-reference button now that a URL is available.
    this._tunerSyncRefBtn();
    // Reset maqam label until the new clip is analyzed
    if (this.refMaqamLabel) {
      this.refMaqamLabel.textContent = '';
      this.refMaqamLabel.hidden = true;
    }
    // Refresh notes for the new clip (each clip has its own notes list)
    this._renderNotes();
    if (url && !(options && options.skipAnalysis)) {
      // Defer the heavy analysis so the panel feels as fast as the maqam
      // library. Without this, every clip click downloads the full MP3,
      // decodes it to PCM, and runs pitch detection on the main thread —
      // 1-3s of perceived lag while the user just wants to hit Listen.
      // requestIdleCallback runs it once the browser is idle; if the user
      // clicks Record first, startRecording's own fallback (line ~1849)
      // kicks off analysis on demand.
      var runAnalysis = function () {
        if (self._loadSeq != null && self.refUrl !== url) return;
        self.analyzeReference().then(function () {
          if (self.drillSection) self._drillRefreshAvailability();
        });
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runAnalysis, { timeout: 2500 });
      } else {
        setTimeout(runAnalysis, 600);
      }
    }
  };

  /**
   * Open the in-studio reference picker (the same dialog behind the
   * "🎵 Choose a reference" bar button). Returns true when the picker exists
   * and was opened — callers fall back to a text hint when it doesn't
   * (older snippet variants without the refbar).
   */
  PracticeStudio.prototype._openRefPicker = function () {
    var openBtn = document.getElementById('SaifPracticeRefChange');
    if (!openBtn) return false;
    try { openBtn.click(); } catch (e) { return false; }
    return true;
  };

  /**
   * Styled "out of Personal Reviews" upsell — replaces window.confirm(),
   * which on iOS renders as a scary system alert that reads like a browser
   * error. Reuses the note-dialog classes so it inherits the overlay look.
   */
  PracticeStudio.prototype._upsellReviewDialog = function (message) {
    var ov = this._upsellOverlay;
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'saif-practice__note-overlay';
      ov.innerHTML =
        '<div class="saif-practice__note-dialog" role="dialog" aria-modal="true" aria-label="Personal Reviews">' +
          '<h4 class="saif-practice__note-title">Out of Personal Reviews</h4>' +
          '<p class="saif-practice__note-sub" data-upsell-msg></p>' +
          '<div class="saif-practice__note-actions">' +
            '<button type="button" class="saif-practice__note-cancel" data-upsell-close>Not now</button>' +
            '<a class="saif-practice__note-send saif-practice__note-send--link" href="' + saifPath('/pages/review-packs') + '">Get reviews →</a>' +
          '</div>' +
        '</div>';
      this.container.appendChild(ov);
      ov.addEventListener('click', function (e) {
        if (e.target === ov || (e.target.closest && e.target.closest('[data-upsell-close]'))) ov.hidden = true;
      });
      this._upsellOverlay = ov;
    }
    var msgEl = ov.querySelector('[data-upsell-msg]');
    if (msgEl) msgEl.textContent = message ||
      'You’re out of Personal Reviews — top up to keep getting Saif’s notes on your recitation.';
    ov.hidden = false;
  };

  /* ==============================================
     MODE TABS — Practice / Tuner
     ============================================== */

  /**
   * Switch between Practice and Tuner modes. Mutually exclusive — only one
   * holds the mic at a time. Refuses to switch mid-recording so we don't
   * lose data; user has to stop first.
   */
  PracticeStudio.prototype.switchMode = function (mode) {
    if (mode !== 'practice' && mode !== 'tuner' && mode !== 'drill' && mode !== 'range') return;
    if (this.currentMode === mode) return;
    if (this.state === 'recording') {
      this.setStatus('Stop recording before switching modes.');
      console.warn('[saif-practice] switchMode blocked — recording in progress');
      return;
    }
    previewStop(); // leaving/entering any mode stops an inline clip preview
    // Stop any other-mode mic activity before switching
    if (this.tunerActive && mode !== 'tuner') this.stopTuner();
    if (this.drillActive && mode !== 'drill') this.stopDrill();
    // Leaving the range tab mid-test forgives the free try (abandoned test).
    if (this.rangeActive && mode !== 'range') this.stopRangeTest(false);

    this.currentMode = mode;
    if (this.tabs) {
      this.tabs.forEach(function (t) {
        var active = t.getAttribute('data-tab') === mode;
        t.classList.toggle('saif-practice__tab--active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    if (this.tunerSection) this.tunerSection.hidden = (mode !== 'tuner');
    if (this.rangeSection) this.rangeSection.hidden = (mode !== 'range');
    if (this.drillSection) {
      this.drillSection.hidden = (mode !== 'drill');
      if (mode === 'drill') {
        this._drillRefreshAvailability();
      }
    }
    // Refresh the in-tuner Play-reference button visibility — it depends on
    // whether refUrl is set, NOT on whether the tuner mic is active.
    if (mode === 'tuner') this._tunerSyncRefBtn();

    // Range tab = voice-profile home: entering it with a saved range and no
    // test in progress shows the saved result, not the idle start screen.
    if (mode === 'range' && this.rangeState === 'idle' && !this.rangeActive) {
      var savedRange = getUserRangeSync();
      if (savedRange) this._rangeShowSaved(savedRange);
    }

    // Hide practice-mode UI when in any non-practice mode.
    // refSection stays visible across modes — user needs to play the reference
    // clip while in Tuner / Drills (target tracking uses it).
    var nonPractice = (mode !== 'practice');
    // liveSection is deliberately NOT in this list any more: the stage is the
    // hero and stays mounted in every mode, which also keeps the card tall
    // enough that the tool sheets do not need to scroll.
    var practiceEls = [this.controlsSection,
                       this.resultSection, this.historySection];
    practiceEls.forEach(function (el) {
      if (!el) return;
      el.style.display = nonPractice ? 'none' : '';
    });
    var savingEl = this.container.querySelector('#SaifPracticeSavingStatus');
    if (savingEl) savingEl.style.display = nonPractice ? 'none' : '';

    slog('[saif-practice] switched to mode:', mode);
  };

  /**
   * Start the always-on tuner. Independent mic stream from the practice
   * recording — they're mutually exclusive via the mode switch but use
   * separate AudioContext instances to keep state simple.
   */
  PracticeStudio.prototype.startTuner = async function () {
    var self = this;
    if (this.tunerActive) return;
    previewStop();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this._setTunerHint('Microphone API not available in this browser.');
      return;
    }
    this._setTunerHint('Requesting microphone access…');
    // Create the AudioContext synchronously in the click gesture — iOS Safari
    // starts post-await contexts 'suspended', so the analyser reads silence.
    this.tunerCtx = createGestureAudioContext();
    try {
      // autoGainControl off — steady input (see startRecording) so the tuner needle doesn't jitter.
      this.tunerStream = await navigator.mediaDevices.getUserMedia({ audio: { autoGainControl: false } });
    } catch (err) {
      console.error('[saif-practice] tuner getUserMedia failed:', err && err.name, err && err.message);
      this._setTunerHint(micErrorMessage(err));
      closeAudioContext(this.tunerCtx);
      this.tunerCtx = null;
      return;
    }

    if (!this.tunerCtx || this.tunerCtx.state === 'closed') {
      this.tunerCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.tunerCtx.state === 'suspended') {
      try { this.tunerCtx.resume(); } catch (e) {}
    }
    var source = this.tunerCtx.createMediaStreamSource(this.tunerStream);
    var analyser = this.tunerCtx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    this.tunerActive = true;
    if (this.tunerStartBtn) {
      this.tunerStartBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg> Stop Tuner';
      this.tunerStartBtn.classList.add('saif-practice__tuner-btn--active');
    }
    this._tunerRefreshTarget();
    this._setTunerHint('Sing a note, then click Capture to lock it as your target. The needle will then show distance from that note.');

    var dataArray = new Float32Array(analyser.fftSize);
    var tunerLevel = { peak: 0 }; // per-session level for adaptiveBoostFrame
    function tick() {
      if (!self.tunerActive) return;
      requestAnimationFrame(tick);

      // Reference tracking: if a reference clip is playing, derive the auto
      // target from its current pitch AND animate a "ghost" indicator on the
      // bar showing where that pitch sits relative to the locked target.
      // Pausing the reference freezes the target and hides the ghost — gives
      // the student room to find the locked note at their own pace.
      var refPlaying = !!(self.refAudioEl && self.refUrl &&
        !self.refAudioEl.paused && !self.refAudioEl.ended);
      if (refPlaying) {
        var t = self.refAudioEl.currentTime || 0;
        var refFreq = self.getRefPitchAt(t);
        if (refFreq) {
          var refMidi = freqToMidi(refFreq);
          var newTarget = Math.round(refMidi);
          if (newTarget !== self.tunerTargetMidi) {
            self.tunerTargetMidi = newTarget;
            self._tunerRefreshTarget();
          }
          // Position the ghost on the bar. Cents-off from the LOCKED target,
          // mapped to 0-100% across the meter (-50¢ left, +50¢ right).
          // Since target = round(refMidi), this oscillates ±50¢ as the
          // reference moves between integer notes — exactly the "back-and-forth
          // on the bar" the user asked for.
          if (self.tunerTargetGhost && self.tunerTargetMidi != null) {
            var refCents = (refMidi - self.tunerTargetMidi) * 100;
            var ghostPct = Math.max(0, Math.min(100, 50 + refCents));
            self.tunerTargetGhost.style.left = ghostPct + '%';
            self.tunerTargetGhost.hidden = false;
          }
        }
      } else if (self.tunerTargetGhost) {
        self.tunerTargetGhost.hidden = true;
      }

      analyser.getFloatTimeDomainData(dataArray);
      var freq = detectPitch(adaptiveBoostFrame(dataArray, tunerLevel), self.tunerCtx.sampleRate);
      self._renderTuner(freq);
    }
    tick();
    self._tunerSyncRefBtn();
    slog('[saif-practice] tuner started');
  };

  PracticeStudio.prototype.stopTuner = function () {
    this.tunerActive = false;
    if (this.tunerStream) {
      this.tunerStream.getTracks().forEach(function (t) { t.stop(); });
      this.tunerStream = null;
    }
    if (this.tunerCtx) {
      try { this.tunerCtx.close(); } catch (e) {}
      this.tunerCtx = null;
    }
    if (this.tunerStartBtn) {
      this.tunerStartBtn.innerHTML = '<span class="saif-practice__record-dot" aria-hidden="true"></span> Start Tuner';
      this.tunerStartBtn.classList.remove('saif-practice__tuner-btn--active');
    }
    if (this.tunerCaptureBtn) this.tunerCaptureBtn.hidden = true;
    this._renderTuner(-1);
    this._setTunerHint('Click Start Tuner and sing — the dial will lock to the closest note.');
    slog('[saif-practice] tuner stopped');
  };

  /**
   * Update the tuner display from a detected frequency.
   *
   * Two modes:
   *   1. No target locked - BandLab-style. Note letter = nearest pitch,
   *      cents-off = distance from that nearest note (-50..+50 across needle).
   *   2. Target locked - cents-off measured against the LOCKED note instead,
   *      so the needle stays meaningful while the student tries to find it.
   */
  PracticeStudio.prototype._renderTuner = function (freq) {
    this._tunerLastFreq = freq;
    if (freq > 0) {
      var midi = freqToMidi(freq);
      var nearest = Math.round(midi);
      var noteName = midiToNoteName(nearest);
      var hasTarget = (this.tunerTargetMidi != null);
      var anchor = hasTarget ? this.tunerTargetMidi : nearest;
      var cents = (midi - anchor) * 100;
      var absCents = Math.abs(cents);
      var inTune = absCents <= 10;
      var matched = hasTarget && absCents <= 50;

      if (this.tunerNote) {
        this.tunerNote.textContent = noteName;
        this.tunerNote.classList.toggle('saif-practice__tuner-box-note--in-tune', inTune && !hasTarget);
      }
      if (this.tunerCents) {
        var sign = cents > 0 ? '+' : '';
        var i18n = window.SaifI18n || {};
        var centsTpl = hasTarget ? (i18n.cents_from_target || '{n} cents from target') : (i18n.cents_only || '{n} cents');
        this.tunerCents.textContent = centsTpl.replace('{n}', sign + cents.toFixed(0));
      }
      if (this.tunerFreq) this.tunerFreq.textContent = freq.toFixed(1) + ' Hz';
      if (this.tunerNeedle) {
        var clamped = Math.max(-50, Math.min(50, cents));
        this.tunerNeedle.style.left = (50 + clamped) + '%';
        this.tunerNeedle.classList.toggle('saif-practice__tuner-needle--in-tune', inTune);
      }
      // Live label that floats with the needle so the magnitude is read
      // exactly where the eye already is. Sign explicit so flat vs sharp
      // is unambiguous; only re-renders when the integer value changes
      // (saves a few hundred DOM writes per second on slow phones).
      if (this.tunerNeedleLabel) {
        var labelCents = Math.round(cents);
        if (labelCents !== this._tunerLabelCents) {
          this._tunerLabelCents = labelCents;
          this.tunerNeedleLabel.textContent =
            (labelCents > 0 ? '+' : '') + labelCents + '¢';
          this.tunerNeedleLabel.classList.toggle('saif-practice__tuner-needle-label--in-tune', inTune);
        }
      }
      if (this.tunerYouBox) {
        this.tunerYouBox.classList.toggle('saif-practice__tuner-box--match', matched && inTune);
      }
      if (this.tunerCaptureBtn) this.tunerCaptureBtn.hidden = !this.tunerActive;
    } else {
      if (this.tunerNote) {
        this.tunerNote.textContent = '—';
        this.tunerNote.classList.remove('saif-practice__tuner-box-note--in-tune');
      }
      // The idle reading, which is what the tuner shows until a note is sung — i.e. most of
      // the time. Same key as a live reading, with an em dash standing in for the number.
      if (this.tunerCents) this.tunerCents.textContent =
        (((window.SaifI18n || {}).cents_only) || '{n} cents').replace('{n}', '—');
      if (this.tunerFreq) this.tunerFreq.textContent = '—';
      if (this.tunerNeedle) {
        this.tunerNeedle.style.left = '50%';
        this.tunerNeedle.classList.remove('saif-practice__tuner-needle--in-tune');
      }
      if (this.tunerNeedleLabel) {
        this.tunerNeedleLabel.textContent = '—';
        this.tunerNeedleLabel.classList.remove('saif-practice__tuner-needle-label--in-tune');
        this._tunerLabelCents = null;
      }
      if (this.tunerYouBox) this.tunerYouBox.classList.remove('saif-practice__tuner-box--match');
    }
  };

  PracticeStudio.prototype._tunerRefreshTarget = function () {
    var hasTarget = (this.tunerTargetMidi != null);
    if (this.tunerTargetNote) {
      this.tunerTargetNote.textContent = hasTarget ? midiToNoteName(this.tunerTargetMidi) : '—';
    }
    if (this.tunerTargetDetail) {
      var refLoaded = !!(this.refAudioEl && this.refUrl);
      this.tunerTargetDetail.textContent = hasTarget
        ? (refLoaded && !this.refAudioEl.paused ? 'Following reference clip' : 'Sing this note')
        : (refLoaded ? 'Press Listen to start auto-tracking' : 'No target locked');
    }
    if (this.tunerTargetBox) {
      this.tunerTargetBox.classList.toggle('saif-practice__tuner-box--locked', hasTarget);
    }
    if (this.tunerClearBtn) this.tunerClearBtn.hidden = !hasTarget;
    if (this.tunerCaptureBtn && this.tunerActive) {
      var label = hasTarget ? 'Re-capture' : 'Capture target';
      var icon = this.tunerCaptureBtn.querySelector('svg');
      this.tunerCaptureBtn.innerHTML = '';
      if (icon) this.tunerCaptureBtn.appendChild(icon);
      this.tunerCaptureBtn.appendChild(document.createTextNode(' ' + label));
    }
  };

  PracticeStudio.prototype._setTunerHint = function (text) {
    if (this.tunerHint) this.tunerHint.textContent = text;
  };

  /**
   * Sync the in-panel Play/Pause Reference button with refAudioEl state.
   * Hides itself when no reference URL is loaded.
   */
  PracticeStudio.prototype._tunerSyncRefBtn = function () {
    if (!this.tunerRefBtn) return;
    var hasRef = !!(this.refAudioEl && this.refUrl);
    this.tunerRefBtn.hidden = !hasRef;
    if (!hasRef) return;
    var playing = !this.refAudioEl.paused && !this.refAudioEl.ended;
    // toggleAttribute, not `.hidden =` — SVGElement has no `hidden` IDL
    // property, so the assignment is a JS expando that never hides anything.
    if (this.tunerRefIconPlay) this.tunerRefIconPlay.toggleAttribute('hidden', playing);
    if (this.tunerRefIconPause) this.tunerRefIconPause.toggleAttribute('hidden', !playing);
    if (this.tunerRefLabel) this.tunerRefLabel.textContent = playing ? 'Pause reference' : 'Play reference';
    // Hide the target ghost when the reference isn't playing — there's nothing
    // to track, and a static ghost would just clutter the bar.
    if (this.tunerTargetGhost) this.tunerTargetGhost.hidden = !playing;
  };

  /* ==============================================
     DRILL MODE — auto-quantized note matching
     ==============================================
     Quantizes the loaded reference's pitch contour into a sequence of
     "stable notes" — discrete MIDI integers that were sustained for at
     least MIN_HOLD_SEC. Then walks the student through them, advancing
     when they hold the target pitch within ±50¢ for HOLD_TARGET_MS.
  */
  var DRILL_MIN_HOLD_SEC = 0.20;     // minimum reference duration to count as a "note"
  var DRILL_HOLD_TARGET_MS = 5000;   // user must hold the target note this long (2026-07: raised from 800ms)
  var DRILL_TOLERANCE_CENTS = 50;    // ±50¢ counts as on-pitch
  var DRILL_GRACE_MS = 300;          // brief dropouts (a breath, one bad frame) shorter than this PAUSE the hold instead of zeroing it

  /**
   * Take refPitchData ({time, freq}[]) and produce a sequence of unique
   * sustained-note MIDI integers. Consecutive duplicates are dropped — if
   * the reference held the same note across a phrase, we only present it
   * once in the drill.
   */
  PracticeStudio.prototype.quantizeReference = function () {
    var ref = this.refPitchData || [];
    if (ref.length < 5) return [];

    // Group into 100ms windows, take median MIDI of each
    var windows = [];
    var winStart = ref[0].time;
    var winSamples = [];
    for (var i = 0; i < ref.length; i++) {
      if (ref[i].time - winStart >= 0.1) {
        if (winSamples.length) {
          winSamples.sort(function (a, b) { return a - b; });
          windows.push({
            time: winStart,
            midi: Math.round(winSamples[Math.floor(winSamples.length / 2)])
          });
        }
        winStart = ref[i].time;
        winSamples = [];
      }
      winSamples.push(freqToMidi(ref[i].freq));
    }
    if (winSamples.length) {
      winSamples.sort(function (a, b) { return a - b; });
      windows.push({ time: winStart, midi: Math.round(winSamples[Math.floor(winSamples.length / 2)]) });
    }

    // Find runs of the same MIDI integer of duration ≥ MIN_HOLD_SEC
    var sequence = [];
    var runStart = 0;
    for (var i = 1; i <= windows.length; i++) {
      var atEnd = (i === windows.length);
      var changed = !atEnd && windows[i].midi !== windows[runStart].midi;
      if (atEnd || changed) {
        var endTime = atEnd ? windows[i - 1].time + 0.1 : windows[i].time;
        var duration = endTime - windows[runStart].time;
        if (duration >= DRILL_MIN_HOLD_SEC) {
          var midi = windows[runStart].midi;
          // Skip if same as the last sequence item (de-dupe across short gaps)
          if (!sequence.length || sequence[sequence.length - 1] !== midi) {
            sequence.push(midi);
          }
        }
        runStart = i;
      }
    }
    return sequence;
  };

  PracticeStudio.prototype._drillRefreshAvailability = function () {
    if (!this.drillSection) return;
    var sequence = this.quantizeReference();
    var hasSequence = sequence.length > 0;
    if (this.drillEmpty) this.drillEmpty.hidden = hasSequence;
    if (this.drillRunner) this.drillRunner.hidden = !hasSequence;
    if (this.drillStartBtn) this.drillStartBtn.hidden = !hasSequence;
    if (hasSequence) {
      this.drillSequence = sequence;
      this.drillIndex = 0;
      this._drillRefreshUI();
    }
  };

  PracticeStudio.prototype._drillRefreshUI = function () {
    if (!this.drillProgress) return;
    var seq = this.drillSequence || [];
    var idx = this.drillIndex || 0;

    // Progress dots
    var dots = '';
    for (var i = 0; i < seq.length; i++) {
      var cls = 'saif-practice__drill-progress-dot';
      if (i < idx) cls += ' saif-practice__drill-progress-dot--done';
      else if (i === idx) cls += ' saif-practice__drill-progress-dot--current';
      dots += '<span class="' + cls + '" aria-hidden="true"></span>';
    }
    this.drillProgress.innerHTML = dots;

    // Target note display
    if (this.drillTargetNote) {
      if (idx < seq.length) {
        this.drillTargetNote.textContent = midiToNoteName(seq[idx]);
        this.drillTargetNote.classList.remove('saif-practice__drill-target-note--hit');
      } else {
        this.drillTargetNote.textContent = '✓';
        this.drillTargetNote.classList.add('saif-practice__drill-target-note--hit');
      }
    }
    if (this.drillTargetDetail) {
      this.drillTargetDetail.textContent = idx < seq.length
        ? 'Note ' + (idx + 1) + ' of ' + seq.length + ' · hold for 5 seconds'
        : 'Drill complete!';
    }
    if (this.drillHoldFill) this.drillHoldFill.style.width = '0%';
    if (this.drillNeedle) this.drillNeedle.style.left = '50%';
  };

  PracticeStudio.prototype.startDrill = async function () {
    var self = this;
    if (this.drillActive) return;
    previewStop();
    if (!this.drillSequence || this.drillSequence.length === 0) {
      this._drillRefreshAvailability();
      if (!this.drillSequence.length) return;
    }
    if (window.SaifTrack) window.SaifTrack('practice_drill_started', {
      sequence_length: this.drillSequence.length,
      ref_url: this.refUrl || null
    });
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this._setDrillCurrent('Microphone API not available.');
      return;
    }
    this._setDrillCurrent('Requesting mic…');
    // Create the AudioContext synchronously in the click gesture — iOS Safari
    // starts post-await contexts 'suspended', so the analyser reads silence.
    this.drillCtx = createGestureAudioContext();
    try {
      // autoGainControl off — steady input (see startRecording) for a consistent drill pitch read.
      this.drillStream = await navigator.mediaDevices.getUserMedia({ audio: { autoGainControl: false } });
    } catch (err) {
      console.error('[saif-practice] drill getUserMedia failed:', err);
      this._setDrillCurrent(micErrorMessage(err));
      closeAudioContext(this.drillCtx);
      this.drillCtx = null;
      return;
    }

    if (!this.drillCtx || this.drillCtx.state === 'closed') {
      this.drillCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.drillCtx.state === 'suspended') {
      try { this.drillCtx.resume(); } catch (e) {}
    }
    var source = this.drillCtx.createMediaStreamSource(this.drillStream);
    var analyser = this.drillCtx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    this.drillActive = true;
    this.drillHoldStart = 0;
    this._drillGapStart = 0;
    this.drillIndex = 0;
    this._drillHadSkips = false;
    this._drillHadHit = false;
    this._drillRefreshUI();

    if (this.drillStartBtn) {
      this.drillStartBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg> Stop Drill';
      this.drillStartBtn.classList.add('saif-practice__tuner-btn--active');
    }
    if (this.drillSkipBtn) this.drillSkipBtn.hidden = false;
    if (this.drillRestartBtn) this.drillRestartBtn.hidden = true;

    var dataArray = new Float32Array(analyser.fftSize);
    var drillLevel = { peak: 0 }; // per-session level for adaptiveBoostFrame
    function tick() {
      if (!self.drillActive) return;
      requestAnimationFrame(tick);
      analyser.getFloatTimeDomainData(dataArray);
      var freq = detectPitch(adaptiveBoostFrame(dataArray, drillLevel), self.drillCtx.sampleRate);
      self._drillTick(freq);
    }
    tick();
    slog('[saif-practice] drill started, sequence:', this.drillSequence);
  };

  PracticeStudio.prototype._drillTick = function (freq) {
    if (this.drillIndex >= this.drillSequence.length) return; // already complete
    // While _drillAdvance is mid-flight (250ms cooldown between notes), the
    // drillIndex has already incremented but the visible target letter still
    // shows the OLD note. If _drillTick runs in this window it would paint
    // the target color based on the new note while the user is still seeing
    // the old letter — a confusing mismatch. Skip painting until the UI catches up.
    if (this.drillAdvancing) return;
    var targetMidi = this.drillSequence[this.drillIndex];
    var now = performance.now();

    if (freq > 0) {
      var midi = freqToMidi(freq);
      var cents = (midi - targetMidi) * 100; // signed: -ve = flat, +ve = sharp
      var absCents = Math.abs(cents);
      var onTarget = absCents <= DRILL_TOLERANCE_CENTS;

      // Needle: -50..+50 cents → 0..100% across the meter
      if (this.drillNeedle) {
        var pct = Math.max(0, Math.min(100, 50 + cents));
        this.drillNeedle.style.left = pct + '%';
        this.drillNeedle.classList.toggle('saif-practice__tuner-needle--in-tune', absCents <= 10);
      }

      // Color the target letter — green within ±10¢ (locked-in), yellow within
      // ±50¢ (close enough to feel like progress). Outside that range it stays
      // neutral so the student knows they're not even on the right note yet.
      if (this.drillTargetNote) {
        this.drillTargetNote.classList.remove(
          'saif-practice__drill-target-note--hit',
          'saif-practice__drill-target-note--close'
        );
        if (absCents <= 10) {
          this.drillTargetNote.classList.add('saif-practice__drill-target-note--hit');
        } else if (absCents <= 50) {
          this.drillTargetNote.classList.add('saif-practice__drill-target-note--close');
        }
      }

      var noteName = midiToNoteName(Math.round(midi));
      this._setDrillCurrent(noteName + ' (' + (cents >= 0 ? '+' : '') + cents.toFixed(0) + '¢)');

      if (onTarget) {
        if (!this.drillHoldStart) this.drillHoldStart = now;
        else if (this._drillGapStart) this.drillHoldStart += now - this._drillGapStart; // recovered from a brief gap: paused time never counts toward the hold
        this._drillGapStart = 0;
        var heldMs = now - this.drillHoldStart;
        var pct = Math.min(100, (heldMs / DRILL_HOLD_TARGET_MS) * 100);
        if (this.drillHoldFill) this.drillHoldFill.style.width = pct + '%';
        if (heldMs >= DRILL_HOLD_TARGET_MS) {
          this._drillAdvance(false);
        }
      } else if (!this._drillGraceHolds(now)) {
        // Lost target for longer than the grace window — reset the hold.
        // A brief blip (a breath, one bad pitch frame) freezes the timer
        // instead (see _drillGraceHolds); only a sustained drop zeroes it.
        this.drillHoldStart = 0;
        this._drillGapStart = 0;
        if (this.drillHoldFill) this.drillHoldFill.style.width = '0%';
      }
    } else {
      // Silence (no pitch): same grace treatment as an off-target frame — a
      // short breath between phrases shouldn't wipe a long hold.
      if (!this._drillGraceHolds(now)) {
        this.drillHoldStart = 0;
        this._drillGapStart = 0;
        if (this.drillHoldFill) this.drillHoldFill.style.width = '0%';
      }
      if (this.drillNeedle) {
        this.drillNeedle.style.left = '50%';
        this.drillNeedle.classList.remove('saif-practice__tuner-needle--in-tune');
      }
      // Drop the target color when no signal is detected so the student isn't
      // misled into thinking they're still on-target during a silence gap.
      if (this.drillTargetNote) {
        this.drillTargetNote.classList.remove(
          'saif-practice__drill-target-note--hit',
          'saif-practice__drill-target-note--close'
        );
      }
      this._setDrillCurrent('Listening…');
    }
  };

  /**
   * Grace window for brief dropouts (one bad pitch frame, a breath, a
   * detector blip): while a hold is in progress, off-target/silent frames
   * shorter than DRILL_GRACE_MS PAUSE the hold timer instead of zeroing it.
   * On recovery _drillTick shifts drillHoldStart forward by the gap length,
   * so paused time never counts and the fill bar stays honest (it freezes
   * during the gap, then resumes from the same width). Returns true while the
   * grace window is still open (caller should NOT reset the hold).
   */
  PracticeStudio.prototype._drillGraceHolds = function (now) {
    if (!this.drillHoldStart) return false;                                 // nothing in progress to protect
    if (!this._drillGapStart) { this._drillGapStart = now; return true; }   // first frame of a gap
    return (now - this._drillGapStart) <= DRILL_GRACE_MS;
  };

  PracticeStudio.prototype._drillAdvance = function (isSkip) {
    // Ignore rapid double-clicks of "Skip note" while the previous advance is
    // still in its 250ms cooldown — otherwise one visual step jumps two notes.
    if (this.drillAdvancing) return;
    this.drillHoldStart = 0;
    this._drillGapStart = 0;
    this.drillIndex++;
    if (isSkip) this._drillHadSkips = true;
    else this._drillHadHit = true; // a genuinely-held note (not a skip)
    // Block _drillTick from painting based on the new drillIndex while the UI
    // still shows the previous letter (it refreshes after the 250ms below).
    this.drillAdvancing = true;
    if (this.drillTargetNote) {
      this.drillTargetNote.classList.add('saif-practice__drill-target-note--hit');
    }
    var self = this;
    setTimeout(function () {
      self._drillRefreshUI();
      if (self.drillIndex >= self.drillSequence.length) {
        // Drill complete
        if (self.drillSkipBtn) self.drillSkipBtn.hidden = true;
        if (self.drillRestartBtn) self.drillRestartBtn.hidden = false;
        self._setDrillCurrent('Done — all notes hit!');
        // Stop the mic so the page goes quiet on completion
        self.stopDrill(/*keepUI=*/true);
        // Mini confetti on full clean run — any skipped note along the way
        // disqualifies the run, not just a skip on the final note.
        if (!self._drillHadSkips && self.drillSection) spawnConfetti(self.drillSection, 1800);
        // A completed drill with at least one genuinely-held note counts as
        // practice for the day — the same daily-idempotent streak credit an
        // unsaved practice run gets. All-skip runs (no held note) earn nothing,
        // so the streak can't be farmed by mashing Skip.
        if (self._drillHadHit) {
          recordPracticeDay();
          try { self._refreshStreakBadge(); } catch (e) {}
        }
      } else {
        self._setDrillCurrent('Listening…');
      }
      // UI is now consistent with drillIndex — let _drillTick run again.
      self.drillAdvancing = false;
    }, 250);
  };

  PracticeStudio.prototype.stopDrill = function (keepUI) {
    this.drillActive = false;
    if (this.drillStream) {
      this.drillStream.getTracks().forEach(function (t) { t.stop(); });
      this.drillStream = null;
    }
    if (this.drillCtx) {
      try { this.drillCtx.close(); } catch (e) {}
      this.drillCtx = null;
    }
    if (this.drillStartBtn) {
      this.drillStartBtn.innerHTML = '<span class="saif-practice__record-dot" aria-hidden="true"></span> Start Drill';
      this.drillStartBtn.classList.remove('saif-practice__tuner-btn--active');
    }
    if (!keepUI) {
      if (this.drillHoldFill) this.drillHoldFill.style.width = '0%';
      this._setDrillCurrent('—');
    }
    slog('[saif-practice] drill stopped');
  };

  PracticeStudio.prototype._setDrillCurrent = function (text) {
    if (this.drillCurrent) this.drillCurrent.textContent = text;
  };

  /* ==============================================
     RANGE MODE — guided vocal-range test.
     State machine: idle → listenLow → confirmLow → listenHigh → confirmHigh
     → result. Each listening step tracks the EXTREME sustained note (lowest,
     then highest) over a window rather than locking the first steady note —
     so the student can slide to their true floor/ceiling. A note "counts" once
     held steady for ~450ms (±1 semitone after octave folding); the step
     auto-locks once that extreme is unbeaten for ~2s (or the student taps
     "That's my lowest/highest"). Gated exactly like Record: a completed test
     spends one free try (at result render); an abandoned test is forgiven.
     See docs/practice-studio.md.
     ============================================== */
  var RANGE_QUALIFY_MS = 450;       // hold a note steady this long for it to count as "sung"
  var RANGE_SETTLE_MS = 2000;       // auto-lock once the extreme goes unbeaten this long
  var RANGE_MAX_WINDOW_MS = 9000;   // hard cap per step (best-so-far locks anyway)
  var RANGE_MANUAL_FALLBACK_MS = 14000; // a capture step stalled this long with nothing locked → reveal the by-ear picker (very last resort)
  var RANGE_ACCEPT_HINT_MS = 3000;   // struggled this long with no auto-lock → pop up the manual "That's my lowest/highest" button
  var RANGE_GAP_MS = 250;           // silence longer than this drops the in-progress hold
  var RANGE_MIN_HOLD_FRAMES = 8;    // and it needs at least this many voiced frames
  var RANGE_CENTER_SETTLE_MS = 800; // hold the natural-voice hum this long to anchor the range
  var FULL_VOICE_RATIO = 0.33;      // an extreme only counts if it's >= this fraction of the
                                    // natural-voice loudness — filters fry (bottom) + strained
                                    // falsetto/breath (top), which are much weaker than full voice
  var MIN_FULLVOICE_RMS = 0.018;    // ...but never below this absolute floor, so a SOFT center hum
                                    // can't shrink the threshold under the noise gate and disable it
  var OCTAVE_SANITY_ST = 15;        // reject an extreme more than this far from center (octave error)
  var RANGE_MIN_SPREAD_ST = 1;      // a CONFIDENT extreme must sit at least this far from center, so a
                                    // residual center hum can't lock as low/high; a genuine small
                                    // descent below/above center is kept as a fallback (see _rangeTick)
  var RANGE_STALL_GAP_MS = 1000;    // a tick gap bigger than this = tab was backgrounded → don't
                                    // count the frozen interval toward settle/timeout

  PracticeStudio.prototype._setRangeHint = function (text) {
    if (this.rangeHint) this.rangeHint.textContent = text;
  };

  PracticeStudio.prototype._rangeMedian = function (arr) {
    if (!arr || !arr.length) return 0;
    var copy = arr.slice().sort(function (a, b) { return a - b; });
    return copy[Math.floor(copy.length / 2)];
  };

  PracticeStudio.prototype._rangeResetHold = function () {
    this._rangeHoldStart = null;
    this._rangeHoldMidis = [];
    this._rangeHoldRms = [];
    if (this.rangeHoldFill) this.rangeHoldFill.style.width = '0%';
  };

  PracticeStudio.prototype.startRangeTest = async function () {
    var self = this;
    if (this.rangeActive) return;
    previewStop();
    this._rangeStarting = true; // guards saved-home hooks during the pre-active await
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this._setRangeHint('Microphone API not available in this browser.');
      this._rangeStarting = false;
      if (this.rangeStartBtn) this.rangeStartBtn.hidden = false; // don't strand a retest
      return;
    }
    // Create the AudioContext synchronously in the click gesture — iOS Safari
    // starts post-await contexts 'suspended', so the analyser reads silence.
    this.rangeCtx = createGestureAudioContext();
    // Same gating as Record: on gated pages the entitlement status is preloaded,
    // so this await settles on a microtask and preserves the mic gesture.
    if (this.gated) {
      var access = await this._ensureStudioAccess();
      if (access === 'locked') {
        closeAudioContext(this.rangeCtx);
        this.rangeCtx = null;
        this._rangeStarting = false;
        this.switchMode('practice');
        this._lockStudioUI();
        if (this.lockEl && this.lockEl.scrollIntoView) {
          try { this.lockEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        }
        return;
      }
      this._pendingRangeConsume = (access === 'preview');
    }
    this._setRangeHint('Requesting microphone access…');
    try {
      // autoGainControl OFF + adaptiveBoostFrame below — matches the tuner/drill/
      // record paths. AGC-on (the old default) neither lifted a soft/raspy voice
      // reliably NOR let the full-voice loudness comparison work (it pumped quiet
      // extremes up toward center), so this path used to miss quiet/raspy voices
      // the Tuner reads fine.
      this.rangeStream = await navigator.mediaDevices.getUserMedia({ audio: { autoGainControl: false } });
    } catch (err) {
      console.error('[saif-practice] range getUserMedia failed:', err && err.name, err && err.message);
      this._setRangeHint(micErrorMessage(err));
      closeAudioContext(this.rangeCtx);
      this.rangeCtx = null;
      this._pendingRangeConsume = false;
      this._rangeStarting = false;
      if (this.rangeStartBtn) this.rangeStartBtn.hidden = false; // don't strand a retest
      return;
    }
    if (!this.rangeCtx || this.rangeCtx.state === 'closed') {
      this.rangeCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.rangeCtx.state === 'suspended') { try { this.rangeCtx.resume(); } catch (e) {} }
    var source = this.rangeCtx.createMediaStreamSource(this.rangeStream);
    var analyser = this.rangeCtx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    this.rangeActive = true;
    this._rangeStarting = false;
    this._rangeLowMidi = null;
    this._rangeHighMidi = null;
    this._rangeCandidate = null;
    this._rangeCenterMidi = null;
    this._rangeCenterRms = 0;
    this._rangeLastTick = 0; // first tick shouldn't see a stale-gap
    this._rangeManualShown = false;
    this._rangeManualLow = null;
    this._rangeManualHigh = null;
    if (this.rangeManualEl) this.rangeManualEl.hidden = true;
    if (window.SaifTrack) window.SaifTrack('vocal_range_test_started', {});
    if (this.rangeResult) this.rangeResult.hidden = true;
    if (this.rangeStage) {
      this.rangeStage.hidden = false;
      // Reveal the live note read-out + hold bar only while a test is running;
      // at rest they read as prominent empty "black bars" on the start screen.
      this.rangeStage.classList.add('saif-practice__range-stage--live');
    }
    this._rangeEnter('listenCenter'); // natural voice first, then glide down/up

    var dataArray = new Float32Array(analyser.fftSize);
    var rangeLevel = { peak: 0 }; // per-session level for adaptiveBoostFrame, so a
    // soft / raspy / far-from-mic voice clears detectPitch's gates the same
    // breath-safe way tuner/drill/record already do. rms below is still computed
    // from the RAW frame (adaptiveBoostFrame returns a copy), keeping the
    // full-voice loudness comparison honest.
    function tick() {
      if (!self.rangeActive) return;
      requestAnimationFrame(tick);
      analyser.getFloatTimeDomainData(dataArray);
      var freq = detectPitch(adaptiveBoostFrame(dataArray, rangeLevel), self.rangeCtx.sampleRate);
      // Loudness of this frame — the full-voice guard compares extremes to the
      // natural-voice hum so fry/falsetto (much weaker) don't count as range.
      var sum = 0;
      for (var ri = 0; ri < dataArray.length; ri++) sum += dataArray[ri] * dataArray[ri];
      var rms = Math.sqrt(sum / dataArray.length);
      // Live mic-input meter: perceptual-ish (sqrt) fill, amber below the voiced
      // floor, so the user can SEE they're being heard (or that they're too quiet).
      if (self.rangeLevelEl) {
        self.rangeLevelEl.style.width = Math.min(100, Math.round(Math.sqrt(rms) * 260)) + '%';
        self.rangeLevelEl.classList.toggle('saif-practice__range-level-fill--quiet', rms < 0.02);
      }
      self._rangeTick(freq, rms);
    }
    tick();
    slog('[saif-practice] range test started');
  };

  PracticeStudio.prototype.stopRangeTest = function (keepResult) {
    this.rangeActive = false;
    if (this.rangeStream) {
      this.rangeStream.getTracks().forEach(function (t) { t.stop(); });
      this.rangeStream = null;
    }
    if (this.rangeCtx) {
      try { this.rangeCtx.close(); } catch (e) {}
      this.rangeCtx = null;
    }
    this._rangeResetHold();
    if (this.rangeLevelEl) this.rangeLevelEl.style.width = '0%';
    // A stopped test is no longer "live" — ALWAYS drop the reveal class (not just
    // on the abandoned path). Otherwise a completed test leaves --live on, and a
    // later Retest (or a mic-denied gap) re-shows the at-rest note + hold bar as
    // the "black bars" before the mic reopens.
    if (this.rangeStage) this.rangeStage.classList.remove('saif-practice__range-stage--live');
    if (!keepResult) {
      // Abandoned before a result — forgive the (unspent) free try, reset to idle.
      this._pendingRangeConsume = false;
      this.rangeState = 'idle';
      if (this.rangeResult) this.rangeResult.hidden = true;
      if (this.rangeStage) this.rangeStage.hidden = false;
      if (this.rangeStartBtn) this.rangeStartBtn.hidden = false;
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.hidden = true;
      if (this.rangeRedoBtn) this.rangeRedoBtn.hidden = true;
      if (this.rangeRetestBtn) this.rangeRetestBtn.hidden = true;
      if (this.rangeNote) this.rangeNote.textContent = '—';
      if (this.rangeStep) this.rangeStep.textContent = (window.SaifI18n && window.SaifI18n.range_intro) || 'Press start — we’ll set your natural voice, then find your low and high.';
      this._setRangeHint('Sing out with an open mouth, then glide down and up.');
      // With a saved range, the tab's resting state is the saved-result home,
      // not the blank start screen.
      var savedRange = getUserRangeSync();
      if (savedRange) this._rangeShowSaved(savedRange);
    }
  };

  // Transition into a capture/confirm state and update the panel chrome.
  // Flow: listenCenter (natural voice) → listenLow → confirmLow → listenHigh →
  // confirmHigh → result. Center auto-advances (no button); extremes glide from
  // the center and lock via settle or the "That's my lowest/highest" button.
  PracticeStudio.prototype._rangeEnter = function (state) {
    this.rangeState = state;
    this._rangeResetHold();
    var capturing = (state === 'listenCenter' || state === 'listenLow' || state === 'listenHigh');
    var confirming = (state === 'confirmLow' || state === 'confirmHigh');
    if (capturing) {
      // Fresh capture window for this step.
      this._rangeBest = null;
      this._rangeBestAt = 0;
      this._rangeStepStart = performance.now();
      this._rangeWeakHint = false;
      this._rangeSawWeak = 0;
      this._rangeUnsteadyUntil = 0;
      this._rangeTooCloseUntil = 0;
      this._rangeFallback = null;
      this._rangeWeakFallback = null;
      this._rangeCaptureStart = performance.now(); // for the accept-reveal + by-ear timeouts
      this._rangeLastMidi = null;
      this._rangeShownMidi = null;
      this._rangeAcceptShown = false;
    }
    if (this.rangeStartBtn) this.rangeStartBtn.hidden = true;
    // Center auto-advances once the hum settles, so it shows no button; the
    // glide steps + confirms show Accept.
    // Capture steps DON'T show Accept immediately — a fast, clear note auto-locks.
    // If nothing locks after RANGE_ACCEPT_HINT_MS, _rangeTick pops it up so the
    // user can lock the note on screen. Confirm steps always show it.
    var showAccept = confirming;
    if (this.rangeAcceptBtn) this.rangeAcceptBtn.hidden = !showAccept;
    if (this.rangeRedoBtn) this.rangeRedoBtn.hidden = !confirming;
    if (state === 'listenCenter') {
      if (this.rangeNote) this.rangeNote.textContent = '—';
      if (this.rangeStep) this.rangeStep.textContent = (window.SaifI18n && window.SaifI18n.range_center) || 'First, sing an open note at a comfortable pitch — mouth open like an “aah”, not a closed hum. This sets your natural voice.';
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.textContent = (window.SaifI18n && window.SaifI18n.range_use_note) || 'Use this note';
      this._setRangeHint('Open your mouth and sing “aah” — relaxed and steady, no need to reach.');
    } else if (state === 'listenLow') {
      if (this.rangeNote) this.rangeNote.textContent = '—';
      if (this.rangeStep) this.rangeStep.textContent = (window.SaifI18n && window.SaifI18n.range_glide_down) || 'Now glide DOWN from there to the lowest note you can still sing full and clear.';
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.textContent = (window.SaifI18n && window.SaifI18n.range_lowest) || 'That’s my lowest';
      this._setRangeHint('Slide down — stop where it turns creaky or weak.');
    } else if (state === 'confirmLow') {
      if (this.rangeStep) this.rangeStep.textContent = 'Lowest note: ' + midiToNoteName(Math.round(this._rangeCandidate));
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.textContent = 'That’s my note';
      this._setRangeHint('Tap “That’s my note”, or Redo to try again.');
    } else if (state === 'listenHigh') {
      if (this.rangeNote) this.rangeNote.textContent = '—';
      if (this.rangeStep) this.rangeStep.textContent = (window.SaifI18n && window.SaifI18n.range_glide_up) || 'Now glide UP to the highest note you can sing in full voice — not falsetto.';
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.textContent = (window.SaifI18n && window.SaifI18n.range_highest) || 'That’s my highest';
      this._setRangeHint('Slide up — stop where it turns breathy or strained.');
    } else if (state === 'confirmHigh') {
      if (this.rangeStep) this.rangeStep.textContent = 'Highest note: ' + midiToNoteName(Math.round(this._rangeCandidate));
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.textContent = 'That’s my note';
      this._setRangeHint('Tap “That’s my note”, or Redo to try again.');
    }
  };

  // Per-frame handler. listenCenter captures the natural-voice anchor (pitch +
  // loudness) and auto-advances; listenLow/High track the most extreme
  // FULL-VOICE, octave-sane note and auto-lock once it settles; confirm states
  // freeze until Accept/Redo. `rms` is this frame's loudness.
  PracticeStudio.prototype._rangeTick = function (freq, rms) {
    var st = this.rangeState;
    var isCenter = (st === 'listenCenter');
    var low = (st === 'listenLow');
    var high = (st === 'listenHigh');
    if (!isCenter && !low && !high) return;
    var now = performance.now();
    // Backgrounded tab: rAF pauses but the clock keeps running. If the gap since
    // the last tick is huge, don't count the frozen interval toward settle/
    // timeout (else the return frame would instantly lock a stale best).
    if (this._rangeLastTick && (now - this._rangeLastTick) > RANGE_STALL_GAP_MS) {
      var skip = now - this._rangeLastTick;
      if (this._rangeBestAt) this._rangeBestAt += skip;
      if (this._rangeStepStart) this._rangeStepStart += skip;
    }
    this._rangeLastTick = now;

    // Very last resort: after a long stall with nothing locked (mic can't read
    // this voice at all), reveal the by-ear manual picker. Auto-detection keeps
    // running underneath, so a voice that recovers can still complete normally.
    if (!this._rangeManualShown && this._rangeBest == null && this._rangeCaptureStart &&
        (now - this._rangeCaptureStart) >= RANGE_MANUAL_FALLBACK_MS && (isCenter || low || high)) {
      this._rangeShowManual();
    }
    // After a short struggle with no auto-lock, pop up the manual "That's my
    // lowest/highest" (center: "Use this note") button so the user can lock the
    // note on screen without holding it and tapping at the same time.
    if (!this._rangeAcceptShown && this._rangeCaptureStart &&
        (now - this._rangeCaptureStart) >= RANGE_ACCEPT_HINT_MS && (isCenter || low || high)) {
      this._rangeAcceptShown = true;
      if (this.rangeAcceptBtn) this.rangeAcceptBtn.hidden = false;
    }

    // --- maintain the in-progress steady-hold buffer (+ its loudness) ---
    if (freq <= 0) {
      if (this._rangeHoldStart != null && (now - (this._rangeLastVoiced || 0)) > RANGE_GAP_MS) {
        this._rangeHoldStart = null;
        this._rangeHoldMidis = [];
        this._rangeHoldRms = [];
      }
    } else {
      var midi = freqToMidi(freq);
      this._rangeLastVoiced = now;
      if (this._rangeHoldStart != null && this._rangeHoldMidis.length) {
        var med = this._rangeMedian(this._rangeHoldMidis);
        var folded = midi;
        while (folded - med > 6) folded -= 12;
        while (med - folded > 6) folded += 12;
        if (Math.abs(folded - med) <= 1.0) {
          this._rangeHoldMidis.push(folded);
          this._rangeHoldRms.push(rms || 0);
        } else {
          // Pitch jumped out of tolerance while voicing → the voice is wavering.
          this._rangeUnsteadyUntil = now + 700;
          this._rangeHoldStart = now;
          this._rangeHoldMidis = [midi];
          this._rangeHoldRms = [rms || 0];
        }
      } else {
        this._rangeHoldStart = now;
        this._rangeHoldMidis = [midi];
        this._rangeHoldRms = [rms || 0];
      }
      var curMed = this._rangeMedian(this._rangeHoldMidis);
      var curRms = this._rangeMedian(this._rangeHoldRms);
      this._rangeLastMidi = curMed; // remember the last voiced note so it stays on screen after silence
      var qualified = (now - this._rangeHoldStart) >= RANGE_QUALIFY_MS &&
                      this._rangeHoldMidis.length >= RANGE_MIN_HOLD_FRAMES;

      if (isCenter) {
        // The natural voice = the first solid sustained note. Capture its
        // loudness as the full-voice reference for the glide steps.
        if (qualified && this._rangeBest == null) {
          this._rangeBest = curMed;
          this._rangeBestAt = now;
          this._rangeCenterRms = curRms;
        }
      } else if (qualified) {
        // A candidate extreme must be FULL VOICE (loud enough vs. the natural
        // voice, but never below an absolute floor) and on the right side of
        // center within the octave-sanity bound.
        var thr = Math.max(this._rangeCenterRms * FULL_VOICE_RATIO, MIN_FULLVOICE_RMS);
        var fullVoice = curRms >= thr;
        var c = this._rangeCenterMidi;
        var spread = (c == null) ? Infinity : (low ? c - curMed : curMed - c);
        var beyondCenter = (c == null) || (spread > 0.4 && spread <= OCTAVE_SANITY_ST);
        var confident = (c == null) || (spread >= RANGE_MIN_SPREAD_ST && spread <= OCTAVE_SANITY_ST);
        if (fullVoice && beyondCenter) {
          // Any full-voice note beyond center is a fallback (so a genuinely
          // narrow voice can still lock SOMETHING), even if it's not far enough
          // to be a "confident" extreme.
          if (this._rangeFallback == null ||
              (low && curMed < this._rangeFallback) ||
              (high && curMed > this._rangeFallback)) {
            this._rangeFallback = curMed;
          }
          if (confident && (this._rangeBest == null ||
              (low && curMed < this._rangeBest - 0.3) ||
              (high && curMed > this._rangeBest + 0.3))) {
            this._rangeBest = curMed;
            this._rangeBestAt = now;
            this._rangeWeakHint = false;
          } else if (!confident) {
            // Full voice but too near the speaking note to be confident yet.
            this._rangeTooCloseUntil = now + 700;
          }
        } else if (!fullVoice && beyondCenter) {
          // A note in range but too weak (fry/breath) — coach, don't extend the
          // confident best. But KEEP the most-extreme weak note as a last-resort
          // fallback so a uniformly-quiet/raspy voice still locks SOMETHING at the
          // window cap instead of looping the glide step forever.
          this._rangeWeakHint = true;
          this._rangeSawWeak = now;
          if (this._rangeWeakFallback == null ||
              (low && curMed < this._rangeWeakFallback) ||
              (high && curMed > this._rangeWeakFallback)) {
            this._rangeWeakFallback = curMed;
          }
        }
      }
    }

    // --- display: big note = captured value (or live note before it qualifies) ---
    var shownMidi = (this._rangeBest != null) ? this._rangeBest
                   : (this._rangeHoldMidis.length ? this._rangeMedian(this._rangeHoldMidis)
                   : this._rangeLastMidi); // keep the last note on screen after you stop singing
    this._rangeShownMidi = shownMidi;      // what the manual Accept will lock
    if (this.rangeNote) this.rangeNote.textContent = (shownMidi != null) ? midiToNoteName(Math.round(shownMidi)) : '—';

    if (isCenter) {
      // Hold the hum briefly to anchor, then move on to the low glide.
      if (this._rangeBest != null) {
        var cs = now - this._rangeBestAt;
        if (this.rangeHoldFill) this.rangeHoldFill.style.width = Math.max(0, Math.min(100, (cs / RANGE_CENTER_SETTLE_MS) * 100)) + '%';
        this._setRangeHint('Your natural voice: ' + midiToNoteName(Math.round(this._rangeBest)) + ' — hold it…');
        if (cs >= RANGE_CENTER_SETTLE_MS) {
          this._rangeCenterMidi = this._rangeBest;
          this._rangeEnter('listenLow');
        }
      } else {
        if (this.rangeHoldFill) this.rangeHoldFill.style.width = '0%';
        // Stalled on the hum (raspy / quiet / wavering): after the window cap,
        // tell the user how to proceed instead of coaching forever. The "Use this
        // note" button is already visible, so they are never stranded.
        if ((now - this._rangeStepStart) >= RANGE_MAX_WINDOW_MS) {
          this._setRangeHint('Still can’t hear you — sing out with your mouth open (not a closed hum), or move closer, then tap “Use this note”.');
        } else {
          this._setRangeHint(this._rangeCoach(now, 0));
        }
      }
      return;
    }

    if (this._rangeBest != null) {
      var settled = now - this._rangeBestAt;
      if (this.rangeHoldFill) this.rangeHoldFill.style.width = Math.max(0, Math.min(100, (settled / RANGE_SETTLE_MS) * 100)) + '%';
      var secs = Math.max(0, Math.ceil((RANGE_SETTLE_MS - settled) / 1000));
      this._setRangeHint(this._rangeCoach(now, secs));
      if (settled >= RANGE_SETTLE_MS || (now - this._rangeStepStart) >= RANGE_MAX_WINDOW_MS) {
        this._rangeCommitBest();
      }
    } else {
      if (this.rangeHoldFill) this.rangeHoldFill.style.width = '0%';
      this._setRangeHint(this._rangeCoach(now, 0));
      // Window elapsed with no CONFIDENT extreme: lock the fallback (closest
      // full-voice note beyond center) so a genuinely narrow voice isn't stuck —
      // else keep coaching (never hangs).
      if ((now - this._rangeStepStart) >= RANGE_MAX_WINDOW_MS) {
        // Prefer a full-voice fallback; else lock the weakest detected note so a
        // quiet/raspy voice never loops here forever. Only truly no-pitch re-arms.
        var capFallback = (this._rangeFallback != null) ? this._rangeFallback : this._rangeWeakFallback;
        if (capFallback != null) {
          this._rangeBest = capFallback;
          this._rangeBestAt = now;
          this._rangeCommitBest();
        } else {
          this._rangeStepStart = now;
        }
      }
    }
  };

  // Live coaching string — tells the user WHY nothing is locking (wavering / too
  // quiet / too close to their speaking note), not just a static instruction.
  PracticeStudio.prototype._rangeCoach = function (now, secs) {
    var st = this.rangeState;
    var isCenter = (st === 'listenCenter');
    var low = (st === 'listenLow');
    var voiced = (now - (this._rangeLastVoiced || 0)) < 300;
    var dir = low ? 'lower' : 'higher';
    var edge = low ? 'floor' : 'ceiling';
    // Live problems while voicing take priority — this is the "tell me why" part.
    if (voiced && now < this._rangeUnsteadyUntil)
      return 'Hold ONE steady note — your voice is wavering right now.';
    if (isCenter) return voiced ? 'Good — keep it open and steady…' : 'Sing an open “aah” (mouth open, not a closed hum) to set your natural voice.';
    if (voiced && now < this._rangeTooCloseUntil)
      return 'That’s near your speaking note — ease a little ' + dir + ' to find your ' + edge + '.';
    if (voiced && (now - this._rangeSawWeak) < 800)
      return 'A little louder and fuller so it locks in.';
    if (this._rangeBest != null) {
      if (this._rangeWeakHint)
        return (low ? 'Lowest full note: ' : 'Highest full note: ') + midiToNoteName(Math.round(this._rangeBest)) +
          ' — going further just gets ' + (low ? 'creaky' : 'breathy') + '. Hold to lock (' + secs + 's)';
      return (low ? 'Lowest' : 'Highest') + ' so far: ' + midiToNoteName(Math.round(this._rangeBest)) +
        ' — keep going ' + dir + ' if it stays full & clear, or hold to lock (' + secs + 's)';
    }
    if (voiced) return 'Hold it steady…';
    return low ? 'Slide down — stop where it turns creaky or weak.'
               : 'Slide up — stop where it turns breathy or strained.';
  };

  // Lock the extreme captured this step and move to its confirm screen.
  PracticeStudio.prototype._rangeCommitBest = function () {
    // Resolve a note to lock: confident best → full-voice fallback → weakest
    // detected fallback → whatever note is live on screen. This makes the
    // "That's my lowest/highest" button ALWAYS do something — a raspy voice that
    // never hit "full voice" can still lock the note it's showing — instead of
    // being an inert no-op when auto-detection hasn't qualified.
    if (this._rangeBest == null) {
      // Lock the note currently on screen: a full-voice fallback if we have one,
      // else the last note you sang (which persists after you stop).
      var pick = (this._rangeFallback != null) ? this._rangeFallback
               : (this._rangeWeakFallback != null) ? this._rangeWeakFallback
               : this._rangeShownMidi;
      if (pick == null) {
        this._setRangeHint((this.rangeState === 'listenLow' ? 'Sing your lowest' : 'Sing your highest') + ' note first — then tap to lock it.');
        return;
      }
      this._rangeBest = pick;
      this._rangeBestAt = performance.now();
    }
    if (this.rangeState === 'listenLow') {
      this._rangeCandidate = this._rangeBest;
      this._rangeEnter('confirmLow');
    } else if (this.rangeState === 'listenHigh') {
      if (this._rangeBest < this._rangeLowMidi + 4) {
        // Too close to the low note — keep listening for a genuinely higher one,
        // but surface Redo so a narrow voice can go back and redo the low
        // instead of being stuck singing higher forever.
        this._setRangeHint('That’s close to your low note — sing higher, or tap Redo to redo your low.');
        if (this.rangeRedoBtn) this.rangeRedoBtn.hidden = false;
        this._rangeBest = null;
        this._rangeBestAt = 0;
        this._rangeStepStart = performance.now();
        this._rangeHoldStart = null;
        this._rangeHoldMidis = [];
        this._rangeHoldRms = [];
        if (this.rangeHoldFill) this.rangeHoldFill.style.width = '0%';
        return;
      }
      this._rangeCandidate = this._rangeBest;
      this._rangeEnter('confirmHigh');
    }
  };

  PracticeStudio.prototype._rangeAccept = function () {
    if (this.rangeState === 'listenCenter') {
      // Manual "Use this note" for the natural-voice anchor — latch the live/best
      // hum so a voice that never auto-settles (raspy/wobbly) can still proceed.
      var c = (this._rangeBest != null) ? this._rangeBest : this._rangeShownMidi;
      if (c == null) { this._setRangeHint('Sing an open note first — then tap “Use this note”.'); return; }
      this._rangeCenterMidi = c;
      this._rangeCenterRms = this._rangeHoldRms.length ? this._rangeMedian(this._rangeHoldRms) : this._rangeCenterRms;
      this._rangeEnter('listenLow');
    } else if (this.rangeState === 'listenLow' || this.rangeState === 'listenHigh') {
      // Manual "that's my lowest/highest" — lock the extreme captured so far.
      this._rangeCommitBest();
    } else if (this.rangeState === 'confirmLow') {
      this._rangeLowMidi = this._rangeCandidate;
      this._rangeEnter('listenHigh');
    } else if (this.rangeState === 'confirmHigh') {
      this._rangeHighMidi = this._rangeCandidate;
      this._rangeFinish();
    }
  };

  PracticeStudio.prototype._rangeRedo = function () {
    if (this.rangeState === 'confirmLow') this._rangeEnter('listenLow');
    else if (this.rangeState === 'confirmHigh') this._rangeEnter('listenHigh');
    // Escape hatch from the "sing higher" loop: redo the low note.
    else if (this.rangeState === 'listenHigh') this._rangeEnter('listenLow');
  };

  PracticeStudio.prototype._rangeFinish = function () {
    if (this.rangeManualEl) this.rangeManualEl.hidden = true;
    this._rangeManualShown = false;
    var low = Math.min(this._rangeLowMidi, this._rangeHighMidi);
    var high = Math.max(this._rangeLowMidi, this._rangeHighMidi);
    this._rangeLowMidi = low;
    this._rangeHighMidi = high;
    this.stopRangeTest(true); // stop the mic, keep the (about-to-render) result
    this.rangeState = 'result';
    if (window.SaifTrack) window.SaifTrack('vocal_range_test_completed', {
      lowMidi: Math.round(low * 10) / 10, highMidi: Math.round(high * 10) / 10
    });
    // A completed test spends one free try (preview users only); mirrors Record.
    if (this._pendingRangeConsume) {
      this._pendingRangeConsume = false;
      this._consumeStudioTry();
    }
    this._rangeRenderResult();
  };

  // opts.fromSaved → render a previously-saved range (no auto-save, shows a
  // "Tested <date>" note). Fresh path (no opts) → auto-save + confirmation note.
  PracticeStudio.prototype._rangeRenderResult = function (opts) {
    var low = this._rangeLowMidi, high = this._rangeHighMidi;
    if (this.rangeStage) this.rangeStage.hidden = true;
    if (this.rangeResult) this.rangeResult.hidden = false;
    if (this.rangeStartBtn) this.rangeStartBtn.hidden = true;
    if (this.rangeAcceptBtn) this.rangeAcceptBtn.hidden = true;
    if (this.rangeRedoBtn) this.rangeRedoBtn.hidden = true;
    if (this.rangeRetestBtn) this.rangeRetestBtn.hidden = false;
    if (this.rangeBarLow) this.rangeBarLow.textContent = midiToNoteName(Math.round(low));
    if (this.rangeBarHigh) this.rangeBarHigh.textContent = midiToNoteName(Math.round(high));
    if (this.rangeSummary) {
      this.rangeSummary.textContent = 'Your comfortable range is about ' +
        midiToNoteName(Math.round(low)) + ' to ' + midiToNoteName(Math.round(high)) + '.';
    }
    this._rangeRenderRecs({ lowMidi: low, highMidi: high });
    if (opts && opts.fromSaved) {
      var when = '';
      try {
        // Guard the falsy case explicitly: new Date(null) is EPOCH ZERO, which
        // passes the isNaN check and rendered "Tested Dec 31, 1969" for ranges
        // saved before testedAt existed (caught by the core-feature sweep).
        if (opts.testedAt) {
          var d = new Date(opts.testedAt);
          if (!isNaN(d.getTime())) when = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        }
      } catch (e) {}
      this._rangeSetSavedNote(when ? 'Tested ' + when : 'From your last test');
    } else {
      // AUTO-SAVE (replaces the old Save button): every fresh result persists
      // immediately; _rangeSave overwrites the note with "Saved — …" on success.
      this._rangeSetSavedNote('');
      this._rangeSave();
    }
  };

  // The prescription answers ONE question — "which clip do I pick?". A single
  // hero (best-for-your-voice), then a few alternates, then a plain "why" +
  // a doorway to the full filtered list. Never an empty/negative result.
  // Measurement only — the range test just records your voice; it does NOT rank
  // or gate clips. Every maqam suits every voice, so the reference auto-tunes to
  // this range (and the per-clip key toggle reverts it). No "clips that fit" list.
  PracticeStudio.prototype._rangeRenderRecs = function (range) {
    var el = this.rangeRecs;
    if (!el) return;
    el.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'saif-practice__range-hint';
    p.textContent = (window.SaifI18n && window.SaifI18n.range_done_hint) || 'Every clip now tunes to your range automatically — pick any maqam and it plays in your key. Use the key toggle on a clip to hear it in the original key any time.';
    el.appendChild(p);
    var browse = document.createElement('button');
    browse.type = 'button';
    browse.className = 'saif-practice__action-btn saif-practice__action-btn--primary saif-practice__range-hero-cta';
    browse.textContent = (window.SaifI18n && window.SaifI18n.browse_clips) || 'Browse clips →';
    browse.addEventListener('click', function () {
      var openBtn = document.getElementById('SaifPracticeRefChange');
      if (openBtn) openBtn.click();
    });
    el.appendChild(browse);
  };

  // (Removed: _rangeAppendHero / _rangeAppendRec — the fit-based "which clip do I
  //  pick?" prescription. The range result is measurement-only now; every maqam
  //  auto-tunes to the saved range.)

  // Buttonless auto-save (called by _rangeRenderResult's fresh path). `retest`
  // is derived from whether a range is ALREADY saved — read before setUserRange.
  PracticeStudio.prototype._rangeSave = function () {
    var self = this;
    var range = { lowMidi: this._rangeLowMidi, highMidi: this._rangeHighMidi };
    if (!isFinite(range.lowMidi) || !isFinite(range.highMidi)) return;
    if (window.SaifTrack) window.SaifTrack('vocal_range_saved', {
      lowMidi: Math.round(range.lowMidi * 10) / 10,
      highMidi: Math.round(range.highMidi * 10) / 10,
      retest: !!getUserRangeSync()
    });
    var localBlob = { v: 1, lowMidi: range.lowMidi, highMidi: range.highMidi, testedAt: new Date().toISOString() };
    // Update the in-memory range cache SYNCHRONOUSLY so a clip picked before the
    // (possibly slow, network) save resolves still auto-tunes into the new range.
    // Without this, picking a clip right after the test read a stale null range
    // and skipped the transpose until a page refresh reloaded it. The async save
    // below just confirms / overwrites with the server's canonical copy.
    setUserRange(localBlob);
    var done = function (saved) {
      setUserRange(saved);
      self._rangeSetSavedNote('Saved — every clip now tunes to your range ✓');
    };
    if (window.SaifSync && window.SaifSync.vocalRange) {
      window.SaifSync.vocalRange.save(range).then(function (r) {
        done((r && r.ok && r.range) ? r.range : localBlob);
      }).catch(function () { done(localBlob); });
    } else {
      done(localBlob);
    }
  };

  // Subtle status line under the result — auto-save confirmation on a fresh
  // test, "Tested <date>" on the saved-result home. JS-created inside the
  // existing #SaifPracticeRangeResult container (no new ids for check-sync).
  PracticeStudio.prototype._rangeSetSavedNote = function (text) {
    if (!this.rangeResult) return;
    if (!this._rangeSavedNoteEl) {
      this._rangeSavedNoteEl = document.createElement('p');
      this._rangeSavedNoteEl.className = 'saif-practice__range-saved-note';
      this.rangeResult.appendChild(this._rangeSavedNoteEl);
    }
    this._rangeSavedNoteEl.textContent = text || '';
    this._rangeSavedNoteEl.hidden = !text;
  };

  PracticeStudio.prototype._rangeRetest = function () {
    if (this.rangeResult) this.rangeResult.hidden = true;
    if (this.rangeStage) {
      this.rangeStage.hidden = false;
      // Not live until startRangeTest reopens the mic — keep the note/hold-bar
      // hidden through the getUserMedia gap (no "black bars" flash on retest).
      this.rangeStage.classList.remove('saif-practice__range-stage--live');
    }
    if (this.rangeRetestBtn) this.rangeRetestBtn.hidden = true;
    this._rangeLowMidi = null;
    this._rangeHighMidi = null;
    this._rangeCandidate = null;
    this.rangeState = 'idle';
    if (this.rangeNote) this.rangeNote.textContent = '—';
    if (this.rangeStep) this.rangeStep.textContent = (window.SaifI18n && window.SaifI18n.range_retest_intro) || 'Press start, then sing down to your lowest comfortable note.';
    this.startRangeTest();
  };

  // ---- By-ear manual range picker (surfaced only after a long detection stall) ----
  var RANGE_MANUAL_MIN_MIDI = 36; // C2
  var RANGE_MANUAL_MAX_MIDI = 74; // D5
  PracticeStudio.prototype._rangeShowManual = function () {
    if (this._rangeManualShown) return;
    this._rangeManualShown = true;
    // Start from sensible notes around the anchor (or a comfortable mid range),
    // which the user nudges by ear with the ▶ Play preview.
    var ctr = (this._rangeCenterMidi != null) ? Math.round(this._rangeCenterMidi) : 55; // ~G3
    if (this._rangeManualLow == null) this._rangeManualLow = Math.max(RANGE_MANUAL_MIN_MIDI, ctr - 7);
    if (this._rangeManualHigh == null) this._rangeManualHigh = Math.min(RANGE_MANUAL_MAX_MIDI, ctr + 7);
    this._rangeManualRender();
    if (this.rangeManualEl) this.rangeManualEl.hidden = false;
    this._setRangeHint('Trouble singing it? Set your range by ear below — tap ▶ Play to hear a note.');
    if (this.rangeManualEl && this.rangeManualEl.scrollIntoView) {
      try { this.rangeManualEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
    }
    if (window.SaifTrack) window.SaifTrack('vocal_range_manual_shown', { state: this.rangeState });
  };
  PracticeStudio.prototype._rangeManualRender = function () {
    if (this.rangeManualLowNote) this.rangeManualLowNote.textContent = midiToNoteName(this._rangeManualLow);
    if (this.rangeManualHighNote) this.rangeManualHighNote.textContent = midiToNoteName(this._rangeManualHigh);
  };
  PracticeStudio.prototype._rangeManualStep = function (which, delta) {
    if (which === 'low') {
      var lo = (this._rangeManualLow != null ? this._rangeManualLow : 48) + delta;
      this._rangeManualLow = Math.max(RANGE_MANUAL_MIN_MIDI, Math.min(lo, (this._rangeManualHigh != null ? this._rangeManualHigh : 62) - 2));
      this._rangePlayTone(this._rangeManualLow);
    } else {
      var hi = (this._rangeManualHigh != null ? this._rangeManualHigh : 62) + delta;
      this._rangeManualHigh = Math.min(RANGE_MANUAL_MAX_MIDI, Math.max(hi, (this._rangeManualLow != null ? this._rangeManualLow : 48) + 2));
      this._rangePlayTone(this._rangeManualHigh);
    }
    this._rangeManualRender();
  };
  // Short sine preview of a note so the user matches by ear without needing names.
  PracticeStudio.prototype._rangePlayTone = function (midi) {
    if (midi == null) return;
    try {
      var ctx = (this.rangeCtx && this.rangeCtx.state !== 'closed') ? this.rangeCtx
              : (this._toneCtx && this._toneCtx.state !== 'closed') ? this._toneCtx
              : (this._toneCtx = new (window.AudioContext || window.webkitAudioContext)());
      if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = midiToFreq(midi);
      var t = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.2, t + 0.03);
      gain.gain.setValueAtTime(0.2, t + 0.65);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.92);
    } catch (e) {}
  };
  PracticeStudio.prototype._rangeManualSave = function () {
    if (this._rangeManualLow == null || this._rangeManualHigh == null) return;
    this._rangeLowMidi = this._rangeManualLow;
    this._rangeHighMidi = this._rangeManualHigh;
    if (this.rangeManualEl) this.rangeManualEl.hidden = true;
    this._rangeFinish(); // stops the mic, saves the range, renders the result
  };

  // Render a previously saved range as the Range tab's home screen. No mic, no
  // try-spend (that lives only in _rangeFinish), no re-save (fromSaved path).
  PracticeStudio.prototype._rangeShowSaved = function (range) {
    var lo = Number(range && range.lowMidi), hi = Number(range && range.highMidi);
    if (!isFinite(lo) || !isFinite(hi) || hi <= lo) return;
    if (this.rangeActive || this._rangeStarting || (this.rangeState !== 'idle' && this.rangeState !== 'result')) return;
    this._rangeLowMidi = lo;
    this._rangeHighMidi = hi;
    this.rangeState = 'result';
    this._rangeRenderResult({ fromSaved: true, testedAt: range.testedAt || null });
  };

  // Thin wrappers so the headless smoke test (which has no mic) can exercise the
  // fit maths directly. The real UI uses the module-level functions.
  PracticeStudio.prototype.rangeComfortZone = function (range) { return rangeComfortZone(range); };
  PracticeStudio.prototype.rangeClassifyClip = function (stats, zone) { return classifyClipStats(stats, zone); };
  PracticeStudio.prototype.rangePickBest = function (zone) { return rangePickBest(zone); };

  PracticeStudio.prototype.resizeCanvases = function () {
    [this.liveCanvas, this.compareCanvas].forEach(function (canvas) {
      if (!canvas) return;
      var rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * (window.devicePixelRatio || 1);
      canvas.height = canvas.parentElement.offsetHeight * (window.devicePixelRatio || 1);
      canvas.style.width = rect.width + 'px';
      canvas.style.height = canvas.parentElement.offsetHeight + 'px';
    });
  };

  /**
   * Show a 3-2-1-Go! countdown overlay before recording begins.
   * Returns a promise that resolves when the countdown finishes.
   */
  PracticeStudio.prototype.runCountdown = function (seconds) {
    var self = this;
    return new Promise(function (resolve) {
      var overlay = self.container.querySelector('.saif-practice__countdown');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'saif-practice__countdown';
        overlay.setAttribute('aria-live', 'assertive');
        // the count belongs ON the stage. Appending to liveSection made it cover
        // the scrub bar below the canvas too, washing the cream strip grey.
        var _stage = self.container.querySelector(".saif-practice__canvas-wrap");
        if (_stage) _stage.appendChild(overlay);
        else if (self.liveSection) self.liveSection.appendChild(overlay);
      }
      overlay.hidden = false;
      overlay.classList.add('saif-practice__countdown--visible');

      // Tap-to-skip: on the ~20th take of a session the fixed 3-count is a
      // minute of dead time. One tap fast-forwards straight to "Go" (the
      // countdown still defaults to a full 3 so first-timers get settled).
      var pending = null;
      var finished = false;
      overlay.style.cursor = 'pointer';
      overlay.setAttribute('role', 'button');
      overlay.setAttribute('title', (window.SaifI18n && window.SaifI18n.countdown_title) || 'Tap to start now');
      function finish() {
        if (finished) return;
        finished = true;
        overlay.onclick = null;
        overlay.textContent = (window.SaifI18n && window.SaifI18n.countdown_go) || 'Go';
        overlay.classList.remove('saif-practice__countdown--pulse');
        void overlay.offsetWidth;
        overlay.classList.add('saif-practice__countdown--pulse');
        setTimeout(function () {
          overlay.classList.remove('saif-practice__countdown--visible');
          overlay.hidden = true;
          resolve();
        }, 300);
      }
      overlay.onclick = function () {
        if (pending) clearTimeout(pending);
        finish();
      };

      var n = seconds;
      function tick() {
        if (finished) return;
        if (n > 0) {
          overlay.textContent = String(n);
          overlay.classList.remove('saif-practice__countdown--pulse');
          void overlay.offsetWidth;
          overlay.classList.add('saif-practice__countdown--pulse');
          n -= 1;
          pending = setTimeout(tick, 800);
        } else {
          finish();
        }
      }
      tick();
    });
  };

  PracticeStudio.prototype.startRecording = async function (capture) {
    var self = this;
    // capture === false → Practice run: live pitch feedback + a score, but the
    // take is NEVER recorded or saved (no MediaRecorder, no blob, no upload).
    // Anything else (default) → Record take: audio is captured and saved
    // privately to the student's own library.
    this._captureAudio = capture !== false;

    // If a history replay temporarily swapped the reference to a saved take's
    // clip, restore the real current-clip reference so THIS take scores against
    // the right thing.
    this._restoreReferenceSnapshot();

    // Re-entrancy guard — a second tap while we're waiting on the entitlement
    // check, the permission prompt, or counting down would spin up a second
    // MediaRecorder over the same chunk arrays and leave the first mic stream
    // hot (privacy leak). CRITICAL: claim the take SYNCHRONOUSLY (set state +
    // disable) before any `await`, so the guard holds across the async gate
    // below — otherwise a second click could slip through during the await.
    if (this.state === 'starting' || this.state === 'recording') {
      slog('[saif-practice] startRecording ignored — already', this.state);
      return;
    }
    // Also clears any lingering 'recorded' state so the post-countdown bail
    // check (`if state === 'recorded'`) doesn't false-fire on a re-record.
    this.state = 'starting';
    previewStop(); // a real take supersedes any inline clip preview
    // Arming a take closes any open sheet from EVERY path — the dock's capture
    // listener only covers the Record button; the Practice-run button does not
    // go through it. Doing it here also precedes the preview-gate picker, so that
    // picker can no longer stack on top of a sheet.
    // A replay still running while a new take arms would put the old take
    // into the room the mic is listening to.
    this._historyStop();
    if (this._openSheetV2) { try { this.sheetCloseV2(); } catch (e) {} }
    try { this._nowbarSync(); } catch (e) {}
    if (this.recordBtn) this.recordBtn.disabled = true;
    if (this.practiceRunBtn) this.practiceRunBtn.disabled = true;

    // ---- Paywall hard gate ----
    // The take buttons stay hidden until the entitlement status has loaded (see
    // _unlockStudioUI), so by the time a user can click this the status promise
    // is already settled — the await resolves on a microtask and the
    // user-gesture context the mic prompt needs is preserved. If locked, refuse
    // the take; if this is the one free preview, arm consumption.
    if (this.gated) {
      var access = await this._ensureStudioAccess();
      if (access === 'locked') {
        this.state = 'idle';
        if (this.recordBtn) this.recordBtn.disabled = false;
        if (this.practiceRunBtn) this.practiceRunBtn.disabled = false;
        this._lockStudioUI();
        if (this.lockEl && this.lockEl.scrollIntoView) {
          try { this.lockEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        }
        return;
      }
      this._pendingStudioConsume = (access === 'preview');

      // Don't let a visitor burn one of their 3 lifetime free takes on a
      // reference-less recording that computeScore will refuse to grade —
      // that's the exact moment the subscription pitch depends on showing a
      // real score. Route them into the picker instead; their take starts
      // properly once a clip is chosen. (The consume guard in
      // onRecordingComplete also forgives noReference takes as a backstop.)
      if (access === 'preview' && !this.refUrl) {
        this.state = 'idle';
        if (this.recordBtn) this.recordBtn.disabled = false;
        if (this.practiceRunBtn) this.practiceRunBtn.disabled = false;
        if (this.recordHint) this.recordHint.hidden = false;
        this._pendingStudioConsume = false;
        if (this._openRefPicker()) {
          this.setStatus('Pick a reference first — your free takes are scored against a clip.');
        } else {
          this.setStatus('Pick a clip in the Maqam Library first — free takes are scored against a reference.');
        }
        return;
      }
    }

    if (window.SaifTrack) {
      window.SaifTrack('practice_recording_started', {
        clip_title: this.refClipTitle || null,
        ref_url: this.refUrl || null
      });
    }

    // Reset any prior pitch-range cache so each recording recomputes
    this._pitchRange = null;
    // Take token — if a stop-during-countdown is followed by a fresh Record
    // before THIS invocation's countdown resolves, the newer take bumps the
    // sequence and the stale invocation bails out below instead of hijacking
    // the new take's stream/context.
    this._takeSeq = (this._takeSeq || 0) + 1;
    var takeSeq = this._takeSeq;
    // Hide the idle hint the moment a take starts; restored on failure /
    // cancel / reset so the next idle state explains itself again.
    if (this.recordHint) this.recordHint.hidden = true;

    // Step 1 (FIRST, synchronously after the click) — request mic permission.
    // Browsers only show the prompt as a direct response to a user gesture;
    // any async work before this would lose that gesture context.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setStatus('Your browser does not support microphone access.');
      console.error('[saif-practice] mediaDevices not available on this browser');
      this.state = 'idle';
      if (this.recordBtn) this.recordBtn.disabled = false;
      if (this.practiceRunBtn) this.practiceRunBtn.disabled = false;
      if (this.recordHint) this.recordHint.hidden = false;
      return;
    }

    /* SAIF_MIC_INAPP_V1 — stop before the permission prompt that cannot succeed. Letting them
       press Record, wait, and get a refusal costs a take and teaches them the studio is
       broken. Telling them first costs one sentence. */
    var _inApp = saifInAppBrowser();
    if (_inApp) {
      this.setStatus('Recording needs your normal browser. The ' + _inApp + ' browser cannot ' +
        'reach the microphone. Tap the three dots at the top and choose Open in browser.');
      if (window.SaifTrack) window.SaifTrack('practice_mic_inapp_blocked', { app: _inApp });
      this.state = 'idle';
      if (this.recordBtn) this.recordBtn.disabled = false;
      if (this.practiceRunBtn) this.practiceRunBtn.disabled = false;
      if (this.recordHint) this.recordHint.hidden = false;
      return;
    }

    // In-app browsers (Instagram/older iOS WebViews) can have getUserMedia but
    // no MediaRecorder — `new MediaRecorder(...)` would throw mid-take. Degrade
    // honestly to a Practice run (live trail + score, nothing saved) instead.
    if (this._captureAudio && !window.MediaRecorder) {
      this._captureAudio = false;
      this.setStatus('This browser can’t save recordings — running a practice take instead (live feedback + score, nothing saved).');
      console.warn('[saif-practice] MediaRecorder unavailable — degrading Record take to Practice run');
    }

    // A fresh take resets the one-shot auto-retry used by the save chain.
    this._saveRetried = false;

    // Create the analysis AudioContext synchronously in the gesture too —
    // iOS Safari starts post-await contexts 'suspended' (analyser reads
    // silence → empty pitch trail + bogus low-confidence score). Closed on
    // every failure path and in stopRecording.
    this.audioCtx = createGestureAudioContext();

    // Prime the reference element inside the gesture so the post-countdown
    // play() isn't blocked by Safari's autoplay policy — an element that has
    // been gesture-played once may be replayed programmatically later.
    if (this.refAudioEl && this.refUrl) {
      try {
        // Full volume for the take. The maqam-library clip bar sets .volume/.muted
        // on this SAME element and only resets them when ITS modal reopens — so a
        // slider someone nudged (or muted) there silently carried into every
        // practice run: the reported "reference is too quiet during a practice
        // run". A take always starts at full reference volume.
        this.refAudioEl.volume = 1;
        this.refAudioEl.muted = false;
        var primeP = this.refAudioEl.play();
        this.refAudioEl.pause();
        if (primeP && typeof primeP.catch === 'function') primeP.catch(function () {});
      } catch (e) {}
    }

    this.setStatus('Waiting for microphone permission — choose Allow.');
    try {
      // Stop any superseded stream before overwriting the handle — otherwise
      // the old mic track leaks and the browser's mic indicator stays lit.
      if (this.stream) {
        try { this.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
        this.stream = null;
      }
      // autoGainControl OFF: the browser's auto-gain constantly re-rides the
      // input level, pumping room noise up in quiet moments (false pitch spikes)
      // and making the captured level drift frame-to-frame. Off = a steady
      // signal and a steady pitch trail. Soft/far-from-mic voices that AGC used
      // to boost above detectPitch's rms gate are compensated on the analysis
      // path by adaptiveBoostFrame (session-level, breath-safe) — so quiet
      // speakers keep their pitch points without AGC's pumping.
      // echoCancellation + noiseSuppression OFF by default. The browser's echo
      // canceller is a SOFTWARE process that runs on the reference render stream
      // even with headphones, and ducks a voice sung in UNISON with the reference
      // (mistaking the voice for echo) — that was the "voice takes long to pick
      // up / spikes / cuts out when a reference plays" bug. Off = clean pickup.
      // Tradeoff on SPEAKERS: the reference can bleed into the mic — mitigated by
      // the reference-bleed level gate (see the live loop) + a headphones nudge.
      // ?aecon=1 (or localStorage saif:practice:aecon=1) restores the old
      // echo-cancelled behaviour for A/B testing.
      var _aecOn = false;
      try { _aecOn = /[?&]aecon=1/.test(location.search || '') || (window.localStorage && localStorage.getItem('saif:practice:aecon') === '1'); } catch (e) {}
      // channelCount {ideal:1} — force MONO capture. With AEC off (the default),
      // phones hand back a 2-channel stream whose single physical mic fills only
      // the LEFT channel (right silent), so the raw recorded blob plays in the
      // left ear only. A mono capture makes MediaRecorder emit a true mono blob
      // that the <audio> element centers to both ears. 'ideal' (not exact) so it
      // degrades gracefully on any device that can't honor a strict mono request.
      var _micAudio = _aecOn
        ? { autoGainControl: false, channelCount: { ideal: 1 } }
        : { autoGainControl: false, echoCancellation: false, noiseSuppression: false, channelCount: { ideal: 1 } };
      this._recAecOff = !_aecOn; // AEC off => the reference can bleed in on speakers; enable the bleed gate below
      // Re-read the headphones switch per take (the reject is skipped when on).
      try { this._headphonesMode = !!(window.localStorage && localStorage.getItem('saif:practice:headphones') === '1'); } catch (e) { this._headphonesMode = false; }
      slog('[saif-practice] record mic constraints:', JSON.stringify(_micAudio));
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: _micAudio });
    } catch (e) {
      console.error('[saif-practice] getUserMedia failed:', e && e.name, e && e.message);
      var msg = micErrorMessage(e);
      this.setStatus(msg);
      if (window.SaifTrack) window.SaifTrack('practice_mic_error', {
        error_name: (e && e.name) || 'unknown',
        error_message: (e && e.message) || '',
        /* Which app they were trapped in, so this is one query next time rather than an
           excursion through raw user agents. */
        in_app: saifInAppBrowser() || ''
      });
      // Reset so Record works immediately after the user re-grants the mic —
      // leaving state at 'starting' would trip the re-entrancy guard forever.
      this.state = 'idle';
      if (this.recordBtn) this.recordBtn.disabled = false;
      if (this.practiceRunBtn) this.practiceRunBtn.disabled = false;
      if (this.recordHint) this.recordHint.hidden = false;
      closeAudioContext(this.audioCtx);
      this.audioCtx = null;
      return;
    }

    // Mic death mid-take (Bluetooth drop, USB unplug, OS revoking the device)
    // previously left the student reciting into a dead microphone with the UI
    // still saying "recording". Stop honestly and keep what was captured.
    this.stream.getTracks().forEach(function (t) {
      t.addEventListener('ended', function () {
        if (self.state !== 'recording') return;
        console.warn('[saif-practice] mic track ended mid-take — stopping to salvage the take');
        self.setStatus('The microphone disconnected — recording stopped, and everything captured so far was kept.');
        try { self.stopRecording(); } catch (e) {}
      });
    });

    // Keep the screen awake while recording — phones dim + lock mid-take,
    // which kills the mic and loses the recitation. Best-effort; the API is
    // absent on older browsers and that's fine.
    this._wakeLock = null;
    try {
      if (navigator.wakeLock && navigator.wakeLock.request) {
        navigator.wakeLock.request('screen').then(function (wl) {
          self._wakeLock = wl;
        }).catch(function () {});
      }
    } catch (e) {}

    // Step 2 — kick off reference analysis in the background if needed.
    // We don't await this: if it's still in flight when recording starts,
    // the karaoke target will simply appear as soon as it lands.
    if (this.refUrl && (!this.refPitchData || this.refPitchData.length === 0)) {
      this.analyzeReference().catch(function () {});
    }

    // Reveal the live section now so the countdown is visible
    if (this.recordBtn) this.recordBtn.hidden = true;
    if (this.practiceRunBtn) this.practiceRunBtn.hidden = true;
    if (this.stopBtn) this.stopBtn.hidden = false;
    if (this.liveSection) {
      this.liveSection.hidden = false;
      this.liveSection.classList.remove('saif-practice__live--idle');
    }
    if (this.resultSection) this.resultSection.hidden = true;
    // On phones the live canvas usually sits below the fold — without this the
    // 3-2-1 countdown plays entirely off-screen. Also hand focus to Stop so
    // keyboard/screen-reader users aren't left on a now-hidden button.
    if (this.liveSection) {
      try {
        this.liveSection.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      } catch (e) {}
    }
    if (this.stopBtn) { try { this.stopBtn.focus({ preventScroll: true }); } catch (e) {} }
    this.setStatus('Get ready — recording starts after the count…');

    // Pause and rewind the reference so the countdown starts from a clean state
    if (this.refAudioEl && this.refUrl) {
      try { this.refAudioEl.pause(); this.refAudioEl.currentTime = 0; } catch (e) {}
    }
    // Show the stage already framed at t=0 BEHIND the count — the student sees
    // exactly the picture the take will open with, instead of a blanked canvas.
    try { this._drawIdleCanvas(); } catch (e) {}

    // 3-2-1-Go! before mic + reference fire together
    await this.runCountdown(3);

    // Bail out silently if a newer take superseded this one while we counted
    // down — its stream/context belong to the new take, so don't touch them.
    if (takeSeq !== this._takeSeq) {
      slog('[saif-practice] startRecording bailing out — superseded by a newer take');
      return;
    }

    // Bail out if the user already pressed stop during the countdown.
    // (stopRecording sets state to 'recorded'; checking 'idle' here is wrong
    // because state STARTS as 'idle' and isn't changed before this point —
    // including 'idle' in this condition would make every recording bail out.)
    if (this.state === 'recorded') {
      if (this.stream) {
        this.stream.getTracks().forEach(function (t) { t.stop(); });
        this.stream = null;
      }
      closeAudioContext(this.audioCtx);
      this.audioCtx = null;
      if (this.recordBtn) this.recordBtn.disabled = false;
      if (this.practiceRunBtn) this.practiceRunBtn.disabled = false;
      slog('[saif-practice] startRecording bailing out — user pressed stop during countdown');
      return;
    }

    // Reuse the gesture-created context; fall back to lazy creation if that
    // failed (matches the old behavior). Resume in case it suspended anyway.
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.audioCtx.state === 'suspended') {
      try { this.audioCtx.resume(); } catch (e) {}
    }
    var source = this.audioCtx.createMediaStreamSource(this.stream);
    var analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    this.recordedChunks = [];
    this.recPitchData = [];
    this._rawPitchWin = [];   // fresh despike history per take (see _liveDespikeFreq)
    this._rawPitchAt = null;
    // Only the Record-take path spins up a MediaRecorder. A Practice run runs
    // the same live analyser/pitch trail below but never instantiates one, so
    // no audio is ever captured, buffered, or uploaded.
    if (this._captureAudio) {
      // Pick the first MIME type the browser can both record AND play back.
      // Default selection by MediaRecorder is fine on Chrome (webm/opus) but
      // Safari needs mp4. Falling through to undefined lets the browser pick.
      var mimeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg'];
      var chosenMime;
      for (var i = 0; i < mimeCandidates.length; i++) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(mimeCandidates[i])) {
          chosenMime = mimeCandidates[i];
          break;
        }
      }
      slog('[saif-practice] chosen MediaRecorder mimeType:', chosenMime || '(browser default)');
      this.mediaRecorder = chosenMime
        ? new MediaRecorder(this.stream, { mimeType: chosenMime })
        : new MediaRecorder(this.stream);
      slog('[saif-practice] actual mediaRecorder.mimeType:', this.mediaRecorder.mimeType);
    } else {
      this.mediaRecorder = null;
      slog('[saif-practice] practice run — no MediaRecorder; nothing will be saved');
    }
    // Snapshot the reference playback rate at record-start time. The
    // recording's pitch trail color-coding compares user-elapsed against
    // reference time mapped through this rate (user-time × rate = ref-time).
    // Without this, slow-speed (0.5×) recordings lose accuracy coloring
    // halfway through because the lookup runs off the end of the clip.
    this.recordingPlaybackRate = (this.refAudioEl && this.refAudioEl.playbackRate) || 1;

    if (this._captureAudio) {
    this.mediaRecorder.ondataavailable = function (e) {
      if (e.data.size > 0) self.recordedChunks.push(e.data);
    };

    // Snapshot the recorder + the fact that THIS take captured audio, so the
    // async onstop can't read mutable instance state that a superseding take
    // (e.g. a fast Practice-run tap during the save window) may have already
    // flipped — which would otherwise discard this just-recorded take or
    // null-deref a reassigned this.mediaRecorder.
    var recorderForStop = this.mediaRecorder;
    this.mediaRecorder.onstop = function () {
      slog('[saif-practice] mediaRecorder.onstop fired, chunks:', self.recordedChunks.length);
      // Stop the reference too so it doesn't keep playing after recording ends
      if (self.refAudioEl) {
        try { self.refAudioEl.pause(); self.refAudioEl.currentTime = 0; } catch (e) {}
        if (self.refPlayBtn) self.refPlayBtn.classList.remove('saif-practice__btn--active');
      }
      // Use the recorder's actual mimeType (not a hardcoded value), otherwise
      // the blob lies about its format and playback fails silently on Safari.
      var blobType = recorderForStop.mimeType || 'audio/webm';
      self.recordedBlob = new Blob(self.recordedChunks, { type: blobType });
      // Revoke the previous take's object URL before overwriting the handle —
      // recording again without "Try Again" skips reset(), so this is the
      // only place the old URL can be released.
      if (self.recordedUrl) {
        try { URL.revokeObjectURL(self.recordedUrl); } catch (e) {}
      }
      self.recordedUrl = URL.createObjectURL(self.recordedBlob);
      slog('[saif-practice] blob created — type:', blobType, 'size:', self.recordedBlob.size, 'bytes');

      // Pre-decode the recording so play/seek is instant instead of waiting
      // for the codec to spin up on the first click. With a blob URL the fetch
      // is local — only cost is the codec decode, which we overlap with the
      // user reviewing their score. _wirePlayerEvents hooks up the transport
      // UI (play/pause icon, progress bar, time display).
      if (self._playbackAudio) {
        try { self._playbackAudio.pause(); } catch (e) {}
      }
      self._playbackAudio = new Audio(self.recordedUrl);
      self._playbackAudio.preload = 'auto';
      setPreservePitch(self._playbackAudio);
      self._wirePlayerEvents(self._playbackAudio);
      // Seed the known take length (last analyzed frame time) as a duration
      // backstop, so the scrubber, time readout, and seeking all work even when
      // the recorded blob reports a bogus duration — MediaRecorder frequently
      // yields duration = 0 / NaN / Infinity, which was leaving the transport
      // stuck at "0:00" and refusing to seek. _wirePlayerEvents just reset
      // _playbackDuration to null, so set it AFTER. A real finite blob duration
      // still wins (see _refreshPlayerUI); this only fills in when it's missing.
      var knownDur = (self.recPitchData && self.recPitchData.length)
        ? self.recPitchData[self.recPitchData.length - 1].time : 0;
      // Fallback when the contour is empty (no voiced frames were detected):
      // the whole-second record clock, which stopRecording does not clear.
      if (knownDur <= 0 && self._lastTimerSec > 0) knownDur = self._lastTimerSec;
      if (knownDur > 0) self._playbackDuration = knownDur;
      self._playbackAudio.load();
      self._playerHasPlayed = false; // fresh recording → button should read "Play", not "Resume"
      self._setPlayIcon(false);
      self._refreshPlayerUI();
      try {
        // Pass `true` explicitly — onstop only exists for a Record-take, so
        // this take captured audio regardless of any later _captureAudio flip.
        self.onRecordingComplete(true);
      } catch (err) {
        console.error('[saif-practice] onRecordingComplete threw:', err);
      }
    };

    this.mediaRecorder.onerror = function (e) {
      // Log-only left the student "recording" into a broken recorder. Stop
      // honestly — onstop still fires and salvages the chunks captured so far.
      console.error('[saif-practice] mediaRecorder error:', e && e.error);
      if (self.state === 'recording') {
        self.setStatus('Recording hit a problem — stopping and keeping what was captured.');
        try { self.stopRecording(); } catch (err) {}
      }
    };

    // Start mic + reference audio in lockstep
    try {
      this.mediaRecorder.start(100);
      slog('[saif-practice] mediaRecorder.start(100) ok, state:', this.mediaRecorder.state);
    } catch (err) {
      console.error('[saif-practice] mediaRecorder.start failed:', err);
      this.setStatus('Could not start recording: ' + (err && err.message || err) + ' — tap Record to try again.');
      // Full cleanup — leaving state at 'starting' bricked the Record button
      // (the re-entrancy guard rejected every later click), and the hot mic
      // stream kept the browser's recording indicator lit.
      this.state = 'idle';
      if (this.stream) {
        try { this.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e2) {}
        this.stream = null;
      }
      closeAudioContext(this.audioCtx);
      this.audioCtx = null;
      if (this.stopBtn) this.stopBtn.hidden = true;
      this._idleStage();   // keep the stage mounted; just return it to rest
      if (this.recordBtn) { this.recordBtn.hidden = false; this.recordBtn.disabled = false; }
      if (this.practiceRunBtn) { this.practiceRunBtn.hidden = false; this.practiceRunBtn.disabled = false; }
      if (this.recordHint) this.recordHint.hidden = false;
      return;
    }
    } // end capture-only MediaRecorder setup
    if (this.refAudioEl && this.refUrl) {
      try {
        this.refAudioEl.currentTime = 0;
        var refPlayP = this.refAudioEl.play();
        if (refPlayP && typeof refPlayP.catch === 'function') {
          refPlayP.catch(function (err) {
            console.warn('[saif-practice] reference play() blocked:', err && err.name, err && err.message);
            self.setStatus('The browser blocked the reference clip — tap "Listen to Reference" to start it.');
          });
        }
      } catch (e) {}
    }
    this.state = 'recording';
    // Only tell the student to follow the gold line when a reference is
    // loaded — without one there IS no gold line, just their white trail.
    // Either way, end with the one thing they need to DO next: press Stop.
    var hasRefLine = (this.refPitchData && this.refPitchData.length) || this.refUrl;
    if (!this._captureAudio) {
      this.setStatus(hasRefLine
        ? 'Practice run — follow the gold line. Press Stop for your score (nothing is saved).'
        : 'Practice run — sing or recite, then press Stop for your score (nothing is saved).');
    } else {
      this.setStatus(hasRefLine
        ? 'Follow the gold line — press Stop whenever you\'re done.'
        : 'Recording — sing or recite, then press Stop whenever you\'re done.');
    }
    // Live timer next to the Stop button: the take is rolling, here's how long.
    if (this.recTimer) {
      this.recTimer.textContent = '0:00';
      this.recTimer.hidden = false;
    }
    this._lastTimerSec = -1;

    // Hard cap — auto-stop after MAX_RECORDING_DURATION_MS. Cleared in
    // stopRecording / reset so it doesn't fire stale.
    if (this._maxDurationTimer) clearTimeout(this._maxDurationTimer);
    this._maxDurationTimer = setTimeout(function () {
      if (self.state === 'recording') {
        slog('[saif-practice] hit max duration (' + (MAX_RECORDING_DURATION_MS / 1000) + 's) — auto-stopping');
        self.setStatus('Reached the ' + (MAX_RECORDING_DURATION_MS / 60000) + '-minute limit — stopping...');
        self.stopRecording();
      }
    }, MAX_RECORDING_DURATION_MS);

    // Recompute canvas dimensions now that the live section is visible.
    // The init-time call ran while liveSection was hidden, so the canvas was
    // sized 0x0 and the pitch trail drew to nothing. This makes it visible.
    this.resizeCanvases();
    slog('[saif-practice] live canvas resized:',
      this.liveCanvas ? this.liveCanvas.width + 'x' + this.liveCanvas.height : 'no canvas');

    // Start live visualization
    var bufferLength = analyser.fftSize;
    var dataArray = new Float32Array(bufferLength);
    var startTime = Date.now();
    // Pause-desync hook: MediaRecorder.pause() drops paused time from the
    // audio file, but this Date.now()-anchored pitch clock kept running — so
    // every reference-pause desynced the saved take from its pitch contour.
    // The refAudioEl pause/play listeners measure the paused wall-time and
    // shift the clock forward through this hook on resume.
    self._recClockAdjust = function (deltaMs) { startTime += deltaMs; };
    self._recPausedAtMs = null;

    // Cap recPitchData at MAX_PITCH_SAMPLES (6000 entries ~= 100s @ 60Hz).
    // Without this cap, a 5-min recording produces ~18000 samples and the
    // O(n²) steadiness pass in computeScore freezes the UI for 3-5 seconds.
    // Strategy: once we hit cap, double the sampling stride and downsample
    // the existing array in place. A second cap-hit doubles again, and so on.
    // This keeps the timeline coverage uniform while bounding the array.
    var MAX_PITCH_SAMPLES = 6000;
    var pitchSampleStride = 1;
    var pitchSampleCounter = 0;
    var micLevel = { peak: 0 }; // per-take session level for adaptiveBoostFrame
    // Reference-bleed gate (speakers, AEC off): the reference played aloud can
    // leak into the mic, so during pauses the mic would track the reference, not
    // the student. The old blunt "below 35% of the session peak → reject" also
    // cut genuinely soft singing and penalized headphone users. Now the reject
    // decision lives in isReferenceBleed (quiet AND in unison with the reference),
    // self-calibrated to this take's actual bleed level, and OFF entirely when the
    // student has toggled "🎧 I'm on headphones".
    self._bleedPeak = 0; // measured bleed amplitude this take (0 = not yet calibrated)
    self._acceptStreak = 0;   // consecutive accepted voiced frames (bleed-gate hysteresis)
    // Onset-confirmation state: the first K voiced frames after any silence are
    // held out of the contour until they agree, then committed together (below).
    self._onsetPend = [];
    self._onsetConfirmed = false;
    self._lastVoicedT = null;

    function draw() {
      if (self.state !== 'recording') return;
      requestAnimationFrame(draw);
      // Recorder paused (reference clip paused) → the audio file isn't gaining
      // time, so the pitch contour must not either. Keep the rAF loop alive
      // but skip analysis/pushes until resume shifts the clock (see above).
      if (self.mediaRecorder && self.mediaRecorder.state === 'paused') return;
      // Practice run (no MediaRecorder) or record take: while a loaded
      // reference is paused, the audio timeline isn't advancing, so skip
      // appending frames — otherwise the Date.now() pitch clock runs on and the
      // contour drifts ahead of the paused audio. Resume shifts the clock back.
      if (self.refUrl && self.refAudioEl && self.refAudioEl.paused) return;

      analyser.getFloatTimeDomainData(dataArray);
      var elapsed = (Date.now() - startTime) / 1000;
      // floorRatio 0.05 ALWAYS — never starve a soft voice's boost just because a
      // reference is playing; bleed is handled after detection (below), not by
      // suppressing the boost.
      var freq = detectPitch(adaptiveBoostFrame(dataArray, micLevel, 0.05), self.audioCtx.sampleRate);
      // Live mic-input meter (same pattern as the Range tab's): the record view
      // used to reveal "the mic isn't hearing you" only AFTER the take, via the
      // low-confidence banner — a wasted take. Amber below the voiced floor.
      if (self._liveLevelEl === undefined) {
        self._liveLevelEl = document.getElementById('SaifPracticeLiveLevelFill') || null;
      }
      if (self._liveLevelEl) {
        var _lvl = micLevel.framePeak || 0;
        self._liveLevelEl.style.width = Math.min(100, Math.round(Math.sqrt(_lvl) * 150)) + '%';
        self._liveLevelEl.classList.toggle('saif-practice__range-level-fill--quiet', _lvl < 0.06);
      }
      // Reference-bleed reject (speakers only; skipped on headphones): a frame is
      // bleed only when it is quiet relative to the student's own voiced level AND
      // at the reference's own pitch at this instant — so soft singing at the
      // student's OWN pitch is kept.
      //  - Hysteresis: mid-phrase, 3+ consecutively accepted voiced frames mean the
      //    mic is clearly hearing the student — a single quiet frame right after
      //    (a phrase tail, a soft syllable) is theirs, not sudden bleed. Skip the test.
      //  - The bleed-level estimate only learns from frames FAR below the student's
      //    own voiced level (<10% of the session anchor). Learning from every
      //    rejected frame let the student's own soft unison voice inflate the
      //    estimate, which raised the reject bound, which rejected more — a spiral
      //    that ended with the studio "not picking up" quiet singers at all.
      var _refBleed = !self._headphonesMode && self._recAecOff && self.refUrl &&
        self.refAudioEl && !self.refAudioEl.paused && !self.refAudioEl.ended;
      if (_refBleed && freq > 0 && (self._acceptStreak || 0) < 3) {
        var _refNow = self.getRefPitchAt(elapsed * (self.recordingPlaybackRate || 1));
        var _rMidi = _refNow ? freqToMidi(_refNow) : null;
        if (isReferenceBleed(micLevel.framePeak || 0, micLevel.peak || 0, self._bleedPeak || 0, freqToMidi(freq), _rMidi)) {
          var _fp = micLevel.framePeak || 0;
          if (_fp < 0.10 * (micLevel.peak || 0)) {
            self._bleedPeak = self._bleedPeak ? (self._bleedPeak * 0.9 + _fp * 0.1) : _fp;
          }
          freq = -1;
        }
      }
      // Voicing-run bookkeeping: the accept streak drives the bleed-gate
      // hysteresis above; the unvoiced run drives adaptiveBoostFrame's faster
      // anchor decay during silence (both reset naturally at each transition).
      if (freq > 0) {
        self._acceptStreak = Math.min((self._acceptStreak || 0) + 1, 10);
        micLevel.unvoicedRun = 0;
      } else {
        self._acceptStreak = 0;
        micLevel.unvoicedRun = (micLevel.unvoicedRun || 0) + 1;
      }

      // Tick the recording timer once a second (not every frame).
      var timerSec = Math.floor(elapsed);
      if (timerSec !== self._lastTimerSec) {
        self._lastTimerSec = timerSec;
        if (self.recTimer) self.recTimer.textContent = fmtClock(elapsed);
      }

      // Causal despike once per voiced FRAME (not per stride-gated push) so
      // the ring sees every reading and the whole live view — trail, comet,
      // playhead dot, note label, in-tune streak, range auto-fit — draws the
      // spike-free value. Raw freq remains what's stored and scored.
      var dispFreq = freq > 0 ? self._liveDespikeFreq(freq, elapsed) : freq;

      // Stride/cap-aware store (unchanged policy, factored so the onset commit
      // below can flush several held frames through the same bookkeeping).
      function pushSample(t, rawF, dispF) {
        if ((pitchSampleCounter++ % pitchSampleStride) !== 0) return;
        self.recPitchData.push({ time: t, freq: rawF, disp: dispF });
        // If we hit the cap, halve the existing data and double the stride
        // so subsequent pushes stay below cap with uniform spacing.
        if (self.recPitchData.length >= MAX_PITCH_SAMPLES) {
          var halved = [];
          for (var i = 0; i < self.recPitchData.length; i += 2) {
            halved.push(self.recPitchData[i]);
          }
          self.recPitchData = halved;
          pitchSampleStride *= 2;
          slog('[saif-practice] pitch buffer capped — stride now',
            pitchSampleStride, 'samples=' + self.recPitchData.length);
        }
      }

      // ONSET CONFIRMATION (K=3). The first frames after any silence are the
      // detector's least reliable — low periodicity, harmonic (octave) locks —
      // and the live despiker has no history there, so they used to draw AND
      // store raw garbage: the reported "jump whenever the mic first picks up
      // audio after a pause". Hold the first K voiced frames; when K agree
      // (they always arrive within one gap window), octave-fold each toward
      // their own median and commit all of them at once. A genuine new-register
      // phrase confirms unchanged (the median IS the new register); an isolated
      // 1-2 frame blip surrounded by silence never confirms and is dropped —
      // sub-50ms voiced blips are noise/bleed, never real recitation.
      // Cost: the trail/dot appear ~2 frames (~35ms) later at each phrase start.
      if (freq > 0) {
        var gapBefore = (self._lastVoicedT == null || (elapsed - self._lastVoicedT) > 0.15);
        self._lastVoicedT = elapsed;
        if (gapBefore) { self._onsetPend = []; self._onsetConfirmed = false; }
        if (!self._onsetConfirmed) {
          self._onsetPend.push({ time: elapsed, freq: freq });
          if (self._onsetPend.length >= 3) {
            var pmids = [], oi;
            for (oi = 0; oi < self._onsetPend.length; oi++) pmids.push(freqToMidi(self._onsetPend[oi].freq));
            var psorted = pmids.slice().sort(function (a, b) { return a - b; });
            var pmed = psorted[psorted.length >> 1];
            for (oi = 0; oi < self._onsetPend.length; oi++) {
              var pm = pmids[oi];
              while (pm - pmed > 6) pm -= 12;
              while (pmed - pm > 6) pm += 12;
              var pf = midiToFreq(pm);
              pushSample(self._onsetPend[oi].time, pf, pf);
            }
            self._onsetPend = [];
            self._onsetConfirmed = true;
          }
          // While pending: nothing new on screen (the dot/trail hold ~35ms).
          dispFreq = -1;
        } else {
          pushSample(elapsed, freq, dispFreq);
        }
      } else if (self._onsetPend.length && self._lastVoicedT != null &&
                 (elapsed - self._lastVoicedT) > 0.15) {
        // A pending run that went silent before confirming was a noise blip.
        self._onsetPend = [];
      }

      // dispFreq (not raw freq): the dot, note label, in-tune streak, and the
      // persistent range auto-fit must not react to one-frame octave errors.
      // dataArray stays raw — the volume meter should show the true level.
      self.drawLiveVisualization(dataArray, dispFreq, elapsed);
    }

    draw();
  };

  PracticeStudio.prototype.stopRecording = function () {
    // Capture whether a take was actually rolling BEFORE we flip to 'recorded'
    // — a practice run has no MediaRecorder, so we can't lean on its state to
    // tell "finished a run" apart from "cancelled during the countdown".
    var wasRecording = this.state === 'recording';
    var hadRecording = this.mediaRecorder && this.mediaRecorder.state !== 'inactive';
    slog('[saif-practice] stopRecording, prior state:', this.state,
      'mediaRecorder:', this.mediaRecorder ? this.mediaRecorder.state : 'null',
      'capture:', this._captureAudio);
    this.state = 'recorded';
    try { this._nowbarSync(); } catch (e) {}
    if (this.recTimer) this.recTimer.hidden = true;
    if (this._maxDurationTimer) {
      clearTimeout(this._maxDurationTimer);
      this._maxDurationTimer = null;
    }
    // Release the screen wake lock — only held while a take is rolling.
    if (this._wakeLock) {
      try { this._wakeLock.release(); } catch (e) {}
      this._wakeLock = null;
    }
    // Stop the reference clip the instant the take ends. The Record-take path
    // also does this inside mediaRecorder.onstop, but a Practice run has NO
    // onstop callback — without this the reference audio kept playing on into
    // the result view (the "pause doesn't stop the audio" bug).
    if (this.refAudioEl) {
      try { this.refAudioEl.pause(); this.refAudioEl.currentTime = 0; } catch (e) {}
      if (this.refPlayBtn) this.refPlayBtn.classList.remove('saif-practice__btn--active');
    }
    // A finished practice run has no onstop callback to flow into the result
    // view, so we trigger completion ourselves once the UI is reset below.
    var finishPracticeRun = false;
    if (hadRecording) {
      try {
        this.mediaRecorder.stop();
        slog('[saif-practice] mediaRecorder.stop() called — onstop will follow');
      } catch (err) {
        console.error('[saif-practice] mediaRecorder.stop threw:', err);
      }
    } else if (!this._captureAudio && wasRecording) {
      // Practice run that genuinely ran — show the score without saving audio.
      finishPracticeRun = true;
    } else {
      slog('[saif-practice] no active recording to stop — resetting UI');
      // No active recording means user pressed stop during countdown / before mic was active.
      // Hide live section so the screen doesn't get stuck on the countdown UI.
      this._idleStage();   // keep the stage mounted; just return it to rest
      this.setStatus('Cancelled. Tap Record to try again.');
      if (this.recordHint) this.recordHint.hidden = false;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(function (t) { t.stop(); });
      this.stream = null;
    }
    // Close the per-take analysis context — iOS Safari hard-caps concurrent
    // AudioContexts, so leaking one 'running' context per take kills the
    // studio (tuner/drill/live view) after a few recordings. Safe here: the
    // draw loop already exited on the state change above, and MediaRecorder
    // runs off the stream, not this context.
    closeAudioContext(this.audioCtx);
    this.audioCtx = null;
    if (this.stopBtn) this.stopBtn.hidden = true;
    if (this.recordBtn) {
      this.recordBtn.hidden = false;
      this.recordBtn.disabled = false;
    }
    if (this.practiceRunBtn) {
      this.practiceRunBtn.hidden = false;
      this.practiceRunBtn.disabled = false;
    }
    if (finishPracticeRun) {
      try {
        this.onRecordingComplete();
      } catch (err) {
        console.error('[saif-practice] onRecordingComplete (practice run) threw:', err);
      }
    }
  };

  // Causal octave-error corrector for the LIVE pitch trail ONLY (the `disp`
  // field). The autocorrelation detector occasionally reports a note an octave
  // off; drawn frame-by-frame those read as sharp spikes. This folds a reading
  // toward the octave of the recent run ONLY when doing so reveals a clean
  // octave error (within 1.6 semitones of the local median).
  //
  // Two properties are load-bearing (each fixed a verified bug in a prior
  // version of this method — keep them):
  //  1. The median anchor comes from a ring of RAW detector readings, never
  //     from its own folded output. Anchoring on folded values self-reinforces:
  //     a genuine sustained octave leap folds once, re-enters the window, and
  //     every later frame keeps folding — the whole held note latches an
  //     octave wrong. With a raw anchor, a genuine leap moves the median
  //     within ~4 frames and the folding stops (≤ ~70ms of display flicker).
  //  2. A voicing gap (breath/pause — detectPitch returns -1, nothing pushed)
  //     or a new take resets the ring, so a phrase legitimately resumed in a
  //     different register is never folded toward stale pre-gap history.
  //
  // The saved take / score / stored contour never see this: they use the raw
  // freq, despiked non-causally at completion by medianSmoothPitch (which
  // preserves sustained octave shifts correctly).
  PracticeStudio.prototype._liveDespikeFreq = function (freq, elapsed) {
    var win = this._rawPitchWin || (this._rawPitchWin = []);
    var last = this._rawPitchAt;
    // Gap or restart (elapsed goes backwards on a new take) → history is stale.
    // 0.15s matches the draw-side line-break gap exactly: with the old 0.25 a
    // (0.15, 0.25] silence broke the LINE but kept the slew limiter's history,
    // so the first point of the new phrase re-entered on a fake glide from the
    // previous phrase's pitch.
    if (last == null || elapsed < last || elapsed - last > 0.15) { win.length = 0; this._lastDispMidi = null; }
    this._rawPitchAt = elapsed;
    var m = freqToMidi(freq);
    win.push(m);
    if (win.length > 21) win.shift(); // ~0.35s @60fps — outvotes brief octave locks, still follows the melody
    var out = m;
    if (win.length >= 5) {
      // Robust register = the MEDIAN of the last ~0.35s of RAW readings. Over that
      // span the detector is on the true octave more often than not, so the median
      // sits on the true octave — and unlike folding toward the last DRAWN value,
      // a rolling median SELF-CORRECTS, so the trail can't get stuck an octave high
      // after an onset harmonic-lock. An odd-length window keeps the median an
      // actual reading (never a midpoint between two octaves).
      var sorted = win.slice().sort(function (a, b) { return a - b; });
      var med = sorted[sorted.length >> 1];
      // Fold this frame onto the octave nearest that register: a frame-to-frame
      // octave flip (the detector grabbing H2 on a low voice) collapses onto the
      // coherent line instead of spiking a full octave.
      var folded = m;
      while (folded - med > 6) folded -= 12;
      while (med - folded > 6) folded += 12;
      out = folded;
    }
    // Slew-rate limit the DISPLAY for whatever non-octave jump remains (onset
    // transients, 3× harmonic locks, interp garbage) — ~2.75 st/frame. A genuine
    // leap keeps pushing the same way and catches up in 2–4 frames (~50–70ms,
    // already-accepted flicker); melisma is sub-cap so it flows freely.
    if (this._lastDispMidi != null) {
      var d = out - this._lastDispMidi;
      if (d > 2.75) out = this._lastDispMidi + 2.75;
      else if (d < -2.75) out = this._lastDispMidi - 2.75;
    }
    this._lastDispMidi = out;
    // NOTE: display-only. The RAW freq is stored separately for scoring and
    // cleaned non-causally by medianSmoothPitch at completion — untouched here.
    return midiToFreq(out);
  };

  PracticeStudio.prototype.onRecordingComplete = function (capturedArg) {
    var self = this;
    // Trust the snapshot passed by onstop (a Record-take) over live state; the
    // direct practice-run call passes nothing and falls back to current state.
    var captured = (typeof capturedArg === 'boolean') ? capturedArg : this._captureAudio;
    slog('[saif-practice] onRecordingComplete entered, captured:', captured, 'recPitchData:', this.recPitchData.length, 'recordedChunks:', this.recordedChunks.length);
    // Despike the pitch contour before scoring + drawing — removes the
    // autocorrelation detector's octave jumps / breath spikes so the score
    // (especially steadiness) and the compare graph reflect the real
    // performance, not detector noise. Genuine melody is preserved.
    this.recPitchData = medianSmoothPitch(this.recPitchData);
    // Compute score
    var score = this.computeScore();
    slog('[saif-practice] computed score:', score.overall, JSON.stringify(score));

    if (window.SaifTrack) {
      window.SaifTrack('practice_recording_completed', {
        score: typeof score.overall === 'number' ? Math.round(score.overall * 10) / 10 : null,
        pitch: typeof score.pitch === 'number' ? Math.round(score.pitch * 10) / 10 : null,
        consistency: typeof score.consistency === 'number' ? Math.round(score.consistency * 10) / 10 : null,
        steadiness: typeof score.steadiness === 'number' ? Math.round(score.steadiness * 10) / 10 : null,
        low_confidence: !!score.lowConfidence,
        clip_title: this.refClipTitle || null,
        ref_url: this.refUrl || null
      });
      if (typeof score.overall === 'number' && score.overall >= 9) {
        window.SaifTrack('practice_score_milestone', { score: score.overall, threshold: 9 });
      }
    }

    // Track this attempt for streak + per-clip history. Only count "real"
    // attempts: skip when the algorithm flagged low confidence (silent take,
    // mic problem, etc.) so the trend graph isn't poisoned by noise scores.
    if (!score.lowConfidence) {
      recordPracticeDay();
      // Unscored (no-reference) takes still count as practice for the
      // streak, but a null score must not enter the trend history.
      if (!score.noReference) recordScore(this.refUrl, score.overall);
      this._refreshStreakBadge();
      // Feed the dashboard streak/heatmap too — a scored PRACTICE RUN is real
      // practice even though nothing is saved (idempotent per day, no-op when
      // saif-courses.js isn't loaded or the visitor isn't signed in).
      if (window.SaifStreakRecordPractice) {
        try { window.SaifStreakRecordPractice(); } catch (e) {}
      }
    }
    this._renderScoreTrend();

    // Spend the one free take on a real (non-noise) preview recording. A
    // low-confidence take (silence / mic problem) is forgiven so the visitor
    // still gets a genuine try — and so is a no-reference take, which
    // computeScore refuses to grade at all: burning a lifetime free take on
    // an unscoreable recording is exactly the moment we lose the visitor.
    // The current result stays visible; the lock card takes over the take
    // controls so the next take prompts trial/subscribe.
    if (this._pendingStudioConsume && !score.lowConfidence && !score.noReference) {
      this._pendingStudioConsume = false;
      this._consumeStudioTry();
    }

    // Show results. The stage STAYS UP — reading your score while the take you
    // just sang is still on screen is the whole point of the layout.
    // A live take supersedes any saved-take review, so the replay flag is
    // cleared here — otherwise the transport would stay pinned to the Score tab.
    this._reviewingSavedId = null;
    this._setHistoryCompareState('ready');
    if (this.resultSection) this.resultSection.hidden = false;
    // A finished take is a real result — bring the sheet up. (Driven from
    // here rather than from a `hidden` observer, because a reference PREVIEW
    // flips that same flag and would open an empty result.)
    try { this.sheetOpenV2('result'); } catch (e) {}
    // Collapse the full transport until the student presses Listen — the
    // score + next-steps row should be the whole story at first glance.
    if (this.playerEl && this.listenBtn) this.playerEl.hidden = true;
    // Reset the tab strip to Score on every reveal so users see the score
    // first regardless of which tab they were on during the previous take.
    if (this._switchResultTab) this._switchResultTab('score');
    // Hand focus to the result — keyboard/screen-reader users were left on
    // the now-hidden Stop button with no announcement that a score exists.
    if (this.resultSection) {
      try {
        this.resultSection.setAttribute('tabindex', '-1');
        this.resultSection.focus({ preventScroll: prefersReducedMotion() ? false : true });
        this.resultSection.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
      } catch (e) {}
    }

    // Tailor the result actions to the take type: a saved Record-take offers
    // Listen / AI feedback / Submit-for-feedback; a Practice run shows only
    // score + compare and a "not saved" notice.
    this._applyResultMode(captured);

    // Resize the comparison canvas now that its parent is visible — same
    // 0x0-canvas problem the live canvas had before recording started.
    this.resizeCanvases();

    // Belt-and-suspenders: AI feedback element should stay hidden unless we
    // explicitly request feedback. (CSS bugs in the past made [hidden] not
    // honored — this assignment plus logging makes such regressions loud.)
    if (this.feedbackLoading) {
      this.feedbackLoading.hidden = true;
      var stillVisible = this.feedbackLoading.offsetParent !== null;
      slog('[saif-practice] feedbackLoading hidden=true; offsetParent visible?', stillVisible,
        '(should be false; if true, CSS is overriding [hidden])');
    }

    if (this.scoreEl && (score.noReference || score.lowConfidence)) {
      // No reference, or the detector couldn't reliably hear a voice — show an
      // explicit non-score instead of a number. A punishing red "1.5" for what
      // is usually a MIC problem (paired with a celebration animation, no
      // less) read as a judgment of the recitation; the warning banner above
      // already explains what actually happened and how to fix it.
      this.scoreEl.className = 'saif-practice__score-number';
      this.scoreEl.style.clipPath = '';
      this.scoreEl.textContent = '—';
      this._scoreArcSet(0);
    } else if (this.scoreEl) {
      this.scoreEl.className = 'saif-practice__score-number saif-practice__score--' +
        (score.overall >= 8 ? 'great' : score.overall >= 6 ? 'good' : score.overall >= 4 ? 'fair' : 'low');
      // Wipe-reveal: number "writes itself" left-to-right with clip-path
      // while count-up tween runs simultaneously. The two reinforce —
      // user sees the score appearing AND counting up. Stops the score
      // feeling like a `display: block` flip and gives the most-emotional
      // moment in the product its actual weight.
      this.scoreEl.textContent = '0.0';
      var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReduce) {
        this.scoreEl.style.clipPath = 'inset(0 100% 0 0)';
        this.scoreEl.style.transition = 'none';
        void this.scoreEl.offsetWidth; // force reflow so the next change transitions
        this.scoreEl.style.transition = 'clip-path 600ms cubic-bezier(0.77, 0, 0.175, 1)';
        this.scoreEl.style.clipPath = 'inset(0 0 0 0)';
      }
      tween(0, score.overall, 800, function (v) {
        if (self.scoreEl) self.scoreEl.textContent = v.toFixed(1);
        self._scoreArcSet(v / 10);
      });
    }

    // If the algorithm couldn't detect real voice, surface that to the user
    // instead of silently showing a low number with no explanation. Inject
    // ABOVE the score-card so it doesn't get crammed into the score-card's
    // flex layout (which would squish the metric labels off-screen).
    var scoreCard = this.scoreBreakdown ? this.scoreBreakdown.closest('.saif-practice__score-card') : null;
    if (score.noReference && scoreCard && scoreCard.parentElement) {
      var existingNoRef = scoreCard.parentElement.querySelector('.saif-practice__low-confidence-warning');
      if (existingNoRef) existingNoRef.remove();
      // Route to the IN-STUDIO picker (one tap) rather than narrating a trip
      // to another page; the library link stays as a browsing fallback.
      scoreCard.insertAdjacentHTML('beforebegin',
        '<p class="saif-practice__low-confidence-warning" role="status">' +
        'No reference loaded — recordings can\'t be scored without one. ' +
        '<button type="button" class="saif-practice__pick-ref-link">Choose a reference</button> ' +
        'to get a score, or browse the <a href="' + saifPath('/pages/maqam-library') + '">Maqam Reference Library</a>.' +
        '</p>');
    } else if (score.lowConfidence && score.warning && scoreCard && scoreCard.parentElement) {
      var existing = scoreCard.parentElement.querySelector('.saif-practice__low-confidence-warning');
      if (existing) existing.remove();
      var warningHTML = '<p class="saif-practice__low-confidence-warning" role="status">' +
        '⚠️ We couldn\'t reliably hear your recitation — ' + score.warning + '. ' +
        'Try recording in a quieter space, closer to the mic, and reciting clearly for at least a few seconds.' +
      '</p>';
      scoreCard.insertAdjacentHTML('beforebegin', warningHTML);
    } else if (scoreCard && scoreCard.parentElement) {
      var prior = scoreCard.parentElement.querySelector('.saif-practice__low-confidence-warning');
      if (prior) prior.remove();
    }

    if (this.scoreBreakdown && (score.noReference || score.lowConfidence)) {
      // No reference / couldn't hear a voice → no metrics; empty the breakdown
      // rather than animating three bars that read as a terrible performance.
      this.scoreBreakdown.innerHTML = '';
    } else if (this.scoreBreakdown) {
      // Render the static markup first with width:0 so we can animate fills.
      this.scoreBreakdown.innerHTML =
        '<div class="saif-practice__metric">' +
          '<span class="saif-practice__metric-label">Pitch Accuracy</span>' +
          '<div class="saif-practice__metric-bar"><div class="saif-practice__metric-fill" data-target="' + (score.pitch * 10) + '" style="width:0%;"></div></div>' +
          '<span class="saif-practice__metric-value" data-target="' + score.pitch + '">0.0</span>' +
        '</div>' +
        '<div class="saif-practice__metric">' +
          '<span class="saif-practice__metric-label">Melody Flow</span>' +
          '<div class="saif-practice__metric-bar"><div class="saif-practice__metric-fill" data-target="' + (score.consistency * 10) + '" style="width:0%;"></div></div>' +
          '<span class="saif-practice__metric-value" data-target="' + score.consistency + '">0.0</span>' +
        '</div>' +
        '<div class="saif-practice__metric">' +
          '<span class="saif-practice__metric-label">Vocal Steadiness</span>' +
          '<div class="saif-practice__metric-bar"><div class="saif-practice__metric-fill" data-target="' + (score.steadiness * 10) + '" style="width:0%;"></div></div>' +
          '<span class="saif-practice__metric-value" data-target="' + score.steadiness + '">0.0</span>' +
        '</div>';

      // The weakest metric becomes a doorway to the tool that fixes it. Only
      // when it is actually weak (<6) — a student who scored well is not nagged.
      // Drill walks the clip note by note (pitch, phrase-to-phrase movement);
      // Tune is the live readout for holding one note steady.
      var _metrics = [
        { i: 0, v: score.pitch, tool: 'drill' },
        { i: 1, v: score.consistency, tool: 'drill' },
        { i: 2, v: score.steadiness, tool: 'tune' }
      ];
      var _weakest = _metrics.slice().sort(function (a, b) { return a.v - b.v; })[0];
      if (_weakest && _weakest.v < 6) {
        var _rows = this.scoreBreakdown.querySelectorAll('.saif-practice__metric');
        var _row = _rows[_weakest.i];
        if (_row) {
          _row.classList.add('saif-practice__metric--fix');
          _row.setAttribute('role', 'button');
          _row.setAttribute('tabindex', '0');
          _row.setAttribute('data-fix-tool', _weakest.tool);
          _row.setAttribute('title', 'Open the tool that helps with this');
          var _selfFix = this;
          var _openFix = function () {
            try { if (_selfFix.sheetOpenV2) _selfFix.sheetOpenV2('tools'); } catch (e) {}
            setTimeout(function () {
              var tile = document.querySelector('[data-tool-tile="' + _weakest.tool + '"]');
              if (tile) tile.click();
            }, 260);
            if (window.SaifTrack) window.SaifTrack('practice_fix_tool_opened', { tool: _weakest.tool });
          };
          _row.addEventListener('click', _openFix);
          _row.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); _openFix(); }
          });
        }
      }

      // Tween each fill width and value in parallel.
      this.scoreBreakdown.querySelectorAll('.saif-practice__metric-fill').forEach(function (el, idx) {
        var target = parseFloat(el.getAttribute('data-target')) || 0;
        // Stagger by 100ms so the bars don't all shoot together — feels classier.
        setTimeout(function () {
          tween(0, target, 700, function (v) { el.style.width = v + '%'; });
        }, idx * 120);
      });
      this.scoreBreakdown.querySelectorAll('.saif-practice__metric-value').forEach(function (el, idx) {
        var target = parseFloat(el.getAttribute('data-target')) || 0;
        setTimeout(function () {
          tween(0, target, 700, function (v) { el.textContent = v.toFixed(1); });
        }, idx * 120);
      });
    }

    // ── Teach + motivate ─────────────────────────────────────────────────
    // The score screen used to just judge; everything below turns each take
    // into a next step. All the data was already computed per-take — the
    // per-metric detail strings previously went only to console.log.
    var newPersonalBest = false;
    if (this.scoreBreakdown && !score.noReference && !score.lowConfidence) {
      // Progress vs your own history on this clip. recordScore() already ran
      // above, so the current take is the history's last entry.
      var statsLine = '';
      var hist = getScoreHistory(this.refUrl);
      if (hist.length >= 2) {
        var currScore = hist[hist.length - 1].score;
        var prevScore = hist[hist.length - 2].score;
        var prevBest = -Infinity;
        for (var hi = 0; hi < hist.length - 1; hi++) {
          if (typeof hist[hi].score === 'number' && hist[hi].score > prevBest) prevBest = hist[hi].score;
        }
        var delta = currScore - prevScore;
        newPersonalBest = isFinite(prevBest) && currScore > prevBest;
        statsLine = '<p class="saif-practice__score-stats">' +
          (delta >= 0 ? '+' : '−') + Math.abs(delta).toFixed(1) + ' vs last take' +
          ' · best ' + Math.max(currScore, prevBest).toFixed(1) +
          ' · attempt ' + hist.length +
          (newPersonalBest ? ' · <strong class="saif-practice__pb-chip">New personal best</strong>' : '') +
          '</p>';
      }

      // One coaching line for the WEAKEST metric — what to actually do
      // differently on the next take, not just a number.
      var weakest = 'pitch';
      if (score.consistency < score[weakest]) weakest = 'consistency';
      if (score.steadiness < score[weakest]) weakest = 'steadiness';
      var COACH = {
        pitch: 'Coach’s tip: replay the reference at 0.75× and match each note before going full speed — pitch accuracy climbs fastest that way.',
        consistency: 'Coach’s tip: connect the notes inside each phrase — glide between them without jumps, and save breaths for the phrase ends.',
        steadiness: 'Coach’s tip: pick the longest note in the clip and practice holding it perfectly even — steadiness is built one sustained note at a time.'
      };
      var coaching = '<p class="saif-practice__coach-line">' + COACH[weakest] + '</p>';

      // Per-metric explanations, tucked behind a disclosure.
      var detailsHtml =
        '<details class="saif-practice__score-details">' +
          '<summary>What do these numbers mean?</summary>' +
          '<p>Your score is out of 10 — how closely your take matched the reference, in three parts:</p>' +
          '<p><strong>Pitch — 60% of your score.</strong> Are you hitting the right notes? Singing an octave up or down is fine. <em>This take: ' + escapeAttr(score.pitchDetail || '—') + '</em></p>' +
          '<p><strong>Melody flow — 20%.</strong> Does your line rise and fall the way the reference does? <em>This take: ' + escapeAttr(score.consistencyDetail || '—') + '</em></p>' +
          '<p><strong>Steadiness — 20%.</strong> Are your held notes smooth, not shaky? Only wobble beyond the melody itself counts. <em>This take: ' + escapeAttr(score.steadinessDetail || '—') + '</em></p>' +
          '<p>Pitch matters most; flow and steadiness are a fifth each. Match the reference on all three — starting late or your octave never costs points — and you score a full 10.</p>' +
        '</details>';

      // Lives inside scoreBreakdown, whose innerHTML is rewritten per take —
      // so this can never duplicate or go stale.
      this.scoreBreakdown.insertAdjacentHTML('beforeend', statsLine + coaching + detailsHtml);
    }

    // ── Personal Review pitch, at the two highest-intent moments ─────────
    // (1) pride — a great take worth Saif's ears; (2) frustration — three
    // flat scores in a row. Both used to pass in silence. Custom-reference
    // takes are excluded (they can't be submitted for review).
    var oldCallout = this.resultSection ? this.resultSection.querySelector('.saif-practice__review-callout') : null;
    if (oldCallout) oldCallout.remove();
    if (captured && !score.noReference && !score.lowConfidence && !this._customRef && scoreCard && scoreCard.parentElement) {
      var calloutText = '';
      if (score.overall >= 8) {
        calloutText = '⭐ This take is worth Saif’s ears — get his personal notes on it.';
      } else {
        var h3 = getScoreHistory(this.refUrl);
        if (h3.length >= 3) {
          var l3 = h3.slice(-3).map(function (e) { return e.score || 0; });
          if (Math.max(l3[0], l3[1], l3[2]) - Math.min(l3[0], l3[1], l3[2]) <= 0.4) {
            calloutText = 'Scores flattening out? A Personal Review from Saif pinpoints exactly what to change.';
          }
        }
      }
      if (calloutText) {
        var calloutCta = (this._reviewCredits === 0)
          ? '<a class="saif-practice__review-callout-buy" href="' + saifPath('/pages/review-packs') + '">Get a Personal Review →</a>'
          : '<button type="button" class="saif-practice__review-callout-btn">Send for Personal Review</button>';
        scoreCard.insertAdjacentHTML('afterend',
          '<div class="saif-practice__review-callout" role="note">' +
            '<span class="saif-practice__review-callout-text">' + calloutText + '</span> ' + calloutCta +
          '</div>');
      }
    }

    this.drawComparison();
    this.setStatus('');

    // Toot responds to EVERY score, with the excitement scaled to it: a warm
    // nod under 8, a bright run at 8-9, the full jubilee at 9+. Delayed to let
    // the number tween most of the way, so he is reacting to something the
    // student can already read. He is optional — _tootTeach no-ops without him.
    if (typeof score.overall === 'number') {
      var _selfCel = this;
      // sheetOpenV2 owns the score seat for both fresh and saved results. React
      // there. The result sheet owns that seat until sheetCloseV2 uncovers the
      // branch; no delayed return timer may move him onto a hidden branch.
      setTimeout(function () { _selfCel._tootTeach('celebrate', score.overall); }, 900);
    }

    // 🎉 Celebration burst for great recordings (≥9.0/10) — and for a new
    // personal best on this clip, which for a plateaued beginner is the far
    // more reachable (and more meaningful) milestone.
    if ((score.overall >= 9 || newPersonalBest) && this.resultSection) {
      slog('[saif-practice] celebration — score ' + score.overall.toFixed(1) +
        (newPersonalBest ? ' (new personal best)' : ''));
      // Slight delay so the score number has tweened most of the way before confetti fires
      setTimeout(function () {
        spawnConfetti(self.resultSection, 2400);
        self._celebrateV2();
      }, 600);
    }

    if (captured) {
      // Persist this take PRIVATELY to the student's library (never auto-sent
      // to an instructor). They can later submit it for feedback by choice.
      this.saveRecordingToLibrary(score);

      // Auto-request AI feedback if API configured
      if (this.apiUrl) {
        slog('[saif-practice] AI feedback enabled (apiUrl set) — requesting');
        this.requestAIFeedback();
      } else {
        slog('[saif-practice] AI feedback skipped (no apiUrl) — loading spinner stays hidden');
      }
    } else {
      // Practice run — nothing recorded, nothing saved, nothing to submit.
      slog('[saif-practice] practice run complete — score shown, no audio saved');
      this._lastSavedRecordingId = null;
      // A new take starts with its own (empty) notes.
      if (this._resetTransientNotes) this._resetTransientNotes();
      if (this.feedbackSubmitBtn) this.feedbackSubmitBtn.hidden = true;
    }
  };

  /**
   * Show/hide the result-view actions based on whether the take was a saved
   * Record-take (this._captureAudio === true) or a throwaway Practice run.
   * Listen + the transport + AI feedback + Submit-for-feedback only apply to a
   * saved take; a practice run shows the "not saved" notice instead.
   */
  PracticeStudio.prototype._applyResultMode = function (capturedArg) {
    var saved = (typeof capturedArg === 'boolean') ? capturedArg : this._captureAudio;
    if (this.listenBtn) this.listenBtn.hidden = !saved;
    // A live take HAS a recording — leave the transport to _syncPlayerEnabled
    // rather than hiding it here and stranding the student with an inert Play.
    try { this._syncPlayerEnabled(); } catch (e) {}
    // AI-feedback action + its result tab (only present when api_url is set).
    if (this.submitBtn) this.submitBtn.hidden = !saved;
    var aiTab = this.container.querySelector('#SaifPracticeResultTabFeedback');
    if (aiTab) aiTab.hidden = !saved;
    // Submit-for-feedback always starts hidden + reset on a fresh result; for a
    // saved take, saveRecordingToLibrary re-reveals it once there's a server id
    // to submit (so a prior take's "Submitted ✓" never flashes onto a new one).
    if (this.feedbackSubmitBtn) {
      this.feedbackSubmitBtn.hidden = true;
      this._resetFeedbackButton();
    }
    if (this.practiceRunNotice) this.practiceRunNotice.hidden = saved;
  };

  /** Restore the Submit-for-feedback button to its default, enabled state. */
  PracticeStudio.prototype._resetFeedbackButton = function () {
    var btn = this.feedbackSubmitBtn;
    if (!btn) return;
    btn.disabled = false;
    btn.classList.remove('saif-practice__action-btn--submitted');
    var label = btn.querySelector('.saif-practice__feedback-btn-label');
    if (label) label.textContent = SUBMIT_LABEL;
  };

  /**
   * Show the submit-for-feedback note dialog. opts.onSend(noteText) fires when
   * the student confirms (with the optional note); Cancel/backdrop dismiss it.
   * Falls back to onSend('') if the dialog markup isn't present.
   */
  PracticeStudio.prototype._promptSubmitNote = function (opts) {
    opts = opts || {};
    var overlay = this.noteOverlay, input = this.noteInput;
    if (!overlay || !input) { if (opts.onSend) opts.onSend(''); return; }
    if (this.noteTitleEl && opts.title) this.noteTitleEl.textContent = opts.title;
    if (this.noteSubEl && opts.sub) this.noteSubEl.textContent = opts.sub;
    input.value = '';
    this._noteSendCb = opts.onSend || null;
    overlay.hidden = false;
    setTimeout(function () { try { input.focus(); } catch (e) {} }, 30);
  };

  PracticeStudio.prototype._closeNoteDialog = function () {
    if (this.noteOverlay) this.noteOverlay.hidden = true;
    this._noteSendCb = null;
  };

  /**
   * Submit the most recently SAVED take to the instructor — first collects an
   * optional note via the dialog, then flips submittedForFeedback server-side
   * so it enters the "Studio review" queue. Requires sign-in.
   */
  /**
   * A signed-out student tapped "Submit for feedback". Nothing can be sent
   * without an account, so ask for one here rather than after a note dialog.
   * Its own dialog: _upsellReviewDialog is hard-wired to "Out of Personal
   * Reviews" with a review-packs link, which is the wrong ask here.
   */
  PracticeStudio.prototype._promptSignInToSubmit = function () {
    var loginUrl = saifPath('/account/login') + '?return_url=' +
      encodeURIComponent(location.pathname + location.search);
    var ov = this._signInOverlay;
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'saif-practice__note-overlay';
      ov.innerHTML =
        '<div class="saif-practice__note-dialog" role="dialog" aria-modal="true" aria-label="Sign in to send this take">' +
          '<h4 class="saif-practice__note-title">Send this to Saif</h4>' +
          '<p class="saif-practice__note-sub">This take is saved on this device only. Create a free account to send it to Saif for a Personal Review — and to keep your recordings after you close the page.</p>' +
          '<div class="saif-practice__note-actions">' +
            '<button type="button" class="saif-practice__note-cancel" data-signin-close>Not now</button>' +
            '<a class="saif-practice__note-send saif-practice__note-send--link" data-signin-go>Create a free account</a>' +
          '</div>' +
        '</div>';
      this.container.appendChild(ov);
      ov.addEventListener('click', function (e) {
        if (e.target === ov || (e.target.closest && e.target.closest('[data-signin-close]'))) ov.hidden = true;
      });
      this._signInOverlay = ov;
    }
    var go = ov.querySelector('[data-signin-go]');
    if (go) go.setAttribute('href', loginUrl);
    ov.hidden = false;
  };

  PracticeStudio.prototype.submitForFeedback = function () {
    var self = this;
    var id = this._lastSavedRecordingId;
    var sync = window.SaifSync;
    var statusEl = this.container.querySelector('#SaifPracticeSavingStatus');
    function setStatus(msg) {
      if (!statusEl) return;
      statusEl.hidden = false; statusEl.textContent = msg;
    }
    if (!id) {
      setStatus(this.recordedBlob
        ? 'One moment — this take is still saving. Try again in a second.'
        : 'Record a take first, then submit it for feedback.');
      return;
    }
    // Check the student is actually signed in, not merely that the method
    // exists — sync.recordings.submit is always defined, so the old guard
    // never fired: a signed-out tap opened the note dialog, ran a submit that
    // made no request at all, and only reported failure at the very end.
    if (!sync || !sync.recordings || !sync.recordings.submit) {
      setStatus('Sign in to submit recordings for instructor feedback.'); return;
    }
    if (!sync.ready) { this._promptSignInToSubmit(); return; }
    // Out of Personal Reviews — prompt to buy more instead of opening the note
    // dialog and failing at the end. (Server still authoritatively gates.)
    if (this._reviewCredits === 0) {
      this._upsellReviewDialog('You’re out of Personal Reviews, so this take can’t be sent for review yet.');
      return;
    }
    this._promptSubmitNote({
      title: 'Submit for feedback',
      sub: 'Add a note for your teacher (optional) — a question, or what you’d like them to focus on.',
      onSend: function (note) { self._doSubmitForFeedback(id, note); }
    });
  };

  /** Perform the actual submit of one saved take (with an optional note). */
  // Render the remaining-Personal-Review pill next to the submit button. Pass
  // null to hide it (count unknown); a number (incl. 0) shows it.
  PracticeStudio.prototype._renderReviewCredits = function (n) {
    var el = this.container.querySelector('#SaifPracticeReviewCredits');
    var buy = this.container.querySelector('#SaifPracticeReviewBuy');
    var hdr = this.container.querySelector('#SaifPracticeReviewCreditsHeader');
    if (n != null) this._reviewCredits = n;
    // Contextual pill next to the submit button.
    if (el) {
      if (n == null) { el.hidden = true; }
      else { el.textContent = n + ' Personal Review' + (n === 1 ? '' : 's') + ' left'; el.hidden = false; }
    }
    // Top-up link beside the pill; emphasized at zero.
    if (buy) {
      if (n == null) { buy.hidden = true; }
      else {
        buy.hidden = false;
        buy.textContent = n <= 0 ? 'Get reviews →' : 'Get more →';
        buy.classList.toggle('saif-practice__review-buy--urgent', n <= 0);
      }
    }
    // Always-visible chip in the studio header — and it means it now: we never
    // hide the header chip. An unknown count (signed out, or the worker has not
    // answered yet) renders 0 instead, so the header never reflows.
    if (hdr) {
      var shown = (n == null) ? 0 : n;
      // English pluralises with an "s"; Arabic has five number shapes and cannot,
      // so each language supplies its own whole sentence and only {n} is filled in.
      var i18n = window.SaifI18n || {};
      var tpl = (shown === 1)
        ? (i18n.reviews_left_one || '1 review left')
        : (i18n.reviews_left_other || '{n} reviews left');
      hdr.textContent = tpl.replace('{n}', shown);
      hdr.hidden = false;
      // Only flag "urgent" once we actually KNOW the count is zero — a pending
      // lookup shouldn't render as a warning.
      hdr.classList.toggle('saif-practice__header-credits--urgent', n != null && n <= 0);
    }
  };

  PracticeStudio.prototype._doSubmitForFeedback = function (id, note) {
    var self = this;
    var btn = this.feedbackSubmitBtn;
    var sync = window.SaifSync;
    var statusEl = this.container.querySelector('#SaifPracticeSavingStatus');
    function setStatus(msg, autoHide) {
      if (!statusEl) return;
      statusEl.hidden = false;
      statusEl.textContent = msg;
      if (autoHide) setTimeout(function () { if (statusEl) statusEl.hidden = true; }, 4000);
    }
    if (!sync || !sync.recordings || !sync.recordings.submit) {
      setStatus('Sign in to submit recordings for instructor feedback.'); return;
    }
    if (btn) {
      btn.disabled = true;
      var l = btn.querySelector('.saif-practice__feedback-btn-label');
      if (l) l.textContent = (window.SaifI18n && window.SaifI18n.submitting_feedback) || 'Submitting…';
    }
    if (window.SaifTrack) {
      window.SaifTrack('practice_submitted_for_feedback', {
        recording_id: id, clip_title: this.refClipTitle || null, has_note: !!(note && note.length)
      });
    }
    sync.recordings.submit(id, true, this.refUrl || null, note || null).then(function (r) {
      if (r && r.ok) {
        if (btn) {
          btn.classList.add('saif-practice__action-btn--submitted');
          btn.disabled = true;
          var lab = btn.querySelector('.saif-practice__feedback-btn-label');
          if (lab) lab.textContent = (window.SaifI18n && window.SaifI18n.submitted) || 'Submitted ✓';
        }
        setStatus('Sent to Saif ✓ — you’ll get an email when his review is ready, and a 💬 badge will appear on this take in your history.');
        if (r.reviewCreditsRemaining != null) self._renderReviewCredits(r.reviewCreditsRemaining);
        self.refreshHistory();
      } else if (r && r.error === 'NOT_SIGNED_IN') {
        setStatus('Sign in to submit recordings for instructor feedback.');
        self._resetFeedbackButton();
      } else if (r && r.error === 'NO_REVIEW_CREDITS') {
        self._renderReviewCredits(0);
        self._resetFeedbackButton();
        setStatus('This take wasn’t sent — you’re out of Personal Reviews.');
        self._upsellReviewDialog('This take wasn’t sent — you’re out of Personal Reviews.');
      } else {
        console.error('[saif-practice] submit for feedback rejected:', r && r.error);
        setStatus('We couldn’t submit that recording — please try again.');
        self._resetFeedbackButton();
      }
    }).catch(function (err) {
      console.error('[saif-practice] submit for feedback failed:', err);
      setStatus('We couldn’t submit that recording — please try again.');
      self._resetFeedbackButton();
    });
  };

  /* ----- History multi-select + bulk actions ----- */
  PracticeStudio.prototype._toggleSelectMode = function () {
    this._selectMode = !this._selectMode;
    this._selectedRecs = {};
    var listEl = this.container.querySelector('#SaifPracticeHistoryList');
    if (listEl) {
      listEl.classList.toggle('saif-practice__history-list--select', this._selectMode);
      listEl.querySelectorAll('.saif-practice__history-check').forEach(function (b) { b.checked = false; });
      listEl.querySelectorAll('.saif-practice__history-item--selected').forEach(function (it) {
        it.classList.remove('saif-practice__history-item--selected');
      });
    }
    if (this.historySelectBtn) {
      this.historySelectBtn.setAttribute('aria-pressed', this._selectMode ? 'true' : 'false');
      this.historySelectBtn.textContent = this._selectMode ? 'Done' : 'Select';
    }
    this._updateBulkBar();
  };

  PracticeStudio.prototype._onRecCheck = function (cb) {
    var id = cb.getAttribute('data-id');
    if (!id) return;
    if (cb.checked) this._selectedRecs[id] = true; else delete this._selectedRecs[id];
    var item = cb.closest('.saif-practice__history-item');
    if (item) item.classList.toggle('saif-practice__history-item--selected', cb.checked);
    this._updateBulkBar();
  };

  PracticeStudio.prototype._selectedIds = function () {
    return Object.keys(this._selectedRecs || {});
  };

  PracticeStudio.prototype._updateBulkBar = function () {
    var bar = this.historyBulkbar;
    if (!bar) return;
    var n = this._selectedIds().length;
    if (this._selectMode && n > 0) {
      bar.hidden = false;
      if (this.historyBulkCount) this.historyBulkCount.textContent =
        (((window.SaifI18n || {}).n_selected) || '{n} selected').replace('{n}', n);
    } else {
      bar.hidden = true;
    }
  };

  PracticeStudio.prototype._exitSelectAndRefresh = function () {
    this._selectedRecs = {};
    this._selectMode = false;
    var listEl = this.container.querySelector('#SaifPracticeHistoryList');
    if (listEl) listEl.classList.remove('saif-practice__history-list--select');
    if (this.historySelectBtn) {
      this.historySelectBtn.setAttribute('aria-pressed', 'false');
      this.historySelectBtn.textContent = (window.SaifI18n && window.SaifI18n.select) || 'Select';
    }
    this._updateBulkBar();
    this.refreshHistory();
  };

  // opts (optional): { signIn: true } appends a real "Sign in →" link using
  // the storefront login URL (returns to this page after auth) — every
  // "Sign in to …" message used to be a dead sentence with nothing to click.
  PracticeStudio.prototype._bulkStatus = function (msg, autoHide, opts) {
    var statusEl = this.container.querySelector('#SaifPracticeSavingStatus');
    if (!statusEl) return;
    statusEl.hidden = false;
    if (opts && opts.signIn) {
      statusEl.innerHTML = escapeAttr(msg) +
        ' <a class="saif-practice__signin-link" href="' + escapeAttr(this.loginUrl) + '">Sign in →</a>';
    } else {
      statusEl.textContent = msg;
    }
    if (autoHide) setTimeout(function () { if (statusEl) statusEl.hidden = true; }, 4000);
  };

  PracticeStudio.prototype._bulkSubmit = function () {
    var self = this;
    // Device-local takes can't reach an instructor — exclude them from submit.
    var ids = this._selectedIds().filter(function (id) { return id.indexOf('local_') !== 0; });
    var sync = window.SaifSync;
    if (!ids.length) { this._bulkStatus('Selected takes are device-only — sign in to submit them.', true, { signIn: true }); return; }
    if (!sync || !sync.recordings || !sync.recordings.submitMany) {
      this._bulkStatus('Sign in to submit recordings for instructor feedback.', true, { signIn: true }); return;
    }
    this._promptSubmitNote({
      title: 'Submit ' + ids.length + ' recording' + (ids.length > 1 ? 's' : '') + ' for feedback',
      sub: 'Add a note for your teacher (optional) — it’s attached to all ' + ids.length + ' submissions.',
      onSend: function (note) {
        self._bulkStatus('Submitting ' + ids.length + '…');
        // ONE atomic request — avoids the metafield write-race that N
        // concurrent single submits would hit.
        sync.recordings.submitMany(ids, note || null).then(function (r) {
          if (r && r.ok) {
            var n = (typeof r.submitted === 'number') ? r.submitted : ids.length;
            self._bulkStatus('Sent ' + n + ' to Saif ✓ — you’ll get an email when each review is ready (💬 badges appear in your history).');
            if (r.reviewCreditsRemaining != null) self._renderReviewCredits(r.reviewCreditsRemaining);
            if (window.SaifTrack) window.SaifTrack('practice_bulk_submitted', { count: n, attempted: ids.length });
          } else if (r && r.error === 'NOT_SIGNED_IN') {
            self._bulkStatus('Sign in to submit recordings for instructor feedback.', true, { signIn: true });
          } else if (r && r.error === 'NO_REVIEW_CREDITS') {
            if (r.reviewCreditsRemaining != null) self._renderReviewCredits(r.reviewCreditsRemaining);
            self._bulkStatus('Nothing was sent — you have ' + (r.reviewCreditsRemaining || 0) + ' Personal Review' + (r.reviewCreditsRemaining === 1 ? '' : 's') + ' left but selected ' + (r.needed || ids.length) + ' takes.', true);
            self._upsellReviewDialog('Nothing was sent — you don’t have enough Personal Reviews for the selected takes.');
          } else {
            console.error('[saif-practice] bulk submit rejected:', r && r.error);
            self._bulkStatus('We couldn’t submit those recordings — please try again.', true);
          }
          self._exitSelectAndRefresh();
        }).catch(function (err) {
          console.error('[saif-practice] bulk submit failed:', err);
          self._bulkStatus('We couldn’t submit those recordings — please try again.', true);
          self._exitSelectAndRefresh();
        });
      }
    });
  };

  PracticeStudio.prototype._bulkDelete = function () {
    var self = this;
    var ids = this._selectedIds();
    if (!ids.length) return;
    var sync = window.SaifSync;
    if (!sync || !sync.recordings || !sync.recordings.deleteMany) { this._bulkStatus('Sign in to delete saved recordings.', true, { signIn: true }); return; }
    if (!confirm('Delete ' + ids.length + ' recording' + (ids.length > 1 ? 's' : '') + '? This cannot be undone.')) return;
    // Same reason as the single delete: if one of these is the take currently
    // playing, its row and pause button are about to disappear.
    if (this._historyPlayingId && ids.indexOf(this._historyPlayingId) !== -1) this._historyStop();
    self._bulkStatus('Deleting ' + ids.length + '…');
    // ONE atomic request — N concurrent single deletes raced on the shared
    // recordings metafield (last write wins), so most deletions were lost.
    sync.recordings.deleteMany(ids).then(function (r) {
      if (r && r.ok) {
        var n = (typeof r.deleted === 'number') ? r.deleted : ids.length;
        self._bulkStatus('Deleted ' + n + ' recording' + (n === 1 ? '' : 's') + '.', true);
        if (window.SaifTrack) window.SaifTrack('practice_bulk_deleted', { count: n, attempted: ids.length });
      } else {
        console.error('[saif-practice] bulk delete rejected:', r && r.error);
        self._bulkStatus('We couldn’t delete those recordings — please try again.', true);
      }
      self._exitSelectAndRefresh();
    }).catch(function (err) {
      console.error('[saif-practice] bulk delete failed:', err);
      self._bulkStatus('We couldn’t delete those recordings — please try again.', true);
      self._exitSelectAndRefresh();
    });
  };

  PracticeStudio.prototype.saveRecordingToLibrary = function (score) {
    var self = this;
    if (!this.recordedBlob) return;
    var sync = window.SaifSync;
    var historyEl = this.container.querySelector('#SaifPracticeHistoryList');
    var savingEl = this.container.querySelector('#SaifPracticeSavingStatus');

    // Remember the score so the delegated "Retry save" button can re-run this
    // whole save with the same metadata.
    this._lastSaveScore = score;

    // 0-byte guard — an interrupted MediaRecorder can produce an empty blob;
    // uploading it would "succeed" into an unplayable take.
    if (!this.recordedBlob.size) {
      if (savingEl) {
        savingEl.hidden = false;
        savingEl.textContent = (window.SaifI18n && window.SaifI18n.save_empty) || 'Nothing was captured — the recording came back empty. Try recording again.';
      }
      console.warn('[saif-practice] save skipped — empty blob');
      return;
    }

    if (savingEl) {
      savingEl.hidden = false;
      savingEl.textContent = sync && sync.ready ? 'Saving recording...' : 'Saved locally (sign in to sync)';
    }

    var lessonId = this.container.getAttribute('data-lesson-id') || null;
    var duration = this.recPitchData.length > 0 ? this.recPitchData[this.recPitchData.length - 1].time : null;
    // Downsample both contours for storage. The reference contour is saved too
    // so the instructor's review can show the same reference-vs-student chart.
    var pitchData = packPitchForStorage(this.recPitchData, 300);
    var referencePitchData = packPitchForStorage(this.refPitchData, 300);

    if (!sync || !sync.ready) {
      // Local fallback — still register it so the practice history list shows up
      sync = window.SaifSync; // refresh, in case it loaded late
    }

    var clipTitleParts = [];
    if (this.refClipTitle) clipTitleParts.push(this.refClipTitle);
    var mqLabelEl = this.refMaqamLabel;
    if (mqLabelEl && !mqLabelEl.hidden) {
      var mqText = (mqLabelEl.textContent || '').trim();
      if (mqText && clipTitleParts.indexOf(mqText) === -1) clipTitleParts.push(mqText);
    }
    var clipTitleStr = clipTitleParts.join(' · ') || null;

    var breakdown = null;
    if (score && typeof score === 'object') {
      breakdown = {};
      if (typeof score.pitch === 'number') breakdown.pitch = Math.round(score.pitch * 10) / 10;
      if (typeof score.consistency === 'number') breakdown.consistency = Math.round(score.consistency * 10) / 10;
      if (typeof score.steadiness === 'number') breakdown.steadiness = Math.round(score.steadiness * 10) / 10;
      if (Object.keys(breakdown).length === 0) breakdown = null;
    }

    var saver = (sync && sync.recordings) ? sync.recordings.save(this.recordedBlob, {
      lessonId: lessonId,
      score: score && typeof score.overall === 'number' ? Math.round(score.overall * 10) / 10 : null,
      breakdown: breakdown,
      clipTitle: clipTitleStr,
      duration: duration,
      pitchData: pitchData,
      // The reference clip's pitch contour — lets the instructor render the
      // reference-vs-student comparison chart for a submitted take.
      referencePitchData: referencePitchData,
      // The clip the student was imitating — stored so the instructor can play
      // it side-by-side with the recording when reviewing a submission.
      referenceUrl: (this._customRef ? null : (this.refUrl || null)),
      // Saved PRIVATELY. The student explicitly submits later (button below /
      // history) to send a take to their instructor.
      submittedForFeedback: false
    }) : Promise.resolve({ ok: false, error: 'NO_SYNC' });

    saver.then(function (r) {
      // Remember the saved take's id so "Submit for feedback" knows which
      // recording to send. Worker returns { recording }, local returns { recording } too.
      var savedId = (r && r.recording && r.recording.id) || (r && r.id) || null;
      self._lastSavedRecordingId = savedId;
      // A saved take is real practice — feed the streak/heatmap (no-op when
      // saif-courses.js isn't on the page or the visitor isn't signed in).
      if (savedId && window.SaifStreakRecordPractice) {
        try { window.SaifStreakRecordPractice(); } catch (e) {}
      }
      // The worker just bumped the account streak for this save — drop the
      // cached snapshot and re-render so the badge reflects it immediately.
      if (savedId) {
        self._serverStreakPromise = null;
        try { self._refreshStreakBadge(); } catch (e) {}
      }
      // Reveal the Submit-for-feedback action now there's a take to submit.
      // Local (not-signed-in) saves still show it; the button then prompts
      // sign-in when tapped. A practice run never reaches here.
      if (self.feedbackSubmitBtn && self._captureAudio) {
        self._resetFeedbackButton();
        self.feedbackSubmitBtn.hidden = !(r && r.ok && savedId) || !!self._customRef;
      }
      if (savingEl) {
        if (r && r.ok) {
          savingEl.textContent = (r.source === 'local')
            ? 'Saved on this device — it lasts until you close the page; sign in to keep takes permanently'
            : 'Saved to your library (private)';
          setTimeout(function () { if (savingEl) savingEl.hidden = true; }, 4000);
        } else {
          // Friendly copy per worker error code + a real retry path. The blob
          // is still in memory (this.recordedBlob), so a failed upload is
          // recoverable — silently eating it lost the exact takes students
          // cared enough about to save.
          var errCode = (r && r.error) || 'unknown';
          var friendly = {
            DAILY_UPLOAD_LIMIT: 'Daily upload limit reached — this take can’t be saved until tomorrow.',
            RECORDING_TOO_LARGE: 'This take is too large to save — try a shorter recording.',
            NO_SYNC: 'Sign in to save takes to your library.',
            NOT_SIGNED_IN: 'Sign in to save takes to your library.'
          }[errCode];
          var retriable = !friendly; // unknown/network-ish codes are worth retrying
          savingEl.innerHTML = escapeAttr(friendly || ('Couldn’t save this take (' + errCode + ').')) +
            (retriable ? ' <button type="button" class="saif-practice__save-retry">Retry save</button>' : '');
          console.error('[saif-practice] save failed:', r);
        }
      }
      // Clip titles are stored server-side at save time (clipTitle in the
      // recordings.save metadata) and read back in refreshHistory, so the old
      // localStorage title-bridge that lived here has been removed. It was inert
      // anyway: it gated on r.id, but save() resolves { recording } — r.id was
      // always undefined, so the map never gained an entry.
      // Refresh history twice: immediately (catches the optimistic-update
      // case + clears any UI staleness) and after a short delay so the
      // Shopify metafield write has time to propagate. Without the
      // delayed retry, the list call sometimes returns the OLD list and
      // the new recording silently doesn't appear.
      if (historyEl) {
        self.refreshHistory();
        setTimeout(function () { self.refreshHistory(); }, 1200);
      }
    }).catch(function (err) {
      // The save chain had no .catch — a rejected upload (network loss, a
      // FileReader failure) hung "Saving…" forever and the take was silently
      // gone. Keep the blob, retry once automatically, then hand the student
      // a Retry button.
      console.error('[saif-practice] save threw:', err);
      if (!self._saveRetried) {
        self._saveRetried = true;
        if (savingEl) { savingEl.hidden = false; savingEl.textContent = (window.SaifI18n && window.SaifI18n.save_retry) || 'Save hit a network problem — retrying…'; }
        setTimeout(function () { self.saveRecordingToLibrary(score); }, 1500);
        return;
      }
      if (savingEl) {
        savingEl.hidden = false;
        savingEl.innerHTML = 'Couldn’t save this take — check your connection. ' +
          '<button type="button" class="saif-practice__save-retry">Retry save</button>';
      }
    });
  };

  /**
   * Pull the student's recording history from the backend (or local fallback)
   * and render it as a list under the practice studio.
   */
  PracticeStudio.prototype.refreshHistory = function () {
    // `self` is needed inside the async list().then() callback below — it
    // references self.onboardEl, self.container, etc. Without this var,
    // a recent change threw `Unhandled Promise Rejection: undefined is
    // not an object (evaluating 'self.container.querySelector')`.
    var self = this;
    var sync = window.SaifSync;
    var listEl = this.container.querySelector('#SaifPracticeHistoryList');
    var emptyEl = this.container.querySelector('#SaifPracticeHistoryEmpty');
    if (!listEl || !sync || !sync.recordings) return;

    sync.recordings.list().then(function (r) {
      if (!r || !r.ok) {
        // NON-destructive failure: one flaky refresh used to wipe the whole
        // rendered library. If we already have rows, keep them and say so;
        // only fall back to the empty state on a truly empty first load.
        if (listEl.children && listEl.children.length) {
          self._bulkStatus('Couldn’t refresh your takes — showing the last loaded list.', true);
          return;
        }
        listEl.innerHTML = '';
        if (emptyEl) emptyEl.hidden = false;
        // Show onboarding when we have no data — first visit or sync error.
        if (self.onboardEl) self.onboardEl.hidden = false;
        return;
      }
      if (r.reviewCreditsRemaining != null) self._renderReviewCredits(r.reviewCreditsRemaining);
      var items = r.recordings || [];
      // Cached for the feedback-pill expander (click delegation needs the
      // full rec object incl. feedback text/audio, not just the DOM row).
      self._historyItems = items;
      if (!items.length) {
        listEl.innerHTML = '';
        if (emptyEl) emptyEl.hidden = false;
        // No recordings yet — surface the 3-step hint.
        if (self.onboardEl) self.onboardEl.hidden = false;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;

      // Pull the locally-stamped clip-title map (saved in onstop after each
      // recording succeeds). Used to label rows like "Bayati \u00b7 May 1 \u00b7 7.8/10".
      var titleMap = {};
      try {
        var rawMap = localStorage.getItem('saif:practice:recordingTitles');
        if (rawMap) titleMap = JSON.parse(rawMap) || {};
      } catch (e) {}

      // Compute best-take ids PER REFERENCE CLIP \u2014 a 9.1 on an easy clip used
      // to permanently outrank every attempt at harder maqams, so the \u2605 badge
      // stopped moving and stopped motivating. Scoped per clip, each clip's
      // best take (min 2 takes on that clip) gets the badge, and beating your
      // best on the clip you're actually drilling moves it. Ties break newest.
      var bestIds = {};
      (function () {
        var groups = {};
        items.forEach(function (rec) {
          if (typeof rec.score !== 'number') return;
          var key = rec.referenceUrl || '(no-reference)';
          (groups[key] = groups[key] || []).push(rec);
        });
        Object.keys(groups).forEach(function (k) {
          var g = groups[k];
          if (g.length < 2) return; // a single take isn't a meaningful "best"
          var best = null, bestT = -1;
          g.forEach(function (rec) {
            var t = rec.createdAt ? new Date(rec.createdAt).getTime() : 0;
            if (!best || rec.score > best.score || (rec.score === best.score && t > bestT)) {
              best = rec; bestT = t;
            }
          });
          if (best) bestIds[best.id] = 1;
        });
      })();

      // Garbage-collect title-map entries for recordings the server no
      // longer has \u2014 keeps localStorage from growing indefinitely.
      try {
        var liveIds = {};
        items.forEach(function (rec) { liveIds[rec.id] = 1; });
        var pruned = {};
        Object.keys(titleMap).forEach(function (k) {
          if (liveIds[k]) pruned[k] = titleMap[k];
        });
        if (Object.keys(pruned).length !== Object.keys(titleMap).length) {
          localStorage.setItem('saif:practice:recordingTitles', JSON.stringify(pruned));
        }
        titleMap = pruned;
      } catch (e) {}

      // Onboarding hint hides as soon as the student has at least one
      // recording — they've completed the loop, the explanation is no
      // longer useful. Persists implicitly via the server-side recording
      // count, no localStorage needed.
      if (self.onboardEl) self.onboardEl.hidden = true;
      // Drop the pulsing halo around the Record button now that the user
      // is no longer a first-timer. The CTA stays the primary visual,
      // just without the "look-at-me" ring.
      var ctrlsEl = self.container.querySelector('.saif-practice__controls');
      if (ctrlsEl) ctrlsEl.classList.add('saif-practice__controls--seasoned');

      // Show up to 30 recent recordings (the worker keeps 100 per student,
      // see MAX_RECORDINGS_PER_STUDENT). Previously capped at 12 which
      // truncated older takes the user wanted to revisit.
      var visible = items.slice(0, 30);

      // ...but never let a paid-for take fall off the end. A submitted or
      // reviewed take is the most valuable thing in the library — it cost a
      // Personal Review credit and carries Saif's notes — so it must not be
      // hidden just because 30 newer practice takes exist. (This is exactly
      // how a reviewed take went "missing": still in R2, still reviewed, but
      // sitting at ~#48 of 55.) Add the dropped ones back, newest first.
      var keptIds = {};
      for (var vi = 0; vi < visible.length; vi++) keptIds[visible[vi].id] = true;
      var paidFor = [];
      for (var ai = 0; ai < items.length; ai++) {
        var it = items[ai];
        if (keptIds[it.id]) continue;
        if (it.submittedForFeedback || it.feedback) paidFor.push(it);
      }
      if (paidFor.length) {
        visible = visible.concat(paidFor).sort(function (a, b) {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
      }

      // Wire the count badge + collapse toggle. List is capped to ~5
      // rows visible by default (CSS max-height + overflow:hidden); the
      // toggle reveals the rest. Without this the page extends forever
      // as the student accumulates recordings.
      var countEl = self.container.querySelector('#SaifPracticeHistoryCount');
      var toggleEl = self.container.querySelector('#SaifPracticeHistoryToggle');
      if (countEl) countEl.textContent = visible.length ? visible.length : '';
      // The list is pixel-capped (max-height), so ROW HEIGHT, not row count,
      // decides whether takes are cropped: feedback pills / two-line titles
      // overflow at 4 rows while a count>5 rule keeps the arrow hidden.
      // Wire optimistically here; the post-render measurement below owns
      // the final hidden state.
      if (toggleEl) {
        var needsToggle = true;
        // First-time wiring — bind once. We DON'T reset expanded state on
        // every refreshHistory because that would collapse the list every
        // time the user records, which is annoying.
        if (needsToggle && !toggleEl._wired) {
          toggleEl._wired = true;
          toggleEl.addEventListener('click', function () {
            var nowExpanded = !listEl.classList.contains('saif-practice__history-list--expanded');
            listEl.classList.toggle('saif-practice__history-list--expanded', nowExpanded);
            toggleEl.setAttribute('aria-expanded', nowExpanded ? 'true' : 'false');
            var labelEl = toggleEl.querySelector('.saif-practice__history-toggle-label');
            if (labelEl) labelEl.textContent = nowExpanded ? 'Show fewer' : 'Show all';
          });
        }
        // Sync the label in case items dropped below threshold and the
        // button was previously labelled "Show fewer". Don't auto-collapse.
        if (!needsToggle) {
          listEl.classList.remove('saif-practice__history-list--expanded');
          toggleEl.setAttribute('aria-expanded', 'false');
        }
      }

      var buildRow = function (rec) {
        var date = rec.createdAt ? new Date(rec.createdAt) : null;
        var dateLabel = date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
                                ' \u00b7 ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
                              : '';
        var scoreLabel = (typeof rec.score === 'number')
          ? '<span class="saif-practice__history-score">' + rec.score.toFixed(1) + '/10</span>'
          : '';
        var fbUnseen = !!(rec.feedback && !rec.feedback.seenAt);
        var feedbackLabel = rec.feedback
          ? '<span class="saif-practice__history-fb' + (fbUnseen ? ' saif-practice__history-fb--new' : '') +
              '" title="Feedback from your instructor">\ud83d\udcac ' + (fbUnseen ? 'New feedback' : 'feedback') + '</span>'
          : '';
        // Prefer the server-stored clipTitle (present on every take saved since
        // the worker began storing it) and fall back to the local title map for
        // legacy rows saved before that.
        var clipTitle = rec.clipTitle || titleMap[rec.id] || '';
        var titleLabel = clipTitle
          ? '<span class="saif-practice__history-clip" title="Reference clip">' + escapeAttr(clipTitle) + '</span>'
          : '';
        var bestBadge = bestIds[rec.id]
          ? '<span class="saif-practice__history-best" title="Your best take on this clip">\u2605 Best</span>'
          : '';
        var topLine = (titleLabel || bestBadge)
          ? '<div class="saif-practice__history-line saif-practice__history-line--primary">' + titleLabel + bestBadge + '</div>'
          : '';
        // Submission control — already-submitted takes show a static pill;
        // server-stored takes that haven't been submitted get a Submit button.
        // Device-local (not-signed-in) takes can't reach an instructor, so
        // they get neither.
        var submitControl = '';
        if (rec.submittedForFeedback) {
          submitControl = '<span class="saif-practice__history-submitted" title="Sent to your instructor for feedback">✓ Submitted</span>';
        } else if (!rec.ephemeral) {
          submitControl = '<button type="button" class="saif-practice__history-submit" data-id="' + escapeAttr(rec.id) + '" title="Submit this take to your instructor for feedback">' + SUBMIT_LABEL + '</button>';
        }
        // Restore checkbox/selected state across re-renders while in select mode.
        var isSel = !!(self._selectedRecs && self._selectedRecs[rec.id]);
        // Anonymous (device-local) takes hold their audio in an in-memory blob:
        // URL that dies when the page closes. A row reloaded from a PAST session
        // therefore points at a dead URL — show an honest "expired" note rather
        // than a Play button that would silently do nothing. (createdAt before
        // this page's timeOrigin means the blob is from a prior session. Signed-in
        // server takes are never ephemeral, so they're unaffected. The span uses
        // a non-play class so the play click-handler skips it.)
        // A take from an earlier page load only has usable audio if the sync
        // layer re-minted its URL from the stored blob (audioRestored). Where
        // it could not — IndexedDB unavailable, or a take saved before takes
        // were persisted — fall back to the "audio not kept" treatment.
        var staleBlob = !!(rec.ephemeral && !rec.audioRestored && rec.createdAt &&
          typeof performance !== 'undefined' && performance.timeOrigin &&
          new Date(rec.createdAt).getTime() < performance.timeOrigin);
        var playControl = staleBlob
          ? '<span class="saif-practice__history-expired" aria-label="Audio expired" ' +
              'style="display:inline-flex;align-items:center;justify-content:center;opacity:0.4;padding:0 0.35rem" ' +
              'title="Saved on this device only \u2014 the audio is not kept after you close the page. Sign in to keep your takes.">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21"/></svg>' +
            '</span>'
          : '<button type="button" class="saif-practice__history-play" data-url="' + escapeAttr(rec.url) + '" aria-label="Play recording">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21"/></svg>' +
            '</button>';
        return '<div class="saif-practice__history-item' + (bestIds[rec.id] ? ' saif-practice__history-item--best' : '') +
            (isSel ? ' saif-practice__history-item--selected' : '') + '" data-rec-id="' + escapeAttr(rec.id) + '">' +
          '<input type="checkbox" class="saif-practice__history-check" data-id="' + escapeAttr(rec.id) + '"' +
            (isSel ? ' checked' : '') + ' aria-label="Select recording">' +
          playControl +
          '<div class="saif-practice__history-meta">' +
            topLine +
            '<div class="saif-practice__history-line saif-practice__history-line--secondary">' +
              '<span class="saif-practice__history-date">' + dateLabel + '</span>' +
              scoreLabel + feedbackLabel +
            '</div>' +
          '</div>' +
          submitControl +
          '<button type="button" class="saif-practice__history-delete" data-id="' + escapeAttr(rec.id) + '" aria-label="Delete recording">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>' +
          '</button>' +
        '</div>';
      };

      // Group the list: takes the student sent to Saif first, then the rest.
      // Headings are plain divs INSIDE the same list element — the play /
      // delete / select / feedback handlers are delegated off this list and
      // match with closest('.saif-practice__history-item'), so they skip these
      // and keep working exactly as before.
      var isSent = function (r) { return !!(r.submittedForFeedback || r.feedback); };
      var sentRows = visible.filter(isSent);
      var otherRows = visible.filter(function (r) { return !isSent(r); });
      var awaiting = sentRows.filter(function (r) { return !r.feedback; }).length;
      if (sentRows.length && otherRows.length) {
        var sentLabel = 'Sent to Saif';
        if (awaiting) sentLabel += ' \u00b7 ' + awaiting + ' awaiting his reply';
        listEl.innerHTML =
          '<div class="saif-practice__history-group saif-practice__history-group--sent">' +
            escapeAttr(sentLabel) + '</div>' +
          sentRows.map(buildRow).join('') +
          '<div class="saif-practice__history-group">Other takes</div>' +
          otherRows.map(buildRow).join('');
      } else {
        // Nothing sent yet (or nothing but sent takes) — one plain list.
        listEl.innerHTML = visible.map(buildRow).join('');
      }

      // Decide the Show-all arrow from real overflow, post-layout.
      if (toggleEl) {
        requestAnimationFrame(function () {
          var overflowing = listEl.scrollHeight > listEl.clientHeight + 4;
          var expanded = listEl.classList.contains('saif-practice__history-list--expanded');
          toggleEl.hidden = !(overflowing || expanded);
        });
      }

      // If a background refresh dropped a recording that was selected (e.g. a
      // cross-device delete), prune it from the selection so the bulk-bar count
      // matches the visible checkboxes and no vanished id reaches a bulk op.
      if (self._selectMode) {
        var liveIds = {};
        visible.forEach(function (rec) { liveIds[rec.id] = 1; });
        Object.keys(self._selectedRecs || {}).forEach(function (rid) {
          if (!liveIds[rid]) delete self._selectedRecs[rid];
        });
        self._updateBulkBar();
      }
    }).catch(function (err) {
      // Same non-destructive rule for a rejected fetch as for a !ok response.
      console.warn('[saif-practice] history refresh failed:', err && err.message);
      if (listEl.children && listEl.children.length) {
        self._bulkStatus('Couldn’t refresh your takes — showing the last loaded list.', true);
      }
    });
  };

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  /**
   * Compute pitch / consistency / steadiness / overall scores from this.recPitchData.
   *
   * PITCH ACCURACY
   *   With reference: average semitone deviation between user's contour and
   *   reference's at the same time, OCTAVE-INVARIANT (singing the same melody
   *   an octave higher still scores high — important since reciters across
   *   ages/genders sing at different ranges of the same maqam).
   *   Without reference: % of samples that fall within ±2 semitones of the
   *   user's own median pitch (catches if they're holding a tonal center).
   *
   * CONSISTENCY
   *   Of consecutive samples within a single phrase (gap ≤150ms), what fraction
   *   change by ≤1 semitone? Phrases are bounded by silence + large jumps;
   *   transitions between phrases (waqf, breath, ayah end) DON'T count as bad.
   *
   * STEADINESS
   *   Sliding-window pitch standard deviation. Low = the held notes are stable;
   *   high = your voice is wobbling. Different from consistency because it
   *   measures jitter WITHIN a sustained note, not transitions between notes.
   *
   * Each metric is logged so it's auditable, not opaque.
   */
  PracticeStudio.prototype.computeScore = function () {
    var pitchData = this.recPitchData;
    // Default: no chart alignment shift. Overwritten once the offset search
    // runs below (only when there's a reference). Every early return leaves
    // this at 0 so drawComparison never reuses a previous take's offset.
    this._scoreAlignOffset = 0;
    if (pitchData.length < 5) {
      slog('[saif-practice] score: too few pitch samples (' + pitchData.length + ') — returning zeros');
      // lowConfidence: this is a mic/silence problem, not a graded performance
      // — the UI shows "—" + the warning instead of a punishing 0, and a
      // gated visitor's free take is forgiven rather than consumed.
      return {
        overall: 0, pitch: 0, consistency: 0, steadiness: 0,
        sampleCount: pitchData.length,
        lowConfidence: true,
        warning: 'almost no voiced audio was detected (mic muted, too far away, or silent take)'
      };
    }

    // NO REFERENCE → NO SCORE. A "pitch accuracy" number with nothing to
    // compare against is not a score (the old fallback graded the reciter
    // against their own median pitch and presented it like a real result).
    // The recording, playback, and pitch drawing still work — only the
    // grade is withheld until there's a reference to grade against.
    if (!(this.refPitchData && this.refPitchData.length > 5)) {
      slog('[saif-practice] score: no reference loaded — no score (noReference)');
      return { overall: null, pitch: null, consistency: null, steadiness: null, sampleCount: pitchData.length, noReference: true };
    }

    // Signal-quality gate — distinguish real voice from "stable noise" (mic
    // hum, fan, breathing). Fan/hum produces lots of detected pitches that
    // are all within 1-2 semitones of each other → high consistency, fake
    // signal of recitation. Real voice spans >2 semitones across phrases.
    var allMidis = pitchData.map(function (d) { return freqToMidi(d.freq); });
    var sortedM = allMidis.slice().sort(function (a, b) { return a - b; });
    // Use 5th and 95th percentiles to ignore outliers
    var lo5 = sortedM[Math.floor(sortedM.length * 0.05)];
    var hi95 = sortedM[Math.floor(sortedM.length * 0.95)];
    var pitchSpan = hi95 - lo5;
    var totalDuration = pitchData[pitchData.length - 1].time - pitchData[0].time;

    // Stricter flatness gate WITHOUT a reference: with nothing to compare
    // against, a steady monotone hum would otherwise score well on pitch +
    // steadiness. Real recitation has melodic movement, so require a wider
    // span (3 semitones) in no-reference mode; 2 is fine when a reference
    // anchors the scoring.
    var hasRefForGate = !!(this.refPitchData && this.refPitchData.length > 5);
    var spanGate = hasRefForGate ? 2 : 3;
    if (pitchSpan < spanGate || totalDuration < 1.5) {
      var reason = pitchSpan < spanGate
        ? 'pitch range only ' + pitchSpan.toFixed(1) + ' semitones (too flat — a hum or held drone, not recitation)'
        : 'only ' + totalDuration.toFixed(1) + 's of voice detected (need ≥1.5s for a meaningful score)';
      slog('[saif-practice] score: low-confidence signal — ' + reason);
      return {
        overall: 1.5,
        pitch: 1.5,
        consistency: 1.5,
        steadiness: 1.5,
        sampleCount: pitchData.length,
        lowConfidence: true,
        warning: reason
      };
    }

    var refData = this.refPitchData;
    var hasRef = refData.length > 5;

    // ============ PITCH ACCURACY ============
    var pitchScore;
    var pitchDetail = '';
    if (hasRef) {
      // Octave-invariant cents deviation vs the reference. A learner often
      // starts slightly early/late or breathes differently — a naive
      // nearest-time match would punish that as a PITCH error. So we first find
      // the single global time offset (within ±0.8s) that best lines the two
      // contours up, then score pitch at that offset. This stops timing slips
      // from dragging down the pitch score.
      //
      // PLAYBACK RATE: a take recorded against the reference at 0.5× hears the
      // reference's t=2s at user-elapsed 4s — so user time must be mapped
      // through the rate that was active during recording (user × rate = ref
      // time). The live trail and compare graph already did this; the score
      // didn't, so slow-speed practice (the canonical beginner workflow) was
      // scored against the wrong timeline.
      var scoreRate = this.recordingPlaybackRate || 1;
      // Precompute MIDI values once — the deviation between two pitches in
      // semitones is exactly the difference of their MIDI values, so the
      // per-pair log2 calls in the hot loop collapse to a subtraction.
      var userMidisPre = new Array(pitchData.length);
      for (var um = 0; um < pitchData.length; um++) userMidisPre[um] = freqToMidi(pitchData[um].freq);
      var refMidisPre = new Array(refData.length);
      for (var rm = 0; rm < refData.length; rm++) refMidisPre[rm] = freqToMidi(refData[rm].freq);
      // Two-pointer nearest-match: both arrays are time-sorted and the mapped
      // user time is monotonic in i, so a persistent cursor replaces the old
      // from-scratch rescan of refData per user sample (O(n+m) per offset
      // instead of O(n·m) — the visible stop-to-score freeze on phones).
      var deviationsAtOffset = function (off) {
        var devs = [];
        var j = 0;
        for (var i = 0; i < pitchData.length; i++) {
          var t = pitchData[i].time * scoreRate + off;
          while (j < refData.length - 1 && refData[j].time < t) j++;
          var best = refData[j];
          var bestDist = Math.abs(best.time - t);
          var bestIdx = j;
          if (j > 0) {
            var dPrev = Math.abs(refData[j - 1].time - t);
            if (dPrev < bestDist) { bestDist = dPrev; bestIdx = j - 1; }
          }
          // 0.15s (was 0.3): with a 300ms reach, a student frame sung during a
          // reference breath/pause was graded against the note before or after
          // the silence — up to 6 folded semitones of error the reciter never
          // made — and fast melisma frames matched a different note entirely.
          // Within 150ms the reference is either genuinely voiced (samples every
          // ~16-50ms) or legitimately silent, in which case the frame is simply
          // not graded rather than graded against the wrong note.
          if (bestDist < REF_REST_SEC) {
            var semis = userMidisPre[i] - refMidisPre[bestIdx];
            // Wrap to nearest pitch class: -6..+6 semitones (octave invariant)
            var folded = ((semis + 6) % 12 + 12) % 12 - 6;
            devs.push(Math.abs(folded));
          }
        }
        return devs;
      };
      var avgOf = function (arr) {
        return arr.length ? arr.reduce(function (a, b) { return a + b; }, 0) / arr.length : Infinity;
      };
      var deviations = deviationsAtOffset(0);
      var bestAvg = avgOf(deviations);
      var baselineN = deviations.length;
      var bestOff = 0;
      // Coarse pass, ±0.8s / 0.08s step. The old ±0.4s window couldn't reach
      // Bluetooth output latency (often 0.5–0.65s), so BT takes were scored —
      // and drawn — against a misaligned timeline. The ≥80%-overlap guard keeps
      // an offset from "winning" just by sliding hard samples off the ends.
      for (var off = -0.8; off <= 0.801; off += 0.08) {
        if (Math.abs(off) < 1e-6) continue;
        var d2 = deviationsAtOffset(off);
        if (d2.length >= baselineN * 0.8) {
          var a2 = avgOf(d2);
          if (a2 < bestAvg) { bestAvg = a2; deviations = d2; bestOff = off; }
        }
      }
      // Fine pass, ±0.06s / 0.02s around the coarse winner — removes the 80ms
      // grid quantization so the aligned contour lands within ~20ms.
      var coarseOff = bestOff;
      for (var foff = coarseOff - 0.06; foff <= coarseOff + 0.061; foff += 0.02) {
        if (Math.abs(foff - coarseOff) < 1e-6) continue;
        var d3 = deviationsAtOffset(foff);
        if (d3.length >= baselineN * 0.8) {
          var a3 = avgOf(d3);
          if (a3 < bestAvg) { bestAvg = a3; deviations = d3; bestOff = foff; }
        }
      }
      // SAVE the winning offset. The compare chart + accuracy coloring reuse it
      // so the PICTURE lines up on the same timeline the score was computed on;
      // previously it was discarded and the trail could sit off the gold line
      // (by the full audio latency) while the number said the pitch was right.
      this._scoreAlignOffset = bestOff;
      if (deviations.length > 0) {
        var avgDev = deviations.reduce(function (a, b) { return a + b; }, 0) / deviations.length;
        // Robust deviation = ½·median + ½·mean.
        //  - Two detections of the SAME voice differ 10–30 cents per frame, and
        //    vibrato-phase mismatch adds more on ornamented frames — the mean
        //    alone has a floor of ~0.3–0.6 st for a take that matches perfectly,
        //    which made 10 arithmetically unreachable (the reference graded
        //    against itself scored ~8.5). The median ignores that spiky noise.
        //  - The mean half keeps sensitivity to broad, systematic error, so a
        //    half-right take can't hide behind a clean median.
        var sortedDevs = deviations.slice().sort(function (a, b) { return a - b; });
        var medDev = sortedDevs[sortedDevs.length >> 1];
        var blendDev = 0.5 * medDev + 0.5 * avgDev;
        // Dead-band 0.25 st (25 cents): fully "in tune" by the product's own
        // definition — the chart colors ≤50c green — then a piecewise slope:
        // gentle (2.2/st) through the "slightly off" zone, steep (5/st) past
        // 0.75 st over the band, where the take is audibly in the wrong place.
        // Outcomes (calibrated in scripts/test-score-calibration.mjs):
        // matching take → 10 · constant 50c flat → ~9.5 · 1 st off → ~7.7 ·
        // unrelated melody → floor.
        var over = Math.max(0, blendDev - 0.25);
        var penalty = Math.min(over, 0.75) * 2.2 + Math.max(0, over - 0.75) * 5.0;
        pitchScore = Math.max(1, 10 - penalty);
        pitchDetail = 'typical ' + medDev.toFixed(2) + ' st off, avg ' + avgDev.toFixed(2) +
          ' st (' + deviations.length + ' samples vs reference)';

        // COVERAGE: deviations exist only where the take overlapped the
        // reference timeline. Without this factor, a take that aligns with
        // the reference for a small slice is graded purely on that slice —
        // a few seconds of accidental overlap could grade like a full
        // performance. Legit pause/waqf differences leave coverage well
        // above 50%, so they are unaffected.
        var matchedFraction = deviations.length / pitchData.length;
        if (matchedFraction < 0.5) {
          var coverageScale = Math.max(0.35, matchedFraction / 0.5);
          pitchScore = Math.max(1, pitchScore * coverageScale);
          pitchDetail += '; only ' + Math.round(matchedFraction * 100) +
            '% of the take overlapped the reference — pitch scaled ×' + coverageScale.toFixed(2);
        }
      } else {
        pitchScore = 1;
        pitchDetail = 'no overlap with reference timeline';
      }
    } else {
      // No reference — score based on how often user stayed near their own
      // median pitch (a tonal center). Rewards holding a coherent recitation,
      // not penalizing legitimate melodic variation.
      var midis = pitchData.map(function (d) { return freqToMidi(d.freq); });
      var sortedMidis = midis.slice().sort(function (a, b) { return a - b; });
      var median = sortedMidis[Math.floor(sortedMidis.length / 2)];
      var inRange = midis.filter(function (m) { return Math.abs(m - median) <= 2; }).length;
      pitchScore = (inRange / midis.length) * 10;
      pitchDetail = (Math.round(inRange / midis.length * 100)) + '% within ±2 semitones of median (no reference loaded)';
    }

    // ============ CONSISTENCY ============
    // Within-phrase smoothness measured at ~100ms intervals. Comparing
    // CONSECUTIVE samples (the previous algorithm) was meaningless because
    // pitch barely moves over 16-33ms regardless of skill — every pair
    // qualified as "smooth" so scores were always pinned at 9.8-10.
    // Now: for each anchor sample, find the next sample ≥100ms later,
    // compare pitch change. "Smooth" means ≤0.5 semitone shift (a tighter
    // threshold for a longer window). Pairs with dt > 200ms or
    // |Δsemitones| > 4 are treated as phrase breaks and skipped.
    var ANCHOR_GAP_S = 0.10;
    var MAX_PAIR_DT_S = 0.20;
    // MELODY-FOLLOWING (reference-relative). The old metric graded the
    // student's movement in ISOLATION: every legitimate 0.7-4 st interval
    // inside a phrase counted as an "unstable" transition, so lively
    // recitation — the entire point of maqam practice — could never approach
    // 10 no matter how well it was performed (the reference graded against
    // itself landed ~8/10 here). Now each ~100ms step of the STUDENT's contour
    // is compared with the REFERENCE's step over the same aligned interval:
    // moving the way the melody moves (including big, deliberate leaps) is
    // exactly right; moving differently — jitter, drift, a different tune —
    // is what loses marks. |Δstudent − Δreference| ≤ 0.8 st counts as
    // following (vibrato + detector noise on two contours stack; 1.0 absorbs them).
    // 1.0 (not 0.8): the student and reference each carry their own vibrato —
    // out-of-phase healthy vibrato alone differs by up to ~1.2 st across a
    // 100ms step. 1.0 forgives technique; erratic movement still fails it.
    var FOLLOW_SEMITONES = 1.0;
    // Nearest reference MIDI within 0.15s of an (aligned) reference-time, or
    // null — two-pointer over the time-sorted refData.
    var _refCursor = 0;
    var refMidiNear = function (t) {
      while (_refCursor < refData.length - 1 && refData[_refCursor].time < t) _refCursor++;
      var best = refData[_refCursor], bestD = Math.abs(best.time - t), bi = _refCursor;
      if (_refCursor > 0) {
        var dPrev = Math.abs(refData[_refCursor - 1].time - t);
        if (dPrev < bestD) { bestD = dPrev; bi = _refCursor - 1; }
      }
      return bestD <= 0.15 ? refMidisPre[bi] : null;
    };
    var stablePairs = 0, totalPairs = 0;
    (function () {
      var i = 0;
      while (i < pitchData.length - 1) {
        var targetT = pitchData[i].time + ANCHOR_GAP_S;
        var j = i + 1;
        while (j < pitchData.length && pitchData[j].time < targetT) j++;
        if (j >= pitchData.length) break;
        var dt = pitchData[j].time - pitchData[i].time;
        if (dt <= MAX_PAIR_DT_S) {
          // Map both anchor endpoints onto the reference timeline the score
          // aligned to (user-time × rate + offset) — same mapping as pitch.
          var rA = refMidiNear(pitchData[i].time * scoreRate + bestOff);
          var rB = refMidiNear(pitchData[j].time * scoreRate + bestOff);
          if (rA != null && rB != null) {
            var dS = userMidisPre[j] - userMidisPre[i];
            var dR = rB - rA;
            totalPairs++;
            if (Math.abs(dS - dR) <= FOLLOW_SEMITONES) stablePairs++;
          }
        }
        i = j; // advance so we don't double-count adjacent pairs
      }
    })();
    var consistencyScore, consistencyDetail;
    if (totalPairs > 0) {
      consistencyScore = Math.min(10, (stablePairs / totalPairs) * 10);
      consistencyDetail = stablePairs + '/' + totalPairs +
        ' ~100ms steps moved with the reference melody (within ±' + FOLLOW_SEMITONES + ' st)';
    } else {
      consistencyScore = 5;
      consistencyDetail = 'too few overlapping samples to compare movement';
    }

    // ============ STEADINESS ============
    // Sliding 500ms window; for each window with ≥4 samples, compute pitch
    // std-dev. Average across windows. Low avg = steady held notes; high = wobble.
    //
    // Two-pointer window with running sums — the old nested rescan was O(n²)
    // and froze the UI for seconds right at the emotional peak of every take
    // (Stop → score reveal). Same numbers, linear time.
    // Sliding-window pitch std-dev for ANY contour (two-pointer, linear time).
    var avgJitterOf = function (arr) {
      var jits = [];
      var mids = new Array(arr.length);
      for (var sm = 0; sm < arr.length; sm++) mids[sm] = freqToMidi(arr[sm].freq);
      var winLo = 0, winHi = -1, winSum = 0, winSumSq = 0;
      for (var i = 0; i < arr.length; i++) {
        var center = arr[i].time;
        while (winHi + 1 < arr.length && arr[winHi + 1].time <= center + 0.25) {
          winHi++;
          winSum += mids[winHi];
          winSumSq += mids[winHi] * mids[winHi];
        }
        while (arr[winLo].time < center - 0.25) {
          winSum -= mids[winLo];
          winSumSq -= mids[winLo] * mids[winLo];
          winLo++;
        }
        var winCount = winHi - winLo + 1;
        if (winCount >= 4) {
          var winMean = winSum / winCount;
          var winVar = winSumSq / winCount - winMean * winMean;
          if (winVar < 0) winVar = 0; // floating-point guard
          jits.push(Math.sqrt(winVar));
        }
      }
      return jits.length > 0
        ? { avg: jits.reduce(function (a, b) { return a + b; }, 0) / jits.length, windows: jits.length }
        : { avg: 0, windows: 0 };
    };
    // REFERENCE-RELATIVE. Absolute window std-dev taxed vibrato and melisma —
    // a 500ms window containing a deliberate 2 st ornament reads ~1.0 std-dev
    // regardless of skill, so expressive recitation capped around 8. The
    // reference carries the same ornaments, so only the EXCESS wobble beyond
    // the reference's own movement is charged: as steady as the melody allows
    // = 10, a genuinely shaky voice (1 st of excess) still reads ~6.
    var stuJit = avgJitterOf(pitchData);
    var refJit = avgJitterOf(refData);
    var avgJitter = stuJit.avg;
    var excessJitter = Math.max(0, avgJitter - Math.max(refJit.avg, 0.25));
    var steadinessScore = Math.max(1, 10 - excessJitter * 4);
    var steadinessDetail = 'avg ' + avgJitter.toFixed(2) + ' st movement across ' + stuJit.windows +
      ' windows (reference itself moves ' + refJit.avg.toFixed(2) + ' st — only the excess counts)';

    // ============ OVERALL ============
    // Pitch dominates (60%); a reference always exists here (the no-reference
    // path returned early above with no score at all). Flow and steadiness are
    // now reference-relative, so they sit near 10 for anyone following the
    // melody — the weighting keeps them from masking real intonation error.
    var overall = pitchScore * 0.6 + consistencyScore * 0.2 + steadinessScore * 0.2;
    // Snap-to-10 inside measurement noise — but ONLY when the pitch itself is
    // genuinely in tune (≥9.7): the components each carry a little irreducible
    // detector noise, so a take that is for all practical purposes a match
    // reads as the 10 it earned rather than a 9.8 that implies something was
    // wrong. A take that is merely smooth (but flat/off) can't snap.
    if (overall >= 9.7 && pitchScore >= 9.7) overall = 10;

    var result = {
      overall: Math.max(0, Math.min(10, overall)),
      pitch: Math.max(0, Math.min(10, pitchScore)),
      consistency: Math.max(0, Math.min(10, consistencyScore)),
      steadiness: Math.max(0, Math.min(10, steadinessScore)),
      hasRef: hasRef,
      sampleCount: pitchData.length,
      // Human-readable per-metric explanations — rendered on the score screen
      // (they used to be console-only, which taught nobody anything).
      pitchDetail: pitchDetail,
      consistencyDetail: consistencyDetail,
      steadinessDetail: steadinessDetail
    };

    // Audit log so anyone debugging can see what each number is based on
    slog('[saif-practice] score breakdown:');
    slog('  pitch:       ' + result.pitch.toFixed(2) + '/10  ← ' + pitchDetail);
    slog('  consistency: ' + result.consistency.toFixed(2) + '/10  ← ' + consistencyDetail);
    slog('  steadiness:  ' + result.steadiness.toFixed(2) + '/10  ← ' + steadinessDetail);
    slog('  overall:     ' + result.overall.toFixed(2) + '/10  (weights: ' +
      '60/20/20 (pitch/melody-flow/steadiness vs the reference)' + ')');

    return result;
  };

  /* ==============================================
     VISUALIZATION
     ============================================== */
  /**
   * Returns the reference pitch (Hz) closest to the given time, or null.
   */
  PracticeStudio.prototype.getRefPitchAt = function (time) {
    var data = this.refPitchData;
    if (!data || data.length === 0) return null;
    var GAP = REF_REST_SEC; // beyond this the reference is silent — nothing to colour against
    // First sample at/after `time`.
    var lo = 0, hi = data.length - 1;
    while (lo < hi) {
      var mid = (lo + hi) >> 1;
      if (data[mid].time < time) lo = mid + 1; else hi = mid;
    }
    var hiPt = data[lo];
    var loPt = lo > 0 ? data[lo - 1] : null;
    // Inside a bracket whose ends are close enough to be one continuous line,
    // interpolate in log-frequency (i.e. linearly in pitch/MIDI space) so the
    // color at the MIDDLE of a glide reads as on-pitch instead of snapping to
    // whichever endpoint happens to be nearer. The old nearest-sample lookup
    // painted the whole span the color of one end — a rising glide looked flat.
    if (loPt && time >= loPt.time && time <= hiPt.time && (hiPt.time - loPt.time) <= GAP) {
      var span = hiPt.time - loPt.time;
      var w = span > 0 ? (time - loPt.time) / span : 0;
      return loPt.freq * Math.pow(hiPt.freq / loPt.freq, w);
    }
    // Outside any bracket (start/end of contour, or across a silence): fall back
    // to the nearest sample only if it's within HALF a gap. Past that there is
    // no reference line here — return null so the caller draws neutral ("no
    // line ⇒ no color") instead of coloring against a far-away pitch.
    var nearest = hiPt;
    if (loPt && Math.abs(loPt.time - time) < Math.abs(hiPt.time - time)) nearest = loPt;
    return Math.abs(nearest.time - time) <= GAP / 2 ? nearest.freq : null;
  };

  /**
   * Karaoke-style scrolling pitch view.
   * - Reference pitch contour scrolls right-to-left through a fixed playhead.
   * - Translucent gold target zone marks the ±50¢ "in tune" range at "now".
   * - User pitch dot at the playhead — solid gold when in tune, faded when off.
   * - Note ladder on the left orients the eye to the actual pitch space.
   */
  /**
   * Player-B v2 dock: the two NEW circles either side of Record.
   *
   * Listen is a remote control for #SaifPracticeRefPlay rather than a second
   * player — one source of truth for playback, so speed/seek/transpose and the
   * reference row's own button can never disagree with the dock. Its icon
   * follows the <audio> element's real play/pause events (not the click), so it
   * stays correct when playback ends on its own or is stopped by a take.
   */
  /**
   * Player-B v2 sheets. Opening a tool sheet goes through switchMode(), so all
   * the existing mode bookkeeping still runs — stopping another tool's mic,
   * forgiving an abandoned range test, blocking mid-recording. History is not a
   * mode, so it opens with the studio still in 'practice'.
   */
  /**
   * Results own two deliberate mascot seats: the score-card edge while the
   * sheet is at rest, and the Studio's OUTER top rim once either the result
   * body or the lesson page has been scrolled. Keep that choice in one place
   * so Score / Compare / Notes and both kinds of scrolling cannot disagree.
   */
  PracticeStudio.prototype._syncResultTootSeat = function (force) {
    if (this._openSheetV2 !== 'result') return false;
    var resultBody = this.container.querySelector('#SaifPracticeResultSheet .saif-practice__sheet-body');
    var bodyScrolled = !!(resultBody && resultBody.scrollTop > 12);
    var pageScrolled = !!(this._resultTootPageScrollArmed &&
      Math.abs(window.scrollY - (this._resultTootOpenScrollY || 0)) > 12);
    var atEdge = bodyScrolled || pageScrolled;
    var wanted = atEdge ? 'edge' : 'score';
    if (!force && atEdge === !!this._resultTootAtEdge && this._tootSeatState === wanted) return true;
    this._resultTootAtEdge = atEdge;
    return this._tootSeat(wanted);
  };

  PracticeStudio.prototype.sheetOpenV2 = function (name) {
    var ids = { tools: 'SaifSheetTools', tuner: 'SaifSheetTuner', drill: 'SaifSheetDrill', range: 'SaifSheetRange', history: 'SaifSheetHistory', ref: 'SaifSheetRef', result: 'SaifPracticeResultSheet' };
    var id = ids[name];
    if (!id) return;
    // The Reference sheet is empty without a clip — the shelf stays hidden, so
    // the cog would open a bare title bar. Send them where they were actually
    // trying to go.
    if (name === 'ref' && !this.refUrl) { this._openRefPicker(); return; }
    // Moving to a different surface leaves the history list behind, so stop
    // anything it was playing (the result sheet is the one exception — it is
    // where a replayed take is reviewed).
    if (name !== 'history' && name !== 'result') this._historyStop();
    if (this.state === 'recording') { this.setStatus('Stop recording first.'); return; }
    if (name === 'tuner' || name === 'drill' || name === 'range') {
      this.switchMode(name);
      // switchMode refuses mid-recording; don't open a sheet it declined.
      if (this.currentMode !== name) return;
    }
    var self = this;
    Object.keys(ids).forEach(function (k) {
      var el = self.container.querySelector('#' + ids[k]);
      if (el) el.hidden = (k !== name);
    });
    // Cancel any in-flight close so a fast reopen does not get hidden by it.
    this._sheetCloseToken = (this._sheetCloseToken || 0) + 1;
    Array.prototype.forEach.call(this.container.querySelectorAll('.saif-practice__sheet--closing'), function (el) {
      el.classList.remove('saif-practice__sheet--closing');
    });
    var scrim = this.container.querySelector('#SaifPracticeScrim');
    if (scrim) { scrim.classList.remove('saif-practice__scrim--closing'); scrim.hidden = false; }
    this._lockCardHeight(true);
    this.container.classList.add('saif-practice--sheet-open');
    this._openSheetV2 = name;
    if (name === 'result') {
      /* onRecordingComplete may perform one small programmatic scroll after
         opening. Arm page-scroll detection only after that settle, so the
         initial score still uses its score seat while later USER scrolling
         moves him to the outer gray Studio rim. */
      clearTimeout(this._resultTootPageArmTimer);
      this._resultTootPageScrollArmed = false;
      this._resultTootOpenScrollY = window.scrollY;
      var _selfResultScroll = this;
      this._resultTootPageArmTimer = setTimeout(function () {
        if (_selfResultScroll._openSheetV2 !== 'result') return;
        _selfResultScroll._resultTootOpenScrollY = window.scrollY;
        _selfResultScroll._resultTootPageScrollArmed = true;
      }, 450);
      if (!this._resultTootPageScrollBound) {
        this._resultTootPageScrollBound = true;
        window.addEventListener('scroll', function () {
          if (_selfResultScroll._openSheetV2 !== 'result' || !_selfResultScroll._resultTootPageScrollArmed) return;
          if (_selfResultScroll._resultTootPageScrollRAF) return;
          _selfResultScroll._resultTootPageScrollRAF = requestAnimationFrame(function () {
            _selfResultScroll._resultTootPageScrollRAF = 0;
            _selfResultScroll._syncResultTootSeat(false);
          });
        }, { passive: true });
      }
    }
    var sheet = this.container.querySelector('#' + id);
    if (sheet) {
      if (name === 'result') {
        var resultBody = sheet.querySelector('.saif-practice__sheet-body');
        if (resultBody) {
          resultBody.scrollTop = 0;
          this._resultTootAtEdge = false;
          if (!resultBody._saifTootScrollBound) {
            resultBody._saifTootScrollBound = true;
            resultBody.addEventListener('scroll', function () {
              if (self._openSheetV2 !== 'result') return;
              self._syncResultTootSeat(false);
            }, { passive: true });
          }
        }
      }
      var focusable = sheet.querySelector('button:not([hidden]), [href], input, select, textarea');
      if (focusable && focusable.focus) { try { focusable.focus({ preventScroll: true }); } catch (e) {} }
      // NO scrollIntoView here: the sheet is pinned to the bottom of the card and
      // is already on screen, so scrolling only ever yanked the page out from
      // under the thumb (measured: ±23px on every open).
    }
    // The sheet is visible now, so its canvases finally have a real size.
    this._sheetResizeV2(name === 'result');
    // Every score surface uses the same stable seat — including a saved take
    // reopened from History. Keeping this in the sheet lifecycle prevents the
    // fresh-result and history paths from drifting apart.
    if (name === 'result') {
      var _selfResultToot = this;
      clearTimeout(this._resultTootSeatTimer);
      this._resultTootSeatTimer = setTimeout(function () {
        _selfResultToot._resultTootSeatTimer = 0;
        if (_selfResultToot._openSheetV2 !== 'result') return;
        try { _selfResultToot._syncResultTootSeat(false); } catch (e) {}
      }, 80);
    } else if (name === 'tuner' || name === 'drill' || name === 'range' || name === 'history') {
      /* These tools replace the covered tree with one stable, meaningful seat:
         the top edge of the drawer they belong to. Retrying only while this
         same sheet owns the screen prevents an earlier flight from stranding
         him in the content or on a hidden History row. */
      var toolTootTries = 0;
      var settleToolToot = function () {
        if (self._openSheetV2 !== name) return;
        var placed = false;
        try { placed = self._tootSeat('tool'); } catch (e) {}
        if (!placed && toolTootTries++ < 8) setTimeout(settleToolToot, 180);
      };
      setTimeout(settleToolToot, 120);
    }
    // Deliberately NOT sending him home here: the sheet is the thing that
    // covers the branch. He stays where he is until it closes.
  };

  /**
   * A canvas inside a hidden sheet measures 0x0, so anything drawn while the
   * sheet was closed is lost. Re-measure (and redraw the comparison) whenever
   * sheet visibility changes.
   */
  PracticeStudio.prototype._sheetResizeV2 = function (drawCompare) {
    try { this.resizeCanvases(); } catch (e) {}
    if (drawCompare) { try { this.drawComparison(); } catch (e) {} }
  };

  /**
   * Pin the card to the height it ALREADY has, so opening a sheet cannot
   * resize it. switchMode() hides the dock and live view for tool sheets, which
   * would otherwise collapse the card out from under an absolutely-positioned
   * sheet; a fixed min-height instead made it grow. Measure, then lock.
   */
  PracticeStudio.prototype._lockCardHeight = function (on) {
    var card = this.container;
    if (!card || !card.style) return;
    if (!on) { card.style.minHeight = ''; return; }
    if (card.style.minHeight) return;              // already locked
    // min-height applies to the CONTENT box, but getBoundingClientRect returns the
    // BORDER box — so locking the measured height straight in inflated the card by
    // exactly its padding + border (measured: +38px on every single open, which is
    // the shake). Convert when the box model calls for it.
    var h = card.getBoundingClientRect().height;
    var cs = window.getComputedStyle ? window.getComputedStyle(card) : null;
    if (cs && cs.boxSizing !== 'border-box') {
      h -= (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) +
           (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    }
    if (h > 0) card.style.minHeight = Math.round(h) + 'px';
  };

  /* Closing a sheet uncovers the stage - that is when he can go home. */
  PracticeStudio.prototype.sheetCloseV2 = function () {
    var self = this;
    /* A delayed score-seat request must never outlive the sheet that owns it.
       Disarm page-scroll ownership during the exit animation as well. */
    clearTimeout(this._resultTootSeatTimer);
    this._resultTootSeatTimer = 0;
    clearTimeout(this._resultTootPageArmTimer);
    this._resultTootPageArmTimer = 0;
    this._resultTootPageScrollArmed = false;
    // Leaving the list must stop the take that is playing — the row button is
    // A review was opened FROM the list, so closing it belongs back at the
    // list rather than dumping the student on the player with no context.
    var backToHistory = (this._openSheetV2 === 'result' && !!this._reviewingSavedId);
    // about to go off screen with the sheet.
    this._historyStop();
    if (backToHistory) {
      this._reviewingSavedId = null;
      var _selfBack = this;
      setTimeout(function () { try { _selfBack.sheetOpenV2('history'); } catch (e) {} }, 0);
    }
    var reduce = prefersReducedMotion();
    // Play the exit, THEN hide. Without this the sheet slid in and was cut dead
    // on close. The token cancels a pending hide if a sheet is reopened fast.
    var token = (this._sheetCloseToken = (this._sheetCloseToken || 0) + 1);
    var ids = ['SaifSheetTools', 'SaifSheetTuner', 'SaifSheetDrill', 'SaifSheetRange', 'SaifSheetHistory', 'SaifSheetRef', 'SaifPracticeResultSheet'];
    var scrim = this.container.querySelector('#SaifPracticeScrim');
    var finish = function () {
      if (self._sheetCloseToken !== token) return;   // superseded by a reopen
      ids.forEach(function (id) {
        var el = self.container.querySelector('#' + id);
        if (el) { el.hidden = true; el.classList.remove('saif-practice__sheet--closing'); }
      });
      if (scrim) { scrim.hidden = true; scrim.classList.remove('saif-practice__scrim--closing'); }
      self._lockCardHeight(false);
      // Choose a destination only after the sheet has uncovered the stage.
      // _tootHome uses the top edge when the branch is still off screen. A
      // result-seat flight may still own Toot for a few frames when the close
      // animation finishes (more common in the taller lesson layout), so retry
      // rather than leaving the Studio's seat state stale until its watchdog
      // happens to recover him.
      var closeTootTries = 0;
      var settleClosedToot = function () {
        if (self._openSheetV2) return;
        var placed = false;
        /* Force redirects an in-progress score/edge flight. SaifToot.perchOn
           cancels the old flight cleanly before starting the visible-home leg,
           so he never waits motionless on a marker the sheet just hid. */
        try { placed = self._tootHome(true); } catch (e) {}
        if (!placed && closeTootTries++ < 8) setTimeout(settleClosedToot, 180);
      };
      setTimeout(settleClosedToot, 0);
    };
    if (reduce) { finish(); } else {
      var any = false;
      ids.forEach(function (id) {
        var el = self.container.querySelector('#' + id);
        if (el && !el.hidden) { el.classList.add('saif-practice__sheet--closing'); any = true; }
      });
      if (scrim && !scrim.hidden) scrim.classList.add('saif-practice__scrim--closing');
      if (any) setTimeout(finish, 250); else finish();
    }
    this.container.classList.remove('saif-practice--sheet-open');
    // Returning to the player also returns to practice mode, which is what
    // stops a running tuner/drill and restores the dock + live view.
    if (this.currentMode !== 'practice' && this.state !== 'recording') {
      try { this.switchMode('practice'); } catch (e) {}
    }
    var wasRef = (this._openSheetV2 === 'ref');
    this._openSheetV2 = null;
    // The result element is deliberately left as-is: dismissing the sheet
    // returns you to the player ready for the next take, it does not discard
    // the score you just earned (it stays in Practice History, and the sheet
    // is reopened by the next real result).
    // The Reference sheet is opened from the now-bar chip, not from More — send
    // focus back where it came from, and un-press the chip.
    var nowChip = this.container.querySelector('#SaifPracticeNowChip');
    if (nowChip) nowChip.setAttribute('aria-expanded', 'false');
    var more = this.container.querySelector('#SaifPracticeDockMore');
    var back = (wasRef && nowChip && !nowChip.hidden) ? nowChip : more;
    if (back && back.focus) { try { back.focus({ preventScroll: true }); } catch (e) {} }
    this._sheetResizeV2(false);
    this._drawIdleCanvas();
  };

  /**
   * With a sheet open the scrim swallows CLICKS on the controls behind it, but
   * Tab still walked straight into them (the dock, the refbar, the onboarding
   * steps) — you could focus and fire a hidden-behind-a-scrim button. Trap the
   * ring inside the open sheet; Esc still closes.
   */
  PracticeStudio.prototype._sheetFocusTrap = function (e) {
    if (e.key !== 'Tab' || !this._openSheetV2) return;
    // The reference picker is a native <dialog> that can be opened FROM a sheet
    // (drill empty state, range "browse clips", the no-reference banner). It runs
    // its own modal focus handling, so the sheet trap must stand down or Tab dies
    // inside the picker.
    if (document.querySelector('dialog[open]')) return;
    var ids = { tools: 'SaifSheetTools', tuner: 'SaifSheetTuner', drill: 'SaifSheetDrill', range: 'SaifSheetRange', history: 'SaifSheetHistory', ref: 'SaifSheetRef', result: 'SaifPracticeResultSheet' };
    var sheet = this.container.querySelector('#' + ids[this._openSheetV2]);
    if (!sheet) return;
    var f = Array.prototype.filter.call(
      sheet.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && !el.hidden && el.offsetParent !== null; }
    );
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (!sheet.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /**
   * Sweep the score dial to a 0..1 fraction. This drives the ring the studio
   * ALREADY draws around the score (.saif-practice__score-main::after, until
   * now a purely decorative conic flourish) rather than adding a second one.
   */
  PracticeStudio.prototype._scoreArcSet = function (frac) {
    var main = this._scoreMainEl ||
      (this._scoreMainEl = this.container.querySelector('.saif-practice__score-main'));
    if (!main) return;
    var f = Math.max(0, Math.min(1, frac || 0));
    main.style.setProperty('--saif-score-deg', (f * 360).toFixed(1) + 'deg');
  };

  /**
   * The result sheet follows #SaifPracticeResult rather than owning its own
   * state: the engine already decides when a result exists (new take, replaying
   * a history row, clearing on clip change), so an attribute observer keeps the
   * sheet honest for every one of those paths without touching them.
   */
  PracticeStudio.prototype.resultWireV2 = function (container) {
    var self = this;
    var result = container.querySelector('#SaifPracticeResult');
    var sheet = container.querySelector('#SaifPracticeResultSheet');
    if (!result || !sheet) return;
    // NOTE: this used to watch #SaifPracticeResult for `hidden` changes, but a
    // reference PREVIEW flips that same flag — so merely picking a clip threw an
    // empty Result sheet over the player. The sheet is now opened from the two
    // call sites that genuinely mean a result exists (renderScore and history
    // replay), which also lets dismissal leave the result intact.

    // A breakdown row answers "where did I lose it?" — send it to the chart.
    container.addEventListener('click', function (ev) {
      var row = ev.target.closest && ev.target.closest('.saif-practice__metric');
      if (!row || !container.contains(row)) return;
      var tab = container.querySelector('.saif-practice__result-tab[data-result-tab="compare"]');
      if (tab) tab.click();
    });
    // The rows are divs; make them reachable and operable from the keyboard.
    new MutationObserver(function () {
      var rows = container.querySelectorAll('.saif-practice__metric');
      Array.prototype.forEach.call(rows, function (r) {
        if (r.getAttribute('tabindex')) return;
        r.setAttribute('tabindex', '0');
        r.setAttribute('role', 'button');
        r.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); r.click(); }
        });
      });
    }).observe(container.querySelector('#SaifPracticeBreakdown') || result, { childList: true });
  };

  /** Gold bloom. The global Toot mascot owns the score reaction. */
  PracticeStudio.prototype._celebrateV2 = function () {
    var bloom = this.container.querySelector('#SaifPracticeBloom');
    if (!bloom) return;
    bloom.classList.remove('saif-practice__bloom--go');
    void bloom.offsetWidth; // restart the animation on a repeat milestone
    bloom.classList.add('saif-practice__bloom--go');
  };

  PracticeStudio.prototype.sheetWireV2 = function (container) {
    var self = this;
    var tools = container.querySelector('#SaifSheetTools');
    if (!tools) return; // older markup (e.g. the embedded lesson studio)
    container.querySelectorAll('[data-tool-tile]').forEach(function (tile) {
      tile.addEventListener('click', function () { self.sheetOpenV2(tile.getAttribute('data-tool-tile')); });
    });
    container.querySelectorAll('[data-sheet-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.sheetCloseV2(); });
    });
    var scrim = container.querySelector('#SaifPracticeScrim');
    if (scrim) scrim.addEventListener('click', function () { self.sheetCloseV2(); });
    document.addEventListener('keydown', function (e) {
      // Let the picker <dialog> consume its own Escape first, or one press
      // closes both the picker and the sheet underneath it.
      if (e.key === 'Escape' && self._openSheetV2 && !document.querySelector('dialog[open]')) {
        e.preventDefault(); self.sheetCloseV2(); return;
      }
      self._sheetFocusTrap(e);
    });
  };

  PracticeStudio.prototype.dockWireV2 = function (container) {
    var self = this;
    var listenBtn = container.querySelector('#SaifPracticeDockListen');
    var playIco = container.querySelector('#SaifPracticeDockListenPlay');
    var pauseIco = container.querySelector('#SaifPracticeDockListenPause');
    var moreBtn = container.querySelector('#SaifPracticeDockMore');

    if (this.recordBtn) {
      this.recordBtn.addEventListener('click', function () {
        if (self._openSheetV2) self.sheetCloseV2();
      }, true);
    }
    if (listenBtn) {
      listenBtn.addEventListener('click', function () {
        if (self._openSheetV2) self.sheetCloseV2();
        // "Is a reference actually loaded?" — the old guard tested
        // refPlayBtn.disabled, which this file assigns NOWHERE, so it was always
        // false and the picker fallback below was unreachable. (refChangeBtn was
        // never assigned either, so that line was doubly dead.)
        if (self.refPlayBtn && (self.refUrl || (self.refAudioEl && self.refAudioEl.getAttribute('src')))) {
          self.refPlayBtn.click(); return;
        }
        self._openRefPicker();
      });
      var syncListen = function () {
        var playing = !!(self.refAudioEl && !self.refAudioEl.paused && !self.refAudioEl.ended);
        if (playIco) playIco.hidden = playing;
        if (pauseIco) pauseIco.hidden = !playing;
        listenBtn.classList.toggle('saif-practice__dock-btn--active', playing);
      };
      if (this.refAudioEl) {
        ['play', 'pause', 'ended', 'emptied'].forEach(function (ev) {
          self.refAudioEl.addEventListener(ev, syncListen);
        });
      }
      syncListen();
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        if (self._openSheetV2) { self.sheetCloseV2(); return; }
        self.sheetOpenV2('tools');
      });
    }
  };


  /* ══════════════ Player-B v2 · the "now practicing" bar ══════════════
     One row replacing the two stacked reference shelves. Two mutually exclusive
     states in one wrapper; the chip is the doorway to #SaifSheetRef, which wraps
     the reference shelf where it stands. Every lookup is null-safe — the lesson
     and maqam-modal embeds render the same snippet, and a cached snippet may
     have no bar at all. */

  // Picker-parity title for a clip the picker never named. refClipTitle is set
  // ONLY inside loadReference, which a preset data-reference-url never reaches,
  // so on a lesson page the title would otherwise render empty. Formats mirror
  // drillList() and the picker's renderClips().
  function refTitleFromUrl(url) {
    var base = String(url || '').split('/').pop().split('?')[0];
    if (!base) return '';
    var d = base.match(/^drill-([a-z]+)-mod(\d+)(?:-(\d+))?\.mp3$/i);
    if (d) {
      return d[1].charAt(0).toUpperCase() + d[1].slice(1) +
        ' · Module ' + d[2] + (d[3] ? ' · Drill ' + d[3] : '');
    }
    var m = base.match(/^maqam-478-([a-z]+)-(\d+)\.mp3$/i);
    if (m) {
      return m[1].charAt(0).toUpperCase() + m[1].slice(1) + ' — clip ' + (parseInt(m[2], 10) + 1);
    }
    return base.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ');
  }

  PracticeStudio.prototype._nowbarSync = function () {
    if (!this.container) return;
    var wrap = this.container.querySelector('#SaifPracticeNowbar');
    if (!wrap) return;                                  // older / cached snippet
    var empty = this.container.querySelector('#SaifPracticeNowEmpty');
    var chip = this.container.querySelector('#SaifPracticeNowChip');
    var has = !!this.refUrl;
    // The maqam-library Practice modal ships its own clip bar and hides the shelf
    // there, so the chip would be a second clip header. Keep the pre-v2 behaviour
    // exactly: Choose/Upload stay put, the chip never appears.
    var inModal = !!(this.container.closest &&
      this.container.closest('.saif-maqam-practice-modal__body'));
    if (empty) empty.hidden = has && !inModal;
    if (chip) chip.hidden = !has || inModal;

    if (!has) {
      // Remove reference pulled the clip out from under an open sheet; don't
      // leave a bare title bar on screen.
      if (this._openSheetV2 === 'ref') { try { this.sheetCloseV2(); } catch (e) {} }
      this._nowThumbPaint();
      return;
    }
    if (chip && !inModal) {
      var titleEl = this.container.querySelector('#SaifPracticeNowTitle');
      var metaEl = this.container.querySelector('#SaifPracticeNowMeta');
      var mqEl = this.container.querySelector('#SaifPracticeNowMaqam');
      if (titleEl) {
        titleEl.textContent = this.refClipTitle ||
          this.container.getAttribute('data-reference-title') ||
          refTitleFromUrl(this.refUrl) || 'Reference clip';
      }
      if (metaEl) {
        var key;
        if (this._customRef) {
          key = 'custom clip';
        } else if (this._refSemitones) {
          var sign = this._refSemitones > 0 ? '+' : '';
          key = (this._refShiftFits ? 'your key (' : 'closer to your key (') +
            sign + this._refSemitones + ')';
        } else {
          key = (window.SaifI18n && window.SaifI18n.original_key) || 'original key';
        }
        var rate = (this.refAudioEl && this.refAudioEl.playbackRate) || 1;
        metaEl.textContent = key + ' · ' + rate + 'x' + (this._headphonesMode ? ' · 🎧' : '');
      }
      if (mqEl) {
        // MIRROR, never move: the key switch is anchored to #SaifPracticeRefMaqam.
        // Reformat rather than copy — _refreshMaqamLabel writes "Ajam · tonic A#3",
        // and uppercasing that verbatim would read "AJAM · TONIC A#3".
        var raw = (this.refMaqamLabel && !this.refMaqamLabel.hidden &&
          this.refMaqamLabel.textContent) || '';
        mqEl.textContent = raw.replace(' · tonic ', ' · ');
        mqEl.hidden = !raw;
      }
      var live = (this.state === 'recording' || this.state === 'starting');
      chip.classList.toggle('saif-practice__now-chip--live', live);
      chip.setAttribute('aria-disabled', live ? 'true' : 'false');
    }
    this._nowThumbPaint();
    this._drawIdleCanvas();
  };

  PracticeStudio.prototype.nowbarWireV2 = function (container) {
    var self = this;
    var chip = container.querySelector('#SaifPracticeNowChip');
    if (chip) {
      chip.addEventListener('click', function () {
        if (chip.getAttribute('aria-disabled') === 'true') return;
        self.sheetOpenV2('ref');
        chip.setAttribute('aria-expanded', self._openSheetV2 === 'ref' ? 'true' : 'false');
      });
    }
    var choose = container.querySelector('#SaifSheetRefChoose');
    if (choose) {
      choose.addEventListener('click', function () {
        // One scrim at a time: the picker is a native <dialog> with its own
        // ::backdrop and it is being opened from INSIDE this sheet.
        try { self.sheetCloseV2(); } catch (e) {}
        self._openRefPicker();
      });
    }
    var upProxy = container.querySelector('#SaifSheetRefUpload');
    var upReal = container.querySelector('#SaifPracticeRefUploadBtn');
    if (upProxy && upReal) {
      upProxy.addEventListener('click', function () { upReal.click(); });
      // The picker writes upload progress AND the disabled state to the ORIGINAL
      // button, which is hidden while a clip is loaded — so the proxy would sit
      // frozen on "Use your own audio" through the whole analyse pass.
      // attributeFilter is REQUIRED: the disabled flag is an attribute mutation, and
      // childList/characterData never fire for it.
      var mirror = function () {
        upProxy.textContent = upReal.textContent;
        upProxy.disabled = upReal.disabled;
      };
      if (window.MutationObserver) {
        try {
          new MutationObserver(mirror).observe(upReal, {
            childList: true, characterData: true, subtree: true,
            attributes: true, attributeFilter: ['disabled']
          });
        } catch (e) {}
      }
      mirror();
    }
    // Speed and headphones both live in the sheet and both already have handlers
    // bound once at init — never re-bind them. ratechange covers the speed pills;
    // the delegated change fires AFTER the checkbox's own listener, so
    // _headphonesMode is already updated when we read it.
    if (this.refAudioEl) {
      ['ratechange', 'emptied', 'loadedmetadata'].forEach(function (ev) {
        self.refAudioEl.addEventListener(ev, function () {
          try { self._nowbarSync(); } catch (e) {}
        });
      });
    }
    container.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('saif-practice__ref-hptoggle-input')) {
        try { self._nowbarSync(); } catch (e2) {}
      }
    });
    // Mandatory, not optional: lesson and standalone pages preset
    // data-reference-url and never call loadReference, so without this nothing
    // ever paints the bar there.
    this._nowbarSync();
  };

  /* ══ the now-practicing thumbnail ══
     A miniature of the reference contour on a mini stage tile. Painted ONLY from
     _nowbarSync — clip change, analysis landing, transpose, history replay,
     resize. NEVER from a rAF loop; the cache key makes a mis-wired per-frame
     caller cost one string compare. */
  PracticeStudio.prototype._nowThumbPaint = function () {
    if (!this.container) return;
    var cvs = this.container.querySelectorAll('#SaifPracticeNowThumb');
    if (!cvs.length) return;
    var pts = this.refPitchData;
    var n = (pts && pts.length) || 0;
    var ready = !!this.refUrl && n > 1;
    var dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    // Length alone is not enough: _applyRefTranspose returns a SAME-LENGTH array
    // with shifted frequencies, so the shift and the end frequencies go in too.
    var key = (ready ? 'r' : 'p') + '|' + n + '|' + (this._refSemitones || 0) + '|' + dpr +
      (n ? ('|' + (pts[0].freq || 0).toFixed(1) + '|' + (pts[n - 1].freq || 0).toFixed(1)) : '');
    if (key === this._nowThumbKey) return;

    var path = null;
    if (ready) {
      // Downsample on a fractional stride with the tail anchored to the true last
      // sample, so a 5,870-point clip costs what a 300-point replayed one costs.
      var CAP = 96, ser = [], lo = Infinity, hi = -Infinity, i, mid, p;
      var step = n > CAP ? n / CAP : 1, cnt = n > CAP ? CAP : n;
      for (i = 0; i < cnt; i++) {
        p = pts[(i === cnt - 1) ? n - 1 : Math.floor(i * step)];
        if (!p || !(p.freq > 0)) continue;
        mid = freqToMidi(p.freq);
        if (mid < lo) lo = mid;
        if (mid > hi) hi = mid;
        ser.push({ t: p.time, m: mid, brk: false });
      }
      if (ser.length > 1) {
        // The break threshold MUST derive from the SAMPLED series' own spacing:
        // refPitchData is silence-gapped, and after the stride consecutive samples
        // sit ~0.39s apart on a median clip — so a fixed 0.15s test would mark
        // EVERY segment a break and the contour would render as a chain of
        // moveTo's, i.e. invisible. Same rule drawComparison uses for replays.
        var dts = [], j, dt;
        for (j = 1; j < ser.length; j++) { dt = ser[j].t - ser[j - 1].t; if (dt > 0) dts.push(dt); }
        dts.sort(function (a, b) { return a - b; });
        var gap = dts.length ? Math.max(0.15, 2.5 * dts[dts.length >> 1]) : 0.15;
        for (j = 1; j < ser.length; j++) ser[j].brk = (ser[j].t - ser[j - 1].t) > gap;
        if (hi - lo < 3) { var c = (hi + lo) / 2; lo = c - 1.5; hi = c + 1.5; }
        var dur = (pts[n - 1] && pts[n - 1].time) || ser[ser.length - 1].t || 1;
        if (!(dur > 0)) dur = 1;
        path = { s: ser, lo: lo, hi: hi, dur: dur };
      }
    }
    // Commit the key only once a paint actually happened — a chip that is still
    // hidden measures 0 and must repaint the moment it is shown.
    var painted = false;
    for (var k = 0; k < cvs.length; k++) {
      if (this._nowThumbPaintOne(cvs[k], path, dpr)) painted = true;
    }
    this._nowThumbKey = painted ? key : null;
  };

  PracticeStudio.prototype._nowThumbPaintOne = function (cv, path, dpr) {
    // A hidden chip and the maqam modal both measure 0; a 0-size canvas throws on
    // some browsers and wastes the paint on all of them.
    var box = cv.clientWidth || 0;
    if (!box) return false;
    var boxH = cv.clientHeight || box;
    cv.width = Math.round(box * dpr);              // assignment clears the canvas
    cv.height = Math.round(boxH * dpr);
    var ctx = cv.getContext('2d');
    if (!ctx) return false;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);        // CSS px from here down
    var W = box, H = boxH;
    // The tile is the stage, shrunk — hand-rolled on purpose. Do NOT call
    // drawStageBackground: its warm lift drifts off performance.now() and its
    // grain pattern is cached on the CONTEXT, so it only reads right when
    // repainted every frame — exactly what this must never do. Same colours.
    var g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#201D18'); g.addColorStop(1, '#151310');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    if (!path) {
      // Pending: analyzeReference is deferred behind requestIdleCallback with a
      // 2500ms timeout, so this is on screen for up to ~2.5s on every clip.
      // A dim resting line, never a hole.
      ctx.strokeStyle = 'rgba(232,200,119,0.30)';
      ctx.lineWidth = 1.5; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(W * 0.22, H * 0.5); ctx.lineTo(W * 0.78, H * 0.5);
      ctx.stroke();
      return true;
    }
    var padX = W * 0.11, padY = H * 0.17;
    var iw = W - padX * 2, ih = H - padY * 2;
    var i, x, y, started = false;
    ctx.strokeStyle = '#E8C877';                   // the lit-trace gold
    ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.shadowBlur = 3; ctx.shadowColor = 'rgba(212,168,75,0.6)';
    ctx.beginPath();
    for (i = 0; i < path.s.length; i++) {
      x = padX + (path.s[i].t / path.dur) * iw;
      y = padY + ih - ((path.s[i].m - path.lo) / (path.hi - path.lo)) * ih;
      if (y < padY) y = padY; else if (y > padY + ih) y = padY + ih;   // spike clamp
      if (!started || path.s[i].brk) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
    return true;
  };


  /**
   * Repaint the resting stage from a path that may run before the canvas has
   * ever been laid out. _drawIdleCanvas reads the BACKING STORE size, which is
   * set only by resizeCanvases(), so a bare redraw is a no-op on a canvas that
   * has not been sized yet — which is how reference analysis finishing could
   * leave the stage black with a clip loaded.
   */
  PracticeStudio.prototype._idleRepaint = function () {
    if (this.state === 'recording') return;  // only a live take owns the canvas
    try { this.resizeCanvases(); } catch (e) {}
    try { this._drawIdleCanvas(); } catch (e) {}
  };

  /**
   * Return the stage to rest: mounted, live-only chrome cleared, reference
   * redrawn. Called from every path that used to unmount it.
   */
  PracticeStudio.prototype._idleStage = function () {
    if (this.liveSection) {
      this.liveSection.hidden = false;
      this.liveSection.classList.add('saif-practice__live--idle');
    }
    if (this.recTimer) this.recTimer.hidden = true;
    if (this.pitchDisplay) {
      this.pitchDisplay.textContent = '';
      this.pitchDisplay.classList.remove('saif-practice__pitch-display--on');
    }
    try { this.resizeCanvases(); } catch (e) {}
    this._drawIdleCanvas();
  };

  /**
   * The resting stage: the whole reference laid out across the canvas, so the
   * student can see the shape of the phrase before pressing Record. Not the
   * live 6-second scrolling window — at rest there is nothing to scroll.
   * Event-driven only (clip change, transpose, resize, take end, sheet close);
   * a resting instrument must not cost a frame loop.
   */
  PracticeStudio.prototype._drawIdleCanvas = function () {
    var canvas = this.liveCanvas;
    if (!canvas) return;
    // Only a live TAKE owns the canvas. 'starting' is deliberately allowed:
    // the live loop bails unless state === 'recording', so during the count
    // nothing else draws, and showing the stage already framed at t=0 is what
    // makes the count read as a lead-in rather than a curtain.
    if (this.state === 'recording') return;
    var W = canvas.width, H = canvas.height;
    if (!(W > 0 && H > 0)) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    var dpr = window.devicePixelRatio || 1;
    var pts = (this.refPitchData && this.refPitchData.length > 1) ? this.refPitchData : null;
    var i;

    ctx.clearRect(0, 0, W, H);
    drawStageBackground(ctx, W, H, dpr);

    // THE SAME PITCH AXIS THE TAKE WILL USE. drawLiveVisualization caches the
    // reference range on _refRange keyed by sample count; sharing that cache is
    // what stops the note ladder jumping the moment Record is pressed.
    if (pts && (!this._refRange || this._refRange.n !== pts.length)) {
      var rlo = Infinity, rhi = -Infinity, mm;
      for (i = 0; i < pts.length; i++) {
        mm = freqToMidi(pts[i].freq);
        if (mm < rlo) rlo = mm;
        if (mm > rhi) rhi = mm;
      }
      this._refRange = { n: pts.length, min: Math.max(36, Math.floor(rlo) - 3), max: Math.min(96, Math.ceil(rhi) + 3) };
    }
    var minMidi = this._refRange ? this._refRange.min : 50;
    var maxMidi = this._refRange ? this._refRange.max : 74;
    if (maxMidi - minMidi < 10) {
      var rc = (minMidi + maxMidi) / 2;
      minMidi = Math.max(24, Math.floor(rc - 5));
      maxMidi = Math.min(108, Math.ceil(rc + 5));
    }
    function midiToY(midi) {
      var norm = (midi - minMidi) / (maxMidi - minMidi);
      if (norm < 0) norm = 0; else if (norm > 1) norm = 1;
      return H - norm * H;
    }
    var ladderEndX = 34 * dpr;
    drawMaqamLanes(ctx, { minMidi: minMidi, maxMidi: maxMidi, midiToY: midiToY, width: W, height: H, dpr: dpr, gutterX: ladderEndX }, pts);
    drawNoteGrid(ctx, { minMidi: minMidi, maxMidi: maxMidi, midiToY: midiToY, width: W, height: H, dpr: dpr, gutterX: ladderEndX });

    if (!pts) {
      // No clip yet — a dim resting line rather than an empty hole, matching the
      // pending state of the now-bar thumbnail.
      this._idleGeom = null;
      ctx.strokeStyle = 'rgba(232, 200, 119, 0.22)';
      ctx.lineWidth = 1.5 * dpr;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(ladderEndX + 18 * dpr, H * 0.58);
      ctx.lineTo(W - 18 * dpr, H * 0.58);
      ctx.stroke();
      // An empty instrument should say what it is waiting for, not sit blank.
      ctx.fillStyle = 'rgba(250, 247, 242, 0.42)';
      ctx.font = (13 * dpr) + 'px "Plus Jakarta Sans", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText((window.SaifI18n && window.SaifI18n.stage_empty) ||
        'Choose a reference to see it here', (ladderEndX + W) / 2, H * 0.44);
      ctx.textAlign = 'left';
      return;
    }

    // ── the sliding window, identical to the take ───────────────────────────
    // 6 seconds visible, playhead anchored 70% from the left. Anchored on the
    // reference's OWN currentTime, so listening scrolls it and dragging the
    // scrubber scrolls it — one timeline, three ways to move it.
    var windowSec = 6;
    var playheadX = W * 0.70;
    var pxPerSec = W / windowSec;
    var anchor = 0;
    if (this.refAudioEl && isFinite(this.refAudioEl.currentTime)) anchor = this.refAudioEl.currentTime || 0;
    function timeToX(t) { return playheadX + (t - anchor) * pxPerSec; }
    var dur = pts[pts.length - 1].time || 1;
    this._idleGeom = {
      playheadX: playheadX, midiToY: midiToY, dpr: dpr, hasPts: true,
      dur: dur, anchor: anchor, pxPerSec: pxPerSec, windowSec: windowSec,
      timeToX: timeToX, gutter: ladderEndX
    };

    drawClipEdges(ctx, timeToX(0), timeToX(dur), W, H, dpr, ladderEndX);

    // Only the visible run is built — this is why the window costs less than the
    // overview it replaces, however long the surah is. prevT tracks the last
    // sample INCLUDING the ones scrolled off, so a rest that straddles the edge
    // of the window is still a rest.
    var visStart = anchor - windowSec * 0.70;
    var visEnd = anchor + windowSec * 0.30;
    var vis = [], prevT = null;
    for (i = 0; i < pts.length; i++) {
      if (pts[i].time >= visStart && pts[i].time <= visEnd) {
        vis.push({
          x: timeToX(pts[i].time),
          midi: freqToMidi(pts[i].freq),
          brk: !!(vis.length && prevT !== null && (pts[i].time - prevT) > REF_REST_SEC)
        });
      }
      prevT = pts[i].time;
    }

    // ONE BAND PER PHRASE — never across a rest, where nothing is sung and
    // computeScore credits nothing.
    var bi, bq, sm, sj, n0, lo0, hi0, bandY, bandSeg, bandFrom = 0;
    ctx.fillStyle = 'rgba(212, 168, 75, 0.12)';
    for (bi = 1; bi <= vis.length; bi++) {
      if (bi < vis.length && !vis[bi].brk) continue;
      bandSeg = vis.slice(bandFrom, bi);
      bandFrom = bi;
      if (bandSeg.length < 3) continue;
      for (bq = 0; bq < bandSeg.length; bq++) {
        sm = 0; n0 = 0;
        lo0 = Math.max(0, bq - 4); hi0 = Math.min(bandSeg.length - 1, bq + 4);
        for (sj = lo0; sj <= hi0; sj++) { sm += bandSeg[sj].midi; n0++; }
        bandSeg[bq].smooth = sm / n0;
      }
      ctx.beginPath();
      for (bq = 0; bq < bandSeg.length; bq++) {
        bandY = midiToY(bandSeg[bq].smooth + 0.5);
        if (bq === 0) ctx.moveTo(bandSeg[bq].x, bandY); else ctx.lineTo(bandSeg[bq].x, bandY);
      }
      for (bq = bandSeg.length - 1; bq >= 0; bq--) {
        ctx.lineTo(bandSeg[bq].x, midiToY(bandSeg[bq].smooth - 0.5));
      }
      ctx.closePath();
      ctx.fill();
    }

    // ---- the spine ----
    ctx.strokeStyle = '#E8C877';
    ctx.lineWidth = 2.5 * dpr;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowBlur = 6 * dpr;
    ctx.shadowColor = 'rgba(212, 168, 75, 0.45)';
    ctx.beginPath();
    for (i = 0; i < vis.length; i++) {
      if (i === 0 || vis[i].brk) ctx.moveTo(vis[i].x, midiToY(vis[i].midi));
      else ctx.lineTo(vis[i].x, midiToY(vis[i].midi));
    }
    ctx.stroke();
    ctx.shadowBlur = 0;

    this._drawRefPlayhead(ctx, W, H);
  };

  /**
   * Where the reciter is, drawn ON the phrase: a lit column at the current
   * reference time plus a bead sitting on the contour at that instant.
   *
   * Placed by CONTOUR TIME, not by the audio's elapsed fraction — the pitch
   * samples carry real media timestamps, so this stays aligned when the clip
   * has trailing silence, and it is immune to the speed pills (playbackRate
   * changes how fast currentTime advances, not what it means) and to range-fit
   * transposition (which shifts frequency, never time).
   */
  PracticeStudio.prototype._drawRefPlayhead = function (ctx, W, H) {
    var g = this._idleGeom;
    if (!g || !g.hasPts) return;
    // On a sliding stage the playhead does not travel — the phrase travels under
    // it. It is the fixed 70% anchor the window is built around, so it is always
    // drawn: it is what tells the student which instant they are looking at.
    var dpr = g.dpr;
    var t = g.anchor;
    var x = g.playheadX;

    var glow = ctx.createLinearGradient(x - 26 * dpr, 0, x + 26 * dpr, 0);
    glow.addColorStop(0, 'rgba(232, 200, 119, 0)');
    glow.addColorStop(0.5, 'rgba(232, 200, 119, 0.13)');
    glow.addColorStop(1, 'rgba(232, 200, 119, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(x - 26 * dpr, 0, 52 * dpr, H);

    ctx.strokeStyle = 'rgba(245, 226, 168, 0.85)';
    ctx.lineWidth = 1.5 * dpr;
    ctx.beginPath();
    ctx.moveTo(x, 6 * dpr);
    ctx.lineTo(x, H - 6 * dpr);
    ctx.stroke();

    // The bead only exists where the reciter is actually voicing — in a rest
    // between phrases getRefPitchAt returns null, and an invented dot there
    // would read as a held note that is not being sung.
    var f = null;
    try { f = this.getRefPitchAt(t); } catch (e) {}
    if (!f) return;   // a rest: no bead, because there is no note to point at
    var y = g.midiToY(freqToMidi(f));
    ctx.shadowBlur = 10 * dpr;
    ctx.shadowColor = 'rgba(232, 200, 119, 0.85)';
    ctx.fillStyle = '#F5E2A8';
    ctx.beginPath();
    ctx.arc(x, y, 4.5 * dpr, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(255, 253, 246, 0.95)';
    ctx.beginPath();
    ctx.arc(x, y, 1.8 * dpr, 0, Math.PI * 2);
    ctx.fill();
  };

  PracticeStudio.prototype.drawLiveVisualization = function (dataArray, currentFreq, elapsed) {
    var canvas = this.liveCanvas;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var W = canvas.width;
    var H = canvas.height;
    var dpr = window.devicePixelRatio || 1;

    ctx.clearRect(0, 0, W, H);

    // Background — the lit niche (gradient + slow warm lift + film grain)
    drawStageBackground(ctx, W, H, dpr);

    // Determine pitch range — auto-fit to the actual voice + reference range
    // (with padding) instead of a fixed 3-octave window. A wide static range
    // squashes the trace into a sliver and forces a cluttered note ladder;
    // fitting to what's actually sung keeps the view tall and the ladder calm.
    //
    // Reference range is cached separately (keyed by sample count so a clip
    // swap or late-arriving analysis recomputes it) and unioned in every
    // frame — this way a take that starts voice-first still makes room for
    // the gold line when the analysis lands mid-recording.
    if (this.refPitchData && this.refPitchData.length > 0 &&
        (!this._refRange || this._refRange.n !== this.refPitchData.length)) {
      var lo = Infinity, hi = -Infinity;
      for (var i = 0; i < this.refPitchData.length; i++) {
        var m = freqToMidi(this.refPitchData[i].freq);
        if (m < lo) lo = m;
        if (m > hi) hi = m;
      }
      this._refRange = {
        n: this.refPitchData.length,
        min: Math.max(36, Math.floor(lo) - 3),
        max: Math.min(96, Math.ceil(hi) + 3)
      };
    }
    var minMidi, maxMidi;
    if (this._pitchRange) {
      minMidi = this._pitchRange.min;
      maxMidi = this._pitchRange.max;
    } else if (this._refRange) {
      minMidi = this._refRange.min;
      maxMidi = this._refRange.max;
    } else {
      // Nothing known yet (no reference, no voice) — modest default window
      // until the first voiced frame fits it to the singer.
      minMidi = 50; // D3
      maxMidi = 74; // D5
    }
    if (currentFreq > 0) {
      var liveMidi = freqToMidi(currentFreq);
      if (!this._pitchRange) {
        // First voiced frame — fit the window to the voice with padding,
        // then union with the reference range below.
        minMidi = Math.max(24, Math.floor(liveMidi) - 5);
        maxMidi = Math.min(108, Math.ceil(liveMidi) + 5);
      } else {
        // Keep 3 semitones of breathing room around the dot; persist any
        // expansion so the ladder doesn't snap back the next frame.
        var pad = 3;
        if (liveMidi - pad < minMidi) minMidi = Math.max(24, Math.floor(liveMidi - pad));
        if (liveMidi + pad > maxMidi) maxMidi = Math.min(108, Math.ceil(liveMidi + pad));
      }
      this._pitchRange = { min: minMidi, max: maxMidi };
    }
    // Union with the reference's range so the gold line always fits on-screen.
    if (this._refRange) {
      if (this._refRange.min < minMidi) minMidi = this._refRange.min;
      if (this._refRange.max > maxMidi) maxMidi = this._refRange.max;
    }
    // Floor the span at 10 semitones so a single held note doesn't zoom the
    // view into a flat line.
    if (maxMidi - minMidi < 10) {
      var rangeCenter = (minMidi + maxMidi) / 2;
      minMidi = Math.max(24, Math.floor(rangeCenter - 5));
      maxMidi = Math.min(108, Math.ceil(rangeCenter + 5));
    }

    function midiToY(midi) {
      var norm = (midi - minMidi) / (maxMidi - minMidi);
      // Clamp into [0, 1] so a stray out-of-range pitch never draws outside
      // the canvas — dot rendering also adds a radius-margin clamp below.
      if (norm < 0) norm = 0;
      else if (norm > 1) norm = 1;
      return H - norm * H;
    }

    // Time window: 6 seconds visible, playhead anchored at 70% from left
    var windowSec = 6;
    var playheadX = W * 0.70;
    var pixelsPerSec = W / windowSec;
    function timeToX(time) {
      return playheadX + (time - elapsed) * pixelsPerSec;
    }
    var ladderEndX = 34 * dpr;

    // ============ Note grid (left edge) ============
    // At most ~5 evenly spaced gridlines with quiet 10.5px labels — the
    // current note already reads in the pitch display under the canvas,
    // so the ladder only needs to orient, not announce.
    drawMaqamLanes(ctx, {
      minMidi: minMidi, maxMidi: maxMidi, midiToY: midiToY,
      width: W, height: H, dpr: dpr, gutterX: ladderEndX
    }, this.refPitchData);
    drawNoteGrid(ctx, {
      minMidi: minMidi, maxMidi: maxMidi, midiToY: midiToY,
      width: W, height: H, dpr: dpr, gutterX: ladderEndX
    });

    // Keep the scrolling traces out of the label gutter — clipped here,
    // restored after the comet trail below.
    ctx.save();
    ctx.beginPath();
    ctx.rect(ladderEndX, 0, W - ladderEndX, H);
    ctx.clip();

    // ============ Target zone band at the playhead ============
    // Look up the reference pitch at the AUDIO's currentTime (not the user's
    // elapsed recording time) so the karaoke band, gold trail, and refAtNow
    // all follow whatever speed the reference is playing at — and freeze
    // when the user pauses the reference. If no reference audio is loaded
    // (or it hasn't started yet), fall back to elapsed for the lesson-page
    // case where the reference plays in lockstep with recording.
    var refTime = (this.refAudioEl && this.refAudioEl.src && this.refAudioEl.duration > 0)
      ? this.refAudioEl.currentTime
      : elapsed;
    var refAtNow = this.getRefPitchAt(refTime);
    if (refAtNow) {
      var refMidi = freqToMidi(refAtNow);
      var bandTop = midiToY(refMidi + 0.5);
      var bandBottom = midiToY(refMidi - 0.5);
      // Soft horizontal glow band centered on playhead
      var grad = ctx.createLinearGradient(playheadX - 60 * dpr, 0, playheadX + 80 * dpr, 0);
      grad.addColorStop(0, 'rgba(184, 134, 11, 0)');
      grad.addColorStop(0.5, 'rgba(184, 134, 11, 0.22)');
      grad.addColorStop(1, 'rgba(184, 134, 11, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(playheadX - 60 * dpr, bandTop, 140 * dpr, bandBottom - bandTop);
    }

    // ============ Reference pitch line (gold) ============
    // Map reference points into the USER's timeline based on playbackRate.
    // A reference point at refTime=T is heard by the user at elapsed=T/rate
    // (e.g. at 0.5× speed, the reference's t=2s plays out at user's elapsed=4s).
    // So we use timeToX(heardAt) and the same coordinate system as the user's
    // pitch trail — graph stays in sync at any playback speed.
    var rate = (this.refAudioEl && this.refAudioEl.playbackRate) || 1;
    if (this.refPitchData && this.refPitchData.length > 1) {
      var visStart = elapsed - (windowSec * 0.70);
      var visEnd = elapsed + (windowSec * 0.30);
      // Collect the visible run once — used for both the band and the spine.
      var visPts = [];
      for (var i = 0; i < this.refPitchData.length; i++) {
        var pt = this.refPitchData[i];
        var heardAt = pt.time / rate;
        if (heardAt < visStart || heardAt > visEnd) continue;
        visPts.push({
          x: timeToX(heardAt),
          midi: freqToMidi(pt.freq),
          brk: !!(visPts.length && (pt.time - this.refPitchData[i - 1].time) > REF_REST_SEC)
        });
      }
      // Same clip boundary the resting stage draws, so pressing Record does not
      // change the picture — only what is being recorded into it.
      var _refDur = this.refPitchData[this.refPitchData.length - 1].time || 1;
      drawClipEdges(ctx, timeToX(0), timeToX(_refDur), W, H, dpr, ladderEndX);

      // ---- the ±50¢ TARGET BAND: the reference as a zone you sing INTO ----
      // Karaoke apps (Smule, SingStar) win on this — the target is a lane that
      // fills, not a second line to eyeball against. Smoothed over 9 samples
      // because the clip's own vibrato would fray the capsule edges into fur.
      if (visPts.length > 2) {
        // One band per phrase — brk already marks where the reference falls
        // silent, and the spine below has always honoured it. The band did not,
        // so it ran through the rests: a target drawn over blank space, which
        // computeScore never credits.
        var bi, sm, sj, n0, lo0, hi0, bq, bandY, bandSeg, bandFrom = 0;
        ctx.fillStyle = 'rgba(212, 168, 75, 0.12)';
        for (bi = 1; bi <= visPts.length; bi++) {
          if (bi < visPts.length && !visPts[bi].brk) continue;
          bandSeg = visPts.slice(bandFrom, bi);
          bandFrom = bi;
          if (bandSeg.length < 3) continue;
          for (bq = 0; bq < bandSeg.length; bq++) {
            sm = 0; n0 = 0;
            lo0 = Math.max(0, bq - 4); hi0 = Math.min(bandSeg.length - 1, bq + 4);
            for (sj = lo0; sj <= hi0; sj++) { sm += bandSeg[sj].midi; n0++; }
            bandSeg[bq].smooth = sm / n0;
          }
          ctx.beginPath();
          for (bq = 0; bq < bandSeg.length; bq++) {
            bandY = midiToY(bandSeg[bq].smooth + 0.5);
            if (bq === 0) ctx.moveTo(bandSeg[bq].x, bandY); else ctx.lineTo(bandSeg[bq].x, bandY);
          }
          for (bq = bandSeg.length - 1; bq >= 0; bq--) {
            ctx.lineTo(bandSeg[bq].x, midiToY(bandSeg[bq].smooth - 0.5));
          }
          ctx.closePath();
          ctx.fill();
        }
      }
      // ---- the spine: calm, receding, softly lit ----
      ctx.strokeStyle = '#E8C877';
      ctx.lineWidth = 2.5 * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowBlur = 6 * dpr;
      ctx.shadowColor = 'rgba(212, 168, 75, 0.45)';
      ctx.beginPath();
      for (var vi = 0; vi < visPts.length; vi++) {
        if (vi === 0 || visPts[vi].brk) ctx.moveTo(visPts[vi].x, midiToY(visPts[vi].midi));
        else ctx.lineTo(visPts[vi].x, midiToY(visPts[vi].midi));
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // ============ User's recorded pitch trail (color-coded by accuracy) ============
    // Each segment is colored emerald/gold/red based on cents-off from the
    // reference pitch at that moment, so the user sees instantly where they
    // were on/off pitch. Falls back to neutral white when no reference loaded.
    if (this.recPitchData.length > 1) {
      ctx.lineWidth = 2 * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      var hasRef = this.refPitchData && this.refPitchData.length > 1;
      var firstSeen = false;
      var prevPt = null;
      var prevX, prevY, prevColor;
      for (var i = 0; i < this.recPitchData.length; i++) {
        var pt = this.recPitchData[i];
        if (pt.time < elapsed - (windowSec * 0.70)) continue;
        // Draw the causally-despiked disp value (falls back to raw freq for
        // takes recorded before disp existed) — display only; scoring uses freq.
        var ptFreq = (pt.disp != null ? pt.disp : pt.freq);
        var x = timeToX(pt.time);
        var y = midiToY(freqToMidi(ptFreq));
        // Map user-elapsed → reference-time at the current playback rate.
        // At 0.5× speed the user's pt.time=4s corresponds to the reference's
        // 2s mark; without this multiplication the trail goes white half-
        // way through a slow-speed take because we'd read past the clip's
        // end of pitch data even though the audio is still playing.
        var ref = hasRef ? this.getRefPitchAt(pt.time * rate) : null;
        var color = pitchAccuracyColorLit(centsOffFromRef(ptFreq, ref)) || 'rgba(250, 247, 242, 0.78)';
        if (firstSeen && prevPt && (pt.time - prevPt.time) <= 0.15) {
          // Continuous segment — draw colored line from prev to current.
          // Glow only on the newest ~40 samples (where the eye is): a canvas
          // shadow on every one of the ~240 visible segments is a real cost on
          // mid-range phones, and the older trail reads fine crisp.
          var glow = (i > this.recPitchData.length - 40);
          ctx.shadowBlur = glow ? 8 * dpr : 0;
          if (glow) ctx.shadowColor = color;
          ctx.strokeStyle = color;
          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        prevPt = pt; prevX = x; prevY = y; prevColor = color;
        firstSeen = true;
      }
      ctx.shadowBlur = 0;
    }

    // ============ Comet trail head — last ~12 frames of pitch as fading glow ============
    // Adds a sense of motion and "where I am right now" beyond the static line.
    // The trail takes the CURRENT accuracy colour, so "am I on pitch right
    // now" reads from the moving head itself, not just the line behind it.
    var _psHeadCents = (currentFreq > 0 && refAtNow) ? centsOffFromRef(currentFreq, refAtNow) : null;
    var _psHeadLit = pitchAccuracyColorLit(_psHeadCents);
    var _psHeadRgb = null;
    if (_psHeadLit) {
      _psHeadRgb = parseInt(_psHeadLit.slice(1, 3), 16) + ', ' +
                   parseInt(_psHeadLit.slice(3, 5), 16) + ', ' +
                   parseInt(_psHeadLit.slice(5, 7), 16);
    }
    var trailLen = 12;
    var trailStart = Math.max(0, this.recPitchData.length - trailLen);
    for (var i = trailStart; i < this.recPitchData.length; i++) {
      var tp = this.recPitchData[i];
      if (tp.time < elapsed - (windowSec * 0.70)) continue;
      var age = (this.recPitchData.length - 1) - i; // 0 = newest, trailLen = oldest
      var alpha = Math.max(0, 1 - age / trailLen) * 0.55;
      var radius = (1.5 + (1 - age / trailLen) * 3) * dpr;
      ctx.beginPath();
      ctx.arc(timeToX(tp.time), midiToY(freqToMidi(tp.disp != null ? tp.disp : tp.freq)), radius, 0, Math.PI * 2);
      ctx.fillStyle = _psHeadRgb
        ? 'rgba(' + _psHeadRgb + ', ' + alpha + ')'
        : 'rgba(250, 247, 242, ' + alpha + ')';
      ctx.fill();
    }

    // End of the scrolling-trace clip region (started after the note grid).
    ctx.restore();

    // ============ Playhead — a spotlight column, not a ruler ============
    var phGrad = ctx.createLinearGradient(playheadX - 28 * dpr, 0, playheadX, 0);
    phGrad.addColorStop(0, 'rgba(232, 200, 119, 0)');
    phGrad.addColorStop(1, 'rgba(232, 200, 119, 0.075)');
    ctx.fillStyle = phGrad;
    ctx.fillRect(playheadX - 28 * dpr, 0, 28 * dpr, H);
    ctx.strokeStyle = 'rgba(232, 200, 119, 0.24)';
    ctx.lineWidth = 1 * dpr;
    ctx.beginPath();
    ctx.moveTo(playheadX, 0);
    ctx.lineTo(playheadX, H);
    ctx.stroke();
    ctx.fillStyle = 'rgba(232, 200, 119, 0.5)';
    ctx.beginPath();
    ctx.moveTo(playheadX - 4 * dpr, 0);
    ctx.lineTo(playheadX + 4 * dpr, 0);
    ctx.lineTo(playheadX, 5 * dpr);
    ctx.closePath();
    ctx.fill();


    // ============ Always-on volume meter (bottom edge) ============
    // Compute volume on every frame regardless of pitch detection so the user
    // gets immediate visual feedback that the mic is hearing them — even for
    // breath, soft speech, or unpitched sound.
    var rmsVol = 0;
    for (var k = 0; k < dataArray.length; k++) rmsVol += dataArray[k] * dataArray[k];
    rmsVol = Math.sqrt(rmsVol / dataArray.length);
    var volNorm = Math.min(1, rmsVol * 6); // amplify so quiet voices register visibly
    var meterH = 4 * dpr;
    var meterY = H - meterH - 2 * dpr;
    var meterPadX = 8 * dpr;
    // Track
    ctx.fillStyle = 'rgba(250, 247, 242, 0.06)';
    ctx.fillRect(meterPadX, meterY, W - meterPadX * 2, meterH);
    // Fill — gold tint, scales with volume
    var meterFillW = (W - meterPadX * 2) * volNorm;
    if (meterFillW > 0) {
      ctx.fillStyle = volNorm > 0.7
        ? 'rgba(212, 168, 75, 0.85)'  // hot — at the loud end
        : 'rgba(184, 134, 11, 0.55)'; // normal speaking
      ctx.fillRect(meterPadX, meterY, meterFillW, meterH);
    }

    // ============ Current pitch dot at the playhead ============
    // Three-state rendering:
    //   1. Pitched + audible  → solid colored dot at correct Y (in-tune gold or off-pitch white)
    //   2. Audible but unpitched → faded dot at last known pitch position (so user sees it didn't disappear, just lost lock)
    //   3. Truly silent  → no dot (volume meter still shows nothing)
    var rms = rmsVol; // reuse from volume meter
    if (currentFreq > 0) {
      var midi = freqToMidi(currentFreq);
      var dotY = midiToY(midi);
      this._lastPitchDotY = dotY;
      this._lastPitchAt = elapsed;

      // Compute "in tune" relative to reference at this moment
      var inTune = false;
      var deltaSemis = 0;
      if (refAtNow) {
        var refM = freqToMidi(refAtNow);
        deltaSemis = midi - refM;
        inTune = Math.abs(deltaSemis) <= 0.5;
      }

      // Track sustained in-tune time so we can reward the user with a
      // celebratory badge for holding a note (rather than just briefly hitting it).
      if (inTune) {
        if (this._inTuneSinceMs == null) this._inTuneSinceMs = performance.now();
      } else {
        this._inTuneSinceMs = null;
      }

      var dotSize = 7 + Math.min(7, rms * 70);
      var headBreath = (inTune && !prefersReducedMotion())
        ? (1 + 0.18 * (0.5 + 0.5 * Math.sin(performance.now() / 255)))
        : 1;

      // Keep the entire dot (including its outer glow) on-canvas so it never
      // gets clipped by the bottom/top edge for very low / very high pitches.
      var maxRadius = (dotSize + 9) * dpr * 1.18;
      if (dotY < maxRadius) dotY = maxRadius;
      else if (dotY > H - maxRadius) dotY = H - maxRadius;

      // Outer glow — carries the accuracy colour and BREATHES while locked, so
      // holding pitch is rewarded over a large area instead of being reported
      // in a corner. Radius is folded into maxRadius above so it never clips.
      var glowRgb = _psHeadRgb || '250, 247, 242';
      ctx.beginPath();
      ctx.arc(playheadX, dotY, (dotSize + 9) * dpr * headBreath, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(' + glowRgb + ', ' + (inTune ? 0.42 : 0.16) + ')';
      ctx.fill();

      // Inner dot — emissive twin of the trail colour
      ctx.beginPath();
      ctx.arc(playheadX, dotY, dotSize * dpr, 0, Math.PI * 2);
      ctx.fillStyle = _psHeadLit || 'rgba(250, 247, 242, 0.65)';
      ctx.fill();

      // Directional arrow if off-pitch and we have a target
      if (refAtNow && !inTune && Math.abs(deltaSemis) > 0.6) {
        ctx.fillStyle = _psHeadLit || 'rgba(212, 168, 75, 0.85)';
        ctx.font = 'bold ' + (16 * dpr) + 'px Plus Jakarta Sans, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        var arrowY = dotY + (deltaSemis > 0 ? 26 * dpr : -26 * dpr);
        ctx.fillText(deltaSemis > 0 ? '↓' : '↑', playheadX, arrowY);
      }

      // Update note label
      if (this.pitchDisplay) {
        var noteText = freqToNote(currentFreq);
        var hzText = Math.round(currentFreq) + ' Hz';
        this.pitchDisplay.textContent = (inTune ? '✓ ' : '') + noteText + ' · ' + hzText;
        this.pitchDisplay.classList.toggle('saif-practice__pitch-display--on', inTune);
      }
    } else if (rms > 0.005 && this._lastPitchDotY != null && elapsed - (this._lastPitchAt || 0) < 1.5) {
      // Audible but unpitched (breath, low volume, momentary detection drop) —
      // hold the dot at its last known position with reduced opacity so the
      // user sees their voice didn't vanish, just lost pitch lock briefly.
      var pulseSize = (5 + Math.min(8, rms * 80)) * dpr;
      ctx.beginPath();
      ctx.arc(playheadX, this._lastPitchDotY, pulseSize, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(250, 247, 242, 0.25)';
      ctx.fill();
      if (this.pitchDisplay) {
        this.pitchDisplay.textContent = (window.SaifI18n && window.SaifI18n.listening) || 'Listening...';
        this.pitchDisplay.classList.remove('saif-practice__pitch-display--on');
      }
    } else if (this.pitchDisplay && rms < 0.003) {
      // Truly silent — clear the label so it doesn't show stale info
      this.pitchDisplay.textContent = '';
      this.pitchDisplay.classList.remove('saif-practice__pitch-display--on');
    }

    // ============ Sustained in-tune badge (top-right) ============
    // Shows after holding the right pitch for >=2 seconds. Encourages
    // sustained accuracy rather than just briefly hitting the target.
    if (this._inTuneSinceMs) {
      var heldMs = performance.now() - this._inTuneSinceMs;
      if (heldMs >= 2000) {
        var fadeIn = Math.min(1, (heldMs - 2000) / 250);
        var heldSec = Math.floor(heldMs / 1000);
        ctx.save();
        ctx.globalAlpha = fadeIn;
        var badgeText = '✓ In tune · ' + heldSec + 's';
        ctx.font = 'bold ' + (12 * dpr) + 'px Plus Jakarta Sans, sans-serif';
        var textWidth = ctx.measureText(badgeText).width;
        var bx = W - textWidth - 24 * dpr;
        var by = 10 * dpr;
        // Pill background
        var pad = 8 * dpr;
        ctx.fillStyle = 'rgba(45, 106, 90, 0.92)';
        var pillX = bx - pad;
        var pillW = textWidth + pad * 2;
        var pillH = 20 * dpr;
        // Rounded rect (use arcs for compatibility — roundRect not in older Safari)
        var r = pillH / 2;
        ctx.beginPath();
        ctx.moveTo(pillX + r, by);
        ctx.arcTo(pillX + pillW, by, pillX + pillW, by + pillH, r);
        ctx.arcTo(pillX + pillW, by + pillH, pillX, by + pillH, r);
        ctx.arcTo(pillX, by + pillH, pillX, by, r);
        ctx.arcTo(pillX, by, pillX + pillW, by, r);
        ctx.closePath();
        ctx.fill();
        // Text
        ctx.fillStyle = '#FAF7F2';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(badgeText, bx, by + pillH / 2);
        ctx.restore();
      }
    }
  };

  /**
   * Render the post-recording pitch comparison. Optional `playbackTime` (in
   * seconds, expressed against the user-recording's timeline) draws a moving
   * playhead + a dot on the user's contour at that time, plus brightens the
   * trail behind the playhead and fades the trail ahead — so the visualization
   * "replays" alongside audio playback.
   *
   * Gaps in the pitch data (silence, breath, no pitch detected for >150ms)
   * are NOT connected with a line — drawing through them would imply the
   * student was on-pitch during those moments, which is misleading.
   */
  // DISPLAY-ONLY smoothing for the recorded contour in the compare graph. The score
  // and the stored take are never touched (recPitchData is left alone), so scoring is
  // unchanged. Per continuity run (split at >0.15 s silence gaps):
  //   1. ONSET/RELEASE repair — right after a silence (and right before the next) the
  //      detector throws a big attack/release spike; anchor the first/last up-to-3
  //      frames to the note's SETTLED pitch measured just past the attack. A LOCAL
  //      anchor means a genuine low/high onset note is never mistaken for a spike.
  //   2. MEDIAN despike (±2) — removes 1-2 frame transients from sudden word/consonant
  //      changes while preserving a genuine SUSTAINED leap (a median tracks a real step).
  //   3. Weighted moving average (±2) — calms the residual frame-to-frame jitter of a
  //      steady voice so the line reads smooth and sits cleanly over the reference.
  // Preserves .time and .disp; returns a NEW array.
  function viewSmoothContour(data) {
    if (!data || data.length < 3) return data;
    var GAP = 0.15, n = data.length, midi = new Array(n), out = new Array(n), i, j, lo, hi, q;
    for (i = 0; i < n; i++) midi[i] = freqToMidi(data[i].freq);
    function med(arr) { var b = arr.slice().sort(function (x, y) { return x - y; }); return b[b.length >> 1]; }
    var runStart = 0;
    for (i = 0; i <= n; i++) {
      var boundary = (i === n) || (i > 0 && (data[i].time - data[i - 1].time > GAP));
      if (!boundary) continue;
      var s = runStart, e = i - 1, len = e - s + 1;
      if (len < 3) { for (j = s; j <= e; j++) out[j] = data[j]; runStart = i; continue; }
      var run = new Array(len);
      for (j = 0; j < len; j++) run[j] = midi[s + j];
      if (len >= 5) {
        var oi = run.slice(3, Math.min(9, len));
        if (oi.length >= 2) { var oa = med(oi); for (j = 0; j < Math.min(3, len); j++) if (Math.abs(run[j] - oa) > 2.5) run[j] = oa; }
        var ri = run.slice(Math.max(0, len - 9), Math.max(1, len - 3));
        if (ri.length >= 2) { var ra = med(ri); for (j = Math.max(0, len - 3); j < len; j++) if (Math.abs(run[j] - ra) > 2.5) run[j] = ra; }
      }
      var mv = new Array(len);
      for (j = 0; j < len; j++) { lo = Math.max(0, j - 2); hi = Math.min(len - 1, j + 2); var w = []; for (q = lo; q <= hi; q++) w.push(run[q]); mv[j] = med(w); }
      var wts = [1, 2, 4, 2, 1];
      for (j = 0; j < len; j++) {
        lo = Math.max(0, j - 2); hi = Math.min(len - 1, j + 2);
        var sum = 0, wt = 0;
        for (q = lo; q <= hi; q++) { var ww = wts[q - j + 2]; sum += mv[q] * ww; wt += ww; }
        out[s + j] = { time: data[s + j].time, freq: midiToFreq(sum / wt), disp: data[s + j].disp };
      }
      runStart = i;
    }
    return out;
  }

  PracticeStudio.prototype.drawComparison = function (playbackTime) {
    var canvas = this.compareCanvas;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var W = canvas.width;
    var H = canvas.height;
    var dpr = window.devicePixelRatio || 1;
    var GAP_SEC = 0.15; // anything beyond this is treated as silence — break the line
    // Adaptive line-break gap: a SAVED take is downsampled to ~300 points, so a
    // long take (>~45s) ends up with points spaced past GAP_SEC — which breaks
    // EVERY segment and renders the replayed contour invisible (confirmed:
    // test-replay.mjs, a 90s take dropped to 24% trail density). Widen the gap to
    // track the contour's own sampling (median dt) so sparse replayed contours
    // reconnect, while dense live contours (dt≈16–50ms) stay at 0.15s and real
    // silences still break the line.
    function computeGap(arr) {
      if (!arr || arr.length < 3) return GAP_SEC;
      var dts = [];
      for (var gi = 1; gi < arr.length; gi++) { var gdt = arr[gi].time - arr[gi - 1].time; if (gdt > 0) dts.push(gdt); }
      if (!dts.length) return GAP_SEC;
      dts.sort(function (a, b) { return a - b; });
      return Math.max(GAP_SEC, 2.5 * dts[dts.length >> 1]);
    }

    ctx.clearRect(0, 0, W, H);
    drawStageBackground(ctx, W, H, dpr);

    var recData = (this._viewSmoothing === false) ? this.recPitchData : viewSmoothContour(this.recPitchData);
    var refData = this.refPitchData;
    var recGap = computeGap(recData); // widens for downsampled (replayed) contours; stays 0.15 for live
    var refGap = computeGap(refData);
    var recMaxTime = recData.length > 0 ? recData[recData.length - 1].time : 0;
    var refMaxTime = refData.length > 0 ? refData[refData.length - 1].time : 0;
    // ONE shared time axis. Each contour used to be stretched to its own
    // duration, so a take shorter than the clip (most takes) visually
    // misaligned "your line" against the gold line — the whole point of the
    // graph. The reference is mapped into the USER's heard timeline (ref time
    // ÷ playback rate), then both plot on max(user, reference-as-heard).
    var cmpRate = this.recordingPlaybackRate || 1;
    // Reuse the score's global alignment offset (reference-time seconds) so the
    // gold line AND the accuracy coloring sit on the same timeline the score
    // was computed on. A student sample at user-time u maps to reference-time
    // u·rate + alignOff; inversely, a reference sample at tr is HEARD at
    // user-time (tr − alignOff)/rate. Unset/no-reference → 0 (unchanged chart).
    var alignOff = this._scoreAlignOffset || 0;
    var refHeardMax = (refMaxTime - alignOff) / cmpRate;
    var axisMax = Math.max(recMaxTime, refHeardMax);

    // Compute Y-axis range from the actual data (both user + reference) so
    // low voices don't get squashed against the bottom edge or high voices
    // against the top. 3 semitones of padding either side gives breathing room.
    var rangeMin = Infinity, rangeMax = -Infinity;
    for (var i = 0; i < recData.length; i++) {
      var m = freqToMidi(recData[i].freq);
      if (m < rangeMin) rangeMin = m;
      if (m > rangeMax) rangeMax = m;
    }
    for (var i = 0; i < refData.length; i++) {
      var m = freqToMidi(refData[i].freq);
      if (m < rangeMin) rangeMin = m;
      if (m > rangeMax) rangeMax = m;
    }
    if (!isFinite(rangeMin) || !isFinite(rangeMax)) {
      rangeMin = 48; rangeMax = 84; // fallback to default if no data
    } else {
      rangeMin = Math.floor(rangeMin) - 3;
      rangeMax = Math.ceil(rangeMax) + 3;
      // Always show at least an octave so the chart isn't a flat line for
      // perfectly held single notes.
      if (rangeMax - rangeMin < 12) {
        var center = (rangeMin + rangeMax) / 2;
        rangeMin = Math.floor(center - 6);
        rangeMax = Math.ceil(center + 6);
      }
    }
    var rangeSpan = rangeMax - rangeMin;

    function midiToY(midi) {
      var norm = (midi - rangeMin) / rangeSpan;
      if (norm < 0) norm = 0;
      else if (norm > 1) norm = 1;
      return H - norm * H;
    }

    // ============ Note grid (left edge) ============
    // Same quiet grid as the live canvas: at most ~5 evenly spaced lines,
    // small labels at ~38% ink confined to the left gutter. Contours plot
    // to the right of the gutter so the labels never collide with traces.
    var ladderEndX = 34 * dpr;
    var plotW = W - ladderEndX;
    drawMaqamLanes(ctx, {
      minMidi: rangeMin, maxMidi: rangeMax, midiToY: midiToY,
      width: W, height: H, dpr: dpr, gutterX: ladderEndX
    }, refData);
    drawNoteGrid(ctx, {
      minMidi: rangeMin, maxMidi: rangeMax, midiToY: midiToY,
      width: W, height: H, dpr: dpr, gutterX: ladderEndX
    });
    function timeToX(time, maxTime) {
      return ladderEndX + (time / maxTime) * plotW;
    }

    /**
     * Draw a pitch contour as connected line segments, breaking the path
     * whenever consecutive samples are >GAP_SEC apart (silence). Optional
     * splitTime drops the path into "before" and "after" buckets so the
     * caller can color them differently for the replay playhead.
     */
    function drawContour(data, maxTime, color, lineW, timeScale, timeShift, gap) {
      var brk = gap || GAP_SEC;
      if (data.length < 2 || maxTime <= 0) return;
      var scale = timeScale || 1;
      var shift = timeShift || 0; // heard-time seconds, added after scaling (uniform → gap logic unaffected)
      ctx.strokeStyle = color;
      ctx.lineWidth = lineW * dpr;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      var prev = null;
      for (var i = 0; i < data.length; i++) {
        var pt = data[i];
        var px = timeToX(pt.time * scale + shift, maxTime);
        var py = midiToY(freqToMidi(pt.freq));
        if (!prev || (pt.time - prev.time) > brk) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
        prev = pt;
      }
      ctx.stroke();
    }

    // Reference contour — always full opacity (the karaoke target), mapped
    // into the user's heard timeline on the SHARED axis (see axisMax above).
    drawContour(refData, axisMax, 'rgba(232, 200, 119, 0.42)', 3, 1 / cmpRate, -alignOff / cmpRate, refGap);
    // The "Reference" / "Your Recording" captions that used to be painted here
    // are gone: the DOM legend under the chart already names both series, with
    // better contrast and in text a screen reader can read.

    // User recording contour — color-coded per segment by accuracy vs reference
    // (emerald in tune, gold close, red off). When playbackTime is provided,
    // segments BEFORE the playhead render at full opacity, AFTER fade to ~30%.
    // Self-contained drawing (doesn't reuse drawContour because that uses a
    // single color for the whole path).
    var playing = typeof playbackTime === 'number' && recMaxTime > 0;
    var hasRefForColoring = refData.length > 1;
    var self = this;
    function drawUserContour(opacityMultiplier, splitBeforeTime) {
      if (recData.length < 2 || recMaxTime <= 0) return;
      ctx.lineWidth = 2.5 * dpr;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      var prev = null;
      var prevX, prevY;
      for (var i = 0; i < recData.length; i++) {
        var p = recData[i];
        if (splitBeforeTime != null) {
          // "before" pass = points up to splitBeforeTime; "after" pass = the rest
          if (opacityMultiplier > 0.5 && p.time > splitBeforeTime) { prev = null; continue; }
          if (opacityMultiplier <= 0.5 && p.time < splitBeforeTime) { prev = null; continue; }
        }
        var px = timeToX(p.time, axisMax);
        var py = midiToY(freqToMidi(p.freq));
        if (prev && (p.time - prev.time) <= recGap) {
          // Color this segment by how close the user was to the reference.
          // Multiply by the rate that was active during recording so the
          // user-time → reference-time mapping is correct at any playback
          // speed. (See note in drawLiveVisualization for the same fix.)
          var recRate = self.recordingPlaybackRate || 1;
          var ref = hasRefForColoring ? self.getRefPitchAt(p.time * recRate + alignOff) : null;
          var color = pitchAccuracyColorLit(centsOffFromRef(p.freq, ref)) || '#34C79A';
          if (opacityMultiplier < 1) {
            // Fade by parsing hex → rgba
            var r = parseInt(color.slice(1, 3), 16);
            var g = parseInt(color.slice(3, 5), 16);
            var b = parseInt(color.slice(5, 7), 16);
            color = 'rgba(' + r + ',' + g + ',' + b + ',' + (0.85 * opacityMultiplier).toFixed(2) + ')';
          }
          ctx.strokeStyle = color;
          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(px, py);
          ctx.stroke();
        }
        prev = p; prevX = px; prevY = py;
      }
    }

    if (recData.length > 1 && recMaxTime > 0) {
      if (playing) {
        drawUserContour(0.35, playbackTime); // remaining (faded)
        drawUserContour(1, playbackTime);    // played (full)
      } else {
        drawUserContour(1, null);
      }
    }

    // Replay playhead + moving dot
    if (playing) {
      var headX = timeToX(playbackTime, axisMax);
      // Vertical playhead line
      ctx.strokeStyle = 'rgba(212, 168, 75, 0.55)';
      ctx.lineWidth = 1.5 * dpr;
      ctx.setLineDash([4 * dpr, 4 * dpr]);
      ctx.beginPath();
      ctx.moveTo(headX, 0);
      ctx.lineTo(headX, H);
      ctx.stroke();
      ctx.setLineDash([]);

      // Moving dot at the user's pitch at this time. Find the closest
      // pitch sample within the gap window — if there's none, the user was
      // silent here so don't draw a dot at all (matches the broken-line rule).
      var nearest = null;
      var nearestGap = recGap; // widen the dot's snap window for sparse (replayed) contours too
      for (var j = 0; j < recData.length; j++) {
        var dt = Math.abs(recData[j].time - playbackTime);
        if (dt < nearestGap) { nearest = recData[j]; nearestGap = dt; }
      }
      if (nearest) {
        var dotX = timeToX(nearest.time, axisMax);
        var dotY = midiToY(freqToMidi(nearest.freq));
        // Outer glow
        ctx.beginPath();
        ctx.arc(dotX, dotY, 12 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(212, 168, 75, 0.30)';
        ctx.fill();
        // Inner dot
        ctx.beginPath();
        ctx.arc(dotX, dotY, 5 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = '#D4A84B';
        ctx.fill();
      }
    }
  };

  /* ==============================================
     REFERENCE AUDIO ANALYSIS
     ============================================== */
  PracticeStudio.prototype.analyzeReference = function () {
    var self = this;
    if (!this.refUrl) return Promise.resolve();

    // Race-guard: increment a sequence number so a stale .then() (from a
    // previous loadReference call) can't overwrite refPitchData if the
    // user clicked clip A then clip B in quick succession.
    if (self._loadSeq == null) self._loadSeq = 0;
    self._loadSeq += 1;
    var mySeq = self._loadSeq;

    // Fast path — pre-computed pitch JSON. The maqam-478 dataset clips never
    // change, so scripts/build-maqam-pitch-data.js runs offline once and ships
    // the results in maqam-478-pitch-data.json. When the studio loads one of
    // those clips we read the array directly, skipping fetch + decode + the
    // O(n²) autocorrelation loop entirely. Saves 1-3s per clip click on the
    // main thread. Falls back to live decode for non-dataset clips (e.g.
    // user-uploaded recordings or future content).
    var precomputed = lookupPrecomputedPitch(this.refUrl);
    if (precomputed) {
      slog('[saif-practice] using pre-computed pitch data —', precomputed.length, 'points');
      if (mySeq === self._loadSeq) {
        self.refPitchData = self._applyRefTranspose(coherReferenceContour(medianSmoothPitch(precomputed)));
        // The contour only exists now — repaint the resting stage, which was
        // drawn in its "no clip" state while this analysis was still running.
        self._idleRepaint();
        self._refreshMaqamLabel();
      }
      return Promise.resolve();
    }

    // Core-manifest miss on a dataset clip → try the per-maqam extension
    // file before paying for a live decode. One small fetch per family per
    // page; merged into the manifest so subsequent clips hit synchronously.
    var refUrlForExt = this.refUrl;
    return fetchExtendedPitch(refUrlForExt).then(function (extended) {
      if (extended) {
        slog('[saif-practice] using extended pitch data —', extended.length, 'points');
        if (mySeq === self._loadSeq) {
          self.refPitchData = self._applyRefTranspose(coherReferenceContour(medianSmoothPitch(extended)));
        // The contour only exists now — repaint the resting stage, which was
        // drawn in its "no clip" state while this analysis was still running.
        self._idleRepaint();
          self._refreshMaqamLabel();
        }
        return;
      }
      return liveDecodeReference();
    });

    function liveDecodeReference() {

    // Browsers cap concurrent AudioContexts at ~6. Without an explicit
    // close() the contexts orphan, so rapid clip swaps eventually throw
    // "Failed to construct 'AudioContext'". Wrap the decode-and-analyze
    // pipeline in try/finally semantics so the context closes whether
    // decoding succeeded or failed.
    var ctx = new (window.AudioContext || window.webkitAudioContext)();
    function closeCtx() {
      try { ctx.close(); } catch (e) { /* already closed / older browser */ }
    }

    return fetch(refUrlForExt)
      .then(function (r) {
        // A 403/404/500 used to flow into decodeAudioData as an HTML error
        // page and die in the swallow-all catch — the student just saw a
        // studio that silently never drew the gold line.
        if (!r.ok) throw new Error('reference fetch HTTP ' + r.status);
        return r.arrayBuffer();
      })
      .then(function (buffer) {
        return ctx.decodeAudioData(buffer);
      })
      .then(function (audioBuffer) {
        var data = toMonoNormalized(audioBuffer);
        var sampleRate = audioBuffer.sampleRate;
        var chunkSize = 2048;
        var pitchData = [];

        for (var i = 0; i < data.length - chunkSize; i += chunkSize / 2) {
          var chunk = data.slice(i, i + chunkSize);
          var freq = detectPitch(chunk, sampleRate);
          var time = i / sampleRate;
          if (freq > 0) {
            pitchData.push({ time: time, freq: freq });
          }
        }

        // Bail out if a newer loadReference has been issued while we were
        // decoding — clobbering refPitchData with stale data would mismatch
        // the karaoke target against the now-current reference clip.
        if (mySeq !== self._loadSeq) {
          slog('[saif-practice] analyzeReference seq', mySeq,
            'stale (current=' + self._loadSeq + ') — discarding');
          return;
        }

        self.refPitchData = self._applyRefTranspose(coherReferenceContour(medianSmoothPitch(pitchData)));
        // The contour only exists now — repaint the resting stage, which was
        // drawn in its "no clip" state while this analysis was still running.
        self._idleRepaint();
        // Once pitch data is in, run maqam detection. Centralized here so all
        // call paths (loadReference, startRecording, initial preload) share
        // the same labelling step. Heuristic — see detectMaqam() docs.
        self._refreshMaqamLabel();
      })
      .catch(function (err) {
        // Scoring adapts (no reference = no grade), but TELL the student —
        // an invisible failure here just looks like a broken studio.
        console.warn('[saif-practice] reference analysis failed:', err && err.message);
        if (mySeq === self._loadSeq) {
          self.setStatus('Couldn’t load the reference clip — check your connection, then reopen the clip.');
        }
      })
      .then(closeCtx, closeCtx); // finally — always close
    }
  };

  /**
   * Begin playback of a history-list recording AND replay its pitch graph
   * in sync. The pitchData was serialized into the recording metafield at
   * save time, so it round-trips back here from sync.recordings.list().
   * We swap it into self.recPitchData, reveal the result panel on the
   * Compare tab, hook timeupdate → drawComparison so the graph plays
   * along with the audio, and update the row's button to a pause icon
   * while playing. On end / pause we restore the play icon.
   */
  /**
   * Stop EVERY audio source the studio can have playing — the recorded-take
   * transport, a history-list take, and the reference/original clip. Called
   * when the practice modal closes (the close handler previously missed the
   * history audio, so a recording kept playing after close).
   */
  PracticeStudio.prototype.stopAllAudio = function () {
    if (this._playbackAudio) { try { this._playbackAudio.pause(); } catch (e) {} }
    if (this._historyAudio) {
      try { this._historyAudio.pause(); } catch (e) {}
      try { this._historyResetButton(); } catch (e) {}
    }
    if (this.refAudioEl) {
      try { this.refAudioEl.pause(); } catch (e) {}
      if (this.refPlayBtn) this.refPlayBtn.classList.remove('saif-practice__btn--active');
    }
  };

  /**
   * Put the live reference back after a history replay temporarily swapped it
   * to the replayed take's clip. Called before a new recording and on clip
   * switch, so a fresh take always scores against the CURRENT clip.
   */
  PracticeStudio.prototype._restoreReferenceSnapshot = function () {
    // A new take (or clip switch) supersedes any history replay that had
    // commandeered the shared contour — re-enable Retry-save / AI-feedback.
    this._historyReplayActive = false;
    if (!this._refSnapshot) return;
    this.refUrl = this._refSnapshot.refUrl;
    this.refPitchData = this._refSnapshot.refPitchData;
    this.refClipTitle = this._refSnapshot.refClipTitle;
    if (this.refAudioEl && this._refSnapshot.refSrc != null) {
      try {
        if (this.refAudioEl.getAttribute('src') !== this._refSnapshot.refSrc) {
          this.refAudioEl.src = this._refSnapshot.refSrc;
        }
      } catch (e) {}
    }
    this._refSnapshot = null;
  };

  PracticeStudio.prototype._startHistoryPlayback = function (playBtn, url, recId) {
    var self = this;

    slog('[saif-practice] history play start — url:', url, '| recId:', recId);
    if (!url) {
      console.warn('[saif-practice] history play: url is empty — recording has no playback URL');
      return;
    }

    // CRITICAL: audio.play() must be called synchronously inside the
    // user-gesture click handler — if we await a fetch first the browser
    // drops the gesture-trust and blocks playback (Chrome mobile + Safari
    // are strict about this). Create + play right now; enrich with
    // pitchData / graph replay async after the list resolves.
    var audio = new Audio(url);
    setPreservePitch(audio);
    self._historyAudio = audio;
    self._historyPlayingId = recId;
    self._historyButton = playBtn;
    self._setHistoryButtonPlaying(playBtn, true);

    // Hand this audio to the PLAYBACK TRANSPORT rather than running a second,
    // parallel player. _ensurePlaybackAudio now returns it, so play/pause,
    // skip, scrub, the time readout and the graph playhead all drive this take.
    if (self._playbackAudio && self._playbackAudio !== audio) {
      try { self._playbackAudio.pause(); } catch (e) {}
    }
    self._playbackAudio = audio;
    self._playbackSourceId = recId;
    try { self._wirePlayerEvents(audio); } catch (e) {}

    audio.addEventListener('play',  function () { self._setHistoryButtonPlaying(playBtn, true); });
    audio.addEventListener('pause', function () { self._setHistoryButtonPlaying(playBtn, false); });
    audio.addEventListener('ended', function () { self._historyResetButton(); });
    audio.addEventListener('error', function () {
      var code = audio.error && audio.error.code;
      var msg = audio.error && audio.error.message;
      console.error('[saif-practice] history audio element error — code:', code, 'msg:', msg, 'url:', url);
      if (window.SaifTrack) window.SaifTrack('practice_audio_load_failed', {
        code: code || null,
        message: msg || '',
        url: url
      });
      self._historyResetButton();
    });

    // Open the review for THIS take, synchronously on the tap — not later,
    // inside a fetch callback, which is what used to make the sheet appear a
    // beat late or not at all.
    self._reviewSavedTake(recId);

    var playPromise = audio.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(function () {
        slog('[saif-practice] history play() resolved — playing:', !audio.paused);
      }).catch(function (err) {
        console.error('[saif-practice] history play() rejected:', err && err.name, err && err.message);
        self._historyResetButton();
      });
    }

    // Async enrichment — find the recording in the cached list (so the
    // user's pitch contour can replay in the Compare tab in sync with
    // the audio). If the list call fails, audio still plays normally;
    // just no graph replay for this take.
    var sync = window.SaifSync;
    if (!sync || !sync.recordings) return;
    // full:true — this is the one place we need the heavy pitch-contour
    // arrays (to replay the comparison graph for this saved take).
    sync.recordings.list({ full: true }).then(function (r) {
      // Bail if the user already started a different recording or paused
      // before we got back from the list call.
      if (self._historyAudio !== audio) return;

      var rec = (r && r.recordings || []).find(function (x) { return x.id === recId; });
      if (!rec || !Array.isArray(rec.pitchData) || !rec.pitchData.length) {
        self._setHistoryCompareState('unavailable',
          'A detailed pitch trace is not available for this take. Its score and audio are still available.');
        return;
      }

      // Keep the richer record in memory. A second replay in this session can
      // then reuse the contours immediately while normal history stays light.
      for (var cacheIdx = 0; cacheIdx < (self._historyItems || []).length; cacheIdx++) {
        if (self._historyItems[cacheIdx].id === recId) {
          self._historyItems[cacheIdx] = rec;
          break;
        }
      }

      // Hydrate recPitchData and reveal the Compare tab so the graph
      // re-draws as the audio plays. Server stores { t, f }; converter
      // back to the { time, freq } shape drawComparison expects.
      // Mark that a history replay has commandeered the shared contour, so the
      // live-take actions (Retry-save, AI-feedback) refuse to persist/submit
      // THIS saved take's data. Cleared by _restoreReferenceSnapshot (new take).
      self._historyReplayActive = true;
      self.recPitchData = rec.pitchData.map(function (p) {
        return { time: p.t, freq: p.f };
      });
      // Saved takes don't record which playback speed they were made at —
      // assume 1× for the replay axis/coloring rather than inheriting a stale
      // rate from whatever the LAST live take happened to use. Same for the
      // score's alignment offset: it belongs to the last LIVE take, so clear it
      // (replay draws on the un-shifted timeline; a stale offset would slide the
      // gold line off this take's trail).
      self.recordingPlaybackRate = 1;
      self._scoreAlignOffset = 0;
      // Snapshot the LIVE reference once before we overwrite it for this
      // replay. Without this, replaying a saved take from a DIFFERENT clip
      // would leave self.refUrl/refPitchData/refAudioEl pointing at that take's
      // clip, so the NEXT recording would be scored + visualized against the
      // wrong reference. _restoreReferenceSnapshot() (called at the top of
      // startRecording / on clip switch) puts the real reference back.
      if (!self._refSnapshot) {
        self._refSnapshot = {
          refUrl: self.refUrl,
          refPitchData: self.refPitchData,
          refClipTitle: self.refClipTitle,
          refSrc: self.refAudioEl ? self.refAudioEl.getAttribute('src') : null
        };
      }
      // Hydrate the REFERENCE contour so the comparison graph draws the gold
      // reference line for THIS saved take (not a stale one), and load the
      // take's original clip so the student can replay what they imitated.
      self.refPitchData = (Array.isArray(rec.referencePitchData) ? rec.referencePitchData : []).map(function (p) {
        return { time: p.t, freq: p.f };
      });
      if (rec.referenceUrl) {
        self.refUrl = rec.referenceUrl;
        if (rec.clipTitle) self.refClipTitle = rec.clipTitle;
        if (self.refAudioEl) {
          try { self.refAudioEl.pause(); } catch (e) {}
          if (self.refAudioEl.getAttribute('src') !== rec.referenceUrl) {
            try { self.refAudioEl.src = rec.referenceUrl; } catch (e) {}
          }
        }
        // Reveal the reference player so "Listen to Reference" plays the
        // original. (Inside the maqam modal the studio's own reference row is
        // hidden in favour of the modal clip bar, so this is a no-op there.)
        if (self.refSection) self.refSection.hidden = false;
      }
      // Deliberately NOT opening the result sheet here. This runs inside the
      // pitch-data fetch, so swapping sheets now would (a) happen a beat after
      // the tap, (b) take away the row's pause button — the only control the
      // student has over the audio they just started — and (c) never happen at
      // all when the fetch fails, leaving audio playing with no player. The
      // contour is still hydrated below, so the graph is ready if they open the
      // result themselves.
      self._setHistoryCompareState('ready');
      if (self.resultSection) self.resultSection.hidden = false;
      // Finishing this async load must not yank the student off the Score tab.
      // Refresh only when Compare is already the visible panel.
      var comparePanel = self.container.querySelector('[data-result-panel=compare]');
      if (comparePanel && !comparePanel.hidden) {
        try { self.resizeCanvases(); } catch (e) {}
        try { self.drawComparison(audio.currentTime || 0); } catch (e) {}
      }

      audio.addEventListener('timeupdate', function () {
        if (self._historyAudio !== audio) return;
        try { self.drawComparison(audio.currentTime || 0); } catch (e) {}
      });
    }).catch(function (err) {
      console.warn('[saif-practice] history pitch-data fetch failed (audio still plays):', err && err.message);
      if (self._historyAudio !== audio) return;
      self._setHistoryCompareState('error',
        'The pitch comparison could not load right now. Your score and audio are still available.');
    });
  };

  /**
   * Give the comparison canvas an honest async/legacy state. Full pitch arrays
   * are excluded from the lightweight history request, so a replay must never
   * expose a stale contour while those arrays load.
   */
  PracticeStudio.prototype._setHistoryCompareState = function (state, message) {
    var wrap = this.container && this.container.querySelector('#SaifPracticeCompareWrap');
    if (!wrap) return;
    var box = this.container.querySelector('#SaifPracticeCompareState');
    if (!box) {
      box = document.createElement('div');
      box.id = 'SaifPracticeCompareState';
      box.className = 'saif-practice__compare-state';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      var spinner = document.createElement('span');
      spinner.className = 'saif-practice__compare-state-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      var label = document.createElement('span');
      label.id = 'SaifPracticeCompareStateText';
      box.appendChild(spinner);
      box.appendChild(label);
      wrap.appendChild(box);
    }
    var compare = wrap.closest('.saif-practice__compare');
    var textEl = box.querySelector('#SaifPracticeCompareStateText');
    box.classList.remove('saif-practice__compare-state--loading',
      'saif-practice__compare-state--unavailable', 'saif-practice__compare-state--error');
    if (compare) compare.classList.toggle('saif-practice__compare--pending', state !== 'ready');
    if (state === 'ready') {
      box.hidden = true;
      if (textEl) textEl.textContent = '';
      return;
    }
    box.classList.add('saif-practice__compare-state--' + state);
    if (textEl) textEl.textContent = message || 'Loading this take’s pitch comparison…';
    box.hidden = false;
  };

  /** Draw the selected saved take against its server-backed score history. */
  PracticeStudio.prototype._renderSavedScoreTrend = function (rec) {
    if (!this.scoreTrendBox || !this.scoreTrendSvg || !rec) return;
    var key = rec.referenceUrl || rec.clipTitle || rec.lessonId || '';
    if (!key) { this.scoreTrendBox.hidden = true; return; }
    var pts = (this._historyItems || []).filter(function (item) {
      var itemKey = item.referenceUrl || item.clipTitle || item.lessonId || '';
      return itemKey === key && typeof item.score === 'number';
    }).slice().sort(function (a, b) {
      return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
    });
    if (pts.length < 2) { this.scoreTrendBox.hidden = true; return; }
    pts = pts.slice(-10);
    var w = 120, h = 36, pad = 2;
    var stepX = (w - pad * 2) / (pts.length - 1);
    var coords = pts.map(function (item, i) {
      var score = Math.max(0, Math.min(10, item.score));
      return (pad + i * stepX).toFixed(1) + ',' +
        (pad + (1 - score / 10) * (h - pad * 2)).toFixed(1);
    });
    var selectedIdx = -1;
    for (var i = 0; i < pts.length; i++) if (pts[i].id === rec.id) selectedIdx = i;
    if (selectedIdx < 0) selectedIdx = pts.length - 1;
    var selected = coords[selectedIdx].split(',');
    var firstScore = pts[0].score, lastScore = pts[pts.length - 1].score;
    var color = lastScore >= firstScore + 0.5 ? '#5BC891'
      : lastScore <= firstScore - 0.5 ? '#D87C7C' : '#D4A84B';
    var areaPath = 'M' + coords[0] + ' L' + coords.join(' L') +
      ' L' + (pad + (pts.length - 1) * stepX).toFixed(1) + ',' + (h - pad).toFixed(1) +
      ' L' + pad.toFixed(1) + ',' + (h - pad).toFixed(1) + ' Z';
    var ns = 'http://www.w3.org/2000/svg';
    this.scoreTrendSvg.innerHTML = '';
    var area = document.createElementNS(ns, 'path');
    area.setAttribute('d', areaPath);
    area.setAttribute('fill', color);
    area.setAttribute('opacity', '0.18');
    this.scoreTrendSvg.appendChild(area);
    var line = document.createElementNS(ns, 'polyline');
    line.setAttribute('points', coords.join(' '));
    line.setAttribute('fill', 'none');
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', '1.6');
    line.setAttribute('stroke-linecap', 'round');
    line.setAttribute('stroke-linejoin', 'round');
    this.scoreTrendSvg.appendChild(line);
    var dot = document.createElementNS(ns, 'circle');
    dot.setAttribute('cx', selected[0]);
    dot.setAttribute('cy', selected[1]);
    dot.setAttribute('r', '2.8');
    dot.setAttribute('fill', color);
    dot.setAttribute('stroke', '#fffaf1');
    dot.setAttribute('stroke-width', '1.2');
    this.scoreTrendSvg.appendChild(dot);
    this.scoreTrendBox.hidden = false;
  };

  PracticeStudio.prototype._savedTakeGroup = function (rec) {
    if (!rec) return [];
    var key = rec.referenceUrl || rec.clipTitle || rec.lessonId || '';
    if (!key) return [];
    return (this._historyItems || []).filter(function (item) {
      var itemKey = item.referenceUrl || item.clipTitle || item.lessonId || '';
      return itemKey === key && typeof item.score === 'number';
    }).slice().sort(function (a, b) {
      return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
    });
  };

  PracticeStudio.prototype._savedMetricRow = function (label, value) {
    var row = document.createElement('div');
    row.className = 'saif-practice__metric';
    var labelEl = document.createElement('span');
    labelEl.className = 'saif-practice__metric-label';
    labelEl.textContent = label;
    var bar = document.createElement('div');
    bar.className = 'saif-practice__metric-bar';
    var fill = document.createElement('div');
    fill.className = 'saif-practice__metric-fill';
    fill.style.width = (value * 10) + '%';
    bar.appendChild(fill);
    var valueEl = document.createElement('span');
    valueEl.className = 'saif-practice__metric-value';
    valueEl.textContent = value.toFixed(1);
    row.appendChild(labelEl);
    row.appendChild(bar);
    row.appendChild(valueEl);
    return row;
  };

  PracticeStudio.prototype._appendSavedScoreExplainer = function (target) {
    var details = document.createElement('details');
    details.className = 'saif-practice__score-details';
    var summary = document.createElement('summary');
    summary.textContent = 'What do these numbers mean?';
    details.appendChild(summary);
    [
      'Your score compares this take with its reference in three parts:',
      'Pitch — 60%. How closely your notes matched. Octave differences do not cost points.',
      'Melody flow — 20%. How closely your rises, falls and transitions followed the reference.',
      'Steadiness — 20%. How evenly you held notes beyond the movement already present in the melody.'
    ].forEach(function (copy) {
      var p = document.createElement('p');
      p.textContent = copy;
      details.appendChild(p);
    });
    target.appendChild(details);
  };

  PracticeStudio.prototype._renderSavedTakeScore = function (rec) {
    var c = this.container;
    if (!c) return;
    ['.saif-practice__review-callout', '.saif-practice__low-confidence-warning'].forEach(function (sel) {
      var stale = c.querySelector(sel);
      if (stale) stale.hidden = true;
    });
    var breakdown = rec && rec.breakdown;
    var pitch = breakdown && breakdown.pitch != null ? parseFloat(breakdown.pitch) : NaN;
    var flow = breakdown && breakdown.consistency != null ? parseFloat(breakdown.consistency) : NaN;
    var steady = breakdown && breakdown.steadiness != null ? parseFloat(breakdown.steadiness) : NaN;
    var hasBreakdown = isFinite(pitch) && isFinite(flow) && isFinite(steady);
    if (!hasBreakdown) {
      if (this.scoreBreakdown) {
        this.scoreBreakdown.innerHTML = '';
        var oldTake = document.createElement('p');
        oldTake.className = 'saif-practice__saved-score-note';
        oldTake.textContent = 'This take was saved before detailed scoring was added, so only its overall score is available. New saved takes include pitch, melody flow and steadiness.';
        this.scoreBreakdown.appendChild(oldTake);
      }
      if (this.scoreTrendBox) this.scoreTrendBox.hidden = true;
      return;
    }

    pitch = Math.max(0, Math.min(10, pitch));
    flow = Math.max(0, Math.min(10, flow));
    steady = Math.max(0, Math.min(10, steady));
    var group = this._savedTakeGroup(rec);
    var attemptIdx = -1, best = -Infinity;
    for (var gi = 0; gi < group.length; gi++) {
      if (group[gi].id === rec.id) attemptIdx = gi;
      if (group[gi].score > best) best = group[gi].score;
    }
    var when = '';
    try {
      var ts = rec.createdAt || rec.created_at || rec.time;
      var takeDate = ts ? new Date(ts) : null;
      if (takeDate && isFinite(takeDate.getTime())) {
        when = takeDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch (e) {}
    var statsText = 'Saved take' + (when ? ' · ' + when : '') + (rec.clipTitle ? ' · ' + rec.clipTitle : '');
    if (attemptIdx > 0) {
      var delta = rec.score - group[attemptIdx - 1].score;
      statsText += ' · ' + (delta >= 0 ? '+' : '−') + Math.abs(delta).toFixed(1) + ' vs previous';
    }
    if (isFinite(best)) statsText += ' · best ' + best.toFixed(1);
    if (attemptIdx >= 0) statsText += ' · attempt ' + (attemptIdx + 1) + ' of ' + group.length;
    if (attemptIdx > 0 && rec.score >= best) statsText += ' · personal best';

    var weakest = pitch <= flow && pitch <= steady ? 'pitch' : (flow <= steady ? 'flow' : 'steady');
    var coach = weakest === 'pitch'
      ? 'Coach’s tip: replay the reference at 0.75× and match each note before returning to full speed.'
      : weakest === 'flow'
        ? 'Coach’s tip: connect the notes inside each phrase, then save your breath for the phrase ending.'
        : 'Coach’s tip: choose the longest note and practise holding it evenly from beginning to end.';
    if (this.scoreBreakdown) {
      this.scoreBreakdown.innerHTML = '';
      this.scoreBreakdown.appendChild(this._savedMetricRow('Pitch Accuracy', pitch));
      this.scoreBreakdown.appendChild(this._savedMetricRow('Melody Flow', flow));
      this.scoreBreakdown.appendChild(this._savedMetricRow('Vocal Steadiness', steady));
      var stats = document.createElement('p');
      stats.className = 'saif-practice__score-stats';
      stats.textContent = statsText;
      this.scoreBreakdown.appendChild(stats);
      var coachLine = document.createElement('p');
      coachLine.className = 'saif-practice__coach-line';
      coachLine.textContent = coach;
      this.scoreBreakdown.appendChild(coachLine);
      this._appendSavedScoreExplainer(this.scoreBreakdown);
    }
    this._renderSavedScoreTrend(rec);
  };

  /**
   * Show a SAVED take with every trustworthy field we persisted: overall
   * score, metric bars, progress, coaching and trend. Older takes that predate
   * breakdown storage get a clear explanation instead of borrowed live data.
   */
  PracticeStudio.prototype._reviewSavedTake = function (recId) {
    var rec = (this._historyItems || []).filter(function (r) { return r.id === recId; })[0];
    var c = this.container;
    if (!c) return;

    if (this.scoreEl) {
      var hasScore = rec && typeof rec.score === 'number';
      this.scoreEl.className = 'saif-practice__score-number' + (hasScore ? ' saif-practice__score--' +
        (rec.score >= 8 ? 'great' : rec.score >= 6 ? 'good' : rec.score >= 4 ? 'fair' : 'low') : '');
      this.scoreEl.style.clipPath = '';
      this.scoreEl.textContent = hasScore ? (rec.score === 10 ? '10' : rec.score.toFixed(1)) : '—';
      try { this._scoreArcSet(hasScore ? rec.score / 10 : 0); } catch (e) {}
    }
    this._renderSavedTakeScore(rec);
    var expectsPitch = !!(rec && (rec.hasPitch || (Array.isArray(rec.pitchData) && rec.pitchData.length)));
    this._setHistoryCompareState(expectsPitch ? 'loading' : 'unavailable', expectsPitch
      ? 'Loading this take’s pitch comparison…'
      : 'A detailed pitch trace is not available for this take. Its score and audio are still available.');
    // The transport is the point of a replay, so it opens revealed.
    if (this.playerEl) this.playerEl.hidden = false;
    var notice = c.querySelector('#SaifPracticePracticeNotice');
    if (notice) notice.hidden = true;

    if (this.resultSection) this.resultSection.hidden = false;
    // Mark the replay BEFORE switching tabs: the tab rule keeps the transport
    // visible on Score only while a saved take is being reviewed.
    this._reviewingSavedId = recId;
    // The take on screen changed — repaint its notes. Notes are stored per
    // take, but the render used to fire only when the CLIP changed, so the
    // panel kept the previous take's list.
    try { this._renderNotes(); } catch (e) {}
    // Land on SCORE. Opening straight onto Compare showed the graph and left the
    // number on the tab behind it — "my score doesn't show when I click play".
    // The score is stored per take (rec.score, the same field the row badge
    // prints); it was only ever a question of which tab was in front.
    if (this._switchResultTab) this._switchResultTab('score');
    try { this.sheetOpenV2('result'); } catch (e) {}
    try { this.resizeCanvases(); } catch (e) {}
  };

  PracticeStudio.prototype._setHistoryButtonPlaying = function (playBtn, isPlaying) {
    if (!playBtn) return;
    playBtn.classList.toggle('saif-practice__history-play--playing', !!isPlaying);
    playBtn.setAttribute('aria-label', (isPlaying ? 'Pause' : 'Play') + ' recording');
  };

  /**
   * Stop history playback and put the row button back. Everything that takes
   * the student away from the list calls this — closing the sheet, opening a
   * different sheet, arming a take. Before it existed, leaving the sheet left
   * audio playing with no visible control to stop it.
   */
  PracticeStudio.prototype._historyStop = function () {
    if (!this._historyAudio) return;
    try { this._historyAudio.pause(); } catch (e) {}
    // The replay WAS the transport audio; drop it so a later live take does
    // not inherit a saved take's player, and so the transport reports empty
    // rather than driving audio no longer on screen.
    if (this._playbackAudio === this._historyAudio) {
      this._playbackAudio = null;
      this._playbackSourceId = null;
      this._playbackDuration = null;
    }
    this._historyResetButton();
  };

  PracticeStudio.prototype._historyResetButton = function () {
    if (this._historyButton) {
      this._setHistoryButtonPlaying(this._historyButton, false);
    }
    this._historyAudio = null;
    this._historyPlayingId = null;
    this._historyButton = null;
  };

  /**
   * Switch which result panel (Score / Compare / AI Feedback / Notes) is
   * visible. Side effect: when the Compare tab is activated the comparison
   * canvas is resized + redrawn — needed because canvas dimensions can be
   * 0×0 if the panel was [hidden] when the result was first rendered.
   */
  PracticeStudio.prototype._switchResultTab = function (name) {
    if (!this._resultTabs || !this._resultPanels) return;
    this._resultTabs.forEach(function (t) {
      var on = (t.getAttribute('data-result-tab') === name);
      t.classList.toggle('saif-practice__result-tab--active', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this._resultPanels.forEach(function (p) {
      p.hidden = (p.getAttribute('data-result-panel') !== name);
    });
    var resultBody = this.container.querySelector('#SaifPracticeResultSheet .saif-practice__sheet-body');
    if (resultBody) resultBody.scrollTop = 0;
    if (this._openSheetV2 === 'result') this._syncResultTootSeat(false);
    else this._resultTootAtEdge = false;
    // The transport belongs to Compare and Notes, not Score. Compare needs it
    // (the playhead rides the graph) and Notes DEPENDS on it — "add note at
    // playhead" reads the playback position and tapping a note seeks it. On
    // Score it was just noise under the number.
    // ...except while reviewing a saved take: there the transport IS the replay,
    // so it stays put whichever tab is in front.
    // Visibility is decided by _syncPlayerEnabled (is there a recording?), not
    // by which tab is showing — see the note there.
    try { this._syncPlayerEnabled(); } catch (e) {}
    // Notes are per take, so paint them for whichever take is in front now.
    if (name === 'notes') { try { this._renderNotes(); } catch (e) {} }
    if (name === 'compare') {
      // Canvas may have been hidden when the result first rendered →
      // its dimensions are 0×0 until visible. Resize + redraw now.
      try { this.resizeCanvases(); } catch (e) {}
      try { this.drawComparison(); } catch (e) {}
    }
  };

  /**
   * Reveal the analyzed reference's pitch contour as a preview graph. Called
   * once a reference (esp. a custom/uploaded one) finishes pitch analysis, so
   * the user immediately SEES the pitch graph of their audio — the whole point
   * of uploading a reference — instead of a blank studio until they record.
   * drawComparison draws the reference line alone when there's no recording yet;
   * startRecording re-hides the result section, so pressing Record then replaces
   * this preview with the real you-vs-reference comparison.
   */
  PracticeStudio.prototype._showReferencePreview = function () {
    if (!this.resultSection) return;
    if (!this.refPitchData || this.refPitchData.length < 2) return;
    // Never clobber an actual recorded take's result view.
    if (this.recPitchData && this.recPitchData.length) return;
    this.resultSection.hidden = false;
    if (typeof this._switchResultTab === 'function') {
      this._switchResultTab('compare'); // sizes + draws the canvas
    } else {
      try { this.resizeCanvases(); } catch (e) {}
      try { this.drawComparison(); } catch (e) {}
    }
    try { this.resultSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
  };

  /**
   * Render the per-clip notes list. Each note row has a clickable timestamp
   * (seeks playback when clicked) and a delete button. Empty state shows a
   * hint until the first note is added.
   *
   * Note text is rendered via textContent (not innerHTML) so user-typed
   * characters can't accidentally break the markup. The seek button stores
   * its time in a data attribute so the delegated handler can read it.
   */
  PracticeStudio.prototype._renderNotes = function () {
    if (!this.notesListEl) return;
    var notes = this._getTakeNotes();
    this.notesListEl.innerHTML = '';
    if (this.notesEmptyEl) this.notesEmptyEl.hidden = notes.length > 0;
    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      var li = document.createElement('li');
      li.className = 'saif-practice__notes-item';
      var seekBtn = document.createElement('button');
      seekBtn.type = 'button';
      seekBtn.className = 'saif-practice__notes-seek';
      seekBtn.setAttribute('data-time', String(n.time));
      seekBtn.textContent = fmtClock(n.time);
      seekBtn.title = 'Jump playback to ' + fmtClock(n.time);
      var textEl = document.createElement('span');
      textEl.className = 'saif-practice__notes-text';
      textEl.setAttribute('dir', 'auto'); // Arabic notes render RTL correctly
      textEl.textContent = n.text;
      var delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'saif-practice__notes-delete';
      delBtn.setAttribute('data-idx', String(i));
      delBtn.setAttribute('aria-label', (window.SaifI18n && window.SaifI18n.delete_note_aria) || 'Delete note');
      delBtn.title = (window.SaifI18n && window.SaifI18n.delete_note_title) || 'Delete this note';
      delBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';
      li.appendChild(seekBtn);
      li.appendChild(textEl);
      li.appendChild(delBtn);
      this.notesListEl.appendChild(li);
    }
  };

  /**
   * Show / hide the streak badge based on localStorage. We only show the
   * badge when the streak is "live" — practiced today or yesterday — so a
   * stale 17-day streak from two months ago doesn't mislead the user about
   * their current habit.
   */
  PracticeStudio.prototype._refreshStreakBadge = function () {
    var self = this;
    if (!this.streakBadge) return;
    // Local (this-device) streak renders immediately…
    var data = getStreakData();
    var live = (data.lastDate === todayISO() || data.lastDate === yesterdayISO());
    // Shown even at zero, so the header is the same shape for every visitor.
    var shownStreak = (live && data.count > 0) ? data.count : 0;
    if (this.streakCountEl) this.streakCountEl.textContent = String(shownStreak);
    this.streakBadge.hidden = false;
    // …then the SERVER streak takes over for signed-in students. The worker
    // bumps progress.streak on every saved take and lesson complete, so this
    // is the number that follows the account across devices — a student who
    // practices on their phone and checks on a laptop used to see zero here.
    if (!window.SaifSync || !window.SaifSync.progress || !window.SaifSync.progress.get) return;
    if (!this._serverStreakPromise) {
      this._serverStreakPromise = window.SaifSync.progress.get().catch(function () { return null; });
    }
    this._serverStreakPromise.then(function (r) {
      var st = r && r.ok && r.progress && r.progress.streak;
      if (!st || !st.count) return;
      // The worker stamps lastDay in UTC — accept local-today/yesterday too so
      // a late-evening practice doesn't read as a broken streak.
      var utcToday = new Date().toISOString().slice(0, 10);
      var utcYesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      var serverLive = (st.lastDay === utcToday || st.lastDay === utcYesterday ||
                        st.lastDay === todayISO() || st.lastDay === yesterdayISO());
      if (!serverLive) return;
      var localData = getStreakData();
      var localLive = (localData.lastDate === todayISO() || localData.lastDate === yesterdayISO());
      var shown = Math.max(st.count, localLive ? localData.count : 0);
      if (self.streakCountEl) self.streakCountEl.textContent = String(shown);
      self.streakBadge.hidden = false;
    });
  };

  /**
   * Render an inline SVG sparkline of recent scores for the current
   * reference clip. ViewBox is 120×36 (set in markup); we draw a polyline
   * spanning the full width with each point's Y mapped from score 0..10.
   * Hidden if we don't have enough history yet.
   */
  PracticeStudio.prototype._renderScoreTrend = function () {
    if (!this.scoreTrendBox || !this.scoreTrendSvg) return;
    var history = getScoreHistory(this.refUrl);
    if (!history || history.length < 2) {
      this.scoreTrendBox.hidden = true;
      return;
    }
    var pts = history.slice(-10); // last 10 attempts
    var w = 120, h = 36, pad = 2;
    var stepX = pts.length > 1 ? (w - pad * 2) / (pts.length - 1) : 0;
    var coords = pts.map(function (p, i) {
      var x = pad + i * stepX;
      var s = Math.max(0, Math.min(10, p.score || 0));
      var y = pad + (1 - s / 10) * (h - pad * 2);
      return x.toFixed(1) + ',' + y.toFixed(1);
    });
    var lastScore = pts[pts.length - 1].score;
    var firstScore = pts[0].score;
    // Color the line by trend direction: green improving, gold flat, red declining
    var color = lastScore >= firstScore + 0.5 ? '#5BC891'
              : lastScore <= firstScore - 0.5 ? '#D87C7C'
              : '#D4A84B';
    // Build SVG content: a translucent area fill under the line + the line
    // itself + a dot on the latest point so the user's eye lands on "now".
    var areaPath = 'M' + coords[0] +
      ' L' + coords.join(' L') +
      ' L' + (pad + (pts.length - 1) * stepX).toFixed(1) + ',' + (h - pad).toFixed(1) +
      ' L' + pad.toFixed(1) + ',' + (h - pad).toFixed(1) + ' Z';
    var lastCoord = coords[coords.length - 1].split(',');
    this.scoreTrendSvg.innerHTML =
      '<path d="' + areaPath + '" fill="' + color + '" opacity="0.18"/>' +
      '<polyline points="' + coords.join(' ') +
        '" fill="none" stroke="' + color + '" stroke-width="1.6" ' +
        'stroke-linecap="round" stroke-linejoin="round"/>' +
      '<circle cx="' + lastCoord[0] + '" cy="' + lastCoord[1] + '" r="2.4" ' +
        'fill="' + color + '"/>';
    this.scoreTrendBox.hidden = false;
  };

  /**
   * Update the reference maqam badge from current refPitchData. Called after
   * analyzeReference resolves; safe to call repeatedly. When detection is
   * ambiguous the badge is hidden (don't pretend to know).
   */
  PracticeStudio.prototype._refreshMaqamLabel = function () {
    if (!this.refMaqamLabel) return;
    var base = (this.refUrl ? String(this.refUrl).split('/').pop() : '') || '';
    // Drills are exercises, not maqamat — never badge them with a maqam name.
    if (/^drill-/i.test(base)) {
      this.refMaqamLabel.textContent = '';
      this.refMaqamLabel.hidden = true;
      return;
    }
    // For a maqam-library clip, the maqam is KNOWN from the filename — use it
    // as the name (the old heuristic mislabeled Bayati as Nahawand etc.) and
    // only ESTIMATE the tonic, constrained to that maqam. refPitchData has
    // already been range-fit transposed by the time we run, so the tonic
    // correctly follows the student's key.
    var known = knownRefMaqamName(this.refUrl);
    if (known) {
      var t = detectMaqam(this.refPitchData, known);
      this.refMaqamLabel.textContent = known + (t ? ' · tonic ' + t.tonicLabel : '');
      this.refMaqamLabel.hidden = false;
      slog('[saif-practice] maqam from clip:', known,
        t ? ('tonic ' + t.tonicLabel + ' fit ' + t.fitFraction.toFixed(2)) : '(tonic ambiguous)');
      return;
    }
    // Unknown source (user upload) — best-effort guess from the contour.
    var detection = detectMaqam(this.refPitchData);
    if (detection) {
      this.refMaqamLabel.textContent =
        detection.name + ' · tonic ' + detection.tonicLabel;
      this.refMaqamLabel.hidden = false;
      slog('[saif-practice] maqam detected:', detection.name,
        'tonic:', detection.tonicLabel,
        'fit:', detection.fitFraction.toFixed(2));
    } else {
      this.refMaqamLabel.textContent = '';
      this.refMaqamLabel.hidden = true;
      slog('[saif-practice] maqam detection: ambiguous, label hidden');
    }
  };

  /* ==============================================
     RANGE-FIT REFERENCE TRANSPOSITION
     Keep the reference clip's whole melody inside the student's saved vocal
     range by re-pitching the AUDIO (tempo preserved, via saif-pitchshift.js)
     and shifting the pitch contour + drills + tuner + maqam tonic by the same
     N semitones. Our maqam-library clips only; uploads are never shifted. The
     audio and the contour are ALWAYS in the same key — if the DSP fails, the
     studio silently keeps the original key end to end (never a mixed state).
     Isolated in one block so concurrent editors of this file don't collide.
     ============================================== */

  // Minimal-|N| integer that lands the shifted clip inside the classifier's
  // 'good' window (the single source of truth for "fits"). N=0 exactly when the
  // clip already fits; when its span exceeds the zone, best-centre the median.
  function computeRefShift(zone, stats) {
    if (!zone || !stats) return 0;
    var loBound = Math.ceil((zone.lo - 1) - stats.lo);   // smallest N keeping the bottom in
    var hiBound = Math.floor((zone.hi + 1) - stats.hi);  // largest N keeping the top in
    var n = (loBound <= hiBound)
      ? Math.min(Math.max(0, loBound), hiBound)          // in-window integer closest to 0
      : Math.round((zone.lo + zone.hi) / 2 - stats.med); // span > zone: best-centre the median
    return Math.max(-12, Math.min(12, n));               // hard cap ±1 octave
  }

  // Ensure the pitch manifest is loaded AND its resolved value is stashed
  // (clipRangeStats reads pitchManifestPromise._resolvedValue synchronously).
  function manifestReady() {
    return loadPitchManifest().then(function (m) {
      if (pitchManifestPromise && !pitchManifestPromise._resolvedValue) {
        pitchManifestPromise._resolvedValue = m;
      }
      return m;
    });
  }

  // Per-clip {lo,hi,med} stats. clipRangeStats memoizes null PERMANENTLY, so for
  // a clip absent from the loaded manifest (extension clips, n>11) fetch its
  // family file FIRST — reading stats before the merge would poison the memo.
  function statsFor(url) {
    var manifest = pitchManifestPromise && pitchManifestPromise._resolvedValue;
    var key = extractAssetName(url);
    if (manifest && manifest.items && key && manifest.items[key]) {
      return Promise.resolve(clipRangeStats(url));
    }
    return fetchExtendedPitch(url).then(function () { return clipRangeStats(url); },
      function () { return null; });
  }

  // Harness hook — exercise the fit maths directly (mirrors the rangeComfortZone
  // convention nearby).
  PracticeStudio.prototype.computeRefShift = function (zone, stats) {
    return computeRefShift(zone, stats);
  };

  // Stash the pristine (unshifted) contour ALWAYS, then return it shifted by the
  // currently-applied N (a fresh COPY — the shared, family-prefetch-mutated
  // manifest is never multiplied in place) or untouched when N is 0.
  PracticeStudio.prototype._applyRefTranspose = function (series) {
    this._refPitchOriginal = series;
    var n = this._refSemitones || 0;
    if (!n || !Array.isArray(series)) return series;
    var f = Math.pow(2, n / 12);
    return series.map(function (p) { return { time: p.time, freq: p.freq * f }; });
  };

  // Kicked once per clip from loadReference (+ the boot preload). Resets per-clip
  // state, then — for our clips with a saved range — computes N and processes the
  // audio, handing the result to _refShiftTryApply.
  PracticeStudio.prototype._refShiftInit = function (url) {
    var self = this;
    this._refShiftSeq = (this._refShiftSeq || 0) + 1;
    var mySeq = this._refShiftSeq;
    this._refSemitones = 0;      // currently-applied shift
    this._refShiftTarget = 0;    // computed shift for this clip (chip source)
    this._refShiftFits = false;
    this._refShiftApplied = false;
    this._refShiftJob = null;
    this._refPitchOriginal = null;
    this._refChipSync();
    this._refNudgeSync();
    // Uploads are never shifted; bail when there's no url or no DSP module.
    if (!url || this._customRef || !window.SaifPitchShift) return;
    Promise.all([loadUserRange(), manifestReady()]).then(function () {
      if (mySeq !== self._refShiftSeq) return;   // a newer clip superseded us
      self._refNudgeSync();                       // reflect the now-loaded range
      var range = getUserRangeSync();
      if (!range) return;                         // no range → nudge only
      var zone = rangeComfortZone(range);
      if (!zone) return;
      return statsFor(url).then(function (stats) {
        if (mySeq !== self._refShiftSeq || !stats) return;
        var n = computeRefShift(zone, stats);
        if (!n) { self._refShiftTarget = 0; self._refChipSync(); return; } // already fits
        self._refShiftTarget = n;
        self._refShiftFits = (stats.lo + n >= zone.lo - 1 && stats.hi + n <= zone.hi + 1);
        return window.SaifPitchShift.process(url, n).then(function (src) {
          if (mySeq !== self._refShiftSeq) return; // superseded while processing
          if (!src) {
            self._refShiftTarget = 0;
            self._refChipSync();
            if (window.SaifTrack) { try { window.SaifTrack('practice_ref_shift_failed', { clip: url, semitones: n }); } catch (e) {} }
            return;
          }
          self._refShiftTryApply({ n: n, src: src, url: url, seq: mySeq, announce: true });
        });
      });
    }).catch(function () {
      if (mySeq === self._refShiftSeq) { self._refShiftTarget = 0; self._refChipSync(); }
    });
  };

  // Session-commitment gate: only ever retune a virgin clip session.
  PracticeStudio.prototype._refShiftTryApply = function (job) {
    if (!job || job.seq !== this._refShiftSeq) return;   // superseded
    // Once the student has performed against this clip in its current key, keep
    // that key until they re-pick the clip — no mid-session key changes.
    if (this.state === 'recording' || this.drillActive ||
        (this.recPitchData && this.recPitchData.length)) {
      return; // drop
    }
    var el = this.refAudioEl;
    if (el && !el.paused && !el.ended) {
      // Mid-phrase — defer the swap to the next pause/end (re-checks on fire).
      var self = this;
      this._refShiftJob = job;
      var onPause = function () {
        el.removeEventListener('pause', onPause);
        el.removeEventListener('ended', onPause);
        if (self._refShiftJob === job) {
          self._refShiftJob = null;
          job.deferred = true;
          self._refShiftTryApply(job);
        }
      };
      el.addEventListener('pause', onPause);
      el.addEventListener('ended', onPause);
      return;
    }
    this._refShiftApply(job);
  };

  // The atomic swap — flips audio src + contour + derived caches together.
  // Also the toggle's engine (job.src = this.refUrl, job.n = 0 for the OFF way).
  PracticeStudio.prototype._refShiftApply = function (job) {
    var n = job.n || 0;
    var el = this.refAudioEl;
    if (el) {
      try { el.pause(); } catch (e) {}
      el.src = job.src;                 // blob for ON, original url for OFF
      el.currentTime = 0;
      el.preload = 'auto';
      setPreservePitch(el);             // browsers reset the flag on src change
      try { el.load(); } catch (e) {}
    }
    // this.refUrl / data-reference-url ALWAYS keep the ORIGINAL url (notes,
    // saves, stats, deep-links depend on it) — the blob lives only in <audio>.
    this._refSemitones = n;
    if (n) this._refShiftApplied = true;
    if (this._refPitchOriginal) {
      this.refPitchData = this._applyRefTranspose(this._refPitchOriginal);
    }
    this._refRange = null;              // live-canvas Y-fit cache
    try { this._drillRefreshAvailability(); } catch (e) {}
    try { this._refreshMaqamLabel(); } catch (e) {}
    try { this._showReferencePreview(); } catch (e) {}
    this._refChipSync();
    if (job.announce && n) {
      var dir = n > 0 ? 'raised' : 'lowered';
      var abs = Math.abs(n);
      this.setStatus('Key ' + dir + ' ' + abs + ' semitone' + (abs === 1 ? '' : 's') + ' to fit your vocal range.');
      if (window.SaifTrack) {
        try {
          window.SaifTrack('practice_ref_transposed', {
            semitones: n, clip: this.refUrl, fit_after: !!this._refShiftFits, deferred: !!job.deferred
          });
        } catch (e) {}
      }
    }
  };

  // Chip tap — toggle between the student's key and the original (cached blob;
  // no reprocessing). Only blocked WHILE actively recording/drilling; a finished
  // take no longer locks the switch (its score is already saved), so the student
  // can freely flip the key when idle.
  PracticeStudio.prototype._refShiftToggle = function () {
    if (this.state === 'recording' || this.drillActive) {
      this.setStatus('Finish recording first, then you can switch the key.');
      return;
    }
    if (!this._refShiftTarget) return;
    var self = this;
    if (this._refSemitones !== 0) {
      // Currently shifted → back to the original key.
      this._refShiftApply({ n: 0, src: this.refUrl, announce: false });
      if (window.SaifTrack) { try { window.SaifTrack('practice_ref_shift_toggled', { on: false, semitones: this._refShiftTarget, clip: this.refUrl }); } catch (e) {} }
    } else {
      // Back to the student's key — process() returns the cached blob. Capture
      // the clip token + target so a slow (cache-miss) reprocess that resolves
      // after the student switched clips or started a take can't strand a
      // cross-clip / mid-take mixed key (same-key invariant).
      if (!window.SaifPitchShift) return;
      var seq = this._refShiftSeq;
      var target = this._refShiftTarget;
      window.SaifPitchShift.process(this.refUrl, target).then(function (src) {
        if (!src || seq !== self._refShiftSeq) return;
        if (self.state === 'recording' || self.drillActive) return;
        self._refShiftApply({ n: target, src: src, announce: false });
        if (window.SaifTrack) { try { window.SaifTrack('practice_ref_shift_toggled', { on: true, semitones: target, clip: self.refUrl }); } catch (e) {} }
      });
    }
  };

  // Create (once) + update the "In your key" chip next to the reference title.
  /* The bar's title, meta, maqam mirror and thumbnail all depend on state that
     _refreshMaqamLabel is the common downstream of. WRAP rather than patch
     in-body: that function has four exits, and the wrapper runs after all of
     them, so the mirror never reads a stale label. */
  var _saifOrigRefreshMaqam = PracticeStudio.prototype._refreshMaqamLabel;
  PracticeStudio.prototype._refreshMaqamLabel = function () {
    var r = _saifOrigRefreshMaqam.apply(this, arguments);
    try { this._nowbarSync(); } catch (e) {}
    return r;
  };

  PracticeStudio.prototype._refChipSync = function () {
    if (!this._refKeyChip) {
      if (!this.refMaqamLabel && !this.refSection) return;
      // A real on/off SWITCH (track + sliding knob + label) so it reads
      // unmistakably as a toggle — not just a tappable pill.
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'saif-practice__ref-key';
      chip.setAttribute('role', 'switch');
      chip.hidden = true;
      var track = document.createElement('span');
      track.className = 'saif-practice__ref-key-track';
      track.setAttribute('aria-hidden', 'true');
      var knob = document.createElement('span');
      knob.className = 'saif-practice__ref-key-knob';
      track.appendChild(knob);
      var label = document.createElement('span');
      label.className = 'saif-practice__ref-key-label';
      chip.appendChild(track);
      chip.appendChild(label);
      this._refKeyChipLabel = label;
      var self = this;
      chip.addEventListener('click', function () { self._refShiftToggle(); });
      if (this.refMaqamLabel && this.refMaqamLabel.parentNode) {
        this.refMaqamLabel.parentNode.insertBefore(chip, this.refMaqamLabel.nextSibling);
      } else {
        this.refSection.appendChild(chip);
      }
      this._refKeyChip = chip;
    }
    var chipEl = this._refKeyChip;
    var labelEl = this._refKeyChipLabel;
    // Only ever shown once a real shift has actually applied to this clip.
    if (this._customRef || !this._refShiftApplied || !this._refShiftTarget) {
      chipEl.hidden = true;
      return;
    }
    chipEl.classList.remove('saif-practice__ref-key--partial');
    var on = (this._refSemitones !== 0);
    chipEl.setAttribute('aria-checked', on ? 'true' : 'false');
    if (!on) {
      labelEl.textContent = (window.SaifI18n && window.SaifI18n.original_key) || 'Original key';
      chipEl.setAttribute('aria-label', (window.SaifI18n && window.SaifI18n.key_chip_off_aria) || 'Tune this clip to your vocal range (currently off — original key)');
      chipEl.title = (window.SaifI18n && window.SaifI18n.key_chip_off_title) || 'Off — original key. Tap to tune this clip into your vocal range.';
    } else {
      var shown = this._refSemitones;
      var sign = shown > 0 ? '+' : '';
      var abs = Math.abs(shown);
      var det = 'tuned ' + abs + ' semitone' + (abs === 1 ? '' : 's') + ' into your vocal range';
      if (this._refShiftFits) {
        labelEl.textContent = 'Tuned to your key (' + sign + shown + ')';
      } else {
        labelEl.textContent = 'Closer to your key (' + sign + shown + ')';
        chipEl.classList.add('saif-practice__ref-key--partial');
      }
      chipEl.setAttribute('aria-label', 'Tune this clip to your vocal range (currently on — ' + det + ')');
      chipEl.title = 'On — ' + det + '. Tap for the original key.';
    }
    chipEl.hidden = false;
  };

  // Create (once) + update the quiet "Find your range" nudge under the transport
  // shown only when a reference is loaded but no range has been saved yet.
  PracticeStudio.prototype._refNudgeSync = function () {
    if (!this._refNudge) {
      if (!this.refSection) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'saif-practice__ref-rangecta';
      btn.textContent = (window.SaifI18n && window.SaifI18n.find_range_nudge) || 'Find your vocal range → hear every clip in your key';
      btn.hidden = true;
      var self = this;
      btn.addEventListener('click', function () { try { self.switchMode('range'); } catch (e) {} });
      this.refSection.appendChild(btn);
      this._refNudge = btn;
    }
    var show = !!this.refUrl && !this._customRef && !!window.SaifPitchShift && !getUserRangeSync();
    this._refNudge.hidden = !show;
  };

  // Reference-section extras: a "Remove reference" button (clear back to
  // freestyle recording — no gold line, no reference audio) + a headphones
  // recommendation (the reference plays aloud during a take; on speakers it can
  // leak into the mic). Both shown only while a reference is loaded.
  PracticeStudio.prototype._refExtraSync = function () {
    if (!this.refSection) return;
    var self = this;
    if (!this._refRemoveBtn) {
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'saif-practice__ref-remove';
      rm.textContent = (window.SaifI18n && window.SaifI18n.remove_reference) || '✕ Remove reference';
      rm.title = (window.SaifI18n && window.SaifI18n.remove_reference_title) || 'Clear the reference and record freestyle (no gold line)';
      rm.hidden = true;
      rm.addEventListener('click', function () { self.loadReference(''); });
      this.refSection.appendChild(rm);
      this._refRemoveBtn = rm;
    }
    if (!this._refHpToggle) {
      // "🎧 I'm on headphones" — persistent switch (replaces the old static
      // hint). ON disables the speaker-bleed gate entirely (headphones can't
      // bleed), so soft singing is never filtered and headphone users aren't
      // penalized. Same localStorage idiom as the ?aecon flag; styled inline so
      // this stays a one-file change (no CSS push).
      var lab = document.createElement('label');
      lab.className = 'saif-practice__ref-hptoggle';
      lab.style.cssText = 'display:inline-flex;align-items:center;gap:.4em;margin-top:.5em;font-size:.85em;opacity:.8;cursor:pointer;';
      lab.title = (window.SaifI18n && window.SaifI18n.headphones_toggle_title) || 'Turn on if you hear the reference only in your headphones — keeps the mic from filtering your soft singing.';
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.className = 'saif-practice__ref-hptoggle-input';
      cb.style.cssText = 'cursor:pointer;';
      var on = false;
      try { on = !!(window.localStorage && localStorage.getItem('saif:practice:headphones') === '1'); } catch (e) {}
      cb.checked = on;
      self._headphonesMode = on;
      var txt = document.createElement('span');
      txt.textContent = (window.SaifI18n && window.SaifI18n.headphones_toggle) || "🎧 I'm on headphones";
      cb.addEventListener('change', function () {
        self._headphonesMode = cb.checked;
        try { localStorage.setItem('saif:practice:headphones', cb.checked ? '1' : '0'); } catch (e) {}
      });
      lab.appendChild(cb);
      lab.appendChild(txt);
      this.refSection.appendChild(lab);
      this._refHpToggle = lab;
    }
    // Mobile-only nudge: on a phone loudspeaker the reciter acoustically bleeds
    // into the recorded take and there is NO software way to fully remove it from
    // the saved audio (echo cancellation would clean the file but ducks a voice
    // sung in unison with the reference — the cut-out bug we removed). Headphones
    // are the one setup with zero bleed AND zero ducking, so we recommend them.
    if (!this._refHpNudge && isMobileUA()) {
      var nudge = document.createElement('p');
      nudge.className = 'saif-practice__ref-hpnudge';
      nudge.style.cssText = 'margin:.5em 0 0;font-size:.82em;line-height:1.4;opacity:.72;';
      nudge.textContent = (window.SaifI18n && window.SaifI18n.headphones_nudge) || '🎧 For the cleanest recording, use headphones — on a speaker the reciter leaks into your take.';
      // Place the hint just above the "I'm on headphones" toggle.
      this.refSection.insertBefore(nudge, this._refHpToggle);
      this._refHpNudge = nudge;
    }
    var show = !!this.refUrl;
    this._refRemoveBtn.hidden = !show;
    this._refHpToggle.hidden = !show;
    if (this._refHpNudge) this._refHpNudge.hidden = !show;
  };

  /* ==============================================
     PLAYBACK
     ============================================== */
  /* ==============================================
     PLAYBACK TRANSPORT — play/pause toggle, scrub, skip
     ============================================== */

  PracticeStudio.prototype._ensurePlaybackAudio = function () {
    // An already-wired audio wins, whatever created it. A REPLAY of a saved
    // take installs itself here, and without this early return the transport
    // would refuse to drive it whenever there was no live take in the session.
    if (this._playbackAudio) return this._playbackAudio;
    if (!this.recordedUrl) return null;

    var self = this;
    var audio = new Audio(this.recordedUrl);
    audio.preload = 'auto';
    setPreservePitch(audio);
    this._playbackAudio = audio;
    this._wirePlayerEvents(audio);
    return audio;
  };

  PracticeStudio.prototype._wirePlayerEvents = function (audio) {
    var self = this;
    // Forget the previous take's duration — each wired audio recomputes it.
    this._playbackDuration = null;
    audio.onerror = function () {
      console.error('[saif-practice] audio error code:', audio.error && audio.error.code,
        '— recorded codec may not be playable in this browser');
    };
    audio.onloadedmetadata = function () {
      // Chrome bug: MediaRecorder webm blobs report duration=Infinity until
      // you seek past the end once. Standard workaround — seek to a huge
      // time, let 'seeked' land on the real end, cache the now-finite
      // duration, then rewind so the transport shows real time and can seek.
      if (audio.duration === Infinity && !audio._saifDurationFixed) {
        audio._saifDurationFixed = true;
        // Suppress timeupdate-driven UI while we bounce currentTime around,
        // so the transient huge/zero positions never repaint the graph.
        audio._saifFixingDuration = true;
        var onSeeked = function () {
          audio.removeEventListener('seeked', onSeeked);
          if (isFinite(audio.duration) && audio.duration > 0) {
            self._playbackDuration = audio.duration;
          } else if (isFinite(audio.currentTime) && audio.currentTime > 0) {
            // Some builds keep duration=Infinity but clamp currentTime to
            // the real end — use that as the duration.
            self._playbackDuration = audio.currentTime;
          }
          var onRewound = function () {
            audio.removeEventListener('seeked', onRewound);
            audio._saifFixingDuration = false;
            self._refreshPlayerUI();
          };
          audio.addEventListener('seeked', onRewound);
          try { audio.currentTime = 0; } catch (e) {
            audio.removeEventListener('seeked', onRewound);
            audio._saifFixingDuration = false;
            self._refreshPlayerUI();
          }
        };
        audio.addEventListener('seeked', onSeeked);
        try { audio.currentTime = 1e7; } catch (e) {
          audio.removeEventListener('seeked', onSeeked);
          audio._saifFixingDuration = false;
        }
        return;
      }
      self._refreshPlayerUI();
    };
    audio.ondurationchange = function () {
      if (audio._saifFixingDuration) return;
      self._refreshPlayerUI();
    };
    audio.ontimeupdate = function () {
      if (audio._saifFixingDuration) return;
      self._refreshPlayerUI();
      // Re-render the comparison canvas with the playback position so the
      // pitch trail "replays" — splits the trail into played (bright) /
      // remaining (faded), draws a vertical playhead, and a moving dot
      // along the user's contour at the current time.
      self.drawComparison(audio.currentTime || 0);
    };
    audio.onplay = function () { self._setPlayIcon(true); };
    audio.onpause = function () {
      self._setPlayIcon(false);
      // Hold the visualization at the paused position (don't snap back to static)
      self.drawComparison(audio.currentTime || 0);
    };
    audio.onended = function () {
      self._setPlayIcon(false);
      // Snap back to start so the next click plays from the top instead of
      // sitting at the end (which feels like the button is broken).
      try { audio.currentTime = 0; } catch (e) {}
      self._refreshPlayerUI();
      // Restore the static (no-playhead) comparison view
      self.drawComparison();
    };
  };

  PracticeStudio.prototype._setPlayIcon = function (isPlaying) {
    // Resolve the playback state first — it also drives the "Listen"
    // next-step button, which exists even in markup without the transport.
    //   idle    → "Play"     (haven't played yet, or just reset)
    //   playing → "Pause"
    //   paused  → "Resume"   (was playing, then paused mid-playback)
    //   ended   → "Replay"
    var state;
    if (isPlaying) {
      state = 'playing';
      this._playerHasPlayed = true;
    } else if (this._playbackAudio && this._playbackAudio.ended) {
      state = 'ended';
    } else if (this._playerHasPlayed) {
      state = 'paused';
    } else {
      state = 'idle';
    }
    var labelMap = { idle: 'Play', playing: 'Pause', paused: 'Resume', ended: 'Replay' };
    var label = labelMap[state];

    if (this.playbackBtn) {
      // toggleAttribute, not `.hidden =` — SVGElement has no `hidden` IDL
      // property, so the assignment is a JS expando that never hides anything.
      if (this.playerIconPlay) this.playerIconPlay.toggleAttribute('hidden', !!isPlaying);
      if (this.playerIconPause) this.playerIconPause.toggleAttribute('hidden', !isPlaying);
      this.playbackBtn.dataset.state = state;
      this.playbackBtn.setAttribute('aria-label', label + ' recording');
      if (this.playbackLabel) this.playbackLabel.textContent = label;
    }

    // Keep the Listen next-step in sync: it reads "Listen" until playback
    // starts, then mirrors play/pause so it stays a truthful toggle.
    if (this.listenBtn) {
      var listenText = isPlaying ? 'Pause'
        : (state === 'paused' ? 'Resume' : 'Listen');
      if (this.listenLabel) this.listenLabel.textContent = listenText;
      if (this.listenIconPlay) this.listenIconPlay.toggleAttribute('hidden', !!isPlaying);
      if (this.listenIconPause) this.listenIconPause.toggleAttribute('hidden', !isPlaying);
      this.listenBtn.setAttribute('aria-label', listenText + ' — your recording');
    }
  };

  // Effective take-player duration. MediaRecorder blobs lie about duration in
  // several ways: Infinity (Chrome webm), 0/NaN (some Android), AND — the iOS
  // Safari mp4 case — a bogus SMALL positive finite value (~0.1s from the 100ms
  // timeslice) that plays the full clip but reports a fraction of it. A plain
  // `duration > 0` guard trusts that 0.1s and shows "0:00" with a dead scrubber.
  // So take the MAX of the reported duration and the seeded known length (last
  // frame time / record clock): the real value wins when it's plausible, the
  // seed rescues every bogus case.
  PracticeStudio.prototype._effectiveDuration = function (audio) {
    var known = this._playbackDuration || 0;
    var real = (audio && isFinite(audio.duration) && audio.duration > 0) ? audio.duration : 0;
    return Math.max(real, known);
  };

  PracticeStudio.prototype._refreshPlayerUI = function () {
    var audio = this._playbackAudio;
    if (!audio || !this.playerFill) return;
    // _playbackDuration backstops Chrome's duration=Infinity webm bug
    // (cached by the loadedmetadata workaround in _wirePlayerEvents).
    var dur = this._effectiveDuration(audio);
    var cur = audio.currentTime || 0;
    var pct = dur > 0 ? (cur / dur) * 100 : 0;
    if (pct < 0) pct = 0; else if (pct > 100) pct = 100; // known-dur backstop can lag a hair
    this.playerFill.style.width = pct + '%';
    if (this.playerThumb) this.playerThumb.style.left = pct + '%';
    if (this.playerTime) this.playerTime.textContent = formatPlayerTime(cur) + ' / ' + formatPlayerTime(dur);
    // Keep the role="slider" seek bar's ARIA state in sync for screen readers.
    if (this.playerProgress) {
      this.playerProgress.setAttribute('aria-valuenow', String(Math.round(pct)));
      this.playerProgress.setAttribute('aria-valuetext', formatPlayerTime(cur) + ' of ' + formatPlayerTime(dur));
    }
  };

  /**
   * Keep the result transport honest: with no recording loaded there is nothing
   * for Play, the skip arrows or the scrubber to act on, so disable them rather
   * than presenting live-looking controls that silently do nothing.
   */
  PracticeStudio.prototype._syncPlayerEnabled = function () {
    var playable = !!(this._playbackAudio || this.recordedUrl);
    // The transport's visibility follows the recording, not the tab. Hiding it
    // on Score left a real recording unplayable and reserved an empty row.
    if (this.playerEl) this.playerEl.hidden = !playable;
    var ids = ['SaifPracticePlaybackBtn', 'SaifPracticePlayerSkipBack', 'SaifPracticePlayerSkipFwd'];
    for (var i = 0; i < ids.length; i++) {
      var el = this.container.querySelector('#' + ids[i]);
      if (el) el.disabled = !playable;
    }
    if (this.playerEl) this.playerEl.classList.toggle('saif-practice__player--empty', !playable);
  };

  PracticeStudio.prototype.togglePlayback = function () {
    var audio = this._ensurePlaybackAudio();
    if (!audio) {
      console.warn('[saif-practice] togglePlayback — no recording loaded');
      return;
    }
    if (audio.paused || audio.ended) {
      var p = audio.play();
      if (p && typeof p.then === 'function') {
        p.catch(function (err) {
          console.error('[saif-practice] audio.play() rejected:', err && err.name, err && err.message);
        });
      }
    } else {
      audio.pause();
    }
  };

  PracticeStudio.prototype.skipPlayback = function (deltaSec) {
    var audio = this._ensurePlaybackAudio();
    if (!audio) {
      console.warn('[saif-practice] skipPlayback — no _playbackAudio (recording not loaded yet?)');
      return;
    }
    var dur = this._effectiveDuration(audio);
    var prev = audio.currentTime || 0;
    var next = prev + deltaSec;
    if (next < 0) next = 0;
    if (dur > 0 && next > dur) next = dur;
    slog('[saif-practice] skipPlayback delta=' + deltaSec + 's: ' +
      prev.toFixed(2) + 's -> ' + next.toFixed(2) + 's of ' + dur.toFixed(2) + 's' +
      ' | src=' + (audio.currentSrc || audio.src || '?').slice(0, 40));
    try { audio.currentTime = next; } catch (e) {
      console.error('[saif-practice] skipPlayback — currentTime set threw:', e && e.message);
    }
    this._refreshPlayerUI();
  };

  PracticeStudio.prototype.seekPlayback = function (ratio) {
    var audio = this._ensurePlaybackAudio();
    if (!audio) {
      console.warn('[saif-practice] seekPlayback — no _playbackAudio');
      return;
    }
    var dur = this._effectiveDuration(audio);
    if (dur <= 0) {
      console.warn('[saif-practice] seekPlayback — duration not yet known, ignoring seek');
      return;
    }
    try { audio.currentTime = ratio * dur; } catch (e) {
      console.error('[saif-practice] seekPlayback — currentTime set threw:', e && e.message);
    }
    this._refreshPlayerUI();
  };

  /* Reference-clip transport — mirrors the recorded-take player above but
     drives this.refAudioEl. Reference sources are CDN MP3s or local-file blob
     URLs, which always report a finite duration, so the recorded-webm
     duration=Infinity workaround in the take player is deliberately not
     duplicated here. While metadata hasn't loaded (duration NaN — a fresh src
     swap, or first paint) dur resolves to 0: the bar stays zeroed and seeks
     are ignored until loadedmetadata fires. */
  PracticeStudio.prototype._refreshRefTransportUI = function () {
    var audio = this.refAudioEl;
    if (!audio || !this.refFill) return;
    var dur = (isFinite(audio.duration) && audio.duration > 0) ? audio.duration : 0;
    var cur = audio.currentTime || 0;
    var pct = dur > 0 ? (cur / dur) * 100 : 0;
    this.refFill.style.width = pct + '%';
    if (this.refThumb) this.refThumb.style.left = pct + '%';
    if (this.refTimeEl) this.refTimeEl.textContent = formatPlayerTime(cur) + ' / ' + formatPlayerTime(dur);
    if (this.refProgress) {
      this.refProgress.setAttribute('aria-valuenow', String(Math.round(pct)));
      this.refProgress.setAttribute('aria-valuetext', formatPlayerTime(cur) + ' of ' + formatPlayerTime(dur));
    }
    // On the main screen the bar is always mounted, so it has to SAY when there
    // is nothing to scrub — inside the sheet its wrapper did that by being
    // hidden outright.
    if (this.refTransport) {
      this.refTransport.classList.toggle('saif-practice__ref-transport--empty', dur <= 0);
      this.refTransport.setAttribute('aria-disabled', dur > 0 ? 'false' : 'true');
    }
  };

  PracticeStudio.prototype.skipReference = function (deltaSec) {
    var audio = this.refAudioEl;
    if (!audio) return;
    // Don't let a manual reference seek run mid-take: during a record-along
    // (state 'starting'/'recording') the reference must advance only by natural
    // playback, with MediaRecorder pause/resume and the Date.now() pitch clock
    // kept in sync via the play/pause listeners. A seek fires 'seeked', not
    // 'pause', so nothing compensates — it would desync the saved take and jerk
    // the live target band. No-op until the take finishes.
    if (this.state === 'recording' || this.state === 'starting') return;
    var dur = (isFinite(audio.duration) && audio.duration > 0) ? audio.duration : 0;
    if (dur <= 0) return; // metadata not in yet — bar is zeroed, seeks ignored
    var next = (audio.currentTime || 0) + deltaSec;
    if (next < 0) next = 0;
    if (next > dur) next = dur;
    try { audio.currentTime = next; } catch (e) {}
    this._refreshRefTransportUI();
    try { this._drawIdleCanvas(); } catch (e) {}
  };

  PracticeStudio.prototype.seekReference = function (ratio) {
    var audio = this.refAudioEl;
    if (!audio) return;
    if (this.state === 'recording' || this.state === 'starting') return; // inert mid-take (see skipReference)
    var dur = (isFinite(audio.duration) && audio.duration > 0) ? audio.duration : 0;
    if (dur <= 0) return;
    try { audio.currentTime = ratio * dur; } catch (e) {}
    this._refreshRefTransportUI();
    // A drag has to move the stage NOW. The seeked event redraws too, but it can
    // land a frame or more late, which on a pointermove-driven scrub reads as the
    // bar and the phrase disagreeing about where you are.
    try { this._drawIdleCanvas(); } catch (e) {}
  };

  function formatPlayerTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* ==============================================
     AI FEEDBACK (TIER 2)
     ==============================================
     Routes through the Cloudflare Worker app proxy at /apps/saif/score/feedback,
     not the Apps Script directly. The Worker holds the shared secret so the
     Anthropic-burning Apps Script URL never has to be exposed in the page.
     This closes review item H2.
     The legacy `apiUrl` data attribute is now treated only as an enable/disable
     flag: when set, AI feedback is offered; the actual destination is always
     the proxy. The merchant can leave any non-empty value (e.g. "enabled") in
     the section setting. */
  PracticeStudio.prototype.requestAIFeedback = function () {
    // A history replay has commandeered the shared contour (recPitchData /
    // refPitchData now belong to a saved take, not the live one under review),
    // so refuse to submit — the AI would coach on the wrong take. A new take
    // clears _historyReplayActive.
    if (this._historyReplayActive) {
      if (this.feedbackEl) {
        this.feedbackEl.textContent = (window.SaifI18n && window.SaifI18n.feedback_replay) || 'Record a new take to get AI feedback — replaying a saved take does not generate new coaching.';
      }
      return;
    }
    if (!this.apiUrl) {
      if (this.feedbackEl) {
        this.feedbackEl.textContent = (window.SaifI18n && window.SaifI18n.feedback_not_configured) || 'AI feedback is not configured. Enable it in the Practice Studio section settings.';
      }
      return;
    }

    var self = this;
    if (this.feedbackLoading) this.feedbackLoading.hidden = false;
    if (this.feedbackEl) this.feedbackEl.innerHTML = '';
    if (this.submitBtn) this.submitBtn.disabled = true;

    var payload = {
      referencePitch: this.refPitchData.map(function (d) { return { t: Math.round(d.time * 100) / 100, f: Math.round(d.freq) }; }),
      recordingPitch: this.recPitchData.map(function (d) { return { t: Math.round(d.time * 100) / 100, f: Math.round(d.freq) }; }),
      score: this.computeScore(),
      recordingDuration: this.recPitchData.length > 0 ? this.recPitchData[this.recPitchData.length - 1].time : 0
    };

    var fetchPromise = fetch('/apps/saif/score/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('AI endpoint returned ' + r.status);
      return r.json();
    });

    var timeoutPromise = new Promise(function (_, reject) {
      setTimeout(function () { reject(new Error('AI feedback request timed out')); }, 15000);
    });

    Promise.race([fetchPromise, timeoutPromise])
      .then(function (data) {
        if (self.feedbackLoading) self.feedbackLoading.hidden = true;
        if (self.feedbackEl && data.feedback) {
          // Build the feedback DOM via createElement + textContent so any
          // user-controlled strings (data.feedback, data.suggestions[i])
          // can't smuggle HTML/script into the page. The remote endpoint is
          // semi-trusted but we still treat its output as data, not markup.
          self.feedbackEl.textContent = '';
          var title = document.createElement('h4');
          title.className = 'saif-practice__feedback-title';
          title.textContent = (window.SaifI18n && window.SaifI18n.ai_coach_feedback) || 'AI Coach Feedback';
          self.feedbackEl.appendChild(title);
          var body = document.createElement('p');
          body.className = 'saif-practice__feedback-text';
          body.textContent = String(data.feedback);
          self.feedbackEl.appendChild(body);
          if (data.suggestions && data.suggestions.length) {
            var list = document.createElement('ul');
            list.className = 'saif-practice__feedback-list';
            for (var si = 0; si < data.suggestions.length; si++) {
              var li = document.createElement('li');
              li.textContent = String(data.suggestions[si]);
              list.appendChild(li);
            }
            self.feedbackEl.appendChild(list);
          }
        }
      })
      .catch(function (err) {
        console.error('[saif-practice] AI feedback error:', err && err.message);
        if (self.feedbackLoading) self.feedbackLoading.hidden = true;
        if (self.feedbackEl) {
          self.feedbackEl.textContent = '';
          var hint = document.createElement('p');
          hint.className = 'saif-practice__feedback-hint';
          var msg = (err && err.message) ? String(err.message) : 'unknown error';
          hint.textContent = 'Could not reach the AI coach (' + msg + '). Please try again.';
          self.feedbackEl.appendChild(hint);
        }
      })
      .finally(function () {
        if (self.submitBtn) self.submitBtn.disabled = false;
      });
  };

  /* ==============================================
     RESET
     ============================================== */
  /* ==============================================
     PRACTICE STUDIO PAYWALL GATE
     A few free takes, then a full-card lock overlay with trial / subscribe
     CTAs. Only active when the snippet is rendered with data-gated="true".
     Server-authoritative via window.SaifSync.studio (per account for logged-in,
     per IP for anonymous) — the count of remaining takes comes from the server.
     ============================================== */
  PracticeStudio.prototype._initStudioGate = function () {
    if (!this.gated) return;
    var self = this;

    // Point the subscribe CTA at the configured product URL (if any).
    if (this.subscribeBtn && this.subscribeUrl) this.subscribeBtn.setAttribute('href', this.subscribeUrl);
    // NOTE: registerBtn's href is deliberately NOT overwritten here — the
    // snippet authors it with storefront_login_url so the visitor RETURNS to
    // this page (and its Start-trial button) after signing up. registerUrl is
    // kept only as the _startStudioTrial fallback redirect.

    // "Start free trial" → grant the account-linked trial, then unlock.
    if (this.trialBtn) {
      this.trialBtn.addEventListener('click', function () { self._startStudioTrial(); });
    }

    // Load the entitlement snapshot. Stored as a promise so startRecording can
    // await it (closing the race where a fast click beats the network).
    this._studioStatusPromise = this._loadStudioStatus();
  };

  PracticeStudio.prototype._loadStudioStatus = function () {
    var self = this;
    if (!window.SaifSync || !window.SaifSync.studio) {
      // No backend wired — treat as full access so the studio still works.
      // Still apply the gate so a gated render reveals its (default-hidden)
      // take buttons instead of leaving them stuck hidden.
      this._studioStatus = { ok: true, access: 'full', reason: 'no_backend', loggedIn: false };
      this._applyStudioGate();
      return Promise.resolve(this._studioStatus);
    }
    return window.SaifSync.studio.status().then(function (r) {
      // On any error, fail OPEN (don't lock a paying/legit user out over a
      // transient network blip). The hard server-side gate still applies to
      // saves; the worst case is the client UI not showing the lock.
      self._studioStatus = (r && r.ok) ? r : { ok: true, access: 'full', reason: 'status_error', loggedIn: false };
      self._applyStudioGate();
      return self._studioStatus;
    }).catch(function () {
      self._studioStatus = { ok: true, access: 'full', reason: 'status_error', loggedIn: false };
      self._applyStudioGate();
      return self._studioStatus;
    });
  };

  // Await the (possibly in-flight) status load and return the access level.
  PracticeStudio.prototype._ensureStudioAccess = function () {
    if (!this.gated) return Promise.resolve('full');
    var self = this;
    var p = this._studioStatusPromise || this._loadStudioStatus();
    return p.then(function () {
      return (self._studioStatus && self._studioStatus.access) || 'full';
    });
  };

  // Reflect the current entitlement in the UI: lock vs. unlock the take controls.
  PracticeStudio.prototype._applyStudioGate = function () {
    if (!this.gated) return;
    var st = this._studioStatus;
    if (st && st.access === 'locked') this._lockStudioUI();
    else this._unlockStudioUI();
    // Reflect an active free trial (reason 'trial') as a live "Day X of 7"
    // countdown strip below the header; hidden in every other state.
    this._updateTrialUI();
    // If the tour is on the "record" step and built BEFORE entitlement resolved,
    // it's spotlighting the Record-tab fallback while the real take buttons just
    // appeared (unlocked). Re-run that step so it scrolls to + highlights the
    // buttons — matching the replay ("?") path, which always runs post-resolve.
    if (this._tour) {
      var cur = this._tour.steps[this._tour.index];
      if (cur && cur.id === 'record') this._tourShowStep(this._tour.index);
    }
  };

  PracticeStudio.prototype._lockStudioUI = function () {
    if (!this.gated) return;
    // While the spotlight tour runs, keep the studio in a PREVIEW state — lock
    // card hidden AND the take controls left visible — so the "record" step can
    // spotlight the real Record buttons even for a locked visitor. The paywall
    // (and re-hiding the controls) is fully re-applied in _tourEnd. Behind the
    // tour's dimmer the buttons aren't usable, and a tapped record still re-checks
    // entitlement, so this is a safe preview, not an access leak.
    if (this._tour) { if (this.lockEl) this.lockEl.hidden = true; return; }
    // Hide the take controls FIRST and unconditionally — the paywall must block
    // the UI even if the lock-card element somehow isn't in the DOM, so a
    // missing card can never leave the Record button clickable while locked.
    if (this.takeButtonsEl) this.takeButtonsEl.hidden = true;
    if (this.recordHint) this.recordHint.hidden = true;
    if (!this.lockEl) return;
    var st = this._studioStatus || {};
    this.lockEl.hidden = false;
    // Undo any leftover free-trial success morph (icon / hidden actions / class /
    // disabled button) so a paywall re-shown later in the session — e.g. a trial
    // that lapses mid-session — is never a dead end with no CTAs.
    this.lockEl.classList.remove('saif-practice__lock--success');
    if (this._lockIconOrig != null) {
      var _lockIcon = this.lockEl.querySelector('.saif-practice__lock-icon');
      if (_lockIcon) _lockIcon.innerHTML = this._lockIconOrig;
    }
    var _lockActions = this.lockEl.querySelector('.saif-practice__lock-actions');
    if (_lockActions) _lockActions.hidden = false;
    if (this.trialBtn) { this.trialBtn.disabled = false; this.trialBtn.textContent = (window.SaifI18n && window.SaifI18n.start_trial) || 'Start 7-day free trial'; }

    // Tailor the copy + CTAs to who they are.
    var loggedIn = !!st.loggedIn;
    var canTrial = !!st.canStartTrial;
    var hadPaid = !!st.hadPaidAccess; // lapsed subscriber / trial / bundle grant
    if (this.lockTitleEl) {
      this.lockTitleEl.textContent = hadPaid
        ? (window.SaifI18n && window.SaifI18n.access_ended) || 'Your Practice Studio access has ended'
        : (window.SaifI18n && window.SaifI18n.used_free_takes) || "You've used your free takes";
    }
    if (this.lockTextEl) {
      if (hadPaid) {
        this.lockTextEl.textContent = (window.SaifI18n && window.SaifI18n.lock_resubscribe) || 'Resubscribe to keep recording, comparing your pitch, and tracking your progress.';
      } else if (!loggedIn) {
        this.lockTextEl.textContent = (window.SaifI18n && window.SaifI18n.lock_subscribe_signup) || 'Subscribe to keep recording, comparing your pitch, and tracking progress — or sign up free to unlock a 7-day trial.';
      } else if (canTrial) {
        this.lockTextEl.textContent = (window.SaifI18n && window.SaifI18n.lock_subscribe_trial) || 'Subscribe to keep recording, comparing your pitch, and tracking progress — or start your free 7-day trial.';
      } else {
        this.lockTextEl.textContent = (window.SaifI18n && window.SaifI18n.lock_subscribe) || 'Subscribe to keep recording, comparing your pitch, and tracking your progress.';
      }
    }
    // Trial button: logged-in users who haven't trialed yet.
    if (this.trialBtn) this.trialBtn.hidden = !(loggedIn && canTrial);
    // Register button: anonymous visitors (the trial needs an account).
    if (this.registerBtn) this.registerBtn.hidden = loggedIn;
    // Subscribe: always shown (subscribeUrl always has a real fallback), so the
    // lock card is never a dead end — there's always at least one CTA.
    if (this.subscribeBtn) this.subscribeBtn.hidden = !this.subscribeUrl;

    // Move focus to the first visible CTA so keyboard + screen-reader users
    // land on the way forward (their focus was on the now-hidden Record
    // button). Subscribe leads — it's the primary ask on the lock card.
    var firstCta = [this.subscribeBtn, this.trialBtn, this.registerBtn].filter(function (b) {
      return b && !b.hidden;
    })[0];
    if (firstCta && firstCta.focus) {
      // preventScroll: this runs on EVERY load for a gated visitor, and a bare
      // focus() scrolls the element into view — which yanked the whole page
      // down to the lock card before the visitor had done anything.
      try { firstCta.focus({ preventScroll: true }); } catch (e) {}
    }
    try { this._syncTootTree(false); } catch (e) {}
  };

  PracticeStudio.prototype._unlockStudioUI = function () {
    if (!this.gated) return;
    if (this.lockEl) this.lockEl.hidden = true;
    // Only reveal the take controls if we're idle (don't fight an in-progress
    // take or the results view). Both the buttons and the hint start hidden on
    // a gated render until status confirms access, so reveal both here.
    if (this.state === 'idle') {
      if (this.takeButtonsEl) this.takeButtonsEl.hidden = false;
      if (this.recordHint) this.recordHint.hidden = false;
    }
    this._renderTriesRemaining();
    try { this._syncTootTree(true); } catch (e) {}
    // A visitor who loaded already locked has no prior home-seat state. Once
    // access returns, explicitly land him on the new branch after the lock's
    // success transition has had a frame to clear.
    var _selfUnlockToot = this;
    setTimeout(function () { try { _selfUnlockToot._tootHome(); } catch (e) {} }, 180);
  };

  // While on the free preview, prefix the record hint with how many free takes
  // are left (e.g. "2 free takes left · We count you in 3·2·1…") so the visitor
  // knows the trial is finite. Full members / anon-locked show the plain hint.
  PracticeStudio.prototype._renderTriesRemaining = function () {
    if (!this.gated || !this.recordHint) return;
    if (this._recordHintDefault == null) this._recordHintDefault = this.recordHint.textContent || '';
    var st = this._studioStatus || {};
    var n = st.triesRemaining;
    var previewing = st.access === 'preview' && (st.reason === 'free_try' || st.reason === 'anon_free_try');
    if (previewing && typeof n === 'number' && n >= 0) {
      var label = n === 1 ? '1 free take left' : (n + ' free takes left');
      this.recordHint.textContent = label + ' · ' + this._recordHintDefault;
    } else {
      this.recordHint.textContent = this._recordHintDefault;
    }
  };

  // Spend one free take (called once a preview take completes). Decrements the
  // remaining count and only locks when none are left.
  PracticeStudio.prototype._consumeStudioTry = function () {
    if (!this.gated) return;
    var self = this;
    var st = this._studioStatus || (this._studioStatus = {});
    // Optimistically spend one take so a fast "Try again" can't sneak an extra
    // take in before the server round-trip lands; lock only at zero.
    if (typeof st.triesRemaining === 'number') {
      st.triesRemaining = Math.max(0, st.triesRemaining - 1);
      if (st.triesRemaining <= 0) st.access = 'locked';
    } else {
      st.access = 'locked';
    }
    this._applyStudioGate();
    if (!window.SaifSync || !window.SaifSync.studio) { return; }
    window.SaifSync.studio.use().then(function (r) {
      if (r && r.ok) self._studioStatus = r;
      self._applyStudioGate();
    }).catch(function () { self._applyStudioGate(); });
  };

  PracticeStudio.prototype._startStudioTrial = function () {
    var self = this;
    if (!window.SaifSync || !window.SaifSync.studio) return;
    if (this.trialBtn) { this.trialBtn.disabled = true; this.trialBtn.textContent = (window.SaifI18n && window.SaifI18n.starting) || 'Starting…'; }
    if (this.lockMsgEl) this.lockMsgEl.textContent = '';
    window.SaifSync.studio.startTrial().then(function (r) {
      if (r && r.ok && (r.access === 'full' || r.started)) {
        self._studioStatus = r;
        // Celebrate — morph the lock card into a "You're in!" success state with
        // a confetti burst, then reveal the studio and pop in the trial strip.
        // (_trialCelebrate calls _applyStudioGate itself once the moment plays.)
        self._trialCelebrate();
      } else if (r && r.error === 'NOT_SIGNED_IN') {
        // Shouldn't happen (trial button only shows for logged-in), but guard.
        window.location.href = self.registerUrl;
      } else {
        if (self.trialBtn) { self.trialBtn.disabled = false; self.trialBtn.textContent = 'Start free trial'; }
        if (self.lockMsgEl) self.lockMsgEl.textContent = 'Could not start the trial — please try again.';
      }
    }).catch(function () {
      if (self.trialBtn) { self.trialBtn.disabled = false; self.trialBtn.textContent = 'Start free trial'; }
      if (self.lockMsgEl) self.lockMsgEl.textContent = 'Could not start the trial — please try again.';
    });
  };

  // ---- Free-trial countdown --------------------------------------------------
  // The worker's entitlement snapshot carries reason:'trial' + trialEndsAt (ISO)
  // for the 7-day account-linked trial. Render a live "Day X of 7" strip from it,
  // switching to an "Ends in Xh Ym" countdown inside the final 24h. Pure read of
  // the existing status — no extra network calls.
  PracticeStudio.prototype._updateTrialUI = function () {
    var strip = this.trialStripEl;
    if (!strip) return;
    var TOTAL = 7, DAY = 86400000;
    var st = this._studioStatus || {};
    var endMs = st.trialEndsAt ? Date.parse(st.trialEndsAt) : NaN;
    var msLeft = endMs - Date.now();
    var onTrial = st.reason === 'trial' && isFinite(endMs) && msLeft > 0;
    if (!onTrial) {
      strip.hidden = true;
      if (this._trialTimer) { clearInterval(this._trialTimer); this._trialTimer = null; }
      return;
    }
    var ending = msLeft < DAY;    // inside the final 24h
    var dayText;
    if (ending) {
      var hrs = Math.floor(msLeft / 3600000);
      var mins = Math.floor((msLeft % 3600000) / 60000);
      dayText = 'Ends in ' + hrs + 'h ' + mins + 'm';
    } else {
      var daysLeft = Math.ceil(msLeft / DAY);       // 7…2
      var dayNum = TOTAL - daysLeft + 1;             // 1…6
      if (dayNum < 1) dayNum = 1;
      if (dayNum > TOTAL) dayNum = TOTAL;
      dayText = 'Day ' + dayNum + ' of ' + TOTAL;
    }
    if (this.trialDayEl) this.trialDayEl.textContent = dayText;
    strip.classList.toggle('saif-practice__trial--ending', ending);
    var frac = 1 - (msLeft / (TOTAL * DAY));
    if (frac < 0) frac = 0;
    if (frac > 1) frac = 1;
    if (this.trialBarEl) this.trialBarEl.style.width = (frac * 100).toFixed(1) + '%';
    strip.setAttribute('aria-label', 'Free trial — ' + dayText);
    strip.hidden = false;
    if (!this._trialTimer) {
      var self = this;
      // 30s keeps the final-day minute readout within ~half a minute of accurate
      // without spinning the CPU. Guarded so we never stack intervals.
      this._trialTimer = setInterval(function () { self._updateTrialUI(); }, 30000);
    }
  };

  // Trial-activation moment: morph the paywall card into a success state + fire a
  // confetti burst, then reveal the studio and pop the countdown strip in.
  PracticeStudio.prototype._trialCelebrate = function () {
    var self = this;
    var reduce = false;
    try { reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    var lock = this.lockEl;
    if (lock) {
      lock.classList.add('saif-practice__lock--success');
      var iconEl = lock.querySelector('.saif-practice__lock-icon');
      if (iconEl) {
        if (this._lockIconOrig == null) this._lockIconOrig = iconEl.innerHTML;  // so _lockStudioUI can restore it
        iconEl.innerHTML = '<svg class="saif-practice__lock-check" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
      }
      if (this.lockTitleEl) this.lockTitleEl.textContent = (window.SaifI18n && window.SaifI18n.youre_in) || "You're in!";
      if (this.lockTextEl) this.lockTextEl.textContent = (window.SaifI18n && window.SaifI18n.trial_active) || 'Your 7-day free trial is active — happy practicing.';
      var actions = lock.querySelector('.saif-practice__lock-actions');
      if (actions) actions.hidden = true;
      // Announce success in the lock card's existing live region (role=status).
      if (this.lockMsgEl) this.lockMsgEl.textContent = (window.SaifI18n && window.SaifI18n.trial_active_status) || 'Your 7-day free trial is active.';
    }
    // Burst over the whole studio card (above the lock overlay) so it's visible
    // during the success morph AND as the studio reveals.
    if (!reduce) this._spawnConfetti(this.container);
    var delay = reduce ? 650 : 1500;
    setTimeout(function () {
      // A fresh trial should land the user in a clean, ready-to-record studio.
      // The take (Practice/Record) buttons only reveal from the idle state
      // (_unlockStudioUI), so if the user trialed mid-results — e.g. right after
      // spending their last free take, which leaves state='recorded' — reset
      // first, or the buttons would stay hidden after the paywall lifts.
      if (self.state !== 'idle' && typeof self.reset === 'function') {
        try { self.reset(); } catch (e) {}
      }
      // Status is already the trial snapshot — apply the gate to unlock + reveal
      // the take controls; _updateTrialUI (called inside) shows the strip.
      self._applyStudioGate();
      // Focus was on the (now-hidden) trial button; move it onto the revealed
      // Record button so keyboard / screen-reader users aren't left on <body>.
      if (self.recordBtn && self.recordBtn.focus && !self.recordBtn.disabled && !self.recordBtn.hidden) {
        try { self.recordBtn.focus({ preventScroll: true }); } catch (e) {}
      }
      if (self.trialStripEl && !reduce) {
        self.trialStripEl.classList.remove('saif-practice__trial--pop');
        void self.trialStripEl.offsetWidth;   // reflow so the animation re-fires
        self.trialStripEl.classList.add('saif-practice__trial--pop');
      }
      if (lock) lock.classList.remove('saif-practice__lock--success');
    }, delay);
  };

  // Lightweight gold confetti burst inside `host` (the lock card). Self-cleans.
  PracticeStudio.prototype._spawnConfetti = function (host) {
    if (!host || !host.appendChild) return;
    var wrap = document.createElement('div');
    wrap.className = 'saif-practice__confetti';
    wrap.setAttribute('aria-hidden', 'true');
    // Fall distance = the card's height, since translateY % is relative to the
    // (tiny) confetti bit, not its parent. Inherited by every bit via the wrap.
    try { wrap.style.setProperty('--fall', ((host.offsetHeight || 480) + 60) + 'px'); } catch (e) {}
    var colors = ['#B8860B', '#D4A84B', '#E8CE8F', '#FBF8F3', '#8A6508'];
    var restorePos = false;
    try {
      if (window.getComputedStyle(host).position === 'static') { host.style.position = 'relative'; restorePos = true; }
    } catch (e) {}
    for (var i = 0; i < 20; i++) {
      var bit = document.createElement('span');
      bit.className = 'saif-practice__confetti-bit';
      bit.style.left = Math.round(Math.random() * 100) + '%';
      bit.style.background = colors[i % colors.length];
      bit.style.setProperty('--drift', Math.round((Math.random() - 0.5) * 90) + 'px');
      bit.style.setProperty('--rot', Math.round(Math.random() * 540) + 'deg');
      bit.style.animationDelay = (Math.random() * 0.25).toFixed(2) + 's';
      bit.style.animationDuration = (0.9 + Math.random() * 0.7).toFixed(2) + 's';
      wrap.appendChild(bit);
    }
    host.appendChild(wrap);
    setTimeout(function () {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      if (restorePos) { try { host.style.position = ''; } catch (e) {} }
    }, 2100);
  };

  PracticeStudio.prototype.reset = function () {
    this.state = 'idle';
    this.recPitchData = [];
    this.recordedChunks = [];
    this._lastPitchDotY = null;
    this._lastPitchAt = 0;
    this._inTuneSinceMs = null;
    if (this._maxDurationTimer) {
      clearTimeout(this._maxDurationTimer);
      this._maxDurationTimer = null;
    }
    if (this._wakeLock) {
      try { this._wakeLock.release(); } catch (e) {}
      this._wakeLock = null;
    }
    if (this._playbackAudio) {
      try { this._playbackAudio.pause(); } catch (e) {}
      this._playbackAudio = null;
    }
    // Also stop any history-list take still playing (Try Again while a saved
    // recording is playing would otherwise leave it running + its timeupdate
    // listener redrawing the compare canvas). _historyResetButton pauses it,
    // nulls _historyAudio/_historyPlayingId, and restores the row's play icon.
    if (this._historyAudio) {
      try { this._historyAudio.pause(); } catch (e) {}
      try { this._historyResetButton(); } catch (e) {}
    }
    this._playbackDuration = null;
    // Backstop — stopRecording already closes the per-take context, but if a
    // reset ever races a take we'd rather close twice than leak one (iOS
    // Safari hard-caps concurrent AudioContexts).
    closeAudioContext(this.audioCtx);
    this.audioCtx = null;
    // Reset transport UI so the next recording starts at 0:00 with "Play" label
    this._playerHasPlayed = false;
    this._setPlayIcon(false);
    if (this.playerFill) this.playerFill.style.width = '0%';
    if (this.playerThumb) this.playerThumb.style.left = '0%';
    if (this.playerTime) this.playerTime.textContent = '0:00 / 0:00';
    if (this.recordedUrl) URL.revokeObjectURL(this.recordedUrl);
    this.recordedBlob = null;
    this.recordedUrl = null;

    // Back to the default take type for the next attempt.
    this._captureAudio = true;
    this._lastSavedRecordingId = null;
    // Clear any armed (but unspent) free-try consumption. A forgiven
    // low-confidence take must NOT carry its pending-consume into the next
    // take, or "Try again" would spend the free try on the wrong recording.
    this._pendingStudioConsume = false;
    this._resetFeedbackButton();
    if (this.feedbackSubmitBtn) this.feedbackSubmitBtn.hidden = true;
    if (this.practiceRunNotice) this.practiceRunNotice.hidden = true;
    if (this.recordBtn) {
      this.recordBtn.hidden = false;
      this.recordBtn.disabled = false;
    }
    if (this.practiceRunBtn) {
      this.practiceRunBtn.hidden = false;
      this.practiceRunBtn.disabled = false;
    }
    if (this.stopBtn) this.stopBtn.hidden = true;
    if (this.recordHint) this.recordHint.hidden = false;
    if (this.recTimer) this.recTimer.hidden = true;
    if (this.playerEl && this.listenBtn) this.playerEl.hidden = true;
    this._idleStage();
    if (this.resultSection) this.resultSection.hidden = true;
    if (this.feedbackEl) this.feedbackEl.innerHTML = '';
    if (this.pitchDisplay) this.pitchDisplay.textContent = '';
    this.setStatus('');

    // Clear canvases
    [this.liveCanvas, this.compareCanvas].forEach(function (c) {
      if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    });

    // reset() above re-shows the take buttons + hint; if the studio is locked
    // (free take spent), re-hide them and restore the lock card.
    this._applyStudioGate();
  };

  PracticeStudio.prototype.setStatus = function (msg) {
    if (this.statusEl) this.statusEl.textContent = msg;
  };

  /* ==============================================
     FIRST-VISIT TOUR
     A veiled teaser over the (height-clamped) studio invites a first-time
     visitor into a short spotlight walkthrough: a full-viewport dimmer with a
     gold-ringed hole that morphs between the real controls, one plain-language
     line per step. Skippable everywhere (Skip button / Esc); replayable via the
     header "?". The engine DOM is built in JS and appended to document.body —
     NOT inside the card — because .saif-reveal keeps a transform that would
     make it the containing block for position:fixed and break the overlay.
     Seen-state persists in localStorage; only the standalone page renders it.
     ============================================== */
  var TOUR_KEY = 'saif:practice:tourDone';

  PracticeStudio.prototype._initTour = function () {
    if (!this.tourEnabled) return;
    // Never run inside the Shopify theme editor — designMode re-renders would
    // fight the body-appended overlay and confuse a merchant editing the page.
    if (window.Shopify && window.Shopify.designMode) return;
    var self = this;

    // The replay "?" always re-runs the tour (no veil), even after it's "done".
    if (this.tourReplayBtn) {
      this.tourReplayBtn.addEventListener('click', function () {
        self._tourStart({ source: 'replay' });
      });
    }

    // First-visit only: if the key is set the visitor has already seen (or
    // skipped) the tour — leave the studio open and do nothing further.
    var done = false;
    try { done = !!localStorage.getItem(TOUR_KEY); } catch (e) {}
    if (done) return;

    // Wire the teaser's buttons, then reveal it (clamp + veil the card).
    if (this.tourStartBtn) {
      this.tourStartBtn.addEventListener('click', function () {
        self._teaserDismiss();
        self._tourStart({ source: 'teaser' });
      });
    }
    if (this.tourSkipBtn) {
      this.tourSkipBtn.addEventListener('click', function () {
        try { localStorage.setItem(TOUR_KEY, '1'); } catch (e) {}
        if (window.SaifTrack) window.SaifTrack('practice_tour_skipped', { step_index: -1 });
        self._teaserDismiss();
      });
    }
    this._teaserShow();
  };

  // Clamp + veil the studio card and reveal the invitation. `inert` on the
  // card's other children is progressive enhancement — keyboard focus can't
  // wander behind the veil where inert is supported; harmless where it isn't.
  PracticeStudio.prototype._teaserShow = function () {
    if (!this.tourTeaserEl) return;
    this.container.classList.add('saif-practice--tour-veiled');
    var kids = this.container.children;
    this._tourInertKids = [];
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k === this.tourTeaserEl) continue;
      if (k.setAttribute && !(k.hasAttribute && k.hasAttribute('inert'))) {
        try { k.setAttribute('inert', ''); this._tourInertKids.push(k); } catch (e) {}
      }
    }
    this.tourTeaserEl.hidden = false;
    // On mobile the hero + intro stack above push the teaser (and its
    // "Show me around" CTA) below the fold — bring the studio into view so the
    // button is reachable, but only when it isn't already comfortably visible.
    var self = this;
    setTimeout(function () {
      try {
        var btn = self.tourStartBtn;
        if (!btn || !self.container) return;
        var r = btn.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.top < 64 || r.bottom > vh - 16) {
          self.container.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      } catch (e) {}
    }, 60);
    if (window.SaifTrack) window.SaifTrack('practice_tour_teaser_shown', {});
  };

  // Hide the invitation and release the clamp/veil + inert. Used by both the
  // Start path (→ tour) and the Skip path (→ plain open studio).
  PracticeStudio.prototype._teaserDismiss = function () {
    if (this.tourTeaserEl) this.tourTeaserEl.hidden = true;
    this.container.classList.remove('saif-practice--tour-veiled');
    if (this._tourInertKids) {
      for (var i = 0; i < this._tourInertKids.length; i++) {
        try { this._tourInertKids[i].removeAttribute('inert'); } catch (e) {}
      }
      this._tourInertKids = null;
    }
  };

  // The spotlight itinerary. Each step's `target` is a resolver run AT START
  // (targets that are hidden/absent drop cleanly to null); `copy` is one plain
  // line; `radius` is the hole's corner rounding (999 = pill). The finish step
  // has no target — its hole collapses to a 0×0 point at viewport centre (the
  // 200vmax box-shadow still dims the whole page).
  /**
   * The lesson Toot gives. He is the narrator of every line below, so the copy
   * is first person and one idea per step — a teacher says one thing per breath.
   * If the mascot is absent the same words still read as the studio talking.
   */
  // Read on every call: the tour is built long after load, and caching the bridge at parse
  // time would bind whatever existed before saif-i18n ran.
  function TI18N() { return window.SaifI18n || {}; }
  PracticeStudio.prototype._tourSteps = function () {
    var self = this;
    var locked = this._studioStatus && this._studioStatus.access === 'locked';
    var finishBody = locked
      ? (TI18N().tour_finish_locked || 'Tap the ? up top any time to see this again.')
      : (TI18N().tour_finish_free || 'Your first 3 takes are free. Tap the ? up top to see this again.');
    // Opening a sheet is a SIDE EFFECT and must not live in a target resolver:
    // resolvers are re-run on every scroll/resize remeasure, and the sheet needs
    // 300ms to slide in before anything can be measured against it.
    var openTools = function () { if (self.sheetOpenV2) self.sheetOpenV2('tools'); };
    var tool = function (name) {
      return function () {
        return self.container.querySelector('[data-tool-tile="' + name + '"]') ||
               self.container.querySelector('#SaifPracticeDockMore');
      };
    };
    return [
      {
        // Who he is and what the next minute holds. No spotlight: nothing to
        // point at yet, and an introduction should be looked at, not followed.
        id: 'hello',
        target: null,
        heading: (TI18N().tour_hello_heading || 'Hello — I\'m Toot.'),
        copy: (TI18N().tour_hello_copy || 'Welcome to the Practice Studio Tootorial! Let me show you around.'),
        radius: 0
      },
      {
        id: 'refbar',
        target: function () { return self.container.querySelector('.saif-practice__refbar'); },
        copy: (TI18N().tour_refbar || 'Pick what to follow: a drill, a maqam clip, or your own audio.'),
        radius: 14
      },
      {
        // The stage is the biggest change in the studio and the old tour never
        // mentioned it — the canvas only used to exist mid-take.
        id: 'stage',
        target: function () {
          return self.container.querySelector('.saif-practice__canvas-wrap') ||
                 self.container.querySelector('#SaifPracticeLive');
        },
        copy: (TI18N().tour_stage || 'Sing inside the gold band. Drag underneath to move through the clip.'),
        radius: 14
      },
      {
        id: 'record',
        target: function () {
          // Hidden until entitlement resolves on a gated page; the dock is the
          // fallback (the old tab strip is display:none in this layout).
          if (self.takeButtonsEl && !self.takeButtonsEl.hidden) return self.takeButtonsEl;
          return self.container.querySelector('#SaifPracticeDockMore') ||
                 self.container.querySelector('.saif-practice__controls');
        },
        copy: (TI18N().tour_record || 'Record here. I go quiet while your mic is on.'),
        radius: 18
      },
      {
        // Centred: the score panel does not exist until a take has been made.
        id: 'score',
        target: null,
        copy: (window.SaifI18n && window.SaifI18n.tour_score) ||
          'You get a score out of 10, and your line over the reciter\'s.',
        radius: 0
      },
      {
        id: 'tune',
        before: openTools,
        target: tool('tuner'),
        copy: (TI18N().tour_tuner || 'One note off? Tune it.'),
        radius: 999
      },
      {
        id: 'drill',
        before: openTools,
        target: tool('drill'),
        copy: (TI18N().tour_drill || 'A whole phrase off? Drill it, note by note.'),
        radius: 999
      },
      {
        id: 'range',
        before: openTools,
        target: tool('range'),
        copy: (TI18N().tour_range || 'Set your range once — every clip moves into your key.'),
        radius: 999
      },
      {
        id: 'finish',
        before: function () { if (self.sheetCloseV2) self.sheetCloseV2(); },
        target: function () { return null; },
        heading: (TI18N().tour_finish_heading || 'That\'s the studio.'),
        copy: finishBody,
        finish: true,
        radius: 0
      }
    ];
  };

  /**
   * Toot presents the tour when he is on the page. Everything about him is
   * optional: he ships only on the templates that carry the studio, the visitor
   * can hide him, and his script is deferred — so this resolves the API fresh on
   * every call and simply does nothing when he is not there. The tour must never
   * depend on the mascot to work.
   */
  /**
   * Where Toot rests, by studio STATE. One home he always returns to; every
   * other seat is a deliberate exception with a reason:
   *   home     the branch on the stage — the default, and where he returns
   *   tutorial the ? button, when the tour has never been taken (he points at
   *            the thing worth clicking instead of decorating a corner)
   *   refbar   when there is no clip yet, because that is the real first blocker
   *   score    beside a fresh score, where he sings the tier melody
   * Returns false when he is absent or busy; callers never depend on it.
   */
  PracticeStudio.prototype._syncTootTree = function (reseat) {
    var scene = this.container && this.container.closest('.saif-practice-scene');
    if (!scene) return false;
    var card = this.container;
    var tree = card.querySelector('.saif-practice__tree');
    var seat = card.querySelector('.saif-practice__toot-seat');
    var live = card.querySelector('.saif-practice__live');
    var canvas = card.querySelector('.saif-practice__canvas-wrap');
    if (!tree || !seat || !live || !canvas) {
      scene.removeAttribute('data-tree-positioned');
      return false;
    }

    // The paywall veils the branch but Toot's fixed layer would otherwise sit
    // above that veil. Hide only the mascot layer this studio hid, then restore
    // it when access returns; user-preference hiding remains untouched.
    var tootLayer = document.getElementById('toot-layer');
    if (this.lockEl && !this.lockEl.hidden) {
      scene.removeAttribute('data-tree-positioned');
      if (tootLayer && !tootLayer.hidden) {
        tootLayer.hidden = true;
        tootLayer.setAttribute('data-studio-tree-hidden', 'true');
      }
      return false;
    }
    if (tootLayer && tootLayer.getAttribute('data-studio-tree-hidden') === 'true') {
      tootLayer.hidden = false;
      tootLayer.removeAttribute('data-studio-tree-hidden');
    }

    // Use local (untransformed) card pixels. The tree is deliberately inside
    // the card now: overflow:hidden makes the rounded Studio edge its wall and
    // guarantees that neither the trunk nor canopy can escape onto the page.
    var treeWidth = tree.offsetWidth;
    var cardWidth = card.offsetWidth;
    var cardHeight = card.offsetHeight;
    if (!(treeWidth > 0 && cardWidth > 0 && cardHeight > 0)) {
      // Lesson access gating can hide the card after the tree was positioned.
      // Clear readiness so its absolute sibling cannot remain orphaned.
      scene.removeAttribute('data-tree-positioned');
      return false;
    }
    var cr = card.getBoundingClientRect();
    var lr = live.getBoundingClientRect();
    var sy = cr.height / cardHeight;
    if (!(sy > 0) || !isFinite(sy)) sy = 1;

    var scale = treeWidth / 200;
    // The level limb begins at artwork y=175. It normally rests 8px above the
    // dark stage. At full desktop size the artwork's remaining trunk is a few
    // pixels taller than the 280px canvas, so increase only that gap enough to
    // make the trunk and canvas finish on the same baseline. Narrower responsive
    // trees keep the close 8px perch instead of pushing Toot down into the chart.
    // Artwork x=80 centers him in the reserved status/replay pocket.
    var liveTop = (lr.top - cr.top) / sy;
    var branchGap = 8;
    if (treeWidth >= 175 && canvas.offsetHeight > 0) {
      var tailHeight = tree.offsetHeight - (175 * scale);
      branchGap = Math.max(branchGap, tailHeight - canvas.offsetHeight + 1);
    }
    var seatX = 80;
    var top = liveTop - branchGap - (175 * scale);
    top = Math.round(top * 100) / 100;

    tree.style.left = 'auto';
    tree.style.right = '0px';
    tree.style.top = top + 'px';
    // The mobile chart is intentionally shorter than the desktop chart, while
    // the tree artwork keeps enough height to preserve its branch and canopy.
    // Crop only the trunk that would continue below the dark chart. Position
    // nudges cannot solve this: they either move Toot or reopen the top gap.
    var canvasRect = canvas.getBoundingClientRect();
    var canvasBottom = (canvasRect.bottom - cr.top) / sy;
    var treeBottom = top + tree.offsetHeight;
    // One local pixel of overlap hides the antialiased seam introduced by the
    // responsive card scale; visually this lands exactly on the chart edge.
    var cropBottom = Math.max(0, Math.round((treeBottom - canvasBottom + 1) * 100) / 100);
    var treeClip = cropBottom > 0.5 ? 'inset(0 0 ' + cropBottom + 'px 0)' : '';
    tree.style.clipPath = treeClip;
    tree.style.webkitClipPath = treeClip;
    seat.style.left = 'auto';
    seat.style.right = (Math.round((treeWidth - seatX * scale - 1) * 100) / 100) + 'px';
    seat.style.top = (Math.round((top + 175 * scale - 1) * 100) / 100) + 'px';
    scene.setAttribute('data-tree-positioned', 'true');

    // Moving the marker does not trigger Toot's own scroll/resize glue when a
    // header wraps or a reference chip changes height, so refresh a home perch.
    if (reseat && this._tootSeatState === 'home' && !this._openSheetV2) {
      var T = window.SaifToot;
      if (T && typeof T.perchOn === 'function' && document.getElementById('toot-layer')) {
        try { T.perchOn(seat, { rx: 0.5, ry: 0.5 }); } catch (e) {}
      }
    }
    return true;
  };

  PracticeStudio.prototype._watchTootTree = function () {
    if (this._tootTreeWatching) return;
    this._tootTreeWatching = true;
    var self = this;
    var queue = function () {
      if (self._tootTreeFrame) return;
      var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
      self._tootTreeFrame = raf(function () {
        self._tootTreeFrame = 0;
        try { self._syncTootTree(true); } catch (e) {}
      });
    };
    this._syncTootTree(false);
    if (window.ResizeObserver) {
      try {
        this._tootTreeObserver = new ResizeObserver(queue);
        this._tootTreeObserver.observe(this.container);
        var live = this.container.querySelector('.saif-practice__live');
        if (live) this._tootTreeObserver.observe(live);
      } catch (e) {}
    }
  };

  PracticeStudio.prototype._tootSeat = function (where) {
    var T = window.SaifToot;
    if (!T || typeof T.perchOn !== 'function') return false;
    var tootLayer = document.getElementById('toot-layer');
    if (!tootLayer || tootLayer.hidden) return false;
    // He is ~88px tall and stands ON the seat line, so his body extends that
    // far UPWARD. Anything in the studio's top row therefore pushes him outside
    // the card and over the page copy — which is exactly what the owner caught.
    // The branch is the ONLY seat with room above it, so it is the only resting
    // place. Results use a stable marker shared by all three tab panels; Toot's
    // feet meet that panel edge exactly and never drift onto a hidden card.
    where = where === 'score' ? 'score' : (where === 'edge' ? 'edge' : (where === 'tool' ? 'tool' : 'home'));
    var scene = this.container.closest('.saif-practice-scene');
    if (where === 'home') this._syncTootTree(false);
    var edgeSeat = scene && scene.querySelector('.saif-practice__top-toot-seat');
    /* The gray outer rim has a fixed CSS position (top:0). An earlier lesson
       clamp wrote an inline top based on the moving result sheet; after close
       that stale number pointed into the black pitch graph. Always restore the
       actual Studio-rim geometry before choosing any seat. */
    if (edgeSeat && edgeSeat.style) edgeSeat.style.top = '';
    var toolSheet = where === 'tool' && this._openSheetV2
      ? this.container.querySelector('#' + ({
          tuner: 'SaifSheetTuner',
          drill: 'SaifSheetDrill',
          range: 'SaifSheetRange',
          history: 'SaifSheetHistory'
        }[this._openSheetV2] || ''))
      : null;
    var el = where === 'home'
      ? (scene && scene.querySelector('.saif-practice__toot-seat'))
      : (where === 'edge'
        ? edgeSeat
        : (where === 'tool' ? toolSheet : this.container.querySelector('.saif-practice__result-toot-seat')));
    if (!el) return false;
    var placed = T.perchOn(el, where === 'home'
      ? { rx: 0.5, ry: 0.5 }
      : (where === 'edge'
        ? { rx: 0.5, ry: 0.5 }
        : (where === 'tool' ? { rx: 0.78, ry: 0 } : { rx: 0.5, ry: 0 })));
    if (placed) this._tootSeatState = where;
    return placed;
  };

  /** The resting seat for the CURRENT state — used on load and after any
   *  transient seat (a sheet, a score) is done with him. */
  /**
   * Is the branch actually LOOKABLE-AT right now? Present and sized is not
   * enough: an open sheet covers the stage, and #toot-layer is fixed above it,
   * so flying home while a sheet is up rendered him on top of the sheet perched
   * on nothing (the owner's screenshot). elementFromPoint answers "is anything
   * covering this spot" without hard-coding which overlay might be.
   */
  PracticeStudio.prototype._tootBranchVisible = function () {
    this._syncTootTree(false);
    var scene = this.container.closest('.saif-practice-scene');
    var tree = scene && scene.querySelector('.saif-practice__tree');
    var seat = scene && scene.querySelector('.saif-practice__toot-seat');
    if (!tree || !seat) return false;
    var tr = tree.getBoundingClientRect();
    var r = seat.getBoundingClientRect();
    if (!(tr.width > 4 && tr.height > 4 && r.width > 0 && r.height > 0)) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var vw = document.documentElement.clientWidth || window.innerWidth;
    if (tr.bottom < 0 || tr.top > vh || tr.right < 0 || tr.left > vw) return false;
    if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) return false;
    if (this.lockEl && !this.lockEl.hidden) return false;
    try {
      var x = Math.min(vw - 1, Math.max(0, r.left + r.width * 0.5));
      var y = Math.min(vh - 1, Math.max(0, r.top + r.height * 0.5));
      var hit = document.elementFromPoint(x, y);
      if (hit && hit.closest && hit.closest('.saif-practice__sheet, .saif-practice__scrim, .saif-practice__lock')) return false;
    } catch (e) {}
    return true;
  };

  PracticeStudio.prototype._tootHome = function (forceRedirect) {
    /* The global mascot owns its first entrance. Reference/status startup can
       request home while that entrance is still hidden or already airborne;
       ignoring those duplicate requests prevents a second path from replacing
       the side flight underneath him. */
    var layer = document.getElementById('toot-layer');
    if (layer && layer.hasAttribute('data-toot-init-pending')) return false;
    try {
      var d = typeof window.__dbg === 'function' ? window.__dbg() : null;
      if (!forceRedirect && d && d.state === 'fly') return false;
    } catch (e) {}
    // Never fly him onto a branch he'd be sitting in front of rather than on.
    // The Studio top edge is the deliberate holding seat until home is visible.
    if (!this._tootBranchVisible()) return this._tootSeat('edge');
    return this._tootSeat('home');
  };

  PracticeStudio.prototype._tootTeach = function (fn, a, b, c) {
    try {
      var T = window.SaifToot;
      if (!T || typeof T[fn] !== 'function') return false;
      var tootLayer = document.getElementById('toot-layer');
      if (!tootLayer || tootLayer.hidden) return false;   // hidden by preference or a veiled seat
      T[fn](a, b, c);
      return true;
    } catch (e) { return false; }
  };

  // Public entry point (teaser Start + replay "?"). Defers the actual build
  // until the entitlement status has resolved on a gated page, so the record
  // step measures a visible target instead of the still-hidden take buttons.
  PracticeStudio.prototype._tourStart = function (opts) {
    var self = this;
    opts = opts || {};
    if (this._tour || this._tourStarting) return; // already running / mid-start
    this._tourStarting = true;
    // Race the entitlement status against a short timer so a slow/stalled mobile
    // request can't leave the (already un-veiled) studio bare for up to 12s.
    // Building early is safe: the record step falls back to .saif-practice__controls
    // when the take buttons are still hidden, and the remeasure re-places the hole
    // once status lands.
    var ready = this.gated
      ? Promise.race([Promise.resolve(this._studioStatusPromise), new Promise(function (r) { setTimeout(r, 1800); })])
      : Promise.resolve();
    ready.then(function () {
      self._tourStarting = false;
      self._tourBuild(opts);
    }, function () {
      self._tourStarting = false;
      self._tourBuild(opts);
    });
  };

  // Build the overlay DOM once, wire all handlers, and show the first step.
  PracticeStudio.prototype._tourBuild = function (opts) {
    var self = this;
    if (this._tour) return;
    var steps = this._tourSteps();

    var root = document.createElement('div');
    root.className = 'saif-practice-tour';

    var hole = document.createElement('div');
    hole.className = 'saif-practice-tour__hole';
    root.appendChild(hole);

    var card = document.createElement('div');
    card.className = 'saif-practice-tour__card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', (window.SaifI18n && window.SaifI18n.tour_aria) || 'Practice Studio tour');
    // Read the current step's copy when the card is (re)focused each step.
    card.setAttribute('aria-describedby', 'saif-practice-tour-text');
    card.setAttribute('tabindex', '-1');

    var text = document.createElement('p');
    text.className = 'saif-practice-tour__text';
    text.id = 'saif-practice-tour-text';
    card.appendChild(text);

    var dots = document.createElement('div');
    dots.className = 'saif-practice-tour__dots';
    var dotEls = [];
    for (var d = 0; d < steps.length; d++) {
      var dot = document.createElement('span');
      dot.className = 'saif-practice-tour__dot';
      dots.appendChild(dot);
      dotEls.push(dot);
    }
    card.appendChild(dots);

    var nav = document.createElement('div');
    nav.className = 'saif-practice-tour__nav';

    var skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'saif-practice-tour__skip';
    skipBtn.textContent = (window.SaifI18n && window.SaifI18n.tour_skip) || 'Skip';

    var backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'saif-practice-tour__btn saif-practice-tour__btn--ghost';
    backBtn.textContent = (window.SaifI18n && window.SaifI18n.tour_back) || 'Back';

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'saif-practice-tour__btn';
    nextBtn.textContent = 'Next';

    nav.appendChild(skipBtn);   // DOM order = tab order: Skip, Back, Next
    nav.appendChild(backBtn);
    nav.appendChild(nextBtn);
    card.appendChild(nav);

    root.appendChild(card);
    document.body.appendChild(root);

    var t = {
      root: root, hole: hole, card: card, text: text,
      dots: dotEls, skipBtn: skipBtn, backBtn: backBtn, nextBtn: nextBtn,
      steps: steps, index: 0, placed: false,
      // Never restore focus to <body> — on the teaser Start path the trigger is
      // already hidden by the time we build, so activeElement is body; fall back
      // to the always-present replay "?" instead.
      returnFocus: (document.activeElement && document.activeElement !== document.body && document.activeElement.focus)
        ? document.activeElement : this.tourReplayBtn,
      rafId: 0, settleTimer: 0, inertEls: [],
      onKey: null, onResize: null, onScroll: null, onWheel: null, onTouch: null
    };
    this._tour = t;
    // A locked studio temporarily reveals the real controls for the tour. Do
    // that before teachStart so a Toot layer hidden with the paywall is restored
    // in time for him to present his own walkthrough.
    if (this.lockEl) this.lockEl.hidden = true;
    try { this._syncTootTree(true); } catch (e) {}
    // Toot takes the floor: he rises above the tour scrim and his idle wandering
    // and tips stand down for the duration.
    t.toot = this._tootTeach('teachStart');
    if (t.toot) {
      card.classList.add('saif-practice-tour__card--toot');
      var who = document.createElement('span');
      who.className = 'saif-practice-tour__who';
      who.textContent = (window.SaifI18n && window.SaifI18n.tour_who) || 'Toot';
      card.insertBefore(who, text);
    }

    // Confine assistive tech + Tab focus to the tour: inert every other
    // top-level node so aria-modal is real, not advisory (browse-mode readers
    // can't wander the dimmed page behind the overlay). Released in _tourEnd.
    var bodyKids = document.body.children;
    for (var bk = 0; bk < bodyKids.length; bk++) {
      var bnode = bodyKids[bk];
      if (bnode === root) continue;
      if (bnode.setAttribute && !(bnode.hasAttribute && bnode.hasAttribute('inert'))) {
        try { bnode.setAttribute('inert', ''); t.inertEls.push(bnode); } catch (e) {}
      }
    }

    // A small chirp answers each move. This is navigation feedback, not the
    // ambient chattering that was removed — he is quiet between presses.
    backBtn.addEventListener('click', function () {
      if (t.index > 0) { self._tootTeach('teachTick'); self._tourShowStep(t.index - 1); }
    });
    nextBtn.addEventListener('click', function () {
      self._tootTeach('teachTick');
      if (t.index >= t.steps.length - 1) self._tourEnd('finished');
      else self._tourShowStep(t.index + 1);
    });
    skipBtn.addEventListener('click', function () {
      self._tourEnd('skipped');
    });

    // Keyboard: Esc = skip, arrows navigate, Tab is trapped across the three
    // (visible) controls so focus can't escape the modal.
    t.onKey = function (e) {
      if (e.key === 'Escape') { e.preventDefault(); self._tourEnd('skipped'); return; }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        self._tootTeach('teachTick');
        if (t.index >= t.steps.length - 1) self._tourEnd('finished');
        else self._tourShowStep(t.index + 1);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (t.index > 0) { self._tootTeach('teachTick'); self._tourShowStep(t.index - 1); }
        return;
      }
      if (e.key === 'Tab') {
        var order = [t.skipBtn, t.backBtn, t.nextBtn].filter(function (b) {
          return b && !b.hidden && !b.disabled;
        });
        if (!order.length) return;
        var first = order[0];
        var last = order[order.length - 1];
        var active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || active === t.card) { e.preventDefault(); last.focus(); }
        } else {
          if (active === last) { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener('keydown', t.onKey, true);

    // Re-measure the current step on resize/scroll (rAF-throttled). The
    // --no-anim class suppresses the .28s morph so the hole tracks instantly.
    var remeasure = function () {
      if (!self._tour || t.rafId || t.autoScrolling) return; // don't fight our own step scroll
      t.rafId = requestAnimationFrame(function () {
        t.rafId = 0;
        if (!self._tour || t.autoScrolling) return;
        t.root.classList.add('saif-practice-tour--no-anim');
        var step = t.steps[t.index];
        var target = null;
        try { target = step.target ? step.target() : null; } catch (e) { target = null; }
        if (target && !self._tourVisible(target)) target = null;
        self._tourPlace(target, step);
        requestAnimationFrame(function () {
          if (t.root) t.root.classList.remove('saif-practice-tour--no-anim');
        });
      });
    };
    t.onResize = remeasure;
    t.onScroll = remeasure;
    window.addEventListener('resize', t.onResize, true);
    window.addEventListener('scroll', t.onScroll, true);
    // iOS Safari's collapsing URL/toolbar (and pinch-zoom) move the VISUAL
    // viewport without firing window scroll/resize — a fixed spotlight measured
    // from getBoundingClientRect then drifts by the toolbar height. Track the
    // visual viewport so the hole re-aligns with its target.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', t.onResize);
      window.visualViewport.addEventListener('scroll', t.onScroll);
    }

    // Block page scroll under the tour without toggling overflow on <html>
    // (which would jump the layout by the scrollbar width).
    t.onWheel = function (e) { e.preventDefault(); };
    // Block page scroll under the tour, but let the bottom-sheet CARD scroll if
    // its content overflows (long finish copy / iOS Larger Text) so the exits stay reachable.
    t.onTouch = function (e) { if (t.card && t.card.contains(e.target)) return; e.preventDefault(); };
    root.addEventListener('wheel', t.onWheel, { passive: false });
    root.addEventListener('touchmove', t.onTouch, { passive: false });

    // Hide the paywall lock card while the tour runs so it doesn't show through
    // the dimmer, AND preview the real record controls (revealed even for a locked
    // visitor) so the "record" step can spotlight them. _lockStudioUI is guarded on
    // this._tour, and _tourEnd re-applies the gate (re-hiding these) afterwards.
    if (this.lockEl) this.lockEl.hidden = true;
    if (this.takeButtonsEl) this.takeButtonsEl.hidden = false;
    if (this.recordHint) this.recordHint.hidden = false;
    if (window.SaifTrack) window.SaifTrack('practice_tour_started', { source: opts.source || 'teaser' });
    this._tourShowStep(0);
  };

  // Is this target actually laid out (not display:none / zero-box)?
  PracticeStudio.prototype._tourVisible = function (el) {
    if (!el || el.hidden) return false;
    var r = el.getBoundingClientRect();
    return (r.width > 0 || r.height > 0);
  };

  // Advance to step i: update the card chrome, scroll the target to centre,
  // wait for the scroll to settle, then position the hole + card and focus.
  PracticeStudio.prototype._tourShowStep = function (i) {
    var self = this;
    var t = this._tour;
    if (!t) return;
    if (i < 0) i = 0;
    if (i > t.steps.length - 1) i = t.steps.length - 1;
    t.index = i;
    var step = t.steps[i];

    // Card text: the finish step gets a bold heading line + body; the rest a
    // single plain line.
    while (t.text.firstChild) t.text.removeChild(t.text.firstChild);
    if (step.heading) {
      var h = document.createElement('strong');
      h.className = 'saif-practice-tour__heading';
      h.textContent = step.heading;
      t.text.appendChild(h);
    }
    var line = document.createElement('span');
    line.className = 'saif-practice-tour__line';
    line.textContent = step.copy;
    t.text.appendChild(line);

    for (var d = 0; d < t.dots.length; d++) {
      t.dots[d].className = (d === i)
        ? 'saif-practice-tour__dot saif-practice-tour__dot--active'
        : 'saif-practice-tour__dot';
    }
    t.backBtn.hidden = (i === 0);
    var last = (i === t.steps.length - 1);
    t.nextBtn.textContent = last ? (window.SaifI18n && window.SaifI18n.tour_start_practicing) || 'Start practicing' : (window.SaifI18n && window.SaifI18n.tour_next) || 'Next';
    // Announce position so a screen reader conveys progress on each re-focus.
    t.card.setAttribute('aria-label', 'Practice Studio tour — step ' + (i + 1) + ' of ' + t.steps.length);

    // Side effects (open/close a sheet) run ONCE per step, never from the
    // remeasure path. Everything below then waits for the resulting animation.
    try { if (step.before) step.before(); } catch (e) {}

    // Resolve the target (may drop out on a gated/hidden variant).
    var target = null;
    try { target = step.target ? step.target() : null; } catch (e) { target = null; }
    if (target && !self._tourVisible(target)) target = null;

    // Compute where the target should END UP (viewport-centred, clear of the
    // sticky header and — on phones — the bottom-sheet card), place the fixed
    // spotlight THERE, then smooth-scroll the target into it. Because the hole is
    // fixed, the element scrolls to meet it: one motion, no jitter, no per-frame
    // tracking, no competing scrollBy.
    // A sheet that just opened is mid-slide (300ms, translateY(102%) -> 0), so
    // measuring now reads the tile BELOW the card and puts the hole there. Wait
    // for the animation to end (with a timeout so a cancelled/absent animation
    // can't hang the step), then place once, correctly.
    var sheetEl = self._openSheetV2
      ? self.container.querySelector('.saif-practice__sheet:not([hidden])')
      : null;
    var anim = sheetEl && sheetEl.getAnimations ? sheetEl.getAnimations() : null;
    if (anim && anim.length) {
      var done = false;
      var go = function () {
        if (done) return; done = true;
        if (!self._tour || self._tour.index !== i) return;
        self._tourPlaceStep(i, step, target);
      };
      Promise.all(anim.map(function (a) { return a.finished.catch(function () {}); })).then(go);
      setTimeout(go, 420);
      return;
    }
    self._tourPlaceStep(i, step, target);
  };

  /* The measure-scroll-settle half of _tourShowStep, split out so it can be
   * deferred until an opening sheet has stopped moving. */
  PracticeStudio.prototype._tourPlaceStep = function (i, step, target) {
    var self = this;
    var t = this._tour;
    if (!t) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var HEADER = 90, PAD = 8, GAP = 16;
    if (target) {
      var rect = target.getBoundingClientRect();
      var desiredTop = Math.round((vh - rect.height) / 2);
      var isSheet = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
      if (isSheet) {
        var sheetH = t.card ? Math.round(t.card.getBoundingClientRect().height) : 220;
        // Shopify's cookie-consent dialog is fixed to the bottom and sits OVER
        // the tour: it hid Skip/Back/Next on desktop and the entire card (and
        // Toot) on a phone — and the tour auto-starts for precisely the
        // first-time visitors who are shown that banner.
        var maxTop = vh - sheetH - GAP - rect.height - self._tourConsentInset();
        if (desiredTop > maxTop) desiredTop = maxTop;
      }
      if (desiredTop < HEADER + PAD) desiredTop = HEADER + PAD;
      var scrollDelta = Math.round(rect.top - desiredTop);
      // Place hole + card at the destination (morphs .28s from the previous step).
      self._tourPlace(target, step, { top: desiredTop, left: rect.left, width: rect.width, height: rect.height });
      if (Math.abs(scrollDelta) > 1) {
        t.autoScrolling = true; // suppress the remeasure tracker while WE drive the scroll
        try { window.scrollBy({ top: scrollDelta, left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' }); }
        catch (e) { try { window.scrollBy(0, scrollDelta); } catch (e2) {} }
      }
    } else {
      self._tourPlace(null, step); // finish: collapse to viewport centre
    }

    // Once the scroll settles: re-enable tracking, one corrective placement (in
    // case the scroll landed a hair off / the iOS toolbar moved), focus + log.
    self._tourSettle(target, function () {
      if (!self._tour || self._tour.index !== i) return;
      t.autoScrolling = false;
      self._tourPlace(target, step);
      // Placed AFTER the settle, so he flies to where the card has actually
      // landed rather than to where it was on its way. He sings only on the two
      // beats worth a note — hello and goodbye — never over the lesson itself.
      if (t.toot) self._tootTeach('teachAt', t.card, target, { first: i === 0, last: !!step.finish });
      try { t.card.focus({ preventScroll: true }); } catch (e) { try { t.card.focus(); } catch (e2) {} }
      if (window.SaifTrack) window.SaifTrack('practice_tour_step_viewed', { step: step.id, index: i });
    });
  };

  // Poll a target's rect until it stops moving (two equal reads) or 700ms.
  // 80ms ticks. A null target (finish) resolves immediately.
  PracticeStudio.prototype._tourSettle = function (target, cb) {
    var self = this;
    var t = this._tour;
    if (t && t.settleTimer) { clearTimeout(t.settleTimer); t.settleTimer = 0; }
    if (!target) { cb(); return; }
    var start = Date.now();
    var lastTop = null;
    var tick = function () {
      if (!self._tour) return;
      var r = target.getBoundingClientRect();
      var top = Math.round(r.top);
      var elapsed = Date.now() - start;
      if ((lastTop !== null && lastTop === top) || elapsed >= 700) {
        self._tour.settleTimer = 0;
        cb();
        return;
      }
      lastTop = top;
      self._tour.settleTimer = setTimeout(tick, 80);
    };
    self._tour.settleTimer = setTimeout(tick, 80);
  };

  /** Height of Shopify's consent dialog when it is actually on screen, else 0. */
  PracticeStudio.prototype._tourConsentInset = function () {
    try {
      var el = document.querySelector('.shopify-pc__banner, #shopify-pc__banner');
      if (!el) return 0;
      var r = el.getBoundingClientRect();
      if (!(r.height > 0) || r.top > (window.innerHeight || 0)) return 0;
      return Math.round(Math.max(0, (window.innerHeight || 0) - r.top));
    } catch (e) { return 0; }
  };

  // Position the spotlight hole (padded 8px around the target) and the card.
  // Card sits below the hole if there's room, else above, else pinned; at
  // ≤600px the CSS bottom sheet wins and inline top/left are cleared.
  PracticeStudio.prototype._tourPlace = function (target, step, forceRect) {
    var t = this._tour;
    if (!t) return;
    // First paint: snap into place (no diagonal slide from the CSS default
    // corner); every subsequent step keeps the .28s morph.
    var firstPlace = !t.placed;
    if (firstPlace) {
      t.placed = true;
      t.root.classList.add('saif-practice-tour--no-anim');
      requestAnimationFrame(function () {
        if (t.root) t.root.classList.remove('saif-practice-tour--no-anim');
      });
    }
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var HEADER = 90; // keep the hole clear of the ~90px sticky site header
    var PAD = 8;
    var GAP = 16;

    var holeTop, holeLeft, holeW, holeH, radius;
    if (target) {
      var r = forceRect || target.getBoundingClientRect();
      holeTop = r.top - PAD;
      holeLeft = r.left - PAD;
      holeW = r.width + PAD * 2;
      holeH = r.height + PAD * 2;
      if (holeTop < HEADER) { // clamp below the sticky header
        var delta = HEADER - holeTop;
        holeTop = HEADER;
        holeH = Math.max(0, holeH - delta);
      }
      radius = (typeof step.radius === 'number') ? step.radius : 12;
      t.hole.className = 'saif-practice-tour__hole';
    } else {
      // Finish: collapse to a 0×0 point at viewport centre, ring off so no
      // stray gold dot shows — only the dimmer remains.
      holeTop = vh / 2;
      holeLeft = vw / 2;
      holeW = 0;
      holeH = 0;
      radius = 0;
      t.hole.className = 'saif-practice-tour__hole saif-practice-tour__hole--collapsed';
    }
    t.hole.style.top = holeTop + 'px';
    t.hole.style.left = holeLeft + 'px';
    t.hole.style.width = holeW + 'px';
    t.hole.style.height = holeH + 'px';
    t.hole.style.borderRadius = (radius >= 999 ? 999 : radius) + 'px';
    t.card.style.opacity = '1'; // fade the card in on first placement (was 0)

    // Toot RIDES this card (he sits on its top edge), so the studio OWNS its
    // position exactly as without him — placed next to the spotlight, clamped to
    // the viewport. He tracks wherever it lands. (Earlier he sat on the spotlit
    // element and the card hung off his beak; that floated him on empty space and
    // detached the bubble — the owner's screenshots.)

    // Single-source the breakpoint with the CSS @media so JS positioning and the
    // CSS bottom-sheet layout can't disagree (window.innerWidth shifts under iOS pinch-zoom).
    var isSheet = (window.matchMedia && window.matchMedia('(max-width: 600px)').matches) || vw <= 600;
    if (isSheet) {
      // Bottom sheet handled by CSS — clear any stale desktop inline position.
      // (_tourShowStep already scrolls the target clear of the sheet, so no lift.)
      t.card.style.top = '';
      t.card.style.left = '';
      t.card.style.right = '';
      t.card.style.bottom = '';
      return;
    }

    var cardRect = t.card.getBoundingClientRect();
    var cardW = cardRect.width || 360;
    var cardH = cardRect.height || 160;
    // Usable height stops ABOVE Shopify's consent dialog — otherwise the card's
    // Skip/Back/Next row sits underneath it and the tour cannot be advanced.
    var BOTTOM = vh - this._tourConsentInset();
    var top, left;
    if (!target) {
      top = Math.max(HEADER, (BOTTOM - cardH) / 2);
      left = (vw - cardW) / 2;
    } else {
      var below = holeTop + holeH + GAP;
      var above = holeTop - GAP - cardH;
      if (below + cardH <= BOTTOM - GAP) top = below;
      else if (above >= HEADER) top = above;
      else top = Math.max(HEADER, BOTTOM - cardH - GAP);
      left = holeLeft + holeW / 2 - cardW / 2;
    }
    if (left < GAP) left = GAP;
    if (left + cardW > vw - GAP) left = vw - GAP - cardW;
    if (top < HEADER) top = HEADER;
    t.card.style.left = left + 'px';
    t.card.style.top = top + 'px';
    t.card.style.right = 'auto';
    t.card.style.bottom = 'auto';
  };

  // Tear down the tour: remove listeners + DOM, persist "seen" (finish AND
  // skip), fire analytics, restore focus, and on finish nudge the Record btn.
  PracticeStudio.prototype._tourEnd = function (reason) {
    var t = this._tour;
    if (!t) return;
    this._tour = null;
    // BEFORE the card is removed: a perched bird measured against a detached
    // node reads 0x0 and would be flung to the top-left corner.
    if (t.toot) this._tootTeach('teachEnd');
    // The tour card is about to be removed — send him home rather than leaving
    // him riding a detached node.
    var _selfEnd = this;
    setTimeout(function () { try { _selfEnd._tootHome(); } catch (e) {} }, 900);

    if (t.onKey) document.removeEventListener('keydown', t.onKey, true);
    if (t.onResize) window.removeEventListener('resize', t.onResize, true);
    if (t.onScroll) window.removeEventListener('scroll', t.onScroll, true);
    if (window.visualViewport) {
      if (t.onResize) window.visualViewport.removeEventListener('resize', t.onResize);
      if (t.onScroll) window.visualViewport.removeEventListener('scroll', t.onScroll);
    }
    if (t.onWheel && t.root) t.root.removeEventListener('wheel', t.onWheel, { passive: false });
    if (t.onTouch && t.root) t.root.removeEventListener('touchmove', t.onTouch, { passive: false });
    if (t.rafId) cancelAnimationFrame(t.rafId);
    if (t.settleTimer) clearTimeout(t.settleTimer);
    if (t.root && t.root.parentNode) t.root.parentNode.removeChild(t.root);

    // Restore the rest of the page (undo the inert applied in _tourBuild).
    if (t.inertEls) {
      for (var ie = 0; ie < t.inertEls.length; ie++) {
        try { t.inertEls[ie].removeAttribute('inert'); } catch (e) {}
      }
      t.inertEls = null;
    }

    // The tour hid the paywall lock on build — restore the gate now (re-shows the
    // lock if this visitor is locked; no-op otherwise). this._tour is already null,
    // so _lockStudioUI is no longer guarded.
    if (this.gated && this._studioStatus && this._studioStatus.access === 'locked') {
      this._lockStudioUI();
    }

    try { localStorage.setItem(TOUR_KEY, '1'); } catch (e) {}

    if (window.SaifTrack) {
      if (reason === 'finished') window.SaifTrack('practice_tour_completed', {});
      else window.SaifTrack('practice_tour_skipped', { step_index: t.index });
    }

    if (t.returnFocus && t.returnFocus.focus) {
      try { t.returnFocus.focus(); } catch (e) {}
    }

    if (reason === 'finished' && this.recordBtn && !this.recordBtn.hidden) {
      try {
        this.recordBtn.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } catch (e) {}
      // Two gentle pulses to land the eye (disabled under reduced motion).
      this.recordBtn.classList.remove('saif-practice__record-btn--nudge');
      void this.recordBtn.offsetWidth;
      this.recordBtn.classList.add('saif-practice__record-btn--nudge');
      try { this.recordBtn.focus({ preventScroll: true }); } catch (e) {}
    }
  };

  /* ==============================================
     INIT
     ============================================== */
  function init() {
    var container = document.getElementById('SaifPracticeStudio');
    if (!container) {
      console.warn('[saif-practice] #SaifPracticeStudio container not found — studio will not initialize');
      return;
    }

    slog('[saif-practice] initializing practice studio');
    var studio = new PracticeStudio(container);
    try { studio._watchTootTree(); } catch (e) {}
    // Give layout (and the deferred mascot script) a beat, then refresh an
    // already-settled seat. Never issue a second home command during the page
    // entrance: it cancels the side flight and looks like a random teleport.
    setTimeout(function () {
      try {
        var d = typeof window.__dbg === 'function' ? window.__dbg() : null;
        if (d && d.state === 'perch') studio._tootHome();
      } catch (e) {}
    }, 1200);
    window.SaifPracticeStudio = studio; // expose for debugging

    // Deep link: /pages/practice-studio#range opens the vocal-range tab
    // directly (used by the picker's "find your range" CTA and future surfaces).
    if (window.location && window.location.hash === '#range') {
      try { studio.switchMode('range'); } catch (e) {}
    }

    // Kick the saved-range load on every page load (previously only the picker
    // did, so the Range tab always showed the idle start screen). When it lands
    // and the user is already sitting on an idle Range tab (deep-link #range or
    // a fast tab click), upgrade to the saved-result home.
    loadUserRange().then(function (range) {
      if (!range) return;
      if (studio.currentMode === 'range' && studio.rangeState === 'idle' && !studio.rangeActive) {
        studio._rangeShowSaved(range);
      }
    });

    // Pre-analyze reference audio if available — but defer to idle time
    // so the page is interactive immediately. Without this defer, the
    // initial page load downloads + decodes the full MP3 + runs pitch
    // detection synchronously, which feels significantly slower than the
    // maqam library (which only fetches metadata).
    if (studio.refUrl) {
      slog('[saif-practice] scheduling reference analysis:', studio.refUrl);
      // Lesson/standalone pages preset data-reference-url without ever calling
      // loadReference — kick off the range-fit transpose here too.
      try { studio._refShiftInit(studio.refUrl); } catch (e) {}
      var preAnalyze = function () { studio.analyzeReference(); };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(preAnalyze, { timeout: 2500 });
      } else {
        setTimeout(preAnalyze, 600);
      }
    } else {
      slog('[saif-practice] no reference URL — karaoke target will not show');
    }
  }

  /* ==============================================
     REFERENCE PICKER + CUSTOM AUDIO UPLOAD
     Pick any course drill or Maqam-478 clip as the studio reference from
     ANY page the studio is embedded on; or upload your own audio for a
     local-only practice session (no submit-for-review).
     ============================================== */
  (function () {
    var CLIP_BASE = 'https://api.saifvoice.net/maqam-clips/';
    function drillList() {
      var out = [];
      function add(short, label, mod, drill) {
        out.push({
          label: label + ' · Module ' + mod + (drill ? ' · Drill ' + drill : ''),
          file: 'drill-' + short + '-mod' + mod + (drill ? '-' + drill : '') + '.mp3?v=2'
        });
      }
      // July 2026 re-recorded set: one clip per module, full coverage
      // (Module 1s exist now; Foundation Mod 7's five drills are one clip).
      // Keep in sync with DRILL_REFS in saif-courses.js.
      [1, 2, 3, 4, 5, 6, 7, 8].forEach(function (m) { add('foundation', 'Foundation', m); });
      [1, 2, 3, 4, 5, 6, 7, 8].forEach(function (m) { add('precision', 'Precision', m); });
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach(function (m) { add('mastery', 'Mastery', m); });
      return out;
    }
    function maqamGroups() {
      var manifest = pitchManifestPromise && pitchManifestPromise._resolvedValue;
      var groups = {};
      if (manifest && manifest.items) {
        Object.keys(manifest.items).forEach(function (name) {
          var m = name.match(/^maqam-478-(.+)-(\d+)\.mp3$/);
          if (!m) return;
          (groups[m[1]] = groups[m[1]] || []).push({ n: parseInt(m[2], 10), file: name });
        });
        Object.keys(groups).forEach(function (k) { groups[k].sort(function (a, b) { return a.n - b.n; }); });
      }
      return groups;
    }
    function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

    // Always resolve the live instance at call time — capturing it once
    // proved stale on gated pages where the export lands after wiring.
    function liveStudio() {
      var s = window.SaifPracticeStudio;
      return (s && typeof s.loadReference === 'function') ? s : null;
    }
    function wire() {
      var openBtn = document.getElementById('SaifPracticeRefChange');
      var dialog = document.getElementById('SaifPracticeRefPicker');
      if (!openBtn || !dialog || openBtn._wired) return !!(openBtn && openBtn._wired);
      openBtn._wired = true;

      var closeBtn = document.getElementById('SaifPracticeRefPickerClose');
      var drillsEl = document.getElementById('SaifRefPickerDrills');
      var maqamSel = document.getElementById('SaifRefPickerMaqam');
      var clipsEl = document.getElementById('SaifRefPickerClips');
      var uploadBtn = document.getElementById('SaifPracticeRefUploadBtn');
      var uploadInput = document.getElementById('SaifPracticeRefUpload');
      var note = document.getElementById('SaifPracticeRefCustomNote');

      // ---- Find-your-range prompt (the transpose tunes any clip to your key) ----
      // No fit filter / badges / shelf: every maqam suits every voice — it's just
      // the key, and the reference auto-transposes to the student's range.
      loadUserRange(); // kick the saved-range load so the prompt can hide once set

      // One-line CTA shown only when NO range is saved (the shelf replaces it once one is).
      var rangeCta = document.createElement('button');
      rangeCta.type = 'button';
      rangeCta.className = 'saif-practice__refpicker-rangecta';
      rangeCta.textContent = (window.SaifI18n && window.SaifI18n.refpicker_range_cta) || 'Find your vocal range → every clip tunes to your key';
      rangeCta.hidden = true;
      rangeCta.addEventListener('click', function () {
        try { dialog.close(); } catch (e) {}
        var st = liveStudio();
        if (st) st.switchMode('range');
      });
      if (drillsEl && drillsEl.parentNode) drillsEl.parentNode.insertBefore(rangeCta, drillsEl);
      function syncRangeCta() { rangeCta.hidden = !!getUserRangeSync(); }

      function choose(url, title) {
        var st = liveStudio();
        if (!st) return;
        st.loadReference(url, { clipTitle: title });
        if (st.refSection) st.refSection.hidden = false;
        try { dialog.close(); } catch (e) {}
      }

      // (Removed: "★ For your voice" fit shelf, the "Only show clips that fit me"
      //  filter, per-family fit counts, badges and dimming — every maqam is shown
      //  plainly and auto-tunes to the student's key.)

      // Every clip in a maqam, listed plainly — no fit ranking, badges or dimming.
      function renderClips(maqam) {
        var groups = maqamGroups();
        var clips = groups[maqam] || [];
        clipsEl.innerHTML = '';
        clips.forEach(function (c) {
          var label = cap(maqam) + ' — clip ' + (c.n + 1);
          var wrap = document.createElement('span');
          wrap.className = 'saif-practice__refpicker-clipwrap';
          wrap.appendChild(createPreviewButton(CLIP_BASE + c.file, label));
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'saif-practice__refpicker-clip';
          b.textContent = 'Clip ' + (c.n + 1);
          b.setAttribute('data-ref-file', c.file);
          b.addEventListener('click', function () { choose(CLIP_BASE + c.file, label); });
          wrap.appendChild(b);
          clipsEl.appendChild(wrap);
        });
      }

      // Every maqam, alphabetical, plain "(N clips)" labels — no fit ranking.
      function renderMaqamArea(preferSelected) {
        var groups = maqamGroups();
        var names = Object.keys(groups);
        maqamSel.innerHTML = '';
        if (!names.length) {
          var o0 = document.createElement('option');
          o0.textContent = (window.SaifI18n && window.SaifI18n.maqam_loading) || 'Maqam list loading — reopen in a moment';
          maqamSel.appendChild(o0);
          clipsEl.innerHTML = '';
          return;
        }
        names.sort();
        names.forEach(function (n) {
          var o = document.createElement('option');
          o.value = n; o.textContent = cap(n) + ' (' + groups[n].length + ' clips)';
          maqamSel.appendChild(o);
        });
        maqamSel.value = (preferSelected && names.indexOf(preferSelected) !== -1) ? preferSelected : names[0];
        renderClips(maqamSel.value);
      }

      // Rebuild on open and again once the async range load settles (so the
      // "find your range" prompt hides as soon as a saved range lands).
      function renderPicker() {
        syncRangeCta();
        renderMaqamArea(maqamSel.value);
      }

      openBtn.addEventListener('click', function () {
        // Drills — rebuilt every open; NEVER badged (they suit every voice).
        drillsEl.innerHTML = '';
        drillList().forEach(function (d) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'saif-practice__refpicker-clip';
          b.textContent = d.label;
          b.addEventListener('click', function () { choose(CLIP_BASE + d.file, d.label); });
          drillsEl.appendChild(b);
        });
        renderPicker();                      // range may already be cached (sync)
        loadUserRange().then(renderPicker);  // first open: re-render once it lands
        // Self-heal if the pitch manifest is still loading (empty maqam
        // list): re-render when it lands instead of asking the user to reopen.
        if (!Object.keys(maqamGroups()).length) {
          loadPitchManifest().then(function () { if (dialog.open) renderPicker(); });
        }
        // Opening the picker from inside a sheet (drill empty state, range
        // "browse clips", the no-reference banner) would stack the dialog
        // ::backdrop on top of the sheet scrim. One scrim at a time.
        try {
          var _st = window.SaifPracticeStudio;
          if (_st && _st._openSheetV2) _st.sheetCloseV2();
        } catch (e) {}
        try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
      });
      maqamSel.addEventListener('change', function () { renderClips(maqamSel.value); });
      if (closeBtn) closeBtn.addEventListener('click', function () { previewStop(); try { dialog.close(); } catch (e) { dialog.removeAttribute('open'); } });
      dialog.addEventListener('close', function () { previewStop(); });
      dialog.addEventListener('click', function (ev) { if (ev.target === dialog) { try { dialog.close(); } catch (e) {} } });

      /* ---- Custom audio upload ---- */
      if (uploadBtn && uploadInput) {
        uploadBtn.addEventListener('click', function () { uploadInput.click(); });
        uploadInput.addEventListener('change', function () {
          var file = uploadInput.files && uploadInput.files[0];
          uploadInput.value = '';
          if (!file) return;
          if (file.size > 15 * 1024 * 1024) { uploadBtn.textContent = (window.SaifI18n && window.SaifI18n.upload_too_large) || 'File too large (max 15 MB)'; return; }
          var url = URL.createObjectURL(file);
          // Analyze directly via AudioContext. We used to gate this behind a
          // detached <audio> element's `loadedmetadata` event (to read duration
          // first), but that event can silently never fire for blob: URLs / on
          // some mobile browsers — so an upload produced no pitch graph at all.
          // decodeAudioData is reliable and exposes the duration from the decoded
          // buffer, so the length check now lives inside analyzeCustom.
          analyzeCustom(file, url);
        });
      }

      function analyzeCustom(file, url) {
        var st = liveStudio();
        if (!st) { uploadBtn.textContent = (window.SaifI18n && window.SaifI18n.upload_studio_loading) || 'Studio still loading — try again'; return; }
        uploadBtn.disabled = true;
        uploadBtn.textContent = (window.SaifI18n && window.SaifI18n.upload_analyzing) || 'Analyzing 0%…';
        // Point the studio at the file immediately (player works while the
        // pitch analysis streams in) — analysis is skipped here and done in
        // async chunks below so long files can never freeze the page.
        st.loadReference(url, { clipTitle: file.name, custom: true, skipAnalysis: true });
        if (st.refSection) st.refSection.hidden = false;
        var ctx = new (window.AudioContext || window.webkitAudioContext)();
        file.arrayBuffer()
          .then(function (buf) { return ctx.decodeAudioData(buf); })
          .then(function (audioBuffer) {
            // Length guard, now derived from the decoded buffer (reliable) rather
            // than a flaky <audio> metadata probe.
            if (audioBuffer.duration > 300) {
              uploadBtn.disabled = false;
              uploadBtn.textContent = (window.SaifI18n && window.SaifI18n.upload_too_long) || 'Too long — max 5 minutes';
              try { ctx.close(); } catch (e) {}
              try { URL.revokeObjectURL(url); } catch (e) {}
              return;
            }
            var data = toMonoNormalized(audioBuffer);
            var sr = audioBuffer.sampleRate;
            // Playback loudness fix: the uploaded file plays through the <audio>
            // element at its NATIVE level, so a quietly-recorded upload was quiet
            // to practice against (curated clips are loudness-normalized ~0.13 RMS).
            // Re-point the reference at an RMS-matched WAV so "your own audio" is
            // the proper volume. Only the playback SOURCE changes — the analysis
            // (`data`, peak-normalized) and the pitch graph are untouched, and
            // there's no gain node / graph, so this can't reintroduce mic bleed.
            var playUrl = toPlaybackWavUrl(audioBuffer, 0.13);
            if (playUrl) {
              var oldUrl = url;
              url = playUrl; // the analysis-commit check below compares refUrl === url
              st.loadReference(url, { clipTitle: file.name, custom: true, skipAnalysis: true });
              if (st.refSection) st.refSection.hidden = false;
              try { URL.revokeObjectURL(oldUrl); } catch (e) {}
            }
            // BATCH kept small so each synchronous slice is ~30–60ms even on a
            // slow phone (120 was ~1s+ of blocking per yield); the extra
            // setTimeout(0) hops cost a little wall-clock for far smoother UI.
            var CHUNK = 2048, STEP = 1024, BATCH = 24;
            var series = [];
            var i = 0;
            function step() {
              var end = Math.min(i + BATCH * STEP, data.length - CHUNK);
              for (; i < end; i += STEP) {
                var freq = detectPitch(data.slice(i, i + CHUNK), sr);
                if (freq > 0) series.push({ time: i / sr, freq: freq });
              }
              if (i < data.length - CHUNK) {
                uploadBtn.textContent = 'Analyzing ' + Math.round((i / data.length) * 100) + '%…';
                setTimeout(step, 0); // yield to keep the page responsive
              } else {
                // Only commit if the user hasn't switched reference meanwhile
                var stDone = liveStudio();
                if (stDone && stDone.refUrl === url) {
                  stDone.refPitchData = medianSmoothPitch(series);
                  stDone._refreshMaqamLabel();
                  // Mirror the preset-clip path (loadReference → analyzeReference
                  // → _drillRefreshAvailability): now that reference pitch data
                  // exists, activate the note-by-note Drill and redraw any
                  // reference-dependent graph so an upload behaves like a picked clip.
                  try { if (stDone.drillSection) stDone._drillRefreshAvailability(); } catch (e) {}
                  // Immediately show the analyzed reference contour so the user
                  // sees the pitch graph of their uploaded audio right away.
                  try { stDone._showReferencePreview(); } catch (e) {}
                }
                uploadBtn.disabled = false;
                uploadBtn.textContent = (window.SaifI18n && window.SaifI18n.upload_use_own) || '⬆ Use your own audio';
                try { ctx.close(); } catch (e) {}
              }
            }
            step();
          })
          .catch(function () {
            uploadBtn.disabled = false;
            uploadBtn.textContent = (window.SaifI18n && window.SaifI18n.upload_decode_fail) || 'Could not decode that file';
            try { ctx.close(); } catch (e) {}
          });
      }
      return true;
    }
    function boot() {
      var tries = 0;
      (function poll() { if (!wire() && tries++ < 40) setTimeout(poll, 300); })();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
